/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. See License.txt in the project root for
 * license information.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator 2.3.31.0
 * Changes may cause incorrect behavior and will be lost if the code is
 * regenerated.
 */

/* eslint-disable */

import { A365BackendModel, SharedModel, IAdvancedFilter, InactiveStatusType, ActivityStateType, type FactoryTrustMIMode, IParquetPorperties } from 'shell-models-lib';
import { IDataFlowQuery, IDataFlowSink } from './adapters/dataflow/IDataflow';
import { MapperConnectionType } from 'app/model/adfDataMapper/mapperConnectionReference';
import { type ArtifactJobStatus } from '@trident/data-access-artifact-common/src/lib/models/schedule';

// all handcrafted changes to this module are marked with the comment 'handcrafted'.

export module Model {

	export import Resource = SharedModel.Resource;
	export import SubResource = SharedModel.SubResource;
	export import ParameterSpecification = SharedModel.ParameterSpecification;
	export import IntegrationRuntimeReference = SharedModel.IntegrationRuntimeReference;
	export import LinkedService = SharedModel.LinkedService;
	export import LinkedServiceResource = SharedModel.LinkedServiceResource;
	export import ArtifactReference = SharedModel.ArtifactReference;
	export import ManagedVirtualNetworkResource = SharedModel.ManagedVirtualNetworkResource;
	export import ManagedPrivateEndpointResource = SharedModel.ManagedPrivateEndpointResource;
	export import ManagedPrivateEndpointModel = SharedModel.ManagedPrivateEndpointModel;
	export import SecretBase = SharedModel.SecretBase;
	export import SecureString = SharedModel.SecureString;
	export import DataFlowExternalComputeProperties = SharedModel.DataFlowExternalComputeProperties;
	export import LinkedServiceReference = SharedModel.LinkedServiceReference;
	export import AzureKeyVaultSecretReference = SharedModel.AzureKeyVaultSecretReference;
	export import CredentialReference = SharedModel.CredentialReference;
	export import RepoConfigurationType = SharedModel.RepoConfigurationType;
	export import FactoryRepoConfiguration = SharedModel.RepoConfiguration;

	export import Workspace = A365BackendModel.Workspace;
	export import SqlPool = A365BackendModel.SqlPool;
	export import SparkCompute = A365BackendModel.SparkCompute;

	// handcrafted
	export interface CloudError {
		code: string,
		message: string,
		target?: string,
		details?: Array<CloudError>
	}

	/**
	 * @class
	 * Initializes a new instance of the Expression class.
	 * @constructor
	 * Azure Data Factory expression definition.
	 *
	 * @member {string} value Expression value.
	 */
	export interface Expression {
		type: string; // handcrafted
		value: string;
	}

	export interface IFolder { // handcrafted
		name?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the FactoryCmkSettings class.
	 * @constructor
	 * Factory's CMK information.
	 *
	 * @member {string} keyName Key name.
	 * @member {string} vaultBaseUrl Key vault base url
	 * @member {string} keyVersion Key version
	 * @member {string} KeyName Key name.
	 * @member {string} VaultBaseUrl Key vault base url
	 * @member {string} KeyVersion Key version
	 */
	// handcrafted
	export interface FactoryEncryption {
		keyName: string;
		vaultBaseUrl: string;
		keyVersion: string;
		identity?: any;
		// handle incorrectly TitleCase generated by Azure portal
		KeyName?: string;
		VaultBaseUrl?: string;
		KeyVersion?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the GlobalParameterSpecification class.
	 * @constructor
	 * Definition of a single parameter for an entity.
	 *
	 * @member {string} type Parameter type. Possible values include: 'Object',
	 * 'String', 'Int', 'Float', 'Bool', 'Array'
	 * @member {object} [value] Value of parameter.
	 */
	// handcrafted
	export interface GlobalParameterSpecification {
		type: string;
		value?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the GlobalParametersResource class.
	 * @constructor
	 * Definition of all global parameters. Used for ARM template only
	 *
	 * @member {string} name Resource name.
	 * @member {object} properties Properties of the global parameters.
	 *
	 */
	// handcrafted
	export interface GlobalParametersARMResource {
		name: string;
		properties: { [parameterName: string]: GlobalParameterSpecification };
	}

	// handcrafted
	export interface PurviewConfiguration {
		purviewResourceId?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the FactoryProperties class.
	 * @constructor
	 * Factory resource properties.
	 *
	 * @member {string} [provisioningState] Factory provisioning state, example
	 * Succeeded.
	 * @member {date} [createTime] Time the factory was created in ISO8601 format.
	 * @member {string} [version] Version of the factory.
	 * @member {object} [repoConfiguration] Git repo information of the factory.
	 * @member {string} [repoConfiguration.accountName] Account name.
	 * @member {string} [repoConfiguration.repositoryName] Rrepository name.
	 * @member {string} [repoConfiguration.collaborationBranch] Collaboration
	 * branch.
	 * @member {string} [repoConfiguration.rootFolder] Root folder.
	 * @member {string} [repoConfiguration.lastCommitId] Last commit id.
	 * @member {string} [repoConfiguration.type] Polymorphic Discriminator
	 * @member {object} [globalParameters] List of globalParameters for the factory.
	 */
	// TODO: TASK 265560: Remove vstsConfiguration when Madrid no longer call preview version API to fetchFactory/updateFactory/updateRepo
	export interface FactoryProperties {
		readonly provisioningState?: string;
		readonly createTime?: Date;
		version?: string;
		repoConfiguration?: SharedModel.RepoConfiguration;
		purviewConfiguration?: PurviewConfiguration; // handcrafted
		vstsConfiguration?: SharedModel.RepoConfiguration; // handcrafted
		globalParameters?: { [propertyName: string]: GlobalParameterSpecification };
		globalConfigurations?: { [configuration: string]: any };
		encryption?: FactoryEncryption;
		trustModeClaimForMi?: { value: FactoryTrustMIMode, editable: boolean };
	}

	/**
	 * @class
	 * Initializes a new instance of the Factory class.
	 * @constructor
	 * Factory resource type.
	 *
	 * @member {object} [identity] Managed service identity of the factory.
	 * @member {uuid} [identity.principalId] The principal id of the identity.
	 * @member {uuid} [identity.tenantId] The client tenant id of the identity.
	 * @member {object} [properties] Properties of the factory.
	 * @member {string} [properties.provisioningState] Factory provisioning state,
	 * example Succeeded.
	 * @member {date} [properties.createTime] Time the factory was created in
	 * ISO8601 format.
	 * @member {string} [properties.version] Version of the factory.
	 * @member {object} [properties.repoConfiguration] Git repo information of the
	 * factory.
	 * @member {string} [properties.repoConfiguration.accountName] Account name.
	 * @member {string} [properties.repoConfiguration.repositoryName] Rrepository
	 * name.
	 * @member {string} [properties.repoConfiguration.collaborationBranch]
	 * Collaboration branch.
	 * @member {string} [properties.repoConfiguration.rootFolder] Root folder.
	 * @member {string} [properties.repoConfiguration.lastCommitId] Last commit id.
	 * @member {string} [properties.repoConfiguration.type] Polymorphic
	 * Discriminator
	 */
	export interface Factory extends SharedModel.Resource {
		properties?: FactoryProperties;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
		tags?: { [propertyName: string]: string }; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the ManagedVirtualNetworkReference class.
	 * @constructor
	 * Managed Virtual Network reference type.
	 *
	 * @member {string} referenceName Reference managed virtual network name.
	 * @member {object} [parameters] Arguments for managed virtual network.
	 */
	export interface ManagedVirtualNetworkReference {
		referenceName: string;
		type: string;   // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntime class.
	 * @constructor
	 * Azure Data Factory nested object which serves as a compute resource for
	 * activities.
	 *
	 * @member {string} [description] Integration runtime description.
	 * @member {string} type Polymorphic Discriminator
	 */
	export interface IntegrationRuntime {
		description?: string;
		type: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
		managedVirtualNetwork?: ManagedVirtualNetworkReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeResource class.
	 * @constructor
	 * Integration runtime resource type.
	 *
	 * @member {object} properties Integration runtime properties.
	 * @member {string} [properties.description] Integration runtime description.
	 * @member {string} [properties.type] Polymorphic Discriminator
	 */
	export interface IntegrationRuntimeResource extends SharedModel.SubResource {
		properties: IntegrationRuntime;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeStatus class.
	 * @constructor
	 * Integration runtime status.
	 *
	 * @member {string} [dataFactoryName] The data factory name which the
	 * integration runtime belong to.
	 * @member {string} [state] The state of integration runtime. Possible values
	 * include: 'Initial', 'Stopped', 'Started', 'Starting', 'Stopping',
	 * 'NeedRegistration', 'Online', 'Limited', 'Offline', 'AccessDenied'
	 * @member {string} type Polymorphic Discriminator
	 */
	export interface IntegrationRuntimeStatus {
		readonly dataFactoryName?: string;
		readonly state?: string;
		type: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeStatusResponse class.
	 * @constructor
	 * Integration runtime status response.
	 *
	 * @member {string} [name] The integration runtime name.
	 * @member {object} properties Integration runtime properties.
	 * @member {string} [properties.dataFactoryName] The data factory name which
	 * the integration runtime belong to.
	 * @member {string} [properties.state] The state of integration runtime.
	 * Possible values include: 'Initial', 'Stopped', 'Started', 'Starting',
	 * 'Stopping', 'NeedRegistration', 'Online', 'Limited', 'Offline',
	 * 'AccessDenied'
	 * @member {string} [properties.type] Polymorphic Discriminator
	 */
	export interface IntegrationRuntimeStatusResponse {
		readonly name?: string;
		properties: IntegrationRuntimeStatus;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeStatusListResponse class.
	 * @constructor
	 * A list of integration runtime status.
	 *
	 * @member {array} value List of integration runtime status.
	 * @member {string} [nextLink] The link to the next page of results, if any
	 * remaining results exist.
	 */
	export interface IntegrationRuntimeStatusListResponse {
		value: IntegrationRuntimeStatusResponse[];
		nextLink?: string;
	}

	export interface IntegrationRuntimeCreateExpressSHIRInstallLinkParameters {
		Gateway: string;
		GatewayKey: string;
		ScopeId?: string;
		UseExecutableFile?: boolean;
	}

	export interface IntegrationRuntimeCreateExpressSHIRInstallLinkResponse {
		requestId: string;
		clickonceLink: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the UpdateIntegrationRuntimeRequest class.
	 * @constructor
	 * Update integration runtime request.
	 *
	 * @member {string} [autoUpdate] Enables or disables the auto-update feature of
	 * the self-hosted integration runtime. See
	 * https://go.microsoft.com/fwlink/?linkid=854189. Possible values include:
	 * 'On', 'Off'
	 * @member {string} [updateDelayOffset] The time offset (in hours) in the day,
	 * e.g., PT03H is 3 hours. The integration runtime auto update will happen on
	 * that time.
	 */
	export interface UpdateIntegrationRuntimeRequest {
		autoUpdate?: string;
		updateDelayOffset?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the UpdateIntegrationRuntimeNodeRequest class.
	 * @constructor
	 * Update integration runtime node request.
	 *
	 * @member {number} [concurrentJobsLimit] The number of concurrent jobs
	 * permitted to run on the integration runtime node. Values between 1 and
	 * maxConcurrentJobs(inclusive) are allowed.
	 */
	export interface UpdateIntegrationRuntimeNodeRequest {
		concurrentJobsLimit?: number;
	}

	/**
	 * @class
	 * Initializes a new instance of the LinkedIntegrationRuntimeRequest class.
	 * @constructor
	 * Data factory name for linked integration runtime request.
	 *
	 * @member {string} linkedFactoryName The data factory name for linked
	 * integration runtime.
	 */
	export interface LinkedIntegrationRuntimeRequest {
		factoryName: string; // handcrafted
	}

	// handcrafted
	export interface EnableIntegrationRuntimeInteractiveQueryRequest {
		autoTerminationMinutes: number;
	}

	/**
	 * @class
	 * Initializes a new instance of the VariableSpecification class.
	 * @constructor
	 * Definition of a single variable for an entity.
	 *
	 * @member {string} type Variable type.
	 * @member {object} [defaultValue] Default value of variable.
	 */
	export interface VariableSpecification {
		type: string;
		defaultValue?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the LibraryVariableSpecification class.
	 * @constructor
	 * Definition of a single library variable for an entity.
	 *
	 * @member {string} type Variable type.
	 * @member {string} scope Variable scope.
	 * @member {string} variableName Variable name.
	 * @member {string} libraryName Variable library.
	 */
	export interface LibraryVariableSpecification {
		type: string;
		variableName: string;
		libraryName: string
	}

	/**
	 * @class
	 * Initializes a new instance of the Dataset class.
	 * @constructor
	 * The Azure Data Factory nested object which identifies data within different
	 * data stores, such as tables, files, folders, and documents.
	 *
	 * @member {string} [description] Dataset description.
	 * @member {object} [structure] Columns that define the structure of the
	 * dataset. Type: array (or Expression with resultType array), itemType:
	 * DatasetDataElement.
	 * @member {object} linkedServiceName Linked service reference.
	 * @member {string} [linkedServiceName.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [linkedServiceName.parameters] Arguments for LinkedService.
	 * @member {object} [parameters] Parameters for dataset.
	 * @member {array} [annotations] List of tags that can be used for describing
	 * the Dataset.
	 * @member {string} type Polymorphic Discriminator
	 */
	export interface Dataset {
		description?: string;
		folder?: IFolder; // handcrafted
		structure?: any;
		schema?: any; // handcrafted
		linkedServiceName?: SharedModel.LinkedServiceResource | SharedModel.LinkedServiceReference; // handcrafted
		connectionProperties?: { [propertyName: string]: any };
		sqlPool?: SharedModel.ArtifactReference; // handcrafted
		parameters?: { [propertyName: string]: SharedModel.ParameterSpecification };
		annotations?: any[];
		version?: string;
		type: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DatasetResource class.
	 * @constructor
	 * Dataset resource type.
	 *
	 * @member {object} properties Dataset properties.
	 * @member {string} [properties.description] Dataset description.
	 * @member {object} [properties.structure] Columns that define the structure of
	 * the dataset. Type: array (or Expression with resultType array), itemType:
	 * DatasetDataElement.
	 * @member {object} [properties.linkedServiceName] Linked service reference.
	 * @member {string} [properties.linkedServiceName.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [properties.linkedServiceName.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [properties.parameters] Parameters for dataset.
	 * @member {array} [properties.annotations] List of tags that can be used for
	 * describing the Dataset.
	 * @member {string} [properties.type] Polymorphic Discriminator
	 */
	export interface DatasetResource extends SharedModel.SubResource {
		properties: Dataset;
	}

	export interface ExternalReferences {
		connection: string;
		requiresMtls?: boolean;
	}

	export interface AirflowEntityProperties {
		description?: string;
		folder?: IFolder;
		subType?: string;
		scope?: string;
		fileContent?: string;
		dataFactoryId?: string;
	}

	export interface AirflowEntityResource {
		properties: AirflowEntityProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ActivityDependency class.
	 * @constructor
	 * Activity dependency information.
	 *
	 * @member {string} activity Activity name.
	 * @member {array} dependencyConditions Match-Condition for the dependency.
	 */
	export interface ActivityDependency {
		activity: string;
		dependencyConditions: string[];
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
 * @class
 * Initializes a new instance of the ActivityUserProperty class.
 * @constructor
 * Activity dependency information.
 *
 * @member {string} name Property name.
 * @member {array} value The value.
 */
	export interface ActivityUserProperty {
		name: string;
		value?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the Activity class.
	 * @constructor
	 * A pipeline activity.
	 *
	 * @member {string} name Activity name.
	 * @member {string} [description] Activity description.
	 * @member {array} [dependsOn] Activity depends on condition.
	 * @member {object} [userProperties] Activity user properties.
	 * @member {string} type Polymorphic Discriminator
	 */
	export interface Activity {
		name: string;
		description?: string;
		state?: ActivityStateType;
		onInactiveMarkAs?: InactiveStatusType;
		dependsOn?: ActivityDependency[];
		userProperties?: ActivityUserProperty[]; // handcrafted
		type: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	// handcrafted
	export interface DataDependencyTypeProperties {
		timeout?: string;
		sleep?: number;
		dataset?: {
			referenceName: string;
			type: string;
		};
		datasetSettings?: any;
		storeSettings?: any;
		formatSettings?: any;
	}

	// handcrafted
	export interface DataDependency {
		type?: 'data';
		name: string;
		typeProperties: DataDependencyTypeProperties;
	}

	// handcrafted
	export interface DependencyParentReference {
		referenceName: string;
		type?: string;
		offset?: string;
		size?: string;
	}

	// handcrafted
	export interface DependencyMatchOn {
		type: 'label' | 'windowCoverage';
		key?: string;
		windowSize?: string;
		anchor?: string;
	}

	// handcrafted
	export interface DependencyRequire {
		key: string;
		op: string;
		value: string | number | string[];
	}

	// handcrafted
	export interface PipelineDependencyTypeProperties {
		dependencyName: string;
		dependsOn: DependencyParentReference[];
		matchOn: DependencyMatchOn;
		require?: DependencyRequire;
	}

	// handcrafted
	export interface PipelineDependency {
		type?: 'pipeline';
		typeProperties: PipelineDependencyTypeProperties;
	}

	// handcrafted
	export type Dependency = DataDependency | PipelineDependency;

	/**
	 * @class
	 * Initializes a new instance of the PipelineModel class.
	 * @constructor
	 * A data factory pipeline.
	 *
	 * @member {string} [description] The description of the pipeline.
	 * @member {array} [activities] List of activities in pipeline.
	 * @member {object} [parameters] List of parameters for pipeline.
	 * @member {number} [concurrency] The max number of concurrent runs for the
	 * pipeline.
	 * @member {object} [policy] How long a pipeline needs to
	 * run for an alert to trigger.
	 * @member {array} [annotations] List of tags that can be used for describing
	 * the Pipeline.
	 * @member {array} [runDimensions] List of run dimensions for the pipeline for use with chaining triggers
	 */

	export interface PipelineModel {
		description?: string;
		activities?: Activity[];
		parameters?: { [propertyName: string]: SharedModel.ParameterSpecification };
		variables?: { [propertyName: string]: VariableSpecification };
		libraryVariables?: { [propertyName: string]: LibraryVariableSpecification };
		concurrency?: number;
		policy?: PipelinePolicy;
		folder?: IFolder; // handcrafted
		annotations?: any[];
		runDimensions?: string[];
		dependencies?: Dependency[]; // handcrafted
		lastPublishTime?: Date;
		triggerSettings?: any;
		lastModifiedByObjectId?: string;
		extraTridentInfo?: string;
		dependencyConnectionId?: string;
		dataFactoryId?: string;
	}

	// handcrafted
	export interface PipelineSettingsModel {
		resourceId?: string;
		settings?: any;
		activitiesSettings?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the PipelineResource class.
	 * @constructor
	 * Pipeline resource type.
	 *
	 * @member {object} properties Properties of the pipeline.
	 * @member {string} [properties.description] The description of the pipeline.
	 * @member {array} [properties.activities] List of activities in pipeline.
	 * @member {object} [properties.parameters] List of parameters for pipeline.
	 * @member {number} [properties.concurrency] The max number of concurrent runs
	 * for the pipeline.
	 * @member {object} [properties.policy] How long a pipeline needs to
	 * run for an alert to trigger.
	 * @member {array} [properties.annotations] List of tags that can be used for
	 * describing the Pipeline.
	 */
	export interface PipelineResource extends SharedModel.SubResource {
		properties: PipelineModel;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	// handcrafted
	export interface PipelineSettingsResource extends SharedModel.SubResource {
		properties: PipelineSettingsModel;
	}

	/**
	 * @class
	 * Initializes a new instance of the Trigger class.
	 * @constructor
	 * Azure data factory nested object which contains information about creating
	 * pipeline run
	 *
	 * @member {string} [description] Trigger description.
	 * @member {string} [runtimeState] Indicates if trigger is running or not.
	 * Updated when Start/Stop APIs are called on the Trigger. Possible values
	 * include: 'Started', 'Stopped', 'Disabled'
	 * @member {string} type Polymorphic Discriminator
	 */
	export interface Trigger {
		pipelines?: TriggerPipelineReference[]; // handcrafted
		description?: string;
		runtimeState?: string; // handcrafted, Remove readonly, ADF backend doesn't honor this value, it's just used to let GIT Repository persists the status
		type: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the TriggerResource class.
	 * @constructor
	 * Trigger resource type.
	 *
	 * @member {object} properties Properties of the trigger.
	 * @member {string} [properties.description] Trigger description.
	 * @member {string} [properties.runtimeState] Indicates if trigger is running
	 * or not. Updated when Start/Stop APIs are called on the Trigger. Possible
	 * values include: 'Started', 'Stopped', 'Disabled'
	 * @member {string} [properties.type] Polymorphic Discriminator
	 */
	export interface TriggerResource extends SharedModel.SubResource {
		properties: Trigger;
	}

	/**
	 * @class
	 * Initializes a new instance of the CreateRunResponse class.
	 * @constructor
	 * Response body with a run identifier.
	 *
	 * @member {string} runId Identifier of a run.
	 */
	export interface CreateRunResponse {
		runId: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the ErrorResponse class.
	 * @constructor
	 * The object that defines the structure of an Azure Data Factory response.
	 *
	 * @member {string} code Error code.
	 * @member {string} message Error message.
	 * @member {string} [target] Property name/path in request associated with
	 * error.
	 * @member {array} [details] Array with additional error details.
	 */
	export interface ErrorResponse {
		code: string;
		message: string;
		target?: string;
		details?: ErrorResponse[];
	}

	/**
	 * @class
	 * Initializes a new instance of the PbiErrorResponse class.
	 * @constructor
	 * The object that defines the structure of a PBI error response.
	 *
	 * @member {string} code Error code.
	 * @member {string} pbi.error Error details.
	 */
	export interface PbiErrorResponse {
		error: {
			code: string;
			'pbi.error': {
				code: string;
				details: [{
					code: string;
					detail: {
						type: string;
						value: string;
					}
				}];
			};
		};
		headers: {
			requestid: string;
		};
		status: string;
		statusText: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the FactoryVSTSConfiguration class.
	 * @constructor
	 * Factory's VSTS repo information.
	 *
	 * @member {string} projectName VSTS project name.
	 * @member {string} [tenantId] VSTS tenant id.
	 */
	export interface FactoryVSTSConfiguration extends SharedModel.RepoConfiguration {
		projectName: string;
		tenantId?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the FactoryGitHubConfiguration class.
	 * @constructor
	 * Factory's GitHub repo information.
	 *
	 * @member {string} [hostName] GitHub repo host name.
	 */
	export interface FactoryGitHubConfiguration extends SharedModel.RepoConfiguration {
		readonly hostName?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the FactoryRepoUpdate class.
	 * @constructor
	 * Factory's git repo information.
	 *
	 * @member {string} [factoryResourceId] The factory resource id.
	 * @member {object} [repoConfiguration] Git repo information of the factory.
	 * @member {string} [repoConfiguration.accountName] Account name.
	 * @member {string} [repoConfiguration.repositoryName] Rrepository name.
	 * @member {string} [repoConfiguration.collaborationBranch] Collaboration
	 * branch.
	 * @member {string} [repoConfiguration.rootFolder] Root folder.
	 * @member {string} [repoConfiguration.lastCommitId] Last commit id.
	 * @member {string} [repoConfiguration.type] Polymorphic Discriminator
	 */
	// TODO: TASK 265560: Remove vstsConfiguration when Madrid no longer call preview version API to fetchFactory/updateFactory/updateRepo
	export interface FactoryRepoUpdate {
		factoryResourceId?: string;
		repoConfiguration?: SharedModel.RepoConfiguration;
		resourceGroupName?: string; // handcrafted
		vstsConfiguration?: SharedModel.RepoConfiguration; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the PipelineReference class.
	 * @constructor
	 * Pipeline reference type.
	 *
	 * @member {string} referenceName Reference pipeline name.
	 * @member {string} [name] Reference name.
	 */
	export interface PipelineReference {
		referenceName: string;
		type: string;   // handcrafted
		name?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the TriggerPipelineReference class.
	 * @constructor
	 * Pipeline that needs to be triggered with the given parameters.
	 *
	 * @member {object} [pipelineReference] Pipeline reference.
	 * @member {string} [pipelineReference.referenceName] Reference pipeline name.
	 * @member {string} [pipelineReference.name] Reference name.
	 * @member {object} [parameters] Pipeline parameters.
	 */
	export interface TriggerPipelineReference {
		pipelineReference?: PipelineReference;
		parameters?: { [propertyName: string]: any };
	}

	/**
	 * @class
	 * Initializes a new instance of the TriggerDependency class
	 * @constructor
	 * Trigger dependency object that defines referenced trigger, offset and window size
	 *
	 * @member {object} [referenceTrigger] Trigger reference.
	 * @member {string} [pipelineReference.referenceName] Referenced Trigger's name.
	 * @member {string} [pipelineReference.tye] Trigger reference type
	 * @member {object} [offset] Timespan to define a positive of negative delay for the referenced trigger
	 * @member {object} [offset] Timespan to define window slot size to add to the offset of the referenced trigger
	 */
	export interface TriggerDependency {
		type: string;
		referenceTrigger?: TriggerReference;
		offset?: string;
		size?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the TriggerReference class.
	 * @constructor
	 * Trigger reference for dependency specification
	 *
	 * @member {string} referenceName Referenced trigger name.
	 * @member {string} type Trigger Reference type.
	 */
	export interface TriggerReference {
		referenceName: string;
		type: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the FactoryUpdateParameters class.
	 * @constructor
	 * Parameters for updating a factory resource.
	 *
	 * @member {object} [tags] The resource tags.
	 * @member {object} [identity] Managed service identity of the factory.
	 * @member {uuid} [identity.principalId] The principal id of the identity.
	 * @member {uuid} [identity.tenantId] The client tenant id of the identity.
	 */
	export interface FactoryUpdateParameters extends SharedModel.Resource {
		tags?: { [propertyName: string]: string };
	}
	/**
	 * @class
	 * Initializes a new instance of the DebugConfiguration class.
	 * @constructor
	 * Debug configuration type.
	 *
	 * @member {string} property names for Debug configuration key and value.
	 * */
	export interface DebugConfigurations {
		debugConfigurations?: { [propertyName: string]: string };
	}

	/**
	 * @class
	 * Initializes a new instance of the DatasetReference class.
	 * @constructor
	 * Dataset reference type.
	 *
	 * @member {string} referenceName Reference dataset name.
	 * @member {object} [parameters] Arguments for dataset.
	 */
	export interface DatasetReference {
		referenceName: string;
		type: string; // handcrafted
		parameters?: { [propertyName: string]: any };
	}

	/**
	 * @class
	 * Initializes a new instance of the RunQueryFilter class.
	 * @constructor
	 * Query filter option for listing runs.
	 *
	 * @member {string} operand Parameter name to be used for filter. The allowed
	 * operands to query pipeline runs are PipelineName, RunStart, RunEnd and
	 * Status; to query activity runs are ActivityName, ActivityRunStart,
	 * ActivityRunEnd, ActivityType and Status, and to query trigger runs are
	 * TriggerName, TriggerRunTimestamp and Status. Possible values include:
	 * 'PipelineName', 'Status', 'RunStart', 'RunEnd', 'ActivityName',
	 * 'ActivityRunStart', 'ActivityRunEnd', 'ActivityType', 'TriggerName',
	 * 'TriggerRunTimestamp'
	 * @member {string} operator Operator to be used for filter. Possible values
	 * include: 'Equals', 'NotEquals', 'In', 'NotIn'
	 * @member {array} values List of filter values.
	 */
	export interface RunQueryFilter {
		operand: string;
		operator: string;
		values: string[];
	}

	/**
	 * @class
	 * Initializes a new instance of the RunQueryOrderBy class.
	 * @constructor
	 * An object to provide order by options for listing runs.
	 *
	 * @member {string} orderBy Parameter name to be used for order by. The allowed
	 * parameters to order by for pipeline runs are PipelineName, RunStart, RunEnd
	 * and Status; for activity runs are ActivityName, ActivityRunStart,
	 * ActivityRunEnd and Status; for trigger runs are TriggerName,
	 * TriggerRunTimestamp and Status. Possible values include: 'RunStart',
	 * 'RunEnd', 'PipelineName', 'Status', 'ActivityName', 'ActivityRunStart',
	 * 'ActivityRunEnd', 'TriggerName', 'TriggerRunTimestamp'
	 * @member {string} order Sorting order of the parameter. Possible values
	 * include: 'ASC', 'DESC'
	 */
	export interface RunQueryOrderBy {
		orderBy: string;
		order: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the RunFilterParameters class.
	 * @constructor
	 * Query parameters for listing runs.
	 *
	 * @member {string} [continuationToken] The continuation token for getting the
	 * next page of results. Null for first page.
	 * @member {date} lastUpdatedAfter The time at or after which the run event was
	 * updated in 'ISO 8601' format.
	 * @member {date} lastUpdatedBefore The time at or before which the run event
	 * was updated in 'ISO 8601' format.
	 * @member {array} [filters] List of filters.
	 * @member {array} [orderBy] List of OrderBy option.
	 */
	export interface RunFilterParameters {
		continuationToken?: string;
		lastUpdatedAfter: Date;
		lastUpdatedBefore: Date;
		filters?: RunQueryFilter[];
		orderBy?: RunQueryOrderBy[];
	}

	/**
	 * @class
	 * Initializes a new instance of the PipelineRunInvokedBy class.
	 * @constructor
	 * Provides entity name and id that started the pipeline run.
	 *
	 * @member {string} [name] Name of the entity that started the pipeline run.
	 * @member {string} [id] The ID of the entity that started the run.
	 * @member {string} [invokedByType] The type of the entity that started the
	 * run.
	 */
	export interface PipelineRunInvokedBy {
		readonly name?: string;
		readonly id?: string;
		readonly invokedByType?: string;
		readonly pipelineName?: string;
		readonly pipelineRunId?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the PipelineRun class.
	 * @constructor
	 * Information about a pipeline run.
	 *
	 * @member {string} [runId] Identifier of a run.
	 * @member {string} [pipelineName] The pipeline name.
	 * @member {object} [parameters] The full or partial list of parameter name,
	 * value pair used in the pipeline run.
	 * @member {object} [invokedBy] Entity that started the pipeline run.
	 * @member {string} [invokedBy.name] Name of the entity that started the
	 * pipeline run.
	 * @member {string} [invokedBy.id] The ID of the entity that started the run.
	 * @member {string} [invokedBy.invokedByType] The type of the entity that
	 * started the run.
	 * @member {date} [lastUpdated] The last updated timestamp for the pipeline run
	 * event in ISO8601 format.
	 * @member {date} [runStart] The start time of a pipeline run in ISO8601
	 * format.
	 * @member {date} [runEnd] The end time of a pipeline run in ISO8601 format.
	 * @member {number} [durationInMs] The duration of a pipeline run.
	 * @member {string} [status] The status of a pipeline run.
	 * @member {string} [message] The message from a pipeline run.
	 * @member {string} [runDimension] The run dimension from a pipeline run.
	 */
	export interface PipelineRun {
		readonly runId?: string;
		readonly pipelineName?: string;
		readonly parameters?: { [propertyName: string]: string };
		readonly annotations?: string[];
		readonly parameterNames?: string[];     // handcrafted for old api version
		readonly parameterNamesCount?: number;  // handcrafted for old api version
		readonly parameterValues?: string[];    // handcrafted for old api version
		readonly parameterValuesCount?: number; // handcrafted for old api version
		readonly invokedBy?: PipelineRunInvokedBy;
		readonly lastUpdated?: Date;
		readonly runStart?: Date;
		readonly runEnd?: Date;
		readonly durationInMs?: number;
		readonly status?: string;
		readonly message?: string;
		readonly runGroupId?: string;
		readonly runDimension?: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the PipelineRunsQueryResponse class.
	 * @constructor
	 * A list pipeline runs.
	 *
	 * @member {array} value List of pipeline runs.
	 * @member {string} [continuationToken] The continuation token for getting the
	 * next page of results, if any remaining results exist, null otherwise.
	 */
	export interface PipelineRunsQueryResponse {
		value: PipelineRun[];
		continuationToken?: string;
	}


	/**
	 * @class
	 * Initializes a new instance of the ActivityRun class.
	 * @constructor
	 * Information about an activity run in a pipeline.
	 *
	 * @member {string} [pipelineName] The name of the pipeline.
	 * @member {string} [pipelineRunId] The id of the pipeline run.
	 * @member {string} [activityName] The name of the activity.
	 * @member {string} [activityType] The type of the activity.
	 * @member {string} [activityRunId] The id of the activity run.
	 * @member {string} [linkedServiceName] The name of the compute linked service.
	 * @member {string} [status] The status of the activity run.
	 * @member {date} [activityRunStart] The start time of the activity run in 'ISO
	 * 8601' format.
	 * @member {date} [activityRunEnd] The end time of the activity run in 'ISO
	 * 8601' format.
	 * @member {number} [durationInMs] The duration of the activity run.
	 * @member {object} [input] The input for the activity.
	 * @member {object} [output] The output for the activity.
	 * @member {object} [error] The error if any from the activity run.
	 */
	export interface ActivityRun {
		readonly pipelineName?: string;
		readonly pipelineId?: string;
		readonly pipelineRunId?: string;
		readonly activityName?: string;
		readonly activityType?: string;
		readonly activityRunId?: string;
		readonly linkedServiceName?: string;
		readonly status?: string;
		readonly recoveryStatus?: string;
		readonly activityStart?: Date; // handcrafted
		readonly activityRunStart?: Date;
		readonly activityRunEnd?: Date;
		readonly durationInMs?: number;
		readonly id?: string; // handcrafted
		readonly input?: any;
		readonly output?: any;
		readonly executionDetails?: any;
		readonly error?: any;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

    export interface CopyJobRun {
        readonly startTime: string;
        readonly endTime: string;
        readonly jobInstanceStatus: ArtifactJobStatus;
        readonly jobInstanceMessage: string;
        readonly jobInstanceError?: string;
        readonly errorDetails?: {
            readonly additionalParameters?: unknown;
            readonly errorCategory?: unknown;
            readonly errorCode: 'Failed' | string;
            readonly errorMessage: string;
            readonly errorParameters?: unknown;
            readonly errorSource: number;
            readonly isTransient: boolean;
            readonly userInteractionRequest?: unknown;
        };
        readonly properties?: Record<string, unknown>;
    }

	export interface CopyJobActivityRun {
		readonly id: string;
		readonly properties: {
			readonly source: {
				readonly datasetSettings?: {
					readonly schema?: string;
					readonly table?: string;
				};
				readonly displayName?: string;
			};
			readonly destination: {
				readonly datasetSettings?: {
					readonly schema?: string;
					readonly table?: string;
				};
				readonly displayName?: string;
			};
		};
		readonly activityRunId: string;
		readonly copyId?: string;
		readonly activityRunEnd: Date;
		readonly activityRunStart: Date;
		readonly durationInMs: number;
		readonly error?: {
			readonly errorCode: string;
			readonly message: string;
			readonly failureType: string;
			readonly details: string;
		};
		readonly output?: {
			readonly dataRead: number;
			readonly dataWritten: number;
			readonly filesRead: number;
			readonly filesWritten: number;
			readonly rowsRead: number;
			readonly rowsCopied: number;
			readonly rowsInserted: number;
			readonly rowsUpdated: number;
			readonly rowsDeleted: number;
			readonly throughput: number;
            readonly watermarkInfo?: {
                readonly loadType: 'FullLoad' | 'IncrementalLoad' | 'SubsequentLoad';
                readonly lowWatermark: string | null;
                readonly highWatermark: string | null;
            }
		};
		readonly status: 'Succeeded' | string;
		readonly executionDetails: unknown;
        readonly jobStage?: 'Lookup' | 'Copy';
	}

	/**
	 * @class
	 * Initializes a new instance of the ActivityRunsQueryResponse class.
	 * @constructor
	 * A list activity runs.
	 *
	 * @member {array} value List of activity runs.
	 * @member {string} [continuationToken] The continuation token for getting the
	 * next page of results, if any remaining results exist, null otherwise.
	 */
	export interface ActivityRunsQueryResponse {
		value: ActivityRun[];
		continuationToken?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the TriggerRun class.
	 * @constructor
	 * Trigger runs.
	 *
	 * @member {string} [triggerRunId] Trigger run id.
	 * @member {string} [triggerName] Trigger name.
	 * @member {string} [triggerType] Trigger type.
	 * @member {date} [triggerRunTimestamp] Trigger run start time.
	 * @member {string} [status] Trigger run status. Possible values include:
	 * 'Succeeded', 'Failed', 'Inprogress'
	 * @member {string} [message] Trigger error message.
	 * @member {object} [properties] List of property name and value related to
	 * trigger run. Name, value pair depends on type of trigger.
	 * @member {object} [triggeredPipelines] List of pipeline name and run Id
	 * triggered by the trigger run.
	 * @member {object} [dependencyStatus] List of property name and value to store the state of Parent Pipelines
	 * @member {object} [runDimension] Key value pair to store the run dimension value for which the trigger is fired
	 */
	export interface TriggerRun {
		readonly triggerRunId?: string;
		readonly groupId?: string;
		readonly triggerName?: string;
		readonly triggerType?: string;
		readonly triggerRunTimestamp?: Date;
		readonly status?: string;
		readonly message?: string;
		readonly properties?: { [propertyName: string]: string };
		readonly triggeredPipelines?: { [propertyName: string]: string };
		readonly dependencyStatus?: { [propertyName: string]: DependencyStatus };
		readonly runDimension?: { [propertyName: string]: string };
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	export interface DependencyStatus {
		Id: string;
		RunStart: string;
		Status: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the TriggerRunsQueryResponse class.
	 * @constructor
	 * A list of trigger runs.
	 *
	 * @member {array} value List of trigger runs.
	 * @member {string} [continuationToken] The continuation token for getting the
	 * next page of results, if any remaining results exist, null otherwise.
	 */
	export interface TriggerRunsQueryResponse {
		value: TriggerRun[];
		continuationToken?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the OperationDisplay class.
	 * @constructor
	 * Metadata associated with the operation.
	 *
	 * @member {string} [description] The description of the operation.
	 * @member {string} [provider] The name of the provider.
	 * @member {string} [resource] The name of the resource type on which the
	 * operation is performed.
	 * @member {string} [operation] The type of operation: get, read, delete, etc.
	 */
	export interface OperationDisplay {
		description?: string;
		provider?: string;
		resource?: string;
		operation?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the OperationLogSpecification class.
	 * @constructor
	 * Details about an operation related to logs.
	 *
	 * @member {string} [name] The name of the log category.
	 * @member {string} [displayName] Localized display name.
	 * @member {string} [blobDuration] Blobs created in the customer storage
	 * account, per hour.
	 */
	export interface OperationLogSpecification {
		name?: string;
		displayName?: string;
		blobDuration?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the OperationMetricAvailability class.
	 * @constructor
	 * Defines how often data for a metric becomes available.
	 *
	 * @member {string} [timeGrain] The granularity for the metric.
	 * @member {string} [blobDuration] Blob created in the customer storage
	 * account, per hour.
	 */
	export interface OperationMetricAvailability {
		timeGrain?: string;
		blobDuration?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the OperationMetricDimension class.
	 * @constructor
	 * Defines the metric dimension.
	 *
	 * @member {string} [name] The name of the dimension for the metric.
	 * @member {string} [displayName] The display name of the metric dimension.
	 * @member {boolean} [toBeExportedForShoebox] Whether the dimension should be
	 * exported to Azure Monitor.
	 */
	export interface OperationMetricDimension {
		name?: string;
		displayName?: string;
		toBeExportedForShoebox?: boolean;
	}

	/**
	 * @class
	 * Initializes a new instance of the OperationMetricSpecification class.
	 * @constructor
	 * Details about an operation related to metrics.
	 *
	 * @member {string} [name] The name of the metric.
	 * @member {string} [displayName] Localized display name of the metric.
	 * @member {string} [displayDescription] The description of the metric.
	 * @member {string} [unit] The unit that the metric is measured in.
	 * @member {string} [aggregationType] The type of metric aggregation.
	 * @member {string} [enableRegionalMdmAccount] Whether or not the service is
	 * using regional MDM accounts.
	 * @member {string} [sourceMdmAccount] The name of the MDM account.
	 * @member {string} [sourceMdmNamespace] The name of the MDM namespace.
	 * @member {array} [availabilities] Defines how often data for metrics becomes
	 * available.
	 * @member {array} [dimensions] Defines the metric dimension.
	 */
	export interface OperationMetricSpecification {
		name?: string;
		displayName?: string;
		displayDescription?: string;
		unit?: string;
		aggregationType?: string;
		enableRegionalMdmAccount?: string;
		sourceMdmAccount?: string;
		sourceMdmNamespace?: string;
		availabilities?: OperationMetricAvailability[];
		dimensions?: OperationMetricDimension[];
	}

	/**
	 * @class
	 * Initializes a new instance of the OperationServiceSpecification class.
	 * @constructor
	 * Details about a service operation.
	 *
	 * @member {array} [logSpecifications] Details about operations related to
	 * logs.
	 * @member {array} [metricSpecifications] Details about operations related to
	 * metrics.
	 */
	export interface OperationServiceSpecification {
		logSpecifications?: OperationLogSpecification[];
		metricSpecifications?: OperationMetricSpecification[];
	}

	/**
	 * @class
	 * Initializes a new instance of the OperationProperties class.
	 * @constructor
	 * Additional details about an operation.
	 *
	 * @member {object} [serviceSpecification] Details about a service operation.
	 * @member {array} [serviceSpecification.logSpecifications] Details about
	 * operations related to logs.
	 * @member {array} [serviceSpecification.metricSpecifications] Details about
	 * operations related to metrics.
	 */
	export interface OperationProperties {
		serviceSpecification?: OperationServiceSpecification;
	}

	/**
	 * @class
	 * Initializes a new instance of the Operation class.
	 * @constructor
	 * Azure Data Factory API operation definition.
	 *
	 * @member {string} [name] Operation name: {provider}/{resource}/{operation}
	 * @member {string} [origin] The intended executor of the operation.
	 * @member {object} [display] Metadata associated with the operation.
	 * @member {string} [display.description] The description of the operation.
	 * @member {string} [display.provider] The name of the provider.
	 * @member {string} [display.resource] The name of the resource type on which
	 * the operation is performed.
	 * @member {string} [display.operation] The type of operation: get, read,
	 * delete, etc.
	 * @member {object} [properties] Additional details about the operation.
	 * @member {object} [properties.serviceSpecification] Details about a service
	 * operation.
	 * @member {array} [properties.serviceSpecification.logSpecifications] Details
	 * about operations related to logs.
	 * @member {array} [properties.serviceSpecification.metricSpecifications]
	 * Details about operations related to metrics.
	 */
	export interface Operation {
		name?: string;
		origin?: string;
		display?: OperationDisplay;
		properties?: OperationProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ResponsysLinkedServiceTypeProperties class.
	 * @constructor
	 * Responsys linked service properties.
	 *
	 * @member {object} endpoint The endpoint of the Responsys server.
	 * @member {object} clientId The client ID associated with the Responsys
	 * application. Type: string (or Expression with resultType string).
	 * @member {object} [clientSecret] The client secret associated with the
	 * Responsys application. Type: string (or Expression with resultType string).
	 * @member {string} [clientSecret.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true. Type:
	 * boolean (or Expression with resultType boolean).
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true. Type: boolean (or Expression
	 * with resultType boolean).
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * Type: boolean (or Expression with resultType boolean).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface ResponsysLinkedServiceTypeProperties {
		endpoint: any;
		clientId: any;
		clientSecret?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ResponsysLinkedService class.
	 * @constructor
	 * Responsys linked service.
	 *
	 * @member {object} typeProperties Responsys linked service properties.
	 * @member {object} [typeProperties.endpoint] The endpoint of the Responsys
	 * server.
	 * @member {object} [typeProperties.clientId] The client ID associated with the
	 * Responsys application. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.clientSecret] The client secret associated
	 * with the Responsys application. Type: string (or Expression with resultType
	 * string).
	 * @member {string} [typeProperties.clientSecret.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true. Type: boolean (or Expression with resultType boolean).
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true. Type:
	 * boolean (or Expression with resultType boolean).
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true. Type: boolean (or Expression with resultType boolean).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface ResponsysLinkedService extends SharedModel.LinkedService {
		typeProperties: ResponsysLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDatabricksLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure Databricks linked service properties.
	 *
	 * @member {object} domain <REGION>.azuredatabricks.net, domain name of your
	 * Databricks deployment. Type: string (or Expression with resultType string).
	 * @member {object} accessToken Access token for databricks REST API. Refer to
	 * https://docs.azuredatabricks.net/api/latest/authentication.html. Type:
	 * string (or Expression with resultType string).
	 * @member {string} [accessToken.type] Polymorphic Discriminator
	 * @member {object} [existingClusterId] The id of an existing cluster that will
	 * be used for all runs of this job. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [instancePoolId] The id of an instance pool that will
	 * be used for all runs of this job. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [newClusterVersion] The Spark version of new cluster. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [newClusterNumOfWorker] Number of worker nodes that new
	 * cluster should have. A string formatted Int32, like '1' means numOfWorker is
	 * 1 or '1:10' means auto-scale from 1 as min and 10 as max. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [newClusterNodeType] The node types of new cluster. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [newClusterSparkConf] a set of optional, user-specified
	 * Spark configuration key-value pairs.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureDatabricksLinkedServiceTypeProperties {
		domain: any;
		accessToken?: SharedModel.SecretBase;
		workspaceResourceId?: any; //handcrafted
		authentication?: any; //handcrafted
		authenticationReferenceType?: any; //handcrafted
		credential?: SharedModel.CredentialReference; //handcrafted
		existingClusterId?: any;
		instancePoolId?: any;
		newClusterVersion?: any;
		newClusterNumOfWorker?: any;
		newClusterNodeType?: any;
		newClusterSparkConf?: { [propertyName: string]: any };
		newClusterSparkEnvVars?: { [propertyName: string]: any };
		newClusterCustomTags?: { [propertyName: string]: any }; // handcrafted
		newClusterLogDestinationSettings?: IDatabricksClusterLogDestinationSettings; // handcrafted
		newClusterLogDestination?: string; // handcrafted
		encryptedCredential?: any;
		newClusterInitScripts?: any[];
		newClusterDriverNodeType?: any;
		newClusterAttributes?: INewClusterAttributes; //handcrafted
		dockerImage?: IDockerConfiguration; //handcrafted
		policyId?: string //handcrafted;
		clusterOption?: string; //handcrafted
		dataSecurityMode?: string; //handcrafted
	}

	interface IDockerBaseAuth {
		username: string,
		password: SharedModel.SecretBase
	}

	export interface IDockerConfiguration {
		url: string,
		basicAuth?: IDockerBaseAuth;
	}

	export interface INewClusterAttributes {
		firstOnDemand: number,
		availability: string,
		spotBidMaxPrice: number
	}

	export type IDatabricksClusterLogDestinationType = 'dbfs' | 'volumes' | 'abfs' | 'abfss' | string;

	export interface IDatabricksClusterLogDestinationSettings {
		type?: IDatabricksClusterLogDestinationType;
		path?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDatabricksLinkedService class.
	 * @constructor
	 * Azure Databricks linked service.
	 *
	 * @member {object} typeProperties Azure Databricks linked service properties.
	 * @member {object} [typeProperties.domain] <REGION>.azuredatabricks.net,
	 * domain name of your Databricks deployment. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.accessToken] Access token for databricks
	 * REST API. Refer to
	 * https://docs.azuredatabricks.net/api/latest/authentication.html. Type:
	 * string (or Expression with resultType string).
	 * @member {string} [typeProperties.accessToken.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.existingClusterId] The id of an existing
	 * cluster that will be used for all runs of this job. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.newClusterVersion] The Spark version of new
	 * cluster. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.newClusterNumOfWorker] Number of worker
	 * nodes that new cluster should have. A string formatted Int32, like '1' means
	 * numOfWorker is 1 or '1:10' means auto-scale from 1 as min and 10 as max.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.newClusterNodeType] The node types of new
	 * cluster. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.newClusterSparkConf] a set of optional,
	 * user-specified Spark configuration key-value pairs.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureDatabricksLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureDatabricksLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the DataFlowLinkedServiceTypeProperties class.
	 * @constructor
	 * DataFlow linked service properties.
	 *
	 * @member {object} domain <REGION>.azuredatabricks.net, domain name of your
	 * Databricks deployment. Type: string (or Expression with resultType string).
	 * @member {object} accessToken Access token for databricks REST API. Refer to
	 * https://docs.azuredatabricks.net/api/latest/authentication.html. Type:
	 * string (or Expression with resultType string).
	 * @member {string} [accessToken.type] Polymorphic Discriminator
	 * @member {object} [newClusterNumOfWorker] Number of worker nodes that new
	 * cluster should have. A string formatted Int32, like '1' means numOfWorker is
	 * 1 or '1:10' means auto-scale from 1 as min and 10 as max. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [newClusterNodeType] The node types of new cluster. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface DataFlowLinkedServiceTypeProperties {
		domain: any;
		accessToken: SharedModel.SecretBase;
		newClusterNumOfWorker?: any;
		newClusterNodeType?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DataFlowLinkedService class.
	 * @constructor
	 * DataFlow linked service.
	 *
	 * @member {object} typeProperties DataFlow linked service properties.
	 * @member {object} [typeProperties.domain] <REGION>.azuredatabricks.net,
	 * domain name of your Databricks deployment. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.accessToken] Access token for databricks
	 * REST API. Refer to
	 * https://docs.azuredatabricks.net/api/latest/authentication.html. Type:
	 * string (or Expression with resultType string).
	 * @member {string} [typeProperties.accessToken.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.newClusterNumOfWorker] Number of worker
	 * nodes that new cluster should have. A string formatted Int32, like '1' means
	 * numOfWorker is 1 or '1:10' means auto-scale from 1 as min and 10 as max.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.newClusterNodeType] The node types of new
	 * cluster. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface DataFlowLinkedService extends SharedModel.LinkedService {
		typeProperties: DataFlowLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeAnalyticsLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure Data Lake Analytics linked service properties.
	 *
	 * @member {object} accountName The Azure Data Lake Analytics account name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [servicePrincipalId] The ID of the application used to
	 * authenticate against the Azure Data Lake Analytics account. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [servicePrincipalKey] The Key of the application used to
	 * authenticate against the Azure Data Lake Analytics account.
	 * @member {string} [servicePrincipalKey.type] Polymorphic Discriminator
	 * @member {object} tenant The name or ID of the tenant to which the service
	 * principal belongs. Type: string (or Expression with resultType string).
	 * @member {object} [subscriptionId] Data Lake Analytics account subscription
	 * ID (if different from Data Factory account). Type: string (or Expression
	 * with resultType string).
	 * @member {object} [resourceGroupName] Data Lake Analytics account resource
	 * group name (if different from Data Factory account). Type: string (or
	 * Expression with resultType string).
	 * @member {object} [dataLakeAnalyticsUri] Azure Data Lake Analytics URI Type:
	 * string (or Expression with resultType string).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureDataLakeAnalyticsLinkedServiceTypeProperties {
		accountName: any;
		servicePrincipalId?: any;
		servicePrincipalKey?: SharedModel.SecretBase;
		tenant?: any;
		subscriptionId?: any;
		resourceGroupName?: any;
		dataLakeAnalyticsUri?: any;
		encryptedCredential?: any;
		authentication?: any; //handcrafted
		credential?: SharedModel.CredentialReference; //handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeAnalyticsLinkedService class.
	 * @constructor
	 * Azure Data Lake Analytics linked service.
	 *
	 * @member {object} typeProperties Azure Data Lake Analytics linked service
	 * properties.
	 * @member {object} [typeProperties.accountName] The Azure Data Lake Analytics
	 * account name. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.servicePrincipalId] The ID of the
	 * application used to authenticate against the Azure Data Lake Analytics
	 * account. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.servicePrincipalKey] The Key of the
	 * application used to authenticate against the Azure Data Lake Analytics
	 * account.
	 * @member {string} [typeProperties.servicePrincipalKey.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.tenant] The name or ID of the tenant to
	 * which the service principal belongs. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.subscriptionId] Data Lake Analytics account
	 * subscription ID (if different from Data Factory account). Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.resourceGroupName] Data Lake Analytics
	 * account resource group name (if different from Data Factory account). Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.dataLakeAnalyticsUri] Azure Data Lake
	 * Analytics URI Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureDataLakeAnalyticsLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureDataLakeAnalyticsLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightOnDemandLinkedServiceTypeProperties class.
	 * @constructor
	 * HDInsight ondemand linked service properties.
	 *
	 * @member {object} clusterSize Number of worker/data nodes in the cluster.
	 * Suggestion value: 4. Type: string (or Expression with resultType string).
	 * @member {object} timeToLive The allowed idle time for the on-demand
	 * HDInsight cluster. Specifies how long the on-demand HDInsight cluster stays
	 * alive after completion of an activity run if there are no other active jobs
	 * in the cluster. The minimum value is 5 mins. Type: string (or Expression
	 * with resultType string).
	 * @member {object} version Version of the HDInsight cluster.  Type: string (or
	 * Expression with resultType string).
	 * @member {object} linkedServiceName Azure Storage linked service to be used
	 * by the on-demand cluster for storing and processing data.
	 * @member {string} [linkedServiceName.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [linkedServiceName.parameters] Arguments for LinkedService.
	 * @member {object} hostSubscriptionId The customer’s subscription to host the
	 * cluster. Type: string (or Expression with resultType string).
	 * @member {object} [servicePrincipalId] The service principal id for the
	 * hostSubscriptionId. Type: string (or Expression with resultType string).
	 * @member {object} [servicePrincipalKey] The key for the service principal id.
	 * @member {string} [servicePrincipalKey.type] Polymorphic Discriminator
	 * @member {object} tenant The Tenant id/name to which the service principal
	 * belongs. Type: string (or Expression with resultType string).
	 * @member {object} clusterResourceGroup The resource group where the cluster
	 * belongs. Type: string (or Expression with resultType string).
	 * @member {object} [clusterNamePrefix] The prefix of cluster name, postfix
	 * will be distinct with timestamp. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [clusterUserName] The username to access the cluster. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [clusterPassword] The password to access the cluster.
	 * @member {string} [clusterPassword.type] Polymorphic Discriminator
	 * @member {object} [clusterSshUserName] The username to SSH remotely connect
	 * to cluster’s node (for Linux). Type: string (or Expression with resultType
	 * string).
	 * @member {object} [clusterSshPassword] The password to SSH remotely connect
	 * cluster’s node (for Linux).
	 * @member {string} [clusterSshPassword.type] Polymorphic Discriminator
	 * @member {array} [additionalLinkedServiceNames] Specifies additional storage
	 * accounts for the HDInsight linked service so that the Data Factory service
	 * can register them on your behalf.
	 * @member {object} [hcatalogLinkedServiceName] The name of Azure SQL linked
	 * service that point to the HCatalog database. The on-demand HDInsight cluster
	 * is created by using the Azure SQL database as the metastore.
	 * @member {string} [hcatalogLinkedServiceName.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [hcatalogLinkedServiceName.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [virtualNetworkId] The virtual network id. Type: string.
	 * @member {object} [subnetName] The subnet name for the vnet. Type: string.
	 * @member {object} [clusterType] The cluster type. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [sparkVersion] The version of spark if the cluster type is
	 * 'spark'. Type: string (or Expression with resultType string).
	 * @member {object} [coreConfiguration] Specifies the core configuration
	 * parameters (as in core-site.xml) for the HDInsight cluster to be created.
	 * @member {object} [hBaseConfiguration] Specifies the HBase configuration
	 * parameters (hbase-site.xml) for the HDInsight cluster.
	 * @member {object} [hdfsConfiguration] Specifies the HDFS configuration
	 * parameters (hdfs-site.xml) for the HDInsight cluster.
	 * @member {object} [hiveConfiguration] Specifies the hive configuration
	 * parameters (hive-site.xml) for the HDInsight cluster.
	 * @member {object} [mapReduceConfiguration] Specifies the MapReduce
	 * configuration parameters (mapred-site.xml) for the HDInsight cluster.
	 * @member {object} [oozieConfiguration] Specifies the Oozie configuration
	 * parameters (oozie-site.xml) for the HDInsight cluster.
	 * @member {object} [stormConfiguration] Specifies the Storm configuration
	 * parameters (storm-site.xml) for the HDInsight cluster.
	 * @member {object} [yarnConfiguration] Specifies the Yarn configuration
	 * parameters (yarn-site.xml) for the HDInsight cluster.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 * @member {object} [headNodeSize] Specifies the size of the head node for the
	 * HDInsight cluster.
	 * @member {object} [dataNodeSize] Specifies the size of the data node for the
	 * HDInsight cluster.
	 * @member {object} [zookeeperNodeSize] Specifies the size of the Zoo Keeper
	 * node for the HDInsight cluster.
	 */
	export interface HDInsightOnDemandLinkedServiceTypeProperties {
		clusterSize: any;
		timeToLive: any;
		version: any;
		linkedServiceName: SharedModel.LinkedServiceReference;
		hostSubscriptionId: any;
		servicePrincipalId?: any;
		servicePrincipalKey?: SharedModel.SecretBase;
		tenant?: any; // handcrafted
		clusterResourceGroup: any;
		clusterNamePrefix?: any;
		clusterUserName?: any;
		clusterPassword?: SharedModel.SecretBase;
		clusterSshUserName?: any;
		clusterSshPassword?: SharedModel.SecretBase;
		additionalLinkedServiceNames?: SharedModel.LinkedServiceReference[];
		hcatalogLinkedServiceName?: SharedModel.LinkedServiceReference;
		virtualNetworkId?: string; // handcrafted
		subnetName?: string; // handcrafted
		clusterType?: any;
		osType?: string; // handcrafted
		scriptActions?: any[]; // handcrafted
		sparkVersion?: any;
		coreConfiguration?: any;
		hBaseConfiguration?: any;
		hdfsConfiguration?: any;
		hiveConfiguration?: any;
		mapReduceConfiguration?: any;
		oozieConfiguration?: any;
		stormConfiguration?: any;
		yarnConfiguration?: any;
		encryptedCredential?: any;
		headNodeSize?: string; // handcrafted
		dataNodeSize?: string; // handcrafted
		zookeeperNodeSize?: string; // handcrafted
		clusterResourceGroupAuthType?: string;
		credential?: SharedModel.CredentialReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightOnDemandLinkedService class.
	 * @constructor
	 * HDInsight ondemand linked service.
	 *
	 * @member {object} typeProperties HDInsight ondemand linked service
	 * properties.
	 * @member {object} [typeProperties.clusterSize] Number of worker/data nodes in
	 * the cluster. Suggestion value: 4. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.timeToLive] The allowed idle time for the
	 * on-demand HDInsight cluster. Specifies how long the on-demand HDInsight
	 * cluster stays alive after completion of an activity run if there are no
	 * other active jobs in the cluster. The minimum value is 5 mins. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.version] Version of the HDInsight cluster. 
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.linkedServiceName] Azure Storage linked
	 * service to be used by the on-demand cluster for storing and processing data.
	 * @member {string} [typeProperties.linkedServiceName.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [typeProperties.linkedServiceName.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [typeProperties.hostSubscriptionId] The customer’s
	 * subscription to host the cluster. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.servicePrincipalId] The service principal
	 * id for the hostSubscriptionId. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [typeProperties.servicePrincipalKey] The key for the
	 * service principal id.
	 * @member {string} [typeProperties.servicePrincipalKey.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.tenant] The Tenant id/name to which the
	 * service principal belongs. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [typeProperties.clusterResourceGroup] The resource group
	 * where the cluster belongs. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [typeProperties.clusterNamePrefix] The prefix of cluster
	 * name, postfix will be distinct with timestamp. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [typeProperties.clusterUserName] The username to access the
	 * cluster. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.clusterPassword] The password to access the
	 * cluster.
	 * @member {string} [typeProperties.clusterPassword.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.clusterSshUserName] The username to SSH
	 * remotely connect to cluster’s node (for Linux). Type: string (or Expression
	 * with resultType string).
	 * @member {object} [typeProperties.clusterSshPassword] The password to SSH
	 * remotely connect cluster’s node (for Linux).
	 * @member {string} [typeProperties.clusterSshPassword.type] Polymorphic
	 * Discriminator
	 * @member {array} [typeProperties.additionalLinkedServiceNames] Specifies
	 * additional storage accounts for the HDInsight linked service so that the
	 * Data Factory service can register them on your behalf.
	 * @member {object} [typeProperties.hcatalogLinkedServiceName] The name of
	 * Azure SQL linked service that point to the HCatalog database. The on-demand
	 * HDInsight cluster is created by using the Azure SQL database as the
	 * metastore.
	 * @member {string} [typeProperties.hcatalogLinkedServiceName.referenceName]
	 * Reference LinkedService name.
	 * @member {object} [typeProperties.hcatalogLinkedServiceName.parameters]
	 * Arguments for LinkedService.
	 * @member {object} [typeProperties.clusterType] The cluster type. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.sparkVersion] The version of spark if the
	 * cluster type is 'spark'. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [typeProperties.coreConfiguration] Specifies the core
	 * configuration parameters (as in core-site.xml) for the HDInsight cluster to
	 * be created.
	 * @member {object} [typeProperties.hBaseConfiguration] Specifies the HBase
	 * configuration parameters (hbase-site.xml) for the HDInsight cluster.
	 * @member {object} [typeProperties.hdfsConfiguration] Specifies the HDFS
	 * configuration parameters (hdfs-site.xml) for the HDInsight cluster.
	 * @member {object} [typeProperties.hiveConfiguration] Specifies the hive
	 * configuration parameters (hive-site.xml) for the HDInsight cluster.
	 * @member {object} [typeProperties.mapReduceConfiguration] Specifies the
	 * MapReduce configuration parameters (mapred-site.xml) for the HDInsight
	 * cluster.
	 * @member {object} [typeProperties.oozieConfiguration] Specifies the Oozie
	 * configuration parameters (oozie-site.xml) for the HDInsight cluster.
	 * @member {object} [typeProperties.stormConfiguration] Specifies the Storm
	 * configuration parameters (storm-site.xml) for the HDInsight cluster.
	 * @member {object} [typeProperties.yarnConfiguration] Specifies the Yarn
	 * configuration parameters (yarn-site.xml) for the HDInsight cluster.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.headNodeSize] Specifies the size of the
	 * head node for the HDInsight cluster.
	 * @member {object} [typeProperties.dataNodeSize] Specifies the size of the
	 * data node for the HDInsight cluster.
	 * @member {object} [typeProperties.zookeeperNodeSize] Specifies the size of
	 * the Zoo Keeper node for the HDInsight cluster.
	 */
	export interface HDInsightOnDemandLinkedService extends SharedModel.LinkedService {
		typeProperties: HDInsightOnDemandLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceMarketingCloudLinkedServiceTypeProperties class.
	 * @constructor
	 * Salesforce Marketing Cloud linked service properties.
	 *
	 * @member {object} clientId The client ID associated with the Salesforce
	 * Marketing Cloud application. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [clientSecret] The client secret associated with the
	 * Salesforce Marketing Cloud application. Type: string (or Expression with
	 * resultType string).
	 * @member {string} [clientSecret.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true. Type:
	 * boolean (or Expression with resultType boolean).
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true. Type: boolean (or Expression
	 * with resultType boolean).
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * Type: boolean (or Expression with resultType boolean).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface SalesforceMarketingCloudLinkedServiceTypeProperties {
		clientId?: any;
		clientSecret?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
		connectionProperties?: SalesforceMarketingCloudLinkedServiceConnectionProperties;
	}

	// handcrafted
	export interface SalesforceMarketingCloudLinkedServiceConnectionProperties {
		host: any;
		authenticationType: any;
		clientId: any;
		clientSecret?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
		[key: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceMarketingCloudLinkedService class.
	 * @constructor
	 * Salesforce Marketing Cloud linked service.
	 *
	 * @member {object} typeProperties Salesforce Marketing Cloud linked service
	 * properties.
	 * @member {object} [typeProperties.clientId] The client ID associated with the
	 * Salesforce Marketing Cloud application. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.clientSecret] The client secret associated
	 * with the Salesforce Marketing Cloud application. Type: string (or Expression
	 * with resultType string).
	 * @member {string} [typeProperties.clientSecret.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true. Type: boolean (or Expression with resultType boolean).
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true. Type:
	 * boolean (or Expression with resultType boolean).
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true. Type: boolean (or Expression with resultType boolean).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface SalesforceMarketingCloudLinkedService extends SharedModel.LinkedService {
		typeProperties: SalesforceMarketingCloudLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the NetezzaLinkedServiceTypeProperties class.
	 * @constructor
	 * Netezza linked service properties.
	 *
	 * @member {object} [connectionString] An ODBC connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface NetezzaLinkedServiceTypeProperties {
		connectionString?: any;
		pwd?: SharedModel.AzureKeyVaultSecretReference | SharedModel.SecretBase;
		encryptedCredential?: any;
		server?: string;
		port?: number;
		database?: string;
		uid?: string;
		securityLevel?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the NetezzaLinkedService class.
	 * @constructor
	 * Netezza linked service.
	 *
	 * @member {object} typeProperties Netezza linked service properties.
	 * @member {object} [typeProperties.connectionString] An ODBC connection
	 * string. Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface NetezzaLinkedService extends SharedModel.LinkedService {
		typeProperties: NetezzaLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the VerticaLinkedServiceTypeProperties class.
	 * @constructor
	 * Vertica linked service properties.
	 *
	 * @member {object} [connectionString] An ODBC connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface VerticaLinkedServiceTypeProperties {
		connectionString?: any;
		pwd?: string | SharedModel.AzureKeyVaultSecretReference | SharedModel.SecretBase;
		encryptedCredential?: any;
		server?: string;
		port?: number;
		database?: string;
		uid?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the VerticaLinkedService class.
	 * @constructor
	 * Vertica linked service.
	 *
	 * @member {object} typeProperties Vertica linked service properties.
	 * @member {object} [typeProperties.connectionString] An ODBC connection
	 * string. Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface VerticaLinkedService extends SharedModel.LinkedService {
		typeProperties: VerticaLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ZohoLinkedServiceTypeProperties class.
	 * @constructor
	 * Zoho server linked service properties.
	 *
	 * @member {object} endpoint The endpoint of the Zoho server. (i.e.
	 * crm.zoho.com/crm/private)
	 * @member {object} [accessToken] The access token for Zoho authentication.
	 * @member {string} [accessToken.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true.
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface ZohoLinkedServiceTypeProperties {
		endpoint?: any;
		accessToken?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
		connectionProperties?: ZohoLinkedServiceConnectionProperties;
	}

	// handcrafted
	export interface ZohoLinkedServiceConnectionProperties {
		endpoint: any;
		accessToken?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
		authenticationType: any;
		clientId?: any;
		clientSecret?: SharedModel.SecretBase;
		refreshToken?: SharedModel.SecretBase;
		[key: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ZohoLinkedService class.
	 * @constructor
	 * Zoho server linked service.
	 *
	 * @member {object} typeProperties Zoho server linked service properties.
	 * @member {object} [typeProperties.endpoint] The endpoint of the Zoho server.
	 * (i.e. crm.zoho.com/crm/private)
	 * @member {object} [typeProperties.accessToken] The access token for Zoho
	 * authentication.
	 * @member {string} [typeProperties.accessToken.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true.
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface ZohoLinkedService extends SharedModel.LinkedService {
		typeProperties: ZohoLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the XeroLinkedServiceTypeProperties class.
	 * @constructor
	 * Xero Serivce linked service properties.
	 *
	 * @member {object} [host] The endpoint of the Xero server. (i.e. api.xero.com)
	 * @member {object} [consumerKey] The consumer key associated with the Xero
	 * application.
	 * @member {string} [consumerKey.type] Polymorphic Discriminator
	 * @member {object} [privateKey] The private key from the .pem file that was
	 * generated for your Xero private application. You must include all the text
	 * from the .pem file, including the Unix line endings(
	 * ).
	 * @member {string} [privateKey.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true.
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface XeroLinkedServiceTypeProperties {
		host?: any;
		consumerKey?: SharedModel.SecretBase;
		privateKey?: SharedModel.SecretBase;
		tenantId?: any;
		refreshToken?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
		connectionProperties?: XeroLinkedServiceTypeProperties;
		[key: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the XeroLinkedService class.
	 * @constructor
	 * Xero Serivce linked service.
	 *
	 * @member {object} typeProperties Xero Serivce linked service properties.
	 * @member {object} [typeProperties.host] The endpoint of the Xero server.
	 * (i.e. api.xero.com)
	 * @member {object} [typeProperties.consumerKey] The consumer key associated
	 * with the Xero application.
	 * @member {string} [typeProperties.consumerKey.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.privateKey] The private key from the .pem
	 * file that was generated for your Xero private application. You must include
	 * all the text from the .pem file, including the Unix line endings(
	 * ).
	 * @member {string} [typeProperties.privateKey.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true.
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface XeroLinkedService extends SharedModel.LinkedService {
		typeProperties: XeroLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SquareLinkedServiceTypeProperties class.
	 * @constructor
	 * Square Serivce linked service properties.
	 *
	 * @member {object} host The URL of the Square instance. (i.e.
	 * mystore.mysquare.com)
	 * @member {object} clientId The client ID associated with your Square
	 * application.
	 * @member {object} [clientSecret] The client secret associated with your
	 * Square application.
	 * @member {string} [clientSecret.type] Polymorphic Discriminator
	 * @member {object} redirectUri The redirect URL assigned in the Square
	 * application dashboard. (i.e. http://localhost:2500)
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true.
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface SquareLinkedServiceTypeProperties {
		host?: any;
		clientId?: any;
		clientSecret?: SharedModel.SecretBase;
		redirectUri?: any;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		accessToken?: SharedModel.SecretBase;
		refreshToken?: SharedModel.SecretBase;
		encryptedCredential?: any;
		connectionProperties?: SquareLinkedServiceTypeProperties;
		[key: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SquareLinkedService class.
	 * @constructor
	 * Square Serivce linked service.
	 *
	 * @member {object} typeProperties Square Serivce linked service properties.
	 * @member {object} [typeProperties.host] The URL of the Square instance. (i.e.
	 * mystore.mysquare.com)
	 * @member {object} [typeProperties.clientId] The client ID associated with
	 * your Square application.
	 * @member {object} [typeProperties.clientSecret] The client secret associated
	 * with your Square application.
	 * @member {string} [typeProperties.clientSecret.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.redirectUri] The redirect URL assigned in
	 * the Square application dashboard. (i.e. http://localhost:2500)
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true.
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface SquareLinkedService extends SharedModel.LinkedService {
		typeProperties: SquareLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SparkLinkedServiceTypeProperties class.
	 * @constructor
	 * Spark Server linked service properties.
	 *
	 * @member {object} host IP address or host name of the Spark server
	 * @member {object} port The TCP port that the Spark server uses to listen for
	 * client connections.
	 * @member {string} [serverType] The type of Spark server. Possible values
	 * include: 'SharkServer', 'SharkServer2', 'SparkThriftServer'
	 * @member {string} [thriftTransportProtocol] The transport protocol to use in
	 * the Thrift layer. Possible values include: 'Binary', 'SASL', 'HTTP '
	 * @member {string} authenticationType The authentication method used to access
	 * the Spark server. Possible values include: 'Anonymous', 'Username',
	 * 'UsernameAndPassword', 'WindowsAzureHDInsightService'
	 * @member {object} [username] The user name that you use to access Spark
	 * Server.
	 * @member {object} [password] The password corresponding to the user name that
	 * you provided in the Username field
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [httpPath] The partial URL corresponding to the Spark
	 * server.
	 * @member {object} [enableSsl] Specifies whether the connections to the server
	 * are encrypted using SSL. The default value is false.
	 * @member {object} [trustedCertPath] The full path of the .pem file containing
	 * trusted CA certificates for verifying the server when connecting over SSL.
	 * This property can only be set when using SSL on self-hosted IR. The default
	 * value is the cacerts.pem file installed with the IR.
	 * @member {object} [useSystemTrustStore] Specifies whether to use a CA
	 * certificate from the system trust store or from a specified PEM file. The
	 * default value is false.
	 * @member {object} [allowHostNameCNMismatch] Specifies whether to require a
	 * CA-issued SSL certificate name to match the host name of the server when
	 * connecting over SSL. The default value is false.
	 * @member {object} [allowSelfSignedServerCert] Specifies whether to allow
	 * self-signed certificates from the server. The default value is false.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface SparkLinkedServiceTypeProperties {
		host: any;
		port: any;
		serverType?: string;
		thriftTransportProtocol?: string;
		authenticationType: string;
		username?: any;
		password?: SharedModel.SecretBase;
		httpPath?: any;
		enableSsl?: any;
		trustedCertPath?: any;
		useSystemTrustStore?: any;
		allowHostNameCNMismatch?: any;
		allowSelfSignedServerCert?: any;
		encryptedCredential?: any;
		enableServerCertificateValidation?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SparkLinkedService class.
	 * @constructor
	 * Spark Server linked service.
	 *
	 * @member {object} typeProperties Spark Server linked service properties.
	 * @member {object} [typeProperties.host] IP address or host name of the Spark
	 * server
	 * @member {object} [typeProperties.port] The TCP port that the Spark server
	 * uses to listen for client connections.
	 * @member {string} [typeProperties.serverType] The type of Spark server.
	 * Possible values include: 'SharkServer', 'SharkServer2', 'SparkThriftServer'
	 * @member {string} [typeProperties.thriftTransportProtocol] The transport
	 * protocol to use in the Thrift layer. Possible values include: 'Binary',
	 * 'SASL', 'HTTP '
	 * @member {string} [typeProperties.authenticationType] The authentication
	 * method used to access the Spark server. Possible values include:
	 * 'Anonymous', 'Username', 'UsernameAndPassword',
	 * 'WindowsAzureHDInsightService'
	 * @member {object} [typeProperties.username] The user name that you use to
	 * access Spark Server.
	 * @member {object} [typeProperties.password] The password corresponding to the
	 * user name that you provided in the Username field
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.httpPath] The partial URL corresponding to
	 * the Spark server.
	 * @member {object} [typeProperties.enableSsl] Specifies whether the
	 * connections to the server are encrypted using SSL. The default value is
	 * false.
	 * @member {object} [typeProperties.trustedCertPath] The full path of the .pem
	 * file containing trusted CA certificates for verifying the server when
	 * connecting over SSL. This property can only be set when using SSL on
	 * self-hosted IR. The default value is the cacerts.pem file installed with the
	 * IR.
	 * @member {object} [typeProperties.useSystemTrustStore] Specifies whether to
	 * use a CA certificate from the system trust store or from a specified PEM
	 * file. The default value is false.
	 * @member {object} [typeProperties.allowHostNameCNMismatch] Specifies whether
	 * to require a CA-issued SSL certificate name to match the host name of the
	 * server when connecting over SSL. The default value is false.
	 * @member {object} [typeProperties.allowSelfSignedServerCert] Specifies
	 * whether to allow self-signed certificates from the server. The default value
	 * is false.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface SparkLinkedService extends SharedModel.LinkedService {
		typeProperties: SparkLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ShopifyLinkedServiceTypeProperties class.
	 * @constructor
	 * Shopify Serivce linked service properties.
	 *
	 * @member {object} host The endpoint of the Shopify server. (i.e.
	 * mystore.myshopify.com)
	 * @member {object} [accessToken] The API access token that can be used to
	 * access Shopify’s data. The token won't expire if it is offline mode.
	 * @member {string} [accessToken.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true.
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface ShopifyLinkedServiceTypeProperties {
		host: any;
		accessToken?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ShopifyLinkedService class.
	 * @constructor
	 * Shopify Serivce linked service.
	 *
	 * @member {object} typeProperties Shopify Serivce linked service properties.
	 * @member {object} [typeProperties.host] The endpoint of the Shopify server.
	 * (i.e. mystore.myshopify.com)
	 * @member {object} [typeProperties.accessToken] The API access token that can
	 * be used to access Shopify’s data. The token won't expire if it is offline
	 * mode.
	 * @member {string} [typeProperties.accessToken.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true.
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface ShopifyLinkedService extends SharedModel.LinkedService {
		typeProperties: ShopifyLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ServiceNowLinkedServiceTypeProperties class.
	 * @constructor
	 * ServiceNow server linked service properties.
	 *
	 * @member {object} endpoint The endpoint of the ServiceNow server. (i.e.
	 * <instance>.service-now.com)
	 * @member {string} authenticationType The authentication type to use. Possible
	 * values include: 'Basic', 'OAuth2'
	 * @member {object} [username] The user name used to connect to the ServiceNow
	 * server for Basic and OAuth2 authentication.
	 * @member {object} [password] The password corresponding to the user name for
	 * Basic and OAuth2 authentication.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [clientId] The client id for OAuth2 authentication.
	 * @member {object} [clientSecret] The client secret for OAuth2 authentication.
	 * @member {string} [clientSecret.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true.
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface ServiceNowLinkedServiceTypeProperties {
		endpoint: any;
		authenticationType: string;
		username?: any;
		password?: SharedModel.SecretBase | string;
		clientId?: any;
		clientSecret?: SharedModel.SecretBase | string;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ServiceNowLinkedService class.
	 * @constructor
	 * ServiceNow server linked service.
	 *
	 * @member {object} typeProperties ServiceNow server linked service properties.
	 * @member {object} [typeProperties.endpoint] The endpoint of the ServiceNow
	 * server. (i.e. <instance>.service-now.com)
	 * @member {string} [typeProperties.authenticationType] The authentication type
	 * to use. Possible values include: 'Basic', 'OAuth2'
	 * @member {object} [typeProperties.username] The user name used to connect to
	 * the ServiceNow server for Basic and OAuth2 authentication.
	 * @member {object} [typeProperties.password] The password corresponding to the
	 * user name for Basic and OAuth2 authentication.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.clientId] The client id for OAuth2
	 * authentication.
	 * @member {object} [typeProperties.clientSecret] The client secret for OAuth2
	 * authentication.
	 * @member {string} [typeProperties.clientSecret.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true.
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface ServiceNowLinkedService extends SharedModel.LinkedService {
		typeProperties: ServiceNowLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ServiceNowV2LinkedServiceTypeProperties class.
	 * @constructor
	 * ServiceNowV2 server linked service properties.
	 *
	 * @member {string} endpoint The endpoint of the ServiceNow server. (i.e.
	 * <instance>.service-now.com)
	 * @member {string} authenticationType The authentication type to use. Possible
	 * values include: 'Basic', 'OAuth2'
	 * @member {string} [username] The user name used to connect to the ServiceNow
	 * server for Basic and OAuth2 authentication.
	 * @member {string} [password] The password corresponding to the user name for
	 * Basic and OAuth2 authentication.
	 * @member {string} [clientId] The client id for OAuth2 authentication.
	 * @member {string} [clientSecret] The client secret for OAuth2 authentication.
	 * @member {string} [grantType] Specifies the type of OAuth2.0 flow that client app is using to access token.
	 * @member {string} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface ServiceNowV2LinkedServiceTypeProperties {
		endpoint: any;
		authenticationType: string;
		username: any;
		password: SharedModel.SecretBase | string;
		clientId?: any;
		clientSecret?: SharedModel.SecretBase | string;
		grantType?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the ServiceNowLinkedServiceV2 class.
	 * @constructor
	 * ServiceNowV2 server linked service.
	 *
	 * @member {object} typeProperties ServiceNow server linked service properties.
	 * @member {string} [typeProperties.endpoint] The endpoint of the ServiceNow
	 * server. (i.e. <instance>.service-now.com)
	 * @member {string} [typeProperties.authenticationType] The authentication type
	 * to use. Possible values include: 'Basic', 'OAuth2'
	 * @member {string} [typeProperties.username] The user name used to connect to
	 * the ServiceNow server for Basic and OAuth2 authentication.
	 * @member {string} [typeProperties.password] The password corresponding to the
	 * user name for Basic and OAuth2 authentication.
	 * @member {string} [typeProperties.clientId] The client id for OAuth2
	 * authentication.
	 * @member {string} [typeProperties.clientSecret] The client secret for OAuth2
	 * authentication.
	 * @member {string} [grantType] Specifies the type of OAuth2.0 flow that client app is using to access token.
	 * @member {string} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface ServiceNowV2LinkedService extends SharedModel.LinkedService {
		typeProperties: ServiceNowV2LinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the QuickBooksLinkedServiceTypeProperties class.
	 * @constructor
	 * QuickBooks server linked service properties.
	 *
	 * @member {object} endpoint The endpoint of the QuickBooks server. (i.e.
	 * quickbooks.api.intuit.com)
	 * @member {object} companyId The company ID of the QuickBooks company to
	 * authorize.
	 * @member {object} consumerKey The consumer key for OAuth 1.0 authentication.
	 * @member {object} consumerSecret The consumer secret for OAuth 1.0
	 * authentication.
	 * @member {string} [consumerSecret.type] Polymorphic Discriminator
	 * @member {object} accessToken The access token for OAuth 1.0 authentication.
	 * @member {string} [accessToken.type] Polymorphic Discriminator
	 * @member {object} accessTokenSecret The access token secret for OAuth 1.0
	 * authentication.
	 * @member {string} [accessTokenSecret.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface QuickBooksLinkedServiceTypeProperties {
		endpoint?: any;
		companyId?: any;
		consumerKey?: any;
		consumerSecret?: SharedModel.SecretBase;
		accessToken?: SharedModel.SecretBase;
		accessTokenSecret?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		encryptedCredential?: any;
		refreshToken?: any;
		connectionProperties?: QuickBooksLinkedServiceTypeProperties;
		[key: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the QuickBooksLinkedService class.
	 * @constructor
	 * QuickBooks server linked service.
	 *
	 * @member {object} typeProperties QuickBooks server linked service properties.
	 * @member {object} [typeProperties.endpoint] The endpoint of the QuickBooks
	 * server. (i.e. quickbooks.api.intuit.com)
	 * @member {object} [typeProperties.companyId] The company ID of the QuickBooks
	 * company to authorize.
	 * @member {object} [typeProperties.consumerKey] The consumer key for OAuth 1.0
	 * authentication.
	 * @member {object} [typeProperties.consumerSecret] The consumer secret for
	 * OAuth 1.0 authentication.
	 * @member {string} [typeProperties.consumerSecret.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.accessToken] The access token for OAuth 1.0
	 * authentication.
	 * @member {string} [typeProperties.accessToken.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.accessTokenSecret] The access token secret
	 * for OAuth 1.0 authentication.
	 * @member {string} [typeProperties.accessTokenSecret.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface QuickBooksLinkedService extends SharedModel.LinkedService {
		typeProperties: QuickBooksLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the PrestoLinkedServiceTypeProperties class.
	 * @constructor
	 * Presto server linked service properties.
	 *
	 * @member {object} host The IP address or host name of the Presto server.
	 * (i.e. 192.168.222.160)
	 * @member {object} serverVersion The version of the Presto server. (i.e.
	 * 0.148-t) no longer exist in v2
	 * @member {object} catalog The catalog context for all request against the
	 * server.
	 * @member {object} [port] The TCP port that the Presto server uses to listen
	 * for client connections. The default value is 8080.
	 * @member {string} authenticationType The authentication mechanism used to
	 * connect to the Presto server. Possible values include: 'Anonymous', 'LDAP'
	 * @member {object} [username] The user name used to connect to the Presto
	 * server.
	 * @member {object} [password] The password corresponding to the user name.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [enableSsl] Specifies whether the connections to the server
	 * are encrypted using SSL. The default value is false.
	 * @member {object} [trustedCertPath] The full path of the .pem file containing
	 * trusted CA certificates for verifying the server when connecting over SSL.
	 * This property can only be set when using SSL on self-hosted IR. The default
	 * value is the cacerts.pem file installed with the IR.
	 * @member {object} [useSystemTrustStore] Specifies whether to use a CA
	 * certificate from the system trust store or from a specified PEM file. The
	 * default value is false.
	 * @member {object} [allowHostNameCNMismatch] Specifies whether to require a
	 * CA-issued SSL certificate name to match the host name of the server when
	 * connecting over SSL. The default value is false.
	 * @member {object} [allowSelfSignedServerCert] Specifies whether to allow
	 * self-signed certificates from the server. The default value is false.
	 * @member {object} [timeZoneID] The local time zone used by the connection.
	 * Valid values for this option are specified in the IANA Time Zone Database.
	 * The default value is the system time zone.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface PrestoLinkedServiceTypeProperties {
		host: any;
		serverVersion?: any;
		catalog: any;
		port?: any;
		authenticationType: string;
		username?: any;
		password?: SharedModel.SecretBase;
		enableSsl?: any;
		trustedCertPath?: any;
		useSystemTrustStore?: any;
		allowHostNameCNMismatch?: any;
		allowSelfSignedServerCert?: any;
		timeZoneID?: any;
		encryptedCredential?: any;
		enableServerCertificateValidation?: any;
	}

	export interface PowerBIWorkspaceLinkedServiceTypeProperties {
		workspaceID: any;
		tenantID: string; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the PrestoLinkedService class.
	 * @constructor
	 * Presto server linked service.
	 *
	 * @member {object} typeProperties Presto server linked service properties.
	 * @member {object} [typeProperties.host] The IP address or host name of the
	 * Presto server. (i.e. 192.168.222.160)
	 * @member {object} [typeProperties.serverVersion] The version of the Presto
	 * server. (i.e. 0.148-t)
	 * @member {object} [typeProperties.catalog] The catalog context for all
	 * request against the server.
	 * @member {object} [typeProperties.port] The TCP port that the Presto server
	 * uses to listen for client connections. The default value is 8080.
	 * @member {string} [typeProperties.authenticationType] The authentication
	 * mechanism used to connect to the Presto server. Possible values include:
	 * 'Anonymous', 'LDAP'
	 * @member {object} [typeProperties.username] The user name used to connect to
	 * the Presto server.
	 * @member {object} [typeProperties.password] The password corresponding to the
	 * user name.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.enableSsl] Specifies whether the
	 * connections to the server are encrypted using SSL. The default value is
	 * false.
	 * @member {object} [typeProperties.trustedCertPath] The full path of the .pem
	 * file containing trusted CA certificates for verifying the server when
	 * connecting over SSL. This property can only be set when using SSL on
	 * self-hosted IR. The default value is the cacerts.pem file installed with the
	 * IR.
	 * @member {object} [typeProperties.useSystemTrustStore] Specifies whether to
	 * use a CA certificate from the system trust store or from a specified PEM
	 * file. The default value is false.
	 * @member {object} [typeProperties.allowHostNameCNMismatch] Specifies whether
	 * to require a CA-issued SSL certificate name to match the host name of the
	 * server when connecting over SSL. The default value is false.
	 * @member {object} [typeProperties.allowSelfSignedServerCert] Specifies
	 * whether to allow self-signed certificates from the server. The default value
	 * is false.
	 * @member {object} [typeProperties.timeZoneID] The local time zone used by the
	 * connection. Valid values for this option are specified in the IANA Time Zone
	 * Database. The default value is the system time zone.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface PrestoLinkedService extends SharedModel.LinkedService {
		typeProperties: PrestoLinkedServiceTypeProperties;
	}

	export interface PowerBIWorkspaceLinkedService extends SharedModel.LinkedService {
		typeProperties: PowerBIWorkspaceLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the PhoenixLinkedServiceTypeProperties class.
	 * @constructor
	 * Phoenix server linked service properties.
	 *
	 * @member {object} host The IP address or host name of the Phoenix server.
	 * (i.e. 192.168.222.160)
	 * @member {object} [port] The TCP port that the Phoenix server uses to listen
	 * for client connections. The default value is 8765.
	 * @member {object} [httpPath] The partial URL corresponding to the Phoenix
	 * server. (i.e. /gateway/sandbox/phoenix/version). The default value is
	 * hbasephoenix if using WindowsAzureHDInsightService.
	 * @member {string} authenticationType The authentication mechanism used to
	 * connect to the Phoenix server. Possible values include: 'Anonymous',
	 * 'UsernameAndPassword', 'WindowsAzureHDInsightService'
	 * @member {object} [username] The user name used to connect to the Phoenix
	 * server.
	 * @member {object} [password] The password corresponding to the user name.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [enableSsl] Specifies whether the connections to the server
	 * are encrypted using SSL. The default value is false.
	 * @member {object} [trustedCertPath] The full path of the .pem file containing
	 * trusted CA certificates for verifying the server when connecting over SSL.
	 * This property can only be set when using SSL on self-hosted IR. The default
	 * value is the cacerts.pem file installed with the IR.
	 * @member {object} [useSystemTrustStore] Specifies whether to use a CA
	 * certificate from the system trust store or from a specified PEM file. The
	 * default value is false.
	 * @member {object} [allowHostNameCNMismatch] Specifies whether to require a
	 * CA-issued SSL certificate name to match the host name of the server when
	 * connecting over SSL. The default value is false.
	 * @member {object} [allowSelfSignedServerCert] Specifies whether to allow
	 * self-signed certificates from the server. The default value is false.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface PhoenixLinkedServiceTypeProperties {
		host: any;
		port?: any;
		httpPath?: any;
		authenticationType: string;
		username?: any;
		password?: SharedModel.SecretBase;
		enableSsl?: any;
		trustedCertPath?: any;
		useSystemTrustStore?: any;
		allowHostNameCNMismatch?: any;
		allowSelfSignedServerCert?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the PhoenixLinkedService class.
	 * @constructor
	 * Phoenix server linked service.
	 *
	 * @member {object} typeProperties Phoenix server linked service properties.
	 * @member {object} [typeProperties.host] The IP address or host name of the
	 * Phoenix server. (i.e. 192.168.222.160)
	 * @member {object} [typeProperties.port] The TCP port that the Phoenix server
	 * uses to listen for client connections. The default value is 8765.
	 * @member {object} [typeProperties.httpPath] The partial URL corresponding to
	 * the Phoenix server. (i.e. /gateway/sandbox/phoenix/version). The default
	 * value is hbasephoenix if using WindowsAzureHDInsightService.
	 * @member {string} [typeProperties.authenticationType] The authentication
	 * mechanism used to connect to the Phoenix server. Possible values include:
	 * 'Anonymous', 'UsernameAndPassword', 'WindowsAzureHDInsightService'
	 * @member {object} [typeProperties.username] The user name used to connect to
	 * the Phoenix server.
	 * @member {object} [typeProperties.password] The password corresponding to the
	 * user name.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.enableSsl] Specifies whether the
	 * connections to the server are encrypted using SSL. The default value is
	 * false.
	 * @member {object} [typeProperties.trustedCertPath] The full path of the .pem
	 * file containing trusted CA certificates for verifying the server when
	 * connecting over SSL. This property can only be set when using SSL on
	 * self-hosted IR. The default value is the cacerts.pem file installed with the
	 * IR.
	 * @member {object} [typeProperties.useSystemTrustStore] Specifies whether to
	 * use a CA certificate from the system trust store or from a specified PEM
	 * file. The default value is false.
	 * @member {object} [typeProperties.allowHostNameCNMismatch] Specifies whether
	 * to require a CA-issued SSL certificate name to match the host name of the
	 * server when connecting over SSL. The default value is false.
	 * @member {object} [typeProperties.allowSelfSignedServerCert] Specifies
	 * whether to allow self-signed certificates from the server. The default value
	 * is false.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface PhoenixLinkedService extends SharedModel.LinkedService {
		typeProperties: PhoenixLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the PaypalLinkedServiceTypeProperties class.
	 * @constructor
	 * Paypal Serivce linked service properties.
	 *
	 * @member {object} host The URL of the PayPal instance. (i.e.
	 * api.sandbox.paypal.com)
	 * @member {object} clientId The client ID associated with your PayPal
	 * application.
	 * @member {object} [clientSecret] The client secret associated with your
	 * PayPal application.
	 * @member {string} [clientSecret.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true.
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface PaypalLinkedServiceTypeProperties {
		host: any;
		clientId: any;
		clientSecret?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the PaypalLinkedService class.
	 * @constructor
	 * Paypal Serivce linked service.
	 *
	 * @member {object} typeProperties Paypal Serivce linked service properties.
	 * @member {object} [typeProperties.host] The URL of the PayPal instance. (i.e.
	 * api.sandbox.paypal.com)
	 * @member {object} [typeProperties.clientId] The client ID associated with
	 * your PayPal application.
	 * @member {object} [typeProperties.clientSecret] The client secret associated
	 * with your PayPal application.
	 * @member {string} [typeProperties.clientSecret.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true.
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface PaypalLinkedService extends SharedModel.LinkedService {
		typeProperties: PaypalLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the MarketoLinkedServiceTypeProperties class.
	 * @constructor
	 * Marketo server linked service properties.
	 *
	 * @member {object} endpoint The endpoint of the Marketo server. (i.e.
	 * 123-ABC-321.mktorest.com)
	 * @member {object} clientId The client Id of your Marketo service.
	 * @member {object} [clientSecret] The client secret of your Marketo service.
	 * @member {string} [clientSecret.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true.
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface MarketoLinkedServiceTypeProperties {
		endpoint: any;
		clientId: any;
		clientSecret?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the MarketoLinkedService class.
	 * @constructor
	 * Marketo server linked service.
	 *
	 * @member {object} typeProperties Marketo server linked service properties.
	 * @member {object} [typeProperties.endpoint] The endpoint of the Marketo
	 * server. (i.e. 123-ABC-321.mktorest.com)
	 * @member {object} [typeProperties.clientId] The client Id of your Marketo
	 * service.
	 * @member {object} [typeProperties.clientSecret] The client secret of your
	 * Marketo service.
	 * @member {string} [typeProperties.clientSecret.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true.
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface MarketoLinkedService extends SharedModel.LinkedService {
		typeProperties: MarketoLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the MariaDBLinkedServiceTypeProperties class.
	 * @constructor
	 * MariaDB server linked service properties.
	 *
	 * @member {object} [connectionString] An ODBC connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface MariaDBLinkedServiceTypeProperties {
		connectionString?: any;
		pwd?: any;
		encryptedCredential?: any;
		driverVersion?: string;
		server?: any;
		database?: any;
		port?: any,
		username?: any
		password?: any;
		sslMode?: any;
		useSystemTrustStore?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the MariaDBLinkedService class.
	 * @constructor
	 * MariaDB server linked service.
	 *
	 * @member {object} typeProperties MariaDB server linked service properties.
	 * @member {object} [typeProperties.connectionString] An ODBC connection
	 * string. Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface MariaDBLinkedService extends SharedModel.LinkedService {
		typeProperties: MariaDBLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the MagentoLinkedServiceTypeProperties class.
	 * @constructor
	 * Magento server linked service properties.
	 *
	 * @member {object} host The URL of the Magento instance. (i.e.
	 * 192.168.222.110/magento3)
	 * @member {object} [accessToken] The access token from Magento.
	 * @member {string} [accessToken.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true.
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface MagentoLinkedServiceTypeProperties {
		host: any;
		accessToken?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the MagentoLinkedService class.
	 * @constructor
	 * Magento server linked service.
	 *
	 * @member {object} typeProperties Magento server linked service properties.
	 * @member {object} [typeProperties.host] The URL of the Magento instance.
	 * (i.e. 192.168.222.110/magento3)
	 * @member {object} [typeProperties.accessToken] The access token from Magento.
	 * @member {string} [typeProperties.accessToken.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true.
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface MagentoLinkedService extends SharedModel.LinkedService {
		typeProperties: MagentoLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the JiraLinkedServiceTypeProperties class.
	 * @constructor
	 * Jira Serivce linked service properties.
	 *
	 * @member {object} host The IP address or host name of the Jira service. (e.g.
	 * jira.example.com)
	 * @member {object} [port] The TCP port that the Jira server uses to listen for
	 * client connections. The default value is 443 if connecting through HTTPS, or
	 * 8080 if connecting through HTTP.
	 * @member {object} username The user name that you use to access Jira Service.
	 * @member {object} [password] The password corresponding to the user name that
	 * you provided in the username field.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true.
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface JiraLinkedServiceTypeProperties {
		host: any;
		port?: any;
		username: any;
		password?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
		[key: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the JiraLinkedService class.
	 * @constructor
	 * Jira Serivce linked service.
	 *
	 * @member {object} typeProperties Jira Serivce linked service properties.
	 * @member {object} [typeProperties.host] The IP address or host name of the
	 * Jira service. (e.g. jira.example.com)
	 * @member {object} [typeProperties.port] The TCP port that the Jira server
	 * uses to listen for client connections. The default value is 443 if
	 * connecting through HTTPS, or 8080 if connecting through HTTP.
	 * @member {object} [typeProperties.username] The user name that you use to
	 * access Jira Service.
	 * @member {object} [typeProperties.password] The password corresponding to the
	 * user name that you provided in the username field.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true.
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface JiraLinkedService extends SharedModel.LinkedService {
		typeProperties: JiraLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the VivaInsightsLinkedService class.
	 * @constructor
	 * Viva Insights linked service. The connection carries no properties of its own: the
	 * credential and the analysis scope are resolved at run time from the bound connection.
	 */
	export interface VivaInsightsLinkedService extends SharedModel.LinkedService {
	}

	export interface GenericCDataLinkedService extends SharedModel.LinkedService {
	}

	export interface AttioLinkedServiceTypeProperties {
		url: string | Expression;
		authentication: string;
		accessToken?: string | SharedModel.SecretBase;
	}

	export interface AttioLinkedService extends SharedModel.LinkedService {
		version?: string;
		sideBySideConnectorVersion?: string;
		typeProperties: AttioLinkedServiceTypeProperties;
	}

	export interface BitbucketLinkedServiceTypeProperties {
		host: string | Expression;
		accessToken?: string | SharedModel.SecretBase;
	}

	export interface BitbucketLinkedService extends SharedModel.LinkedService {
		version?: string;
		typeProperties: BitbucketLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ImpalaLinkedServiceTypeProperties class.
	 * @constructor
	 * Impala server linked service properties.
	 *
	 * @member {object} host The IP address or host name of the Impala server.
	 * (i.e. 192.168.222.160)
	 * @member {object} [port] The TCP port that the Impala server uses to listen
	 * for client connections. The default value is 21050.
	 * @member {string} authenticationType The authentication type to use. Possible
	 * values include: 'Anonymous', 'SASLUsername', 'UsernameAndPassword'
	 * @member {object} [username] The user name used to access the Impala server.
	 * The default value is anonymous when using SASLUsername.
	 * @member {object} [password] The password corresponding to the user name when
	 * using UsernameAndPassword.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [enableSsl] Specifies whether the connections to the server
	 * are encrypted using SSL. The default value is false.
	 * @member {object} [trustedCertPath] The full path of the .pem file containing
	 * trusted CA certificates for verifying the server when connecting over SSL.
	 * This property can only be set when using SSL on self-hosted IR. The default
	 * value is the cacerts.pem file installed with the IR.
	 * @member {object} [useSystemTrustStore] Specifies whether to use a CA
	 * certificate from the system trust store or from a specified PEM file. The
	 * default value is false.
	 * @member {object} [allowHostNameCNMismatch] Specifies whether to require a
	 * CA-issued SSL certificate name to match the host name of the server when
	 * connecting over SSL. The default value is false.
	 * @member {object} [allowSelfSignedServerCert] Specifies whether to allow
	 * self-signed certificates from the server. The default value is false.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface ImpalaLinkedServiceTypeProperties {
		host: any;
		port?: any;
		authenticationType: string;
		username?: any;
		password?: SharedModel.SecretBase;
		enableSsl?: any;
		trustedCertPath?: any;
		useSystemTrustStore?: any;
		allowHostNameCNMismatch?: any;
		allowSelfSignedServerCert?: any;
		encryptedCredential?: any;
		thriftTransportProtocol?: string;
		enableServerCertificateValidation?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ImpalaLinkedService class.
	 * @constructor
	 * Impala server linked service.
	 *
	 * @member {object} typeProperties Impala server linked service properties.
	 * @member {object} [typeProperties.host] The IP address or host name of the
	 * Impala server. (i.e. 192.168.222.160)
	 * @member {object} [typeProperties.port] The TCP port that the Impala server
	 * uses to listen for client connections. The default value is 21050.
	 * @member {string} [typeProperties.authenticationType] The authentication type
	 * to use. Possible values include: 'Anonymous', 'SASLUsername',
	 * 'UsernameAndPassword'
	 * @member {object} [typeProperties.username] The user name used to access the
	 * Impala server. The default value is anonymous when using SASLUsername.
	 * @member {object} [typeProperties.password] The password corresponding to the
	 * user name when using UsernameAndPassword.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.enableSsl] Specifies whether the
	 * connections to the server are encrypted using SSL. The default value is
	 * false.
	 * @member {object} [typeProperties.trustedCertPath] The full path of the .pem
	 * file containing trusted CA certificates for verifying the server when
	 * connecting over SSL. This property can only be set when using SSL on
	 * self-hosted IR. The default value is the cacerts.pem file installed with the
	 * IR.
	 * @member {object} [typeProperties.useSystemTrustStore] Specifies whether to
	 * use a CA certificate from the system trust store or from a specified PEM
	 * file. The default value is false.
	 * @member {object} [typeProperties.allowHostNameCNMismatch] Specifies whether
	 * to require a CA-issued SSL certificate name to match the host name of the
	 * server when connecting over SSL. The default value is false.
	 * @member {object} [typeProperties.allowSelfSignedServerCert] Specifies
	 * whether to allow self-signed certificates from the server. The default value
	 * is false.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface ImpalaLinkedService extends SharedModel.LinkedService {
		typeProperties: ImpalaLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the HubspotLinkedServiceTypeProperties class.
	 * @constructor
	 * Hubspot Serivce linked service properties.
	 *
	 * @member {object} clientId The client ID associated with your Hubspot
	 * application.
	 * @member {object} [clientSecret] The client secret associated with your
	 * Hubspot application.
	 * @member {string} [clientSecret.type] Polymorphic Discriminator
	 * @member {object} [accessToken] The access token obtained when initially
	 * authenticating your OAuth integration.
	 * @member {string} [accessToken.type] Polymorphic Discriminator
	 * @member {object} [refreshToken] The refresh token obtained when initially
	 * authenticating your OAuth integration.
	 * @member {string} [refreshToken.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true.
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface HubspotLinkedServiceTypeProperties {
		clientId: any;
		clientSecret?: SharedModel.SecretBase;
		accessToken?: SharedModel.SecretBase;
		refreshToken?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the HubspotLinkedService class.
	 * @constructor
	 * Hubspot Serivce linked service.
	 *
	 * @member {object} typeProperties Hubspot Serivce linked service properties.
	 * @member {object} [typeProperties.clientId] The client ID associated with
	 * your Hubspot application.
	 * @member {object} [typeProperties.clientSecret] The client secret associated
	 * with your Hubspot application.
	 * @member {string} [typeProperties.clientSecret.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.accessToken] The access token obtained when
	 * initially authenticating your OAuth integration.
	 * @member {string} [typeProperties.accessToken.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.refreshToken] The refresh token obtained
	 * when initially authenticating your OAuth integration.
	 * @member {string} [typeProperties.refreshToken.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true.
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface HubspotLinkedService extends SharedModel.LinkedService {
		typeProperties: HubspotLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the HiveLinkedServiceTypeProperties class.
	 * @constructor
	 * Hive Server linked service properties.
	 *
	 * @member {object} host IP address or host name of the Hive server, separated
	 * by ';' for multiple hosts (only when serviceDiscoveryMode is enable).
	 * @member {object} [port] The TCP port that the Hive server uses to listen for
	 * client connections.
	 * @member {string} [serverType] The type of Hive server. Possible values
	 * include: 'HiveServer1', 'HiveServer2', 'HiveThriftServer'
	 * @member {string} [thriftTransportProtocol] The transport protocol to use in
	 * the Thrift layer. Possible values include: 'Binary', 'SASL', 'HTTP '
	 * @member {string} authenticationType The authentication method used to access
	 * the Hive server. Possible values include: 'Anonymous', 'Username',
	 * 'UsernameAndPassword', 'WindowsAzureHDInsightService'
	 * @member {object} [serviceDiscoveryMode] true to indicate using the ZooKeeper
	 * service, false not.
	 * @member {object} [zooKeeperNameSpace] The namespace on ZooKeeper under which
	 * Hive Server 2 nodes are added.
	 * @member {object} [useNativeQuery] Specifies whether the driver uses native
	 * HiveQL queries,or converts them into an equivalent form in HiveQL.
	 * @member {object} [username] The user name that you use to access Hive
	 * Server.
	 * @member {object} [password] The password corresponding to the user name that
	 * you provided in the Username field
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [httpPath] The partial URL corresponding to the Hive
	 * server.
	 * @member {object} [enableSsl] Specifies whether the connections to the server
	 * are encrypted using SSL. The default value is false.
	 * @member {object} [trustedCertPath] The full path of the .pem file containing
	 * trusted CA certificates for verifying the server when connecting over SSL.
	 * This property can only be set when using SSL on self-hosted IR. The default
	 * value is the cacerts.pem file installed with the IR.
	 * @member {object} [useSystemTrustStore] Specifies whether to use a CA
	 * certificate from the system trust store or from a specified PEM file. The
	 * default value is false.
	 * @member {object} [allowHostNameCNMismatch] Specifies whether to require a
	 * CA-issued SSL certificate name to match the host name of the server when
	 * connecting over SSL. The default value is false.
	 * @member {object} [allowSelfSignedServerCert] Specifies whether to allow
	 * self-signed certificates from the server. The default value is false.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface HiveLinkedServiceTypeProperties {
		host: any;
		port?: any;
		serverType?: string;
		thriftTransportProtocol?: string;
		authenticationType: string;
		serviceDiscoveryMode?: any;
		zooKeeperNameSpace?: any;
		useNativeQuery?: any;
		username?: any;
		password?: SharedModel.SecretBase;
		httpPath?: any;
		enableSsl?: any;
		trustedCertPath?: any;
		useSystemTrustStore?: any;
		allowHostNameCNMismatch?: any;
		allowSelfSignedServerCert?: any;
		encryptedCredential?: any;
		storageReference?: SharedModel.LinkedServiceReference,
		enableServerCertificateValidation?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the HiveLinkedService class.
	 * @constructor
	 * Hive Server linked service.
	 *
	 * @member {object} typeProperties Hive Server linked service properties.
	 * @member {object} [typeProperties.host] IP address or host name of the Hive
	 * server, separated by ';' for multiple hosts (only when serviceDiscoveryMode
	 * is enable).
	 * @member {object} [typeProperties.port] The TCP port that the Hive server
	 * uses to listen for client connections.
	 * @member {string} [typeProperties.serverType] The type of Hive server.
	 * Possible values include: 'HiveServer1', 'HiveServer2', 'HiveThriftServer'
	 * @member {string} [typeProperties.thriftTransportProtocol] The transport
	 * protocol to use in the Thrift layer. Possible values include: 'Binary',
	 * 'SASL', 'HTTP '
	 * @member {string} [typeProperties.authenticationType] The authentication
	 * method used to access the Hive server. Possible values include: 'Anonymous',
	 * 'Username', 'UsernameAndPassword', 'WindowsAzureHDInsightService'
	 * @member {object} [typeProperties.serviceDiscoveryMode] true to indicate
	 * using the ZooKeeper service, false not.
	 * @member {object} [typeProperties.zooKeeperNameSpace] The namespace on
	 * ZooKeeper under which Hive Server 2 nodes are added.
	 * @member {object} [typeProperties.useNativeQuery] Specifies whether the
	 * driver uses native HiveQL queries,or converts them into an equivalent form
	 * in HiveQL.
	 * @member {object} [typeProperties.username] The user name that you use to
	 * access Hive Server.
	 * @member {object} [typeProperties.password] The password corresponding to the
	 * user name that you provided in the Username field
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.httpPath] The partial URL corresponding to
	 * the Hive server.
	 * @member {object} [typeProperties.enableSsl] Specifies whether the
	 * connections to the server are encrypted using SSL. The default value is
	 * false.
	 * @member {object} [typeProperties.trustedCertPath] The full path of the .pem
	 * file containing trusted CA certificates for verifying the server when
	 * connecting over SSL. This property can only be set when using SSL on
	 * self-hosted IR. The default value is the cacerts.pem file installed with the
	 * IR.
	 * @member {object} [typeProperties.useSystemTrustStore] Specifies whether to
	 * use a CA certificate from the system trust store or from a specified PEM
	 * file. The default value is false.
	 * @member {object} [typeProperties.allowHostNameCNMismatch] Specifies whether
	 * to require a CA-issued SSL certificate name to match the host name of the
	 * server when connecting over SSL. The default value is false.
	 * @member {object} [typeProperties.allowSelfSignedServerCert] Specifies
	 * whether to allow self-signed certificates from the server. The default value
	 * is false.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.storageReference]
	 * Storage referene
	 */
	export interface HiveLinkedService extends SharedModel.LinkedService {
		typeProperties: HiveLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the HBaseLinkedServiceTypeProperties class.
	 * @constructor
	 * HBase server linked service properties.
	 *
	 * @member {object} host The IP address or host name of the HBase server. (i.e.
	 * 192.168.222.160)
	 * @member {object} [port] The TCP port that the HBase instance uses to listen
	 * for client connections. The default value is 9090.
	 * @member {object} [httpPath] The partial URL corresponding to the HBase
	 * server. (i.e. /gateway/sandbox/hbase/version)
	 * @member {string} authenticationType The authentication mechanism to use to
	 * connect to the HBase server. Possible values include: 'Anonymous', 'Basic'
	 * @member {object} [username] The user name used to connect to the HBase
	 * instance.
	 * @member {object} [password] The password corresponding to the user name.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [enableSsl] Specifies whether the connections to the server
	 * are encrypted using SSL. The default value is false.
	 * @member {object} [trustedCertPath] The full path of the .pem file containing
	 * trusted CA certificates for verifying the server when connecting over SSL.
	 * This property can only be set when using SSL on self-hosted IR. The default
	 * value is the cacerts.pem file installed with the IR.
	 * @member {object} [allowHostNameCNMismatch] Specifies whether to require a
	 * CA-issued SSL certificate name to match the host name of the server when
	 * connecting over SSL. The default value is false.
	 * @member {object} [allowSelfSignedServerCert] Specifies whether to allow
	 * self-signed certificates from the server. The default value is false.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface HBaseLinkedServiceTypeProperties {
		host: any;
		port?: any;
		httpPath?: any;
		authenticationType: string;
		username?: any;
		password?: SharedModel.SecretBase;
		enableSsl?: any;
		trustedCertPath?: any;
		allowHostNameCNMismatch?: any;
		allowSelfSignedServerCert?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the HBaseLinkedService class.
	 * @constructor
	 * HBase server linked service.
	 *
	 * @member {object} typeProperties HBase server linked service properties.
	 * @member {object} [typeProperties.host] The IP address or host name of the
	 * HBase server. (i.e. 192.168.222.160)
	 * @member {object} [typeProperties.port] The TCP port that the HBase instance
	 * uses to listen for client connections. The default value is 9090.
	 * @member {object} [typeProperties.httpPath] The partial URL corresponding to
	 * the HBase server. (i.e. /gateway/sandbox/hbase/version)
	 * @member {string} [typeProperties.authenticationType] The authentication
	 * mechanism to use to connect to the HBase server. Possible values include:
	 * 'Anonymous', 'Basic'
	 * @member {object} [typeProperties.username] The user name used to connect to
	 * the HBase instance.
	 * @member {object} [typeProperties.password] The password corresponding to the
	 * user name.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.enableSsl] Specifies whether the
	 * connections to the server are encrypted using SSL. The default value is
	 * false.
	 * @member {object} [typeProperties.trustedCertPath] The full path of the .pem
	 * file containing trusted CA certificates for verifying the server when
	 * connecting over SSL. This property can only be set when using SSL on
	 * self-hosted IR. The default value is the cacerts.pem file installed with the
	 * IR.
	 * @member {object} [typeProperties.allowHostNameCNMismatch] Specifies whether
	 * to require a CA-issued SSL certificate name to match the host name of the
	 * server when connecting over SSL. The default value is false.
	 * @member {object} [typeProperties.allowSelfSignedServerCert] Specifies
	 * whether to allow self-signed certificates from the server. The default value
	 * is false.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface HBaseLinkedService extends SharedModel.LinkedService {
		typeProperties: HBaseLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the GreenplumLinkedServiceTypeProperties class.
	 * @constructor
	 * Greenplum Database linked service properties.
	 *
	 * @member {object} [connectionString] An ODBC connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface GreenplumLinkedServiceTypeProperties {
		connectionString?: any;
		pwd?: SharedModel.AzureKeyVaultSecretReference | SharedModel.SecretBase;
		password?: SharedModel.AzureKeyVaultSecretReference | SharedModel.SecretBase; // v1 akv
		encryptedCredential?: any;
		sslMode?: any;
		host?: any;
		port?: any;
		database?: any;
		username?: any;
		authenticationType?: any;
		// additional fields
		commandTimeout?: any;
		connectionTimeout?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the GreenplumLinkedService class.
	 * @constructor
	 * Greenplum Database linked service.
	 *
	 * @member {object} typeProperties Greenplum Database linked service
	 * properties.
	 * @member {object} [typeProperties.connectionString] An ODBC connection
	 * string. Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface GreenplumLinkedService extends SharedModel.LinkedService {
		typeProperties: GreenplumLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the GoogleBigQueryLinkedServiceTypeProperties class.
	 * @constructor
	 * Google BigQuery service linked service properties.
	 *
	 * @member {object} project The default BigQuery project to query against.
	 * @member {object} [additionalProjects] A comma-separated list of public
	 * BigQuery projects to access.
	 * @member {object} [requestGoogleDriveScope] Whether to request access to
	 * Google Drive. Allowing Google Drive access enables support for federated
	 * tables that combine BigQuery data with data from Google Drive. The default
	 * value is false.
	 * @member {string} authenticationType The OAuth 2.0 authentication mechanism
	 * used for authentication. ServiceAuthentication can only be used on
	 * self-hosted IR. Possible values include: 'ServiceAuthentication',
	 * 'UserAuthentication'
	 * @member {object} [refreshToken] The refresh token obtained from Google for
	 * authorizing access to BigQuery for UserAuthentication.
	 * @member {string} [refreshToken.type] Polymorphic Discriminator
	 * @member {object} [clientId] The client id of the google application used to
	 * acquire the refresh token.
	 * @member {string} [clientId.type] Polymorphic Discriminator
	 * @member {object} [clientSecret] The client secret of the google application
	 * used to acquire the refresh token.
	 * @member {string} [clientSecret.type] Polymorphic Discriminator
	 * @member {object} [email] The service account email ID that is used for
	 * ServiceAuthentication and can only be used on self-hosted IR.
	 * @member {object} [keyFilePath] The full path to the .p12 key file that is
	 * used to authenticate the service account email address and can only be used
	 * on self-hosted IR.
	 * @member {object} [trustedCertPath] The full path of the .pem file containing
	 * trusted CA certificates for verifying the server when connecting over SSL.
	 * This property can only be set when using SSL on self-hosted IR. The default
	 * value is the cacerts.pem file installed with the IR.
	 * @member {object} [useSystemTrustStore] Specifies whether to use a CA
	 * certificate from the system trust store or from a specified PEM file. The
	 * default value is false.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface GoogleBigQueryLinkedServiceTypeProperties {
		project: any;
		additionalProjects?: any;
		requestGoogleDriveScope?: any;
		authenticationType: string;
		refreshToken?: string | SharedModel.SecretBase;
		clientId?: string; // handcrafted
		clientSecret?: string | SharedModel.SecretBase;
		email?: any;
		keyFilePath?: any;
		trustedCertPath?: any;
		useSystemTrustStore?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the GoogleBigQueryV2LinkedServiceTypeProperties class.
	 * @constructor
	 * Google BigQuery service linked service properties.
	 *
	 * @member {object} projectId The default BigQuery project to query against.
	 * @member {string} authenticationType The OAuth 2.0 authentication mechanism
	 * used for authentication. ServiceAuthentication can only be used on
	 * self-hosted IR. Possible values include: 'ServiceAuthentication',
	 * 'UserAuthentication'
	 * @member {object} [refreshToken] The refresh token obtained from Google for
	 * authorizing access to BigQuery for UserAuthentication.
	 * @member {string} [refreshToken.type] Polymorphic Discriminator
	 * @member {object} [clientId] The client id of the google application used to
	 * acquire the refresh token.
	 * @member {string} [clientId.type] Polymorphic Discriminator
	 * @member {object} [clientSecret] The client secret of the google application
	 * used to acquire the refresh token.
	 * @member {string} [clientSecret.type] Polymorphic Discriminator
	 * @member {object} [keyFileContent] The file content to the .p12 key file that is
	 * used to authenticate the service account email address and can only be used
	 * on self-hosted IR.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface GoogleBigQueryV2LinkedServiceTypeProperties {
		projectId: any;
		authenticationType: string;
		refreshToken?: string | SharedModel.SecretBase;
		clientId?: string; // handcrafted
		clientSecret?: string | SharedModel.SecretBase;
		keyFileContent?: string | SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the GoogleBigQueryLinkedService class.
	 * @constructor
	 * Google BigQuery service linked service.
	 *
	 * @member {object} typeProperties Google BigQuery service linked service
	 * properties.
	 * @member {object} [typeProperties.project] The default BigQuery project to
	 * query against.
	 * @member {object} [typeProperties.additionalProjects] A comma-separated list
	 * of public BigQuery projects to access.
	 * @member {object} [typeProperties.requestGoogleDriveScope] Whether to request
	 * access to Google Drive. Allowing Google Drive access enables support for
	 * federated tables that combine BigQuery data with data from Google Drive. The
	 * default value is false.
	 * @member {string} [typeProperties.authenticationType] The OAuth 2.0
	 * authentication mechanism used for authentication. ServiceAuthentication can
	 * only be used on self-hosted IR. Possible values include:
	 * 'ServiceAuthentication', 'UserAuthentication'
	 * @member {object} [typeProperties.refreshToken] The refresh token obtained
	 * from Google for authorizing access to BigQuery for UserAuthentication.
	 * @member {string} [typeProperties.refreshToken.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.clientId] The client id of the google
	 * application used to acquire the refresh token.
	 * @member {string} [typeProperties.clientId.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.clientSecret] The client secret of the
	 * google application used to acquire the refresh token.
	 * @member {string} [typeProperties.clientSecret.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.email] The service account email ID that is
	 * used for ServiceAuthentication and can only be used on self-hosted IR.
	 * @member {object} [typeProperties.keyFilePath] The full path to the .p12 key
	 * file that is used to authenticate the service account email address and can
	 * only be used on self-hosted IR.
	 * @member {object} [typeProperties.trustedCertPath] The full path of the .pem
	 * file containing trusted CA certificates for verifying the server when
	 * connecting over SSL. This property can only be set when using SSL on
	 * self-hosted IR. The default value is the cacerts.pem file installed with the
	 * IR.
	 * @member {object} [typeProperties.useSystemTrustStore] Specifies whether to
	 * use a CA certificate from the system trust store or from a specified PEM
	 * file. The default value is false.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface GoogleBigQueryLinkedService extends SharedModel.LinkedService {
		typeProperties: GoogleBigQueryLinkedServiceTypeProperties;
	}

	export interface GoogleBigQueryV2LinkedService extends SharedModel.LinkedService {
		typeProperties: GoogleBigQueryV2LinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the EloquaLinkedServiceTypeProperties class.
	 * @constructor
	 * Eloqua server linked service properties.
	 *
	 * @member {object} endpoint The endpoint of the Eloqua server. (i.e.
	 * eloqua.example.com)
	 * @member {object} username The site name and user name of your Eloqua account
	 * in the form: sitename/username. (i.e. Eloqua/Alice)
	 * @member {object} [password] The password corresponding to the user name.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true.
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface EloquaLinkedServiceTypeProperties {
		endpoint: any;
		username: any;
		password?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the EloquaLinkedService class.
	 * @constructor
	 * Eloqua server linked service.
	 *
	 * @member {object} typeProperties Eloqua server linked service properties.
	 * @member {object} [typeProperties.endpoint] The endpoint of the Eloqua
	 * server. (i.e. eloqua.example.com)
	 * @member {object} [typeProperties.username] The site name and user name of
	 * your Eloqua account in the form: sitename/username. (i.e. Eloqua/Alice)
	 * @member {object} [typeProperties.password] The password corresponding to the
	 * user name.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true.
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface EloquaLinkedService extends SharedModel.LinkedService {
		typeProperties: EloquaLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the DrillLinkedServiceTypeProperties class.
	 * @constructor
	 * Drill server linked service properties.
	 *
	 * @member {object} [connectionString] An ODBC connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface DrillLinkedServiceTypeProperties {
		connectionString?: any;
		pwd?: SharedModel.AzureKeyVaultSecretReference;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DrillLinkedService class.
	 * @constructor
	 * Drill server linked service.
	 *
	 * @member {object} typeProperties Drill server linked service properties.
	 * @member {object} [typeProperties.connectionString] An ODBC connection
	 * string. Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface DrillLinkedService extends SharedModel.LinkedService {
		typeProperties: DrillLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the CouchbaseLinkedServiceTypeProperties class.
	 * @constructor
	 * Couchbase server linked service properties.
	 *
	 * @member {object} [connectionString] An ODBC connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface CouchbaseLinkedServiceTypeProperties {
		connectionString?: any;
		credString?: SharedModel.AzureKeyVaultSecretReference;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the CouchbaseLinkedService class.
	 * @constructor
	 * Couchbase server linked service.
	 *
	 * @member {object} typeProperties Couchbase server linked service properties.
	 * @member {object} [typeProperties.connectionString] An ODBC connection
	 * string. Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface CouchbaseLinkedService extends SharedModel.LinkedService {
		typeProperties: CouchbaseLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ConcurLinkedServiceTypeProperties class.
	 * @constructor
	 * Concur Serivce linked service properties.
	 *
	 * connect to the Concur server. Possible values include: 'OAuth_2.0', 'OAuth_2.0_Bearer'
	 * @member {object} [clientId] Client ID associated with your Concur account. - kept for backward compatibility
	 * @member {object} [username] The user name that you use to access Concur Service. - kept for backward compatibility
	 * @member {object} [password] The password corresponding to the user name that
	 * you provided in the username field. - kept for backward compatibility
	 * @member {string} [password.type] Polymorphic Discriminator. - kept for backward compatibility
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true. - kept for backward compatibility
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true. - kept for backward compatibility
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true. - kept for backward compatibility
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string). - kept for backward compatibility
	 * @member {object} [connectionProperties] New authentication version
	 */
	export interface ConcurLinkedServiceTypeProperties {
		clientId?: any;
		username?: any;
		password?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
		connectionProperties?: ConcurLinkedServiceConnectionProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ConcurLinkedServiceConnectionProperties class.
	 * @constructor
	 * Concur Serivce linked service properties.
	 *
	 * @member {object} [host] Host name of the FTP server.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [baseUrl] The base URL of Concur.
	 * @member {string} [authenticationType] The authentication type to be used to
	 * connect to the Concur server. Possible values include: 'OAuth_2.0', 'OAuth_2.0_Bearer'
	 * @member {object} [clientId] Client ID associated with your Concur account
	 * @member {object} [clientSecret] The client secret generated from your Adobe I/O Console integration.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [username] The user name that you use to access Concur Service.
	 * @member {object} [password] The password corresponding to the user name that
	 * you provided in the username field.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true.
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface ConcurLinkedServiceConnectionProperties {
		host: any;
		baseUrl: any;
		authenticationType: any;
		clientId: any;
		clientSecret?: SharedModel.SecretBase;
		username: any;
		password?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
		[key: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ConcurLinkedService class.
	 * @constructor
	 * Concur Serivce linked service.
	 *
	 * @member {object} typeProperties Concur Serivce linked service properties.
	 * @member {object} [typeProperties.clientId] Application client_id supplied by
	 * Concur App Management.
	 * @member {object} [typeProperties.username] The user name that you use to
	 * access Concur Service.
	 * @member {object} [typeProperties.password] The password corresponding to the
	 * user name that you provided in the username field.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true.
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface ConcurLinkedService extends SharedModel.LinkedService {
		typeProperties: ConcurLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzurePostgreSqlLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure PostgreSQL linked service properties.
	 *
	 * @member {object} [connectionString] An ODBC connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzurePostgreSqlLinkedServiceTypeProperties {
		connectionString?: any;
		password?: SharedModel.AzureKeyVaultSecretReference | string | SharedModel.SecretBase;
		encryptedCredential?: any;
		server?: any;
		port?: any;
		database?: any;
		username?: any;
		sslMode?: any;

		authenticationType?: string;
		servicePrincipalCredentialType?: string;
		tenant?: any;
		tokenEndpoint?: any;
		azureCloudType?: any;
		servicePrincipalId?: any;
		servicePrincipalKey?: string | SharedModel.SecretBase; //handcraft
		servicePrincipalEmbeddedCert?: string | SharedModel.SecretBase; //handcraft
		servicePrincipalEmbeddedCertPassword?: string | SharedModel.SecretBase; //handcraft
		credential?: SharedModel.CredentialReference;

		// below are not shown in UI, only via additionalProperties (non-parameterizable)
		schema?: string;
		pooling?: boolean;
		timeout?: number;
		commandTimeout?: number;
		trustServerCertificate?: boolean;
		sslCertificate?: string;
		sslKey?: string;
		sslPassword?: SharedModel.SecretBase | string;
		readBufferSize?: number;
		logParameters?: boolean;
		timezone?: string;
		encoding?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzurePostgreSqlLinkedService class.
	 * @constructor
	 * Azure PostgreSQL linked service.
	 *
	 * @member {object} typeProperties Azure PostgreSQL linked service properties.
	 * @member {object} [typeProperties.connectionString] An ODBC connection
	 * string. Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzurePostgreSqlLinkedService extends SharedModel.LinkedService {
		typeProperties: AzurePostgreSqlLinkedServiceTypeProperties;
	}

	// handcrafted: Azure HorizonDB is PostgreSQL wire-compatible and shares the same backend shape.
	export interface AzureHorizonDBLinkedService extends AzurePostgreSqlLinkedService {
	}

	/**
	 * @class
	 * Initializes a new instance of the AmazonMWSLinkedServiceTypeProperties class.
	 * @constructor
	 * Amazon Marketplace Web Service linked service properties.
	 *
	 * @member {object} endpoint The endpoint of the Amazon MWS server, (i.e.
	 * mws.amazonservices.com)
	 * @member {object} marketplaceID The Amazon Marketplace ID you want to
	 * retrieve data from. To retrive data from multiple Marketplace IDs, seperate
	 * them with a comma (,). (i.e. A2EUQ1WTGCTBG2)
	 * @member {object} sellerID The Amazon seller ID.
	 * @member {object} [mwsAuthToken] The Amazon MWS authentication token.
	 * @member {string} [mwsAuthToken.type] Polymorphic Discriminator
	 * @member {object} accessKeyId The access key id used to access data.
	 * @member {object} [secretKey] The secret key used to access data.
	 * @member {string} [secretKey.type] Polymorphic Discriminator
	 * @member {object} [useEncryptedEndpoints] Specifies whether the data source
	 * endpoints are encrypted using HTTPS. The default value is true.
	 * @member {object} [useHostVerification] Specifies whether to require the host
	 * name in the server's certificate to match the host name of the server when
	 * connecting over SSL. The default value is true.
	 * @member {object} [usePeerVerification] Specifies whether to verify the
	 * identity of the server when connecting over SSL. The default value is true.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AmazonMWSLinkedServiceTypeProperties {
		endpoint: any;
		marketplaceID: any;
		sellerID: any;
		mwsAuthToken?: SharedModel.SecretBase;
		accessKeyId: any;
		secretKey?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AmazonMWSLinkedService class.
	 * @constructor
	 * Amazon Marketplace Web Service linked service.
	 *
	 * @member {object} typeProperties Amazon Marketplace Web Service linked
	 * service properties.
	 * @member {object} [typeProperties.endpoint] The endpoint of the Amazon MWS
	 * server, (i.e. mws.amazonservices.com)
	 * @member {object} [typeProperties.marketplaceID] The Amazon Marketplace ID
	 * you want to retrieve data from. To retrive data from multiple Marketplace
	 * IDs, seperate them with a comma (,). (i.e. A2EUQ1WTGCTBG2)
	 * @member {object} [typeProperties.sellerID] The Amazon seller ID.
	 * @member {object} [typeProperties.mwsAuthToken] The Amazon MWS authentication
	 * token.
	 * @member {string} [typeProperties.mwsAuthToken.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.accessKeyId] The access key id used to
	 * access data.
	 * @member {object} [typeProperties.secretKey] The secret key used to access
	 * data.
	 * @member {string} [typeProperties.secretKey.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.useEncryptedEndpoints] Specifies whether
	 * the data source endpoints are encrypted using HTTPS. The default value is
	 * true.
	 * @member {object} [typeProperties.useHostVerification] Specifies whether to
	 * require the host name in the server's certificate to match the host name of
	 * the server when connecting over SSL. The default value is true.
	 * @member {object} [typeProperties.usePeerVerification] Specifies whether to
	 * verify the identity of the server when connecting over SSL. The default
	 * value is true.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AmazonMWSLinkedService extends SharedModel.LinkedService {
		typeProperties: AmazonMWSLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SapHanaLinkedServiceProperties class.
	 * @constructor
	 * Properties specific to this linked service type.
	 *
	 * @member {object} server Host name of the SAP HANA server. Type: string (or
	 * Expression with resultType string).
	 * @member {string} [authenticationType] The authentication type to be used to
	 * connect to the SAP HANA server. Possible values include: 'Basic', 'Windows'
	 * @member {object} [userName] Username to access the SAP HANA server. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [password] Password to access the SAP HANA server.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface SapHanaLinkedServiceProperties {
		server?: any;
		connectionString?: any; // handcraft
		authenticationType?: string;
		userName?: any;
		password?: SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SapHanaLinkedService class.
	 * @constructor
	 * SAP HANA Linked Service.
	 *
	 * @member {object} typeProperties Properties specific to this linked service
	 * type.
	 * @member {object} [typeProperties.server] Host name of the SAP HANA server.
	 * Type: string (or Expression with resultType string).
	 * @member {string} [typeProperties.authenticationType] The authentication type
	 * to be used to connect to the SAP HANA server. Possible values include:
	 * 'Basic', 'Windows'
	 * @member {object} [typeProperties.userName] Username to access the SAP HANA
	 * server. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password to access the SAP HANA
	 * server.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface SapHanaLinkedService extends SharedModel.LinkedService {
		typeProperties: SapHanaLinkedServiceProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SapBWLinkedServiceTypeProperties class.
	 * @constructor
	 * Properties specific to this linked service type.
	 *
	 * @member {object} server Host name of the SAP BW instance. Type: string (or
	 * Expression with resultType string).
	 * @member {object} systemNumber System number of the BW system. (Usually a
	 * two-digit decimal number represented as a string.) Type: string (or
	 * Expression with resultType string).
	 * @member {object} clientId Client ID of the client on the BW system. (Usually
	 * a three-digit decimal number represented as a string) Type: string (or
	 * Expression with resultType string).
	 * @member {object} [userName] Username to access the SAP BW server. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [password] Password to access the SAP BW server.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface SapBWLinkedServiceTypeProperties {
		server: any;
		systemNumber: any;
		clientId: any;
		userName?: any;
		password?: SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SapBWLinkedService class.
	 * @constructor
	 * SAP Business Warehouse Linked Service.
	 *
	 * @member {object} typeProperties Properties specific to this linked service
	 * type.
	 * @member {object} [typeProperties.server] Host name of the SAP BW instance.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.systemNumber] System number of the BW
	 * system. (Usually a two-digit decimal number represented as a string.) Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.clientId] Client ID of the client on the BW
	 * system. (Usually a three-digit decimal number represented as a string) Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.userName] Username to access the SAP BW
	 * server. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password to access the SAP BW
	 * server.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface SapBWLinkedService extends SharedModel.LinkedService {
		typeProperties: SapBWLinkedServiceTypeProperties;
	}

	// handcrafted
	export interface SapOpenHubLinkedServiceTypeProperties {
		server?: any;
		systemNumber?: any;
		messageServer?: any,
		messageServerService?: any,
		systemId?: any,
		logonGroup?: any,
		clientId: any;
		language?: any;
		userName?: any;
		password?: SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	// handcrafted
	export interface SapTableLinkedServiceTypeProperties {
		server?: any;
		systemNumber?: any;
		messageServer?: any,
		messageServerService?: any,
		systemId?: any,
		logonGroup?: any,
		clientId: any;
		language?: any;
		sncMode?: any;
		sncMyName?: any;
		sncPartnerName?: any;
		sncLibraryPath?: any;
		sncQop?: any;
		userName?: any;
		password?: SharedModel.SecretBase | string;
		encryptedCredential?: any;
	}

	// handcrafted
	export interface SapOdpLinkedServiceTypeProperties {
		server?: any;
		systemNumber?: any;
		messageServer?: any,
		messageServerService?: any,
		systemId?: any,
		logonGroup?: any,
		clientId: any;
		language?: any;
		sncMode?: any;
		sncMyName?: any;
		sncPartnerName?: any;
		sncLibraryPath?: any;
		x509CertificatePath?: any;
		sncQop?: any;
		userName?: any;
		subscriberName?: any;
		password?: SharedModel.SecretBase | string;
		encryptedCredential?: any;
		isSSO?: boolean;
	}

	// handcrafted
	export interface SapODataLinkedServiceTypeProperties {
		hostUrl: string;
		catalogServicePath?: string;
		userName: string;
		language?: string;
		password: SharedModel.SecretBase | string;
		clientId?: any;
	}

	// handcrafted
	export interface SapOpenHubLinkedService extends SharedModel.LinkedService {
		typeProperties: SapOpenHubLinkedServiceTypeProperties;
	}

	// handcrafted
	export interface SapTableLinkedService extends SharedModel.LinkedService {
		typeProperties: SapTableLinkedServiceTypeProperties;
	}

	// handcrafted
	export interface SapOdpLinkedService extends SharedModel.LinkedService {
		typeProperties: SapOdpLinkedServiceTypeProperties;
	}

	// handcrafted
	export interface SapODataLinkedService extends SharedModel.LinkedService {
		typeProperties: SapODataLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SftpServerLinkedServiceTypeProperties class.
	 * @constructor
	 * Properties specific to this linked service type.
	 *
	 * @member {object} host The SFTP server host name. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [port] The TCP port number that the SFTP server uses to
	 * listen for client connections. Default value is 22. Type: integer (or
	 * Expression with resultType integer), minimum: 0.
	 * @member {string} [authenticationType] The authentication type to be used to
	 * connect to the FTP server. Possible values include: 'Basic', 'SshPublicKey'
	 * @member {object} [userName] The username used to log on to the SFTP server.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [password] Password to logon the SFTP server for Basic
	 * authentication.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 * @member {object} [privateKeyPath] The SSH private key file path for
	 * SshPublicKey authentication. Only valid for on-premises copy. For
	 * on-premises copy with SshPublicKey authentication, either PrivateKeyPath or
	 * PrivateKeyContent should be specified. SSH private key should be OpenSSH
	 * format. Type: string (or Expression with resultType string).
	 * @member {object} [privateKeyContent] Base64 encoded SSH private key content
	 * for SshPublicKey authentication. For on-premises copy with SshPublicKey
	 * authentication, either PrivateKeyPath or PrivateKeyContent should be
	 * specified. SSH private key should be OpenSSH format.
	 * @member {string} [privateKeyContent.type] Polymorphic Discriminator
	 * @member {object} [passPhrase] The password to decrypt the SSH private key if
	 * the SSH private key is encrypted.
	 * @member {string} [passPhrase.type] Polymorphic Discriminator
	 * @member {object} [skipHostKeyValidation] If true, skip the SSH host key
	 * validation. Default value is false. Type: boolean (or Expression with
	 * resultType boolean).
	 * @member {object} [hostKeyFingerprint] The host key finger-print of the SFTP
	 * server. When SkipHostKeyValidation is false, HostKeyFingerprint should be
	 * specified. Type: string (or Expression with resultType string).
	 */
	export interface SftpServerLinkedServiceTypeProperties {
		host: any;
		port?: any;
		authenticationType?: string;
		userName?: any;
		password?: string | SharedModel.SecretBase;
		encryptedCredential?: any;
		privateKeyPath?: any;
		privateKeyContent?: string | SharedModel.SecretBase;
		passPhrase?: string | SharedModel.SecretBase;
		skipHostKeyValidation?: any;
		hostKeyFingerprint?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SftpServerLinkedService class.
	 * @constructor
	 * A linked service for an SSH File Transfer Protocol (SFTP) server.
	 *
	 * @member {object} typeProperties Properties specific to this linked service
	 * type.
	 * @member {object} [typeProperties.host] The SFTP server host name. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.port] The TCP port number that the SFTP
	 * server uses to listen for client connections. Default value is 22. Type:
	 * integer (or Expression with resultType integer), minimum: 0.
	 * @member {string} [typeProperties.authenticationType] The authentication type
	 * to be used to connect to the FTP server. Possible values include: 'Basic',
	 * 'SshPublicKey'
	 * @member {object} [typeProperties.userName] The username used to log on to
	 * the SFTP server. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password to logon the SFTP server
	 * for Basic authentication.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.privateKeyPath] The SSH private key file
	 * path for SshPublicKey authentication. Only valid for on-premises copy. For
	 * on-premises copy with SshPublicKey authentication, either PrivateKeyPath or
	 * PrivateKeyContent should be specified. SSH private key should be OpenSSH
	 * format. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.privateKeyContent] Base64 encoded SSH
	 * private key content for SshPublicKey authentication. For on-premises copy
	 * with SshPublicKey authentication, either PrivateKeyPath or PrivateKeyContent
	 * should be specified. SSH private key should be OpenSSH format.
	 * @member {string} [typeProperties.privateKeyContent.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.passPhrase] The password to decrypt the SSH
	 * private key if the SSH private key is encrypted.
	 * @member {string} [typeProperties.passPhrase.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.skipHostKeyValidation] If true, skip the
	 * SSH host key validation. Default value is false. Type: boolean (or
	 * Expression with resultType boolean).
	 * @member {object} [typeProperties.hostKeyFingerprint] The host key
	 * finger-print of the SFTP server. When SkipHostKeyValidation is false,
	 * HostKeyFingerprint should be specified. Type: string (or Expression with
	 * resultType string).
	 */
	export interface SftpServerLinkedService extends SharedModel.LinkedService {
		typeProperties: SftpServerLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the FtpServerLinkedServiceTypeProperties class.
	 * @constructor
	 * Properties specific to this linked service type.
	 *
	 * @member {object} host Host name of the FTP server. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [port] The TCP port number that the FTP server uses to
	 * listen for client connections. Default value is 21. Type: integer (or
	 * Expression with resultType integer), minimum: 0.
	 * @member {string} [authenticationType] The authentication type to be used to
	 * connect to the FTP server. Possible values include: 'Basic', 'Anonymous'
	 * @member {object} [userName] Username to logon the FTP server. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [password] Password to logon the FTP server.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 * @member {object} [enableSsl] If true, connect to the FTP server over SSL/TLS
	 * channel. Default value is true. Type: boolean (or Expression with resultType
	 * boolean).
	 * @member {object} [enableServerCertificateValidation] If true, validate the
	 * FTP server SSL certificate when connect over SSL/TLS channel. Default value
	 * is true. Type: boolean (or Expression with resultType boolean).
	 */
	export interface FtpServerLinkedServiceTypeProperties {
		host: any;
		port?: any;
		authenticationType?: string;
		userName?: any;
		password?: string | SharedModel.SecretBase;
		encryptedCredential?: any;
		enableSsl?: any;
		enableServerCertificateValidation?: any;
	}


	/**
	 * @class
	 * Initializes a new instance of the AdobeExperiencePlatformLinkedService class.
	 * @constructor
	 * A linked service for an Adobe Experience Platform.
	 *
	 * @member {object} typeProperties Properties specific to this linked service type.
	 */
	export interface AdobeExperiencePlatformLinkedService extends SharedModel.LinkedService {
		typeProperties: AdobeExperiencePlatformLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AdobeExperiencePlatformLinkedServiceTypeProperties class.
	 * @constructor
	 * Properties specific to this linked service type.
	 *
	 * @member {object} [apiKey] Specific API key value from your Adobe I/O Console integration. Type: string (or Expression with resultType string).
	 * @member {object} [accountId] The Technical Account ID from your Adobe I/O Console integration, in the format: id@techacct.adobe.com.
	 * Type: integer (or Expression with resultType integer), minimum: 0.
	 * @member {string} [orgId] The IMS org credentials from your Adobe I/O Console integration. Type: string (or Expression with resultType string).
	 * @member {object} [clientSecret] The client secret generated from your Adobe I/O Console integration. Type: string (or Expression with resultType string).
	 * @member {object} [privateKeyContent] The private key to generate the JWT token. Type: string (or Expression with resultType string).
	 */
	export interface AdobeExperiencePlatformLinkedServiceTypeProperties {
		apiKey: any;
		accountId?: any;
		orgId?: string;
		clientSecret?: SharedModel.SecretBase;
		privateKeyContent?: SharedModel.SecretBase;
	}

	/**
	 * @class
	 * Initializes a new instance of the FtpServerLinkedService class.
	 * @constructor
	 * A FTP server Linked Service.
	 *
	 * @member {object} typeProperties Properties specific to this linked service
	 * type.
	 * @member {object} [typeProperties.host] Host name of the FTP server. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.port] The TCP port number that the FTP
	 * server uses to listen for client connections. Default value is 21. Type:
	 * integer (or Expression with resultType integer), minimum: 0.
	 * @member {string} [typeProperties.authenticationType] The authentication type
	 * to be used to connect to the FTP server. Possible values include: 'Basic',
	 * 'Anonymous'
	 * @member {object} [typeProperties.userName] Username to logon the FTP server.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password to logon the FTP server.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.enableSsl] If true, connect to the FTP
	 * server over SSL/TLS channel. Default value is true. Type: boolean (or
	 * Expression with resultType boolean).
	 * @member {object} [typeProperties.enableServerCertificateValidation] If true,
	 * validate the FTP server SSL certificate when connect over SSL/TLS channel.
	 * Default value is true. Type: boolean (or Expression with resultType
	 * boolean).
	 */
	export interface FtpServerLinkedService extends SharedModel.LinkedService {
		typeProperties: FtpServerLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the HttpLinkedServiceTypeProperties class.
	 * @constructor
	 * Properties specific to this linked service type.
	 *
	 * @member {object} url The base URL of the HTTP endpoint, e.g.
	 * http://www.microsoft.com. Type: string (or Expression with resultType
	 * string).
	 * @member {string} [authenticationType] The authentication type to be used to
	 * connect to the HTTP server. Possible values include: 'Basic', 'Anonymous',
	 * 'Digest', 'Windows', 'ClientCertificate'
	 * @member {object} [userName] User name for Basic, Digest, or Windows
	 * authentication. Type: string (or Expression with resultType string).
	 * @member {object} [password] Password for Basic, Digest, Windows, or
	 * ClientCertificate with EmbeddedCertData authentication.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [embeddedCertData] Base64 encoded certificate data for
	 * ClientCertificate authentication. For on-premises copy with
	 * ClientCertificate authentication, either CertThumbprint or
	 * EmbeddedCertData/Password should be specified. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [certThumbprint] Thumbprint of certificate for
	 * ClientCertificate authentication. Only valid for on-premises copy. For
	 * on-premises copy with ClientCertificate authentication, either
	 * CertThumbprint or EmbeddedCertData/Password should be specified. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 * @member {object} [enableServerCertificateValidation] If true, validate the
	 * HTTPS server SSL certificate. Default value is true. Type: boolean (or
	 * Expression with resultType boolean).
	 */
	export interface HttpLinkedServiceTypeProperties {
		url: any;
		authenticationType?: string;
		authHeaders?: {
			[key: string]: SecretBase;
		};
		userName?: any;
		password?: string | SharedModel.SecretBase;
		embeddedCertData?: any;
		certThumbprint?: any;
		encryptedCredential?: any;
		enableServerCertificateValidation?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the HttpLinkedService class.
	 * @constructor
	 * Linked service for an HTTP source.
	 *
	 * @member {object} typeProperties Properties specific to this linked service
	 * type.
	 * @member {object} [typeProperties.url] The base URL of the HTTP endpoint,
	 * e.g. http://www.microsoft.com. Type: string (or Expression with resultType
	 * string).
	 * @member {string} [typeProperties.authenticationType] The authentication type
	 * to be used to connect to the HTTP server. Possible values include: 'Basic',
	 * 'Anonymous', 'Digest', 'Windows', 'ClientCertificate'
	 * @member {object} [typeProperties.userName] User name for Basic, Digest, or
	 * Windows authentication. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password for Basic, Digest,
	 * Windows, or ClientCertificate with EmbeddedCertData authentication.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.embeddedCertData] Base64 encoded
	 * certificate data for ClientCertificate authentication. For on-premises copy
	 * with ClientCertificate authentication, either CertThumbprint or
	 * EmbeddedCertData/Password should be specified. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [typeProperties.certThumbprint] Thumbprint of certificate
	 * for ClientCertificate authentication. Only valid for on-premises copy. For
	 * on-premises copy with ClientCertificate authentication, either
	 * CertThumbprint or EmbeddedCertData/Password should be specified. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.enableServerCertificateValidation] If true,
	 * validate the HTTPS server SSL certificate. Default value is true. Type:
	 * boolean (or Expression with resultType boolean).
	 */
	export interface HttpLinkedService extends SharedModel.LinkedService {
		typeProperties: HttpLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSearchLinkedServiceTypeProperties class.
	 * @constructor
	 * Windows Azure Search Service linked service properties.
	 *
	 * @member {object} url URL for Azure Search service. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [key] Admin Key for Azure Search service
	 * @member {string} [key.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureSearchLinkedServiceTypeProperties {
		url: any;
		key?: SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSearchLinkedService class.
	 * @constructor
	 * Linked service for Windows Azure Search Service.
	 *
	 * @member {object} typeProperties Windows Azure Search Service linked service
	 * properties.
	 * @member {object} [typeProperties.url] URL for Azure Search service. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.key] Admin Key for Azure Search service
	 * @member {string} [typeProperties.key.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureSearchLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureSearchLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the CustomDataSourceLinkedService class.
	 * @constructor
	 * Custom linked service.
	 *
	 * @member {object} typeProperties Custom linked service properties.
	 */
	export interface CustomDataSourceLinkedService extends SharedModel.LinkedService {
		typeProperties: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the LakehouseLinkedServiceTypeProperties class.
	 * @constructor
	 * Lakehouse linked service properties.
	 *
	 * @member {string} workspaceId Trident Lakehouse workspace ID
	 * @member {string} artifactId Trident Lakehouse artifact ID
	 * @member {object} credential UserAuth credential
	 * @member {object} servicePrincipalId The ID of the
	 * application used to authenticate against the Azure Data Lake Store account.
	 * Type: string (or Expression with resultType string).
	 * @member {object} servicePrincipalKey The Key of the
	 * application used to authenticate against the Azure Data Lake Store account.
	 * @member {string} servicePrincipalKey.type Polymorphic
	 * Discriminator
	 * @member {object} tenant The name or ID of the tenant to
	 * which the service principal belongs. Type: string (or Expression with
	 * resultType string).
	 * @member {object} encryptedCredential The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 * @member {string} fabricEnvironment Only exists in ADF
	 */
	export interface LakehouseLinkedServiceTypeProperties {
		workspaceId: any;
		artifactId: any;
		rootFolder?: any;
		servicePrincipalId?: any;
		servicePrincipalKey?: string | SharedModel.SecretBase;
		servicePrincipalCredentialType?: string;
		servicePrincipalCredential?: string | SharedModel.SecretBase;
		tenant?: any;
		encryptedCredential?: any;
		azureCloudType?: any;
		credential?: SharedModel.CredentialReference;
		fabricEnvironment?: string;
		tridentDefaultSchema?: any; // Only used in trident as a temporary value which is not saved in backend
		endpoint?: any; // Used for sql analytics endpoint
		authenticationType?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the LakehouseLinkedService class.
	 * @constructor
	 * Linked service for Lakehouse.
	 *
	 * @member {object} typeProperties Lakehouse linked service
	 * properties.
	 * @member {string} [typeProperties.workspaceId] Trident Lakehouse workspace ID
	 * @member {string} [typeProperties.artifactId] Trident Lakehouse artifact ID
	 * @member {object} [typeProperties.credential] UserAuth credential
	 * @member {object} [typeProperties.servicePrincipalId] The ID of the
	 * application used to authenticate against the Azure Data Lake Store account.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.servicePrincipalKey] The Key of the
	 * application used to authenticate against the Azure Data Lake Store account.
	 * @member {string} [typeProperties.servicePrincipalKey.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.tenant] The name or ID of the tenant to
	 * which the service principal belongs. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface LakehouseLinkedService extends SharedModel.LinkedService {
		typeProperties: LakehouseLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the MountedRelationalDatabaseLinkedServiceTypeProperties class.
	 * @constructor
	 * Fabric Mirror Database linked service properties.
	 *
	 * @member {string} workspaceId Trident Mirror Database workspace ID
	 * @member {string} artifactId Trident Mirror Database artifact ID
	 */
	export interface MountedRelationalDatabaseLinkedServiceTypeProperties {
		workspaceId: any;
		artifactId: any;
		rootFolder: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the MountedRelationalDatabaseLinkedService class.
	 * @constructor
	 * Linked service for Mirror Database.
	 *
	 * @member {object} typeProperties Fabric Sql linked service
	 * properties.
	 * @member {string} [typeProperties.workspaceId] Trident Mirror Database workspace ID
	 * @member {string} [typeProperties.artifactId] Trident Mirror Database artifact ID
	 */
	export interface MountedRelationalDatabaseLinkedService extends SharedModel.LinkedService {
		typeProperties: MountedRelationalDatabaseLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the FabricSqlLinkedServiceTypeProperties class.
	 * @constructor
	 * Fabric Sql linked service properties.
	 *
	 * @member {string} workspaceId Trident Fabric Sql workspace ID
	 * @member {string} artifactId Trident Fabric Sql artifact ID
	 */
	export interface FabricSqlLinkedServiceTypeProperties {
		workspaceId: any;
		artifactId: any;
		tdsEndpoint?: any;
		endpoint?: any;
		database?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the FabricSqlLinkedService class.
	 * @constructor
	 * Linked service for Fabric Sql.
	 *
	 * @member {object} typeProperties Fabric Sql linked service
	 * properties.
	 * @member {string} [typeProperties.workspaceId] Trident Fabric Sql workspace ID
	 * @member {string} [typeProperties.artifactId] Trident Fabric Sql artifact ID
	 */
	export interface FabricSqlLinkedService extends SharedModel.LinkedService {
		typeProperties: FabricSqlLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlAnalyticsEndpointLinkedServiceTypeProperties class.
	 * @constructor
	 * Sql Analytics Endpoint linked service properties.
	 *
	 * @member {string} endpoint
	 * @member {string} artifactId
	 */
	export interface SqlAnalyticsEndpointLinkedServiceTypeProperties {
		endpoint: any;
		artifactId: any;
		workspaceId?: any;
		parentObjectId?: any
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlAnalyticsEndpointLinkedService class.
	 * @constructor
	 * Linked service for Sql Analytics Endpoint.
	 *
	 * @member {object} typeProperties Sql Analytics Endpoint linked service
	 * properties.
	 * @member {string} [typeProperties.endpoint] endpoint
	 * @member {string} [typeProperties.artifactId] artifact ID
	 */
	export interface SqlAnalyticsEndpointLinkedService extends SharedModel.LinkedService {
		typeProperties: SqlAnalyticsEndpointLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AmazonRedshiftLinkedServiceTypeProperties class.
	 * @constructor
	 * Amazon Redshift linked service properties.
	 *
	 * @member {object} server The name of the Amazon Redshift server. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [username] The username of the Amazon Redshift source.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [password] The password of the Amazon Redshift source.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} database The database name of the Amazon Redshift source.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [port] The TCP port number that the Amazon Redshift server
	 * uses to listen for client connections. The default value is 5439. Type:
	 * integer (or Expression with resultType integer).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AmazonRedshiftLinkedServiceTypeProperties {
		server: any;
		username?: any;
		password?: string | SharedModel.SecretBase; // handcrafted
		database: any;
		port?: any;
		encryptedCredential?: any;
		sslmode?: any;
		sslinsecure?: boolean;
	}

	/**
	 * @class
	 * Initializes a new instance of the AmazonRedshiftLinkedService class.
	 * @constructor
	 * Linked service for Amazon Redshift.
	 *
	 * @member {object} typeProperties Amazon Redshift linked service properties.
	 * @member {object} [typeProperties.server] The name of the Amazon Redshift
	 * server. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.username] The username of the Amazon
	 * Redshift source. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] The password of the Amazon
	 * Redshift source.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.database] The database name of the Amazon
	 * Redshift source. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.port] The TCP port number that the Amazon
	 * Redshift server uses to listen for client connections. The default value is
	 * 5439. Type: integer (or Expression with resultType integer).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AmazonRedshiftLinkedService extends SharedModel.LinkedService {
		typeProperties: AmazonRedshiftLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AmazonS3LinkedServiceTypeProperties class.
	 * @constructor
	 * Amazon S3 linked service properties.
	 *
	 * @member {object} [accessKeyId] The access key identifier of the Amazon S3
	 * Identity and Access Management (IAM) user. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [secretAccessKey] The secret access key of the Amazon S3
	 * Identity and Access Management (IAM) user.
	 * @member {string} [secretAccessKey.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 * @member {object} [sessionToken] The secret token used to support temporary credential.
	 * @member {string} [authenticationType] The authentication type to be used to
	 * connect to the Concur server. Possible values include: 'OAuth_2.0', 'OAuth_2.0_Bearer'
	 */
	export interface AmazonS3LinkedServiceTypeProperties extends SharedModel.S3SharedProperties {
		sessionToken?: string | SharedModel.SecretBase;
		authenticationType?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AmazonS3CompatibleLinkedServiceTypeProperties class.
	 * @constructor
	 * Amazon S3 Compatibility linked service properties.
	 *
	 * @member {object} [accessKeyId] The access key identifier of the Amazon S3
	 * Identity and Access Management (IAM) user. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [secretAccessKey] The secret access key of the Amazon S3
	 * Identity and Access Management (IAM) user.
	 * @member {string} [secretAccessKey.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 * @member {boolean} [forcePathStyle] (to be deprecated)
	 * if true, will use path-style instead of virtual hosted-style
	 */
	export interface AmazonS3CompatibleLinkedServiceTypeProperties extends SharedModel.S3SharedProperties {
		forcePathStyle?: boolean;
	}

	/**
	 * @class
	 * Initializes a new instance of the TeamDeskLinkedServiceTypeProperties class.
	 * @constructor
	 * TeamDesk linked service properties.
	 *
	 * @member {string} [url] The url of the TeamDesk.
	 * @member {string} [authenticationType] The authentication type to be used to
	 * connect to the TeamDesk server. Possible values include: 'basic', 'token'.
	 * @member {string} User name for Basic authentication. Type: string (or Expression with resultType string).
	 * @member {object} [password] The password of the TeamDesk.
	 * @member {object} [apiToken] The API token of the TeamDesk.
	 */
	export interface TeamDeskLinkedServiceTypeProperties {
		url: string;
		authenticationType: 'basic' | 'token';
		userName?: string;
		password?: string | SharedModel.SecretBase;
		apiToken?: string | SharedModel.SecretBase;
	}


	/**
	 * @class
	 * Initializes a new instance of the QuickbaseLinkedServiceTypeProperties class.
	 * @constructor
	 * Quickbase linked service properties.
	 *
	 * @member {string} [url] The url of the Quickbase.
	 * @member {object} [userToken] The user token of the Quickbase.
	 */
	export interface QuickbaseLinkedServiceTypeProperties {
		url: string;
		userToken?: string | SharedModel.SecretBase;
	}

	/**
	 * @class
	 * Initializes a new instance of the SmartsheetLinkedServiceTypeProperties class.
	 * @constructor
	 * Smartsheet linked service properties.
	 *
	 * @member {object} [apiToken] The user token of the Smartsheet.
	 */
	export interface SmartsheetLinkedServiceTypeProperties {
		apiToken?: string | SharedModel.SecretBase;
	}
	/**
	 * @class
	 * Initializes a new instance of the ZendeskLinkedServiceTypeProperties class.
	 * @constructor
	 * Zendesk linked service properties.
	 *
	 * @member {string} [url] The url of the Zendesk.
	 * @member {string} [authenticationType] The authentication type to be used to
	 * connect to the Zendesk server. Possible values include: 'basic', 'token'.
	 * @member {string} User name for Basic authentication. Type: string (or Expression with resultType string).
	 * @member {object} [password] The password of the Zendesk.
	 * @member {object} [apiToken] The API token of the Zendesk.
	 */
	export interface ZendeskLinkedServiceTypeProperties {
		url: string;
		authenticationType: 'basic' | 'token';
		userName?: string;
		password?: string | SharedModel.SecretBase;
		apiToken?: string | SharedModel.SecretBase;
	}

	/**
	 * @class
	 * Initializes a new instance of the OracleCloudStorage class.
	 * @constructor
	 * Oracle Cloud Storage for S3 linked service properties.
	 *
	 * @member {object} [accessKeyId] The access key identifier of the Amazon S3
	 * Identity and Access Management (IAM) user. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [secretAccessKey] The secret access key of the Amazon S3
	 * Identity and Access Management (IAM) user.
	 * @member {string} [secretAccessKey.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface OracleCloudStorageLinkedServiceTypeProperties extends SharedModel.S3SharedProperties { }

	/**
	 * @class
	 * Initializes a new instance of the WorkdayLinkedServiceTypeProperties class.
	 * @constructor
	 * Workday linked service properties.
	 *
	 * @member {string} [subDomain] The Subdomain of the Workday.
	 * @member {string} [tenantId] The Tenant Id of the Workday.
	 * @member {string} [authenticationType] The authentication type to be used to
	 * connect to the Workday endpoint. Possible values include: 'basic'.
	 * @member {string} [userName] User name for authentication. Type: string (or Expression with resultType string).
	 * @member {object} [password] The password of the Workday.
	 * @member {string} [serviceVersion] Service version to use for workday endpoint.
	 * @member {string} [service] The Service name for Workday.
	 * @member {string} [operation] The Operation name for Workday.
	 */
	export interface WorkdayLinkedServiceTypeProperties {
		subDomain: string;
		tenantId: string;
		authenticationType: 'basic';
		userName?: string;
		password?: string | SharedModel.SecretBase;
		serviceVersion: string;
		service: string;
		operation: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the EventHubsLinkedServiceTypeProperties class.
	 * @constructor
	 * EventHubs linked service properties.
	 *
	 * @member {string} [namespaceName] The namespace of Azure Event Hubs
	 * @member {string} [accessKeyName] Access Key Name for the namespace
	 * @member {object} [accessKey] Access Key of the namespace.
	 */
	export interface EventHubsLinkedServiceTypeProperties {
		namespaceName: string;
		accessKeyName: string;
		accessKey?: string | SharedModel.SecretBase;
	}

	/**
	 * @class
	 * Initializes a new instance of the AmazonS3LinkedService class.
	 * @constructor
	 * Linked service for Amazon S3.
	 *
	 * @member {object} typeProperties Amazon S3 linked service properties.
	 * @member {object} [typeProperties.accessKeyId] The access key identifier of
	 * the Amazon S3 Identity and Access Management (IAM) user. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.secretAccessKey] The secret access key of
	 * the Amazon S3 Identity and Access Management (IAM) user.
	 * @member {string} [typeProperties.secretAccessKey.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AmazonS3LinkedService extends SharedModel.LinkedService {
		typeProperties: AmazonS3LinkedServiceTypeProperties;
	}

	export interface GoogleCloudStorageLinkedService extends SharedModel.LinkedService {
		typeProperties: AmazonS3LinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AmazonS3CompatibleLinkedService class.
	 * @constructor
	 * A general connector that can talk to any S3 API compatible server or cloud
	 *
	 * @member {object} typeProperties Amazon S3 linked service properties.
	 * @member {object} [typeProperties.accessKeyId] The access key identifier of
	 * the Amazon S3 Identity and Access Management (IAM) user. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.secretAccessKey] The secret access key of
	 * the Amazon S3 Identity and Access Management (IAM) user.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 * @member {boolean} [forcePathStyle] (to be deprecated)
	 * if true, will use path-style instead of virtual hosted-style
	 */
	export interface AmazonS3CompatibleLinkedService extends SharedModel.LinkedService {
		typeProperties: AmazonS3CompatibleLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the TeamDeskLinkedService class.
	 * @constructor
	 * Linked service for TeamDesk.
	 *
	 * @member {object} typeProperties TeamDesk linked service properties.
	 * @member {string} [typeProperties.url] The url of the TeamDesk.
	 * @member {string} [typeProperties.authenticationType]  The authentication type to be used to
	 * connect to the TeamDesk server. Possible values include: 'basic', 'token'.
	 * @member {string} [typeProperties.userName] The user name for Basic authentication. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.password] The password of the TeamDesk.
	 * @member {object} [typeProperties.apiToken] The API token of the TeamDesk.
	 */
	export interface TeamDeskLinkedService extends SharedModel.LinkedService {
		typeProperties: TeamDeskLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the QuickbaseLinkedService class.
	 * @constructor
	 * Linked service for Quickbase.
	 *
	 * @member {object} typeProperties Quickbase linked service properties.
	 * @member {string} [typeProperties.url] The url of the Quickbase.
	 * @member {object} [typeProperties.userToken] The user token of the Quickbase.
	 */
	export interface QuickbaseLinkedService extends SharedModel.LinkedService {
		typeProperties: QuickbaseLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SmartsheetLinkedService class.
	 * @constructor
	 * Linked service for Smartsheet.
	 *
	 * @member {object} typeProperties Smartsheet linked service properties.
	 * @member {object} [typeProperties.apiToken] The user token of the Smartsheet.
	 */
	export interface SmartsheetLinkedService extends SharedModel.LinkedService {
		typeProperties: SmartsheetLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ZendeskLinkedService class.
	 * @constructor
	 * Linked service for Zendesk.
	 *
	 * @member {object} typeProperties Zendesk linked service properties.
	 * @member {string} [typeProperties.url] The url of the Zendesk.
	 * @member {string} [typeProperties.authenticationType]  The authentication type to be used to
	 * connect to the Zendesk server. Possible values include: 'basic', 'token'.
	 * @member {string} [typeProperties.userName] The user name for Basic authentication. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.password] The password of the Zendesk.
	 * @member {object} [typeProperties.apiToken] The API token of the Zendesk.
	 */
	export interface ZendeskLinkedService extends SharedModel.LinkedService {
		typeProperties: ZendeskLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the OracleCloudStorageLinkedService class.
	 * @constructor
	 * A connector that talks to Oracle cloud for S3 API
	 *
	 * @member {object} typeProperties Amazon S3 linked service properties.
	 * @member {object} [typeProperties.accessKeyId] The access key identifier of
	 * the Amazon S3 Identity and Access Management (IAM) user. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.secretAccessKey] The secret access key of
	 * the Amazon S3 Identity and Access Management (IAM) user.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 * @member {boolean} [forcePathStyle] (to be deprecated)
	 * if true, will use path-style instead of virtual hosted-style
	 */
	export interface OracleCloudStorageLinkedService extends SharedModel.LinkedService {
		typeProperties: OracleCloudStorageLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the WorkdayLinkedService class.
	 * @constructor
	 * Linked service for Workday.
	 *
	 * @member {object} typeProperties Workday linked service properties.
	 * @member {string} [typeProperties.subDomain] The subdomain of the Workday.
	 * @member {string} [typeProperties.tenantId] The Tenant Id of the Workday.
	 * @member {string} [typeProperties.authenticationType]  The authentication type to be used to
	 * connect to the Workday endpoint. Possible values include: 'basic'.
	 * @member {string} [typeProperties.userName] The user name for authentication. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.password] The password of the Workday.
	 * @member {string} [typeProperties.serviceVersion] Service version to use for Workday endpoint.
	 * @member {string} [typeProperties.service] The Service name of the Workday.
	 * @member {string} [typeProperties.operation] The Operation name of the Workday.
	 */
	export interface WorkdayLinkedService extends SharedModel.LinkedService {
		typeProperties: WorkdayLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the EventHubsLinkedService class.
	 * @constructor
	 * EventHubs linked service.
	 *
	 * @member {object} typeProperties Workday linked service properties.
	 * @member {string} [typeProperties.namespaceName] The namespace of Azure Event Hubs
	 * @member {string} [typeProperties.accessKeyName] Access Key Name for the namespace
	 * @member {object} [typeProperties.accessKey] Access Key of the namespace.
	 */
	export interface EventHubsLinkedService extends SharedModel.LinkedService {
		typeProperties: EventHubsLinkedServiceTypeProperties;
	}
	/**
	 * @class
	 * Initializes a new instance of the KafkaLinkedServiceTypeProperties class.
	 * @constructor
	 * Kafka linked service properties.
	 *
	 * @member {string} [bootstrapServers] list of host:port values separated by commas.
	 * @member {string} [authenticationType] The authentication type to be used to
	 * @member {string} [saslMechanism] supported SASL Mechanism
	 * connect to the Kafka endpoint.
	 * @member {string} [username] User name for authentication. Type: string (or Expression with resultType string).
	 * @member {object} [password] The password of the Kafkaday.
	 */
	export interface KafkaLinkedServiceTypeProperties {
		bootstrapServers: string;
		authenticationType: string;
		saslMechanism: string;
		username?: string;
		password?: string | SharedModel.SecretBase;
		distroType: string;
	}
	/**
	 * @class
	 * Initializes a new instance of the KafkaLinkedService class.
	 * @constructor
	 * Linked service for Kafka.
	 *
	 * @member {object} typeProperties Kafka linked service properties.
	 * @member {string} [typeProperties.bootstrapServers] list of host:port values separated by commas,..
	 * @member {string} [typeProperties.authenticationType]  The authentication type to be used to
	 * connect to the kafka endpoint.
	 * @member {string} [typeProperties.saslMechanism] supported SASL Mechanism
	 * @member {string} [typeProperties.username] The user name for authentication. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.password] The password of the Kafka.
	 * @member {object} [typeProperties.distroType] The password of the Kafka.
	 */
	export interface KafkaLinkedService extends SharedModel.LinkedService {
		typeProperties: KafkaLinkedServiceTypeProperties;
	}
	/**
	 * @class
	 * Initializes a new instance of the SapEccLinkedServiceTypeProperties class.
	 * @constructor
	 * SAP ECC linked service properties.
	 *
	 * @member {string} url The URL of SAP ECC OData API. For example,
	 * '[https://hostname:port/sap/opu/odata/sap/servicename/]'. Type: string (or
	 * Expression with resultType string).
	 * @member {string} [username] The username for Basic authentication. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [password] The password for Basic authentication.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {string} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Either encryptedCredential or username/password must be
	 * provided. Type: string (or Expression with resultType string).
	 */
	export interface SapEccLinkedServiceTypeProperties {
		url: string;
		username?: string;
		password?: SharedModel.SecretBase;
		encryptedCredential?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the SapEccLinkedService class.
	 * @constructor
	 * Linked service for SAP ERP Central Component(SAP ECC).
	 *
	 * @member {object} typeProperties SAP ECC linked service properties.
	 * @member {string} [typeProperties.url] The URL of SAP ECC OData API. For
	 * example, '[https://hostname:port/sap/opu/odata/sap/servicename/]'. Type:
	 * string (or Expression with resultType string).
	 * @member {string} [typeProperties.username] The username for Basic
	 * authentication. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] The password for Basic
	 * authentication.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {string} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Either encryptedCredential or
	 * username/password must be provided. Type: string (or Expression with
	 * resultType string).
	 */
	export interface SapEccLinkedService extends SharedModel.LinkedService {
		typeProperties: SapEccLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SapCloudForCustomerLinkedServiceTypeProperties class.
	 * @constructor
	 * SAP Cloud for Customer linked service properties.
	 *
	 * @member {object} url The URL of SAP Cloud for Customer OData API. For
	 * example, '[https://[tenantname].crm.ondemand.com/sap/c4c/odata/v1]'. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [username] The username for Basic authentication. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [password] The password for Basic authentication.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Either encryptedCredential or username/password must be
	 * provided. Type: string (or Expression with resultType string).
	 */
	export interface SapCloudForCustomerLinkedServiceTypeProperties {
		url: any;
		username?: any;
		password?: SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SapCloudForCustomerLinkedService class.
	 * @constructor
	 * Linked service for SAP Cloud for Customer.
	 *
	 * @member {object} typeProperties SAP Cloud for Customer linked service
	 * properties.
	 * @member {object} [typeProperties.url] The URL of SAP Cloud for Customer
	 * OData API. For example,
	 * '[https://[tenantname].crm.ondemand.com/sap/c4c/odata/v1]'. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.username] The username for Basic
	 * authentication. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] The password for Basic
	 * authentication.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Either encryptedCredential or
	 * username/password must be provided. Type: string (or Expression with
	 * resultType string).
	 */
	export interface SapCloudForCustomerLinkedService extends SharedModel.LinkedService {
		typeProperties: SapCloudForCustomerLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceLinkedServiceTypeProperties class.
	 * @constructor
	 * Salesforce linked service properties.
	 *
	 * @member {object} [environmentUrl] The URL of Salesforce instance. Default is
	 * 'https://login.salesforce.com'. To copy data from sandbox, specify
	 * 'https://test.salesforce.com'. To copy data from custom domain, specify, for
	 * example, 'https://[domain].my.salesforce.com'. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [username] The username for Basic authentication of the
	 * Salesforce instance. Type: string (or Expression with resultType string).
	 * @member {object} [password] The password for Basic authentication of the
	 * Salesforce instance.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [securityToken] The security token is required to remotely
	 * access Salesforce instance.
	 * @member {string} [securityToken.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface SalesforceLinkedServiceTypeProperties {
		environmentUrl?: any;
		username?: any;
		password?: SharedModel.SecretBase | string;
		securityToken?: SharedModel.SecretBase | string;
		apiVersion?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceLinkedService class.
	 * @constructor
	 * Linked service for Salesforce.
	 *
	 * @member {object} typeProperties Salesforce linked service properties.
	 * @member {object} [typeProperties.environmentUrl] The URL of Salesforce
	 * instance. Default is 'https://login.salesforce.com'. To copy data from
	 * sandbox, specify 'https://test.salesforce.com'. To copy data from custom
	 * domain, specify, for example, 'https://[domain].my.salesforce.com'. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.username] The username for Basic
	 * authentication of the Salesforce instance. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.password] The password for Basic
	 * authentication of the Salesforce instance.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.securityToken] The security token is
	 * required to remotely access Salesforce instance.
	 * @member {string} [typeProperties.securityToken.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface SalesforceLinkedService extends SharedModel.LinkedService {
		typeProperties: SalesforceLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceV2LinkedServiceTypeProperties class.
	 * @constructor
	 * Salesforce V2 linked service properties.
	 *
	 * @member {object} [environmentUrl] The URL of Salesforce instance. Default is
	 * 'https://login.salesforce.com'. To copy data from sandbox, specify
	 * 'https://test.salesforce.com'. To copy data from custom domain, specify, for
	 * example, 'https://[domain].my.salesforce.com'. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [clientId] Salesforce OAuth 2.0 Connected App client id
	 * @member {object} [clientSecret] Salesforce OAuth 2.0 Connected App client secret
	 * @member {object} [apiVersion] Salesforce Bulk API version to use. we only support API version >= 47.0
	 * @member {object} [authenticationType] Salesforce Authentication Type, currently only OAuth 2.0 is supported
	 */
	export interface SalesforceV2LinkedServiceTypeProperties {
		environmentUrl?: any;
		clientId?: any;
		clientSecret?: SharedModel.SecretBase | string;
		apiVersion?: any;
		authenticationType?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceV2LinkedService class.
	 * @constructor
	 * Linked service for Salesforce V2.
	 *
	 * @member {object} typeProperties Salesforce linked service properties.
	 * @member {object} [typeProperties.environmentUrl] The URL of Salesforce instance. Default is
	 * 'https://login.salesforce.com'. To copy data from sandbox, specify
	 * 'https://test.salesforce.com'. To copy data from custom domain, specify, for
	 * example, 'https://[domain].my.salesforce.com'. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [typeProperties.clientId] Salesforce OAuth 2.0 Connected App client id
	 * @member {object} [typeProperties.clientSecret] Salesforce OAuth 2.0 Connected App client secret
	 * @member {object} [typeProperties.apiVersion] Salesforce Bulk API version to use. we only support API version >= 47.0
	 */
	export interface SalesforceV2LinkedService extends SharedModel.LinkedService {
		typeProperties: SalesforceV2LinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure Data Lake Store linked service properties.
	 *
	 * @member {object} dataLakeStoreUri Data Lake Store service URI. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [servicePrincipalId] The ID of the application used to
	 * authenticate against the Azure Data Lake Store account. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [servicePrincipalKey] The Key of the application used to
	 * authenticate against the Azure Data Lake Store account.
	 * @member {string} [servicePrincipalKey.type] Polymorphic Discriminator
	 * @member {object} [tenant] The name or ID of the tenant to which the service
	 * principal belongs. Type: string (or Expression with resultType string).
	 * @member {object} [accountName] Data Lake Store account name. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [subscriptionId] Data Lake Store account subscription ID
	 * (if different from Data Factory account). Type: string (or Expression with
	 * resultType string).
	 * @member {object} [resourceGroupName] Data Lake Store account resource group
	 * name (if different from Data Factory account). Type: string (or Expression
	 * with resultType string).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 * exclusive with sasUri property. Type: string, SecureString or
	 * AzureKeyVaultSecretReference.
	 * @member {object} [sasUri] SAS URI of the Azure Storage resource. It is
	 * mutually exclusive with connectionString property.
	 * @member {string} [sasUri.type] Polymorphic Discriminator
	 */
	export interface AzureDataLakeStoreLinkedServiceTypeProperties {
		dataLakeStoreUri: any;
		servicePrincipalId?: any;
		servicePrincipalKey?: string | SharedModel.SecretBase;
		tenant?: any;
		accountName?: any;
		subscriptionId?: any;
		resourceGroupName?: any;
		encryptedCredential?: any;
		azureCloudType?: any;
		credential?: SharedModel.CredentialReference;
	}

	// handcrafted
	export interface AzureBlobFSLinkedServiceTypeProperties {
		url?: any;
		accountKey?: string | SharedModel.SecretBase;
		servicePrincipalId?: any;
		servicePrincipalKey?: string | SharedModel.SecretBase;
		servicePrincipalCredentialType?: string;
		servicePrincipalCredential?: string | SharedModel.SecretBase;
		tenant?: any;
		encryptedCredential?: any;
		azureCloudType?: any;
		credential?: SharedModel.CredentialReference;
		sasUri?: any;
		sasToken?: string | SharedModel.SecretBase;
		wiUrl?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreLinkedService class.
	 * @constructor
	 * Azure Data Lake Store linked service.
	 *
	 * @member {object} typeProperties Azure Data Lake Store linked service
	 * properties.
	 * @member {object} [typeProperties.dataLakeStoreUri] Data Lake Store service
	 * URI. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.servicePrincipalId] The ID of the
	 * application used to authenticate against the Azure Data Lake Store account.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.servicePrincipalKey] The Key of the
	 * application used to authenticate against the Azure Data Lake Store account.
	 * @member {string} [typeProperties.servicePrincipalKey.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.tenant] The name or ID of the tenant to
	 * which the service principal belongs. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.accountName] Data Lake Store account name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.subscriptionId] Data Lake Store account
	 * subscription ID (if different from Data Factory account). Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.resourceGroupName] Data Lake Store account
	 * resource group name (if different from Data Factory account). Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureDataLakeStoreLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureDataLakeStoreLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreCosmosStructuredStreamLinkedService class.
	 * @constructor
	 * Azure Data Lake Store (structured stream) linked service.
	 *
	 * @member {object} typeProperties Azure Data Lake Store linked service
	 * properties.
	 * @member {object} [typeProperties.accountName] Data Lake Store account name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.servicePrincipalId] The ID of the
	 * application used to authenticate against the Azure Data Lake Store account.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.servicePrincipalKey] The Key of the
	 * application used to authenticate against the Azure Data Lake Store account.
	 * @member {string} [typeProperties.servicePrincipalKey.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.tenant] The name or ID of the tenant to
	 * which the service principal belongs. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.accountName] Data Lake Store account name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.subscriptionId] Data Lake Store account
	 * subscription ID (if different from Data Factory account). Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.resourceGroupName] Data Lake Store account
	 * resource group name (if different from Data Factory account). Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureDataLakeStoreCosmosStructuredStreamLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureDataLakeStoreCosmosStructuredStreamLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreCosmosStructuredStreamLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure Data Lake Store (structured stream) linked service properties.
	 *
	 * @member {object} [accountName] Data Lake Store account name. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [servicePrincipalId] The ID of the application used to
	 * authenticate against the Azure Data Lake Store account. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [servicePrincipalKey] The Key of the application used to
	 * authenticate against the Azure Data Lake Store account.
	 * @member {string} [servicePrincipalKey.type] Polymorphic Discriminator
	 * @member {object} [tenant] The name or ID of the tenant to which the service
	 * principal belongs. Type: string (or Expression with resultType string).
	 * @member {object} [accountName] Data Lake Store account name. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [subscriptionId] Data Lake Store account subscription ID
	 * (if different from Data Factory account). Type: string (or Expression with
	 * resultType string).
	 * @member {object} [resourceGroupName] Data Lake Store account resource group
	 * name (if different from Data Factory account). Type: string (or Expression
	 * with resultType string).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureDataLakeStoreCosmosStructuredStreamLinkedServiceTypeProperties {
		accountName: any;
		servicePrincipalId?: any;
		servicePrincipalKey?: SharedModel.SecretBase;
		tenant?: any;
		subscriptionId?: any;
		resourceGroupName?: any;
		encryptedCredential?: any;
		credential?: SharedModel.CredentialReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStorGen2CosmosStructuredStreamLinkedService class.
	 * @constructor
	 * Azure Data Lake Store Gen2 (structured stream) linked service.
	 *
	 * @member {object} typeProperties Azure Data Lake Store linked service
	 * properties.
	 * @member {object} [typeProperties.accountName] Data Lake Store Gen2 account name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.servicePrincipalId] The ID of the
	 * application used to authenticate against the Azure Data Lake Store Gen2 account.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.servicePrincipalKey] The Key of the
	 * application used to authenticate against the Azure Data Lake Store Gen2 account.
	 * @member {string} [typeProperties.servicePrincipalKey.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.accountlKey] The AccountKey
	 * used to authenticate against the Azure Data Lake Store gen2 account.
	 * @member {string} [typeProperties.accountlKey.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.tenant] The name or ID of the tenant to
	 * which the service principal belongs. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.url] Data Lake Store Gen2 url.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureDataLakeStoreGen2CosmosStructuredStreamLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureDataLakeStoreGen2CosmosStructuredStreamLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreGen2CosmosStructuredStreamLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure Data Lake Store Gen2 (structured stream) linked service properties.
	 *
	 * @member {object} [url] Data Lake Store Gen2 url. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [servicePrincipalId] The ID of the application used to
	 * authenticate against the Azure Data Lake Store Gen2 account. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [servicePrincipalKey] The Key of the application used to
	 * authenticate against the Azure Data Lake Store Gen2 account.
	 * @member {string} [servicePrincipalKey.type] Polymorphic Discriminator
	 * @member {object} [tenant] The name or ID of the tenant to which the service
	 * principal belongs. Type: string (or Expression with resultType string).
	 * @member {object} [accountKey] The Key of the application used to
	 * authenticate against the Azure Data Lake Store Gen2 account.
	 * @member {string} [accountKey.type] Polymorphic Discriminator.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureDataLakeStoreGen2CosmosStructuredStreamLinkedServiceTypeProperties {
		url: any;
		accountKey?: SharedModel.SecretBase;
		servicePrincipalId?: any;
		servicePrincipalKey?: SharedModel.SecretBase;
		tenant?: any;
		encryptedCredential?: any;
		credential?: SharedModel.CredentialReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureFunctionLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure Function linked service properties.
	 *
	 * @member {object} [functionAppUrl] The uri pointing to the Azure
	 * Function App. Type: string (or Expression with resultType string).
	 * @member {object} [functionKey] The secret access key of the Azure
	 * Function App.
	 * @member {string} [functionAppKey.type] Polymorphic Discriminator
	 */
	export interface AzureFunctionLinkedServiceTypeProperties {
		authentication?: any;
		functionAppUrl: string;
		functionKey?: string | SharedModel.SecretBase;
		encryptedCredential?: any;
		credential?: SharedModel.CredentialReference;
		resourceId?: any;
		servicePrincipalId?: any;
		servicePrincipalKey?: SharedModel.SecretBase;
		tenant?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureFunctionLinkedService class.
	 * @constructor
	 * Linked service for Azure Function.
	 *
	 * @member {object} typeProperties Azure Function linked service properties.
	 * @member {object} [functionAppUrl] The uri pointing to the Azure
	 * Function App. Type: string (or Expression with resultType string).
	 * @member {object} [functionKey] The secret access key of the Azure
	 * Function App.
	 * @member {string} [functionAppKey.type] Polymorphic Discriminator
	 */
	export interface AzureFunctionLinkedService extends SharedModel.LinkedService {
		properties: { functionAppUrl: string; functionKey?: SecretBase; };
		typeProperties: AzureFunctionLinkedServiceTypeProperties;
	}

	// handcrafted
	export interface AzureBlobFSLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureBlobFSLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the MongoDbLinkedServiceTypeProperties class.
	 * @constructor
	 * MongoDB linked service properties.
	 *
	 * @member {object} server The IP address or server name of the MongoDB server.
	 * Type: string (or Expression with resultType string).
	 * @member {string} [authenticationType] The authentication type to be used to
	 * connect to the MongoDB database. Possible values include: 'Basic',
	 * 'Anonymous'
	 * @member {object} databaseName The name of the MongoDB database that you want
	 * to access. Type: string (or Expression with resultType string).
	 * @member {object} [username] Username for authentication. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [password] Password for authentication.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [authSource] Database to verify the username and password.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [port] The TCP port number that the MongoDB server uses to
	 * listen for client connections. The default value is 27017. Type: integer (or
	 * Expression with resultType integer), minimum: 0.
	 * @member {object} [enableSsl] Specifies whether the connections to the server
	 * are encrypted using SSL. The default value is false. Type: boolean (or
	 * Expression with resultType boolean).
	 * @member {object} [allowSelfSignedServerCert] Specifies whether to allow
	 * self-signed certificates from the server. The default value is false. Type:
	 * boolean (or Expression with resultType boolean).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface MongoDbLinkedServiceTypeProperties {
		server: any;
		authenticationType?: string;
		databaseName: any;
		username?: any;
		password?: SharedModel.SecretBase;
		authSource?: any;
		port?: any;
		enableSsl?: any;
		allowSelfSignedServerCert?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the MongoDbLinkedService class.
	 * @constructor
	 * Linked service for MongoDb data source.
	 *
	 * @member {object} typeProperties MongoDB linked service properties.
	 * @member {object} [typeProperties.server] The IP address or server name of
	 * the MongoDB server. Type: string (or Expression with resultType string).
	 * @member {string} [typeProperties.authenticationType] The authentication type
	 * to be used to connect to the MongoDB database. Possible values include:
	 * 'Basic', 'Anonymous'
	 * @member {object} [typeProperties.databaseName] The name of the MongoDB
	 * database that you want to access. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.username] Username for authentication.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password for authentication.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.authSource] Database to verify the username
	 * and password. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.port] The TCP port number that the MongoDB
	 * server uses to listen for client connections. The default value is 27017.
	 * Type: integer (or Expression with resultType integer), minimum: 0.
	 * @member {object} [typeProperties.enableSsl] Specifies whether the
	 * connections to the server are encrypted using SSL. The default value is
	 * false. Type: boolean (or Expression with resultType boolean).
	 * @member {object} [typeProperties.allowSelfSignedServerCert] Specifies
	 * whether to allow self-signed certificates from the server. The default value
	 * is false. Type: boolean (or Expression with resultType boolean).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface MongoDbLinkedService extends SharedModel.LinkedService {
		typeProperties: MongoDbLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the CassandraLinkedServiceTypeProperties class.
	 * @constructor
	 * Cassandra linked service properties.
	 *
	 * @member {object} host Host name for connection. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [authenticationType] AuthenticationType to be used for
	 * connection. Type: string (or Expression with resultType string).
	 * @member {object} [port] The port for the connection. Type: integer (or
	 * Expression with resultType integer).
	 * @member {object} [username] Username for authentication. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [password] Password for authentication.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface CassandraLinkedServiceTypeProperties {
		host: any;
		authenticationType?: any;
		port?: any;
		username?: any;
		password?: SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the CassandraLinkedService class.
	 * @constructor
	 * Linked service for Cassandra data source.
	 *
	 * @member {object} typeProperties Cassandra linked service properties.
	 * @member {object} [typeProperties.host] Host name for connection. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.authenticationType] AuthenticationType to
	 * be used for connection. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.port] The port for the connection. Type:
	 * integer (or Expression with resultType integer).
	 * @member {object} [typeProperties.username] Username for authentication.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password for authentication.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface CassandraLinkedService extends SharedModel.LinkedService {
		typeProperties: CassandraLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the WebLinkedServiceTypeProperties class.
	 * @constructor
	 * Base definition of WebLinkedServiceTypeProperties, this typeProperties is
	 * polymorphic based on authenticationType, so not flattened in SDK models.
	 *
	 * @member {object} url The URL of the web service endpoint, e.g.
	 * http://www.microsoft.com . Type: string (or Expression with resultType
	 * string).
	 * @member {string} authenticationType Polymorphic Discriminator
	 */
	export interface WebLinkedServiceTypeProperties {
		url: any;
		authenticationType: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the WebLinkedService class.
	 * @constructor
	 * Web linked service.
	 *
	 * @member {object} typeProperties Web linked service properties.
	 * @member {object} [typeProperties.url] The URL of the web service endpoint,
	 * e.g. http://www.microsoft.com . Type: string (or Expression with resultType
	 * string).
	 * @member {string} [typeProperties.authenticationType] Polymorphic
	 * Discriminator
	 */
	export interface WebLinkedService extends SharedModel.LinkedService {
		typeProperties: WebLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ODataLinkedServiceTypeProperties class.
	 * @constructor
	 * OData linked service properties.
	 *
	 * @member {object} url The URL of the OData service endpoint. Type: string (or
	 * Expression with resultType string).
	 * @member {string} [authenticationType] Type of authentication used to connect
	 * to the OData service. Possible values include: 'Basic', 'Anonymous'
	 * @member {object} [userName] User name of the OData service. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [password] Password of the OData service.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 * @member {object} [servicePrincipalKey] Specify the Azure Active Directory application's key.
	 * @member {object} [servicePrincipalEmbeddedCert] Specify the base64 encoded certificate of applications registered in Azure Active Directory.
	 * @member {object} [servicePrincipalEmbeddedCertPassword] Specify the password of the certificate if the certificate is secured with a password.
	 */
	export interface ODataLinkedServiceTypeProperties {
		url: any;
		authenticationType?: string;
		userName?: any;
		password?: string | SharedModel.SecretBase;
		tenant?: string;	// handcrafted
		aadResourceId?: string;	// handcrafted
		aadServicePrincipalCredentialType?: string;	// handcrafted
		servicePrincipalId?: string;	// handcrafted
		servicePrincipalKey?: string | SharedModel.SecretBase;	// handcrafted
		servicePrincipalEmbeddedCert?: string | SharedModel.SecretBase;	// handcrafted
		servicePrincipalEmbeddedCertPassword?: string | SharedModel.SecretBase;	// handcrafted
		encryptedCredential?: any;
		authHeaders?: { [key: string]: SharedModel.SecretBase };
		azureCloudType?: string;
		credential?: any;
	}

	// handcrafted
	export interface SharePointOnlineListLinkedServiceTypeProperties {
		siteUrl?: string;
		tenantId?: string;
		servicePrincipalId?: string;
		servicePrincipalKey?: string | SharedModel.SecretBase;
		servicePrincipalCredentialType?: string;
		servicePrincipalEmbeddedCert?: string | SharedModel.SecretBase;
		servicePrincipalEmbeddedCertPassword?: string | SharedModel.SecretBase;
		encryptedCredential?: any;
		credential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ODataLinkedService class.
	 * @constructor
	 * Open Data Protocol (OData) linked service.
	 *
	 * @member {object} typeProperties OData linked service properties.
	 * @member {object} [typeProperties.url] The URL of the OData service endpoint.
	 * Type: string (or Expression with resultType string).
	 * @member {string} [typeProperties.authenticationType] Type of authentication
	 * used to connect to the OData service. Possible values include: 'Basic',
	 * 'Anonymous'
	 * @member {object} [typeProperties.userName] User name of the OData service.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password of the OData service.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface ODataLinkedService extends SharedModel.LinkedService {
		typeProperties: ODataLinkedServiceTypeProperties;
	}

	//handcrafted
	export interface SharePointOnlineListLinkedService extends SharedModel.LinkedService {
		typeProperties: SharePointOnlineListLinkedServiceTypeProperties;
	}

	export interface SharePointOnlineFileLinkedService extends SharePointOnlineListLinkedService {
	}

	/**
	 * @class
	 * Initializes a new instance of the HdfsLinkedServiceTypeProperties class.
	 * @constructor
	 * HDFS linked service properties.
	 *
	 * @member {object} url The URL of the HDFS service endpoint, e.g.
	 * http://myhostname:50070/webhdfs/v1 . Type: string (or Expression with
	 * resultType string).
	 * @member {object} [authenticationType] Type of authentication used to connect
	 * to the HDFS. Possible values are: Anonymous and Windows. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 * @member {object} [userName] User name for Windows authentication. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [password] Password for Windows authentication.
	 * @member {string} [password.type] Polymorphic Discriminator
	 */
	export interface HdfsLinkedServiceTypeProperties {
		url: any;
		authenticationType?: any;
		encryptedCredential?: any;
		userName?: any;
		password?: SharedModel.SecretBase;
	}

	/**
	 * @class
	 * Initializes a new instance of the HdfsLinkedService class.
	 * @constructor
	 * Hadoop Distributed File System (HDFS) linked service.
	 *
	 * @member {object} typeProperties HDFS linked service properties.
	 * @member {object} [typeProperties.url] The URL of the HDFS service endpoint,
	 * e.g. http://myhostname:50070/webhdfs/v1 . Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.authenticationType] Type of authentication
	 * used to connect to the HDFS. Possible values are: Anonymous and Windows.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.userName] User name for Windows
	 * authentication. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password for Windows
	 * authentication.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 */
	export interface HdfsLinkedService extends SharedModel.LinkedService {
		typeProperties: HdfsLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the OdbcLinkedServiceTypeProperties class.
	 * @constructor
	 * ODBC linked service properties.
	 *
	 * @member {object} connectionString The non-access credential portion of the
	 * connection string as well as an optional encrypted credential. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [authenticationType] Type of authentication used to connect
	 * to the ODBC data store. Possible values are: Anonymous and Basic. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [credential] The access credential portion of the
	 * connection string specified in driver-specific property-value format.
	 * @member {string} [credential.type] Polymorphic Discriminator
	 * @member {object} [userName] User name for Basic authentication. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [password] Password for Basic authentication.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface OdbcLinkedServiceTypeProperties {
		connectionString: any;
		authenticationType?: any;
		credential?: string | SharedModel.SecretBase;
		userName?: any;
		password?: string | SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the OdbcLinkedService class.
	 * @constructor
	 * Open Database Connectivity (ODBC) linked service.
	 *
	 * @member {object} typeProperties ODBC linked service properties.
	 * @member {object} [typeProperties.connectionString] The non-access credential
	 * portion of the connection string as well as an optional encrypted
	 * credential. Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.authenticationType] Type of authentication
	 * used to connect to the ODBC data store. Possible values are: Anonymous and
	 * Basic. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.credential] The access credential portion
	 * of the connection string specified in driver-specific property-value format.
	 * @member {string} [typeProperties.credential.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.userName] User name for Basic
	 * authentication. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password for Basic
	 * authentication.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface OdbcLinkedService extends SharedModel.LinkedService {
		typeProperties: OdbcLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMLLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure ML Web Service linked service properties.
	 *
	 * @member {object} mlEndpoint The Batch Execution REST URL for an Azure ML Web
	 * Service endpoint. Type: string (or Expression with resultType string).
	 * @member {object} apiKey The API key for accessing the Azure ML model
	 * endpoint.
	 * @member {string} [apiKey.type] Polymorphic Discriminator
	 * @member {object} [updateResourceEndpoint] The Update Resource REST URL for
	 * an Azure ML Web Service endpoint. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [servicePrincipalId] The ID of the service principal used
	 * to authenticate against the ARM-based updateResourceEndpoint of an Azure ML
	 * web service. Type: string (or Expression with resultType string).
	 * @member {object} [servicePrincipalKey] The key of the service principal used
	 * to authenticate against the ARM-based updateResourceEndpoint of an Azure ML
	 * web service.
	 * @member {string} [servicePrincipalKey.type] Polymorphic Discriminator
	 * @member {object} [tenant] The name or ID of the tenant to which the service
	 * principal belongs. Type: string (or Expression with resultType string).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureMLLinkedServiceTypeProperties {
		mlEndpoint: any;
		apiKey: SharedModel.SecretBase;
		updateResourceEndpoint?: any;
		servicePrincipalId?: any;
		servicePrincipalKey?: SharedModel.SecretBase;
		tenant?: any;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMLLinkedService class.
	 * @constructor
	 * Azure ML Web Service linked service.
	 *
	 * @member {object} typeProperties Azure ML Web Service linked service
	 * properties.
	 * @member {object} [typeProperties.mlEndpoint] The Batch Execution REST URL
	 * for an Azure ML Web Service endpoint. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.apiKey] The API key for accessing the Azure
	 * ML model endpoint.
	 * @member {string} [typeProperties.apiKey.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.updateResourceEndpoint] The Update Resource
	 * REST URL for an Azure ML Web Service endpoint. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [typeProperties.servicePrincipalId] The ID of the service
	 * principal used to authenticate against the ARM-based updateResourceEndpoint
	 * of an Azure ML web service. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [typeProperties.servicePrincipalKey] The key of the service
	 * principal used to authenticate against the ARM-based updateResourceEndpoint
	 * of an Azure ML web service.
	 * @member {string} [typeProperties.servicePrincipalKey.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.tenant] The name or ID of the tenant to
	 * which the service principal belongs. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureMLLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureMLLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMLServiceLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure ML Service linked service properties.
	 *
	 * @member {object} subscriptionId Azure ML Service workspace subscription ID.
	 * Type: string (or Expression with resultType string).
	 * @member {object} resourceGroupName Azure ML Service workspace resource group name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} mlWorkspaceName Azure ML Service workspace name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [servicePrincipalId] The ID of the service principal used
	 * to authenticate against the ARM-based updateResourceEndpoint of an Azure ML
	 * web service. Type: string (or Expression with resultType string).
	 * @member {object} [servicePrincipalKey] The key of the service principal used
	 * to authenticate against the ARM-based updateResourceEndpoint of an Azure ML
	 * web service.
	 * @member {string} [servicePrincipalKey.type] Polymorphic Discriminator
	 * @member {object} [tenant] The name or ID of the tenant to which the service
	 * principal belongs. Type: string (or Expression with resultType string).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureMLServiceLinkedServiceTypeProperties {
		subscriptionId: any;
		resourceGroupName: any;
		mlWorkspaceName: any;
		servicePrincipalId?: any;
		servicePrincipalKey?: SharedModel.SecretBase;
		tenant?: any;
		encryptedCredential?: any;
		authentication?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMLServiceLinkedService class.
	 * @constructor
	 * Azure ML Web Service linked service.
	 *
	 * @member {object} typeProperties Azure ML Service linked service
	 * properties.
	 * @member {object} [typeProperties.subscriptionId] Azure ML Service workspace subscription ID.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.resourceGroupName] Azure ML Service workspace resource group name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.mlWorkspaceName] Azure ML Service workspace name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.servicePrincipalId] The ID of the service
	 * principal used to authenticate against the ARM-based updateResourceEndpoint
	 * of an Azure ML web service. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [typeProperties.servicePrincipalKey] The key of the service
	 * principal used to authenticate against the ARM-based updateResourceEndpoint
	 * of an Azure ML web service.
	 * @member {string} [typeProperties.servicePrincipalKey.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.tenant] The name or ID of the tenant to
	 * which the service principal belongs. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureMLServiceLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureMLServiceLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the TeradataLinkedServiceTypeProperties class.
	 * @constructor
	 * Teradata linked service properties.
	 *
	 * @member {object} server Server name for connection. Type: string (or
	 * Expression with resultType string).
	 * @member {string} [authenticationType] AuthenticationType to be used for
	 * connection. Possible values include: 'Basic', 'Windows'
	 * @member {object} [username] Username for authentication. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [password] Password for authentication.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface TeradataLinkedServiceTypeProperties {
		server?: any;
		authenticationType?: string;
		username?: any;
		password?: SharedModel.SecretBase;
		connectionString?: any; // handcrafted
		encryptedCredential?: any;
		sslMode?: any
	}

	/**
	 * @class
	 * Initializes a new instance of the TeradataLinkedService class.
	 * @constructor
	 * Linked service for Teradata data source.
	 *
	 * @member {object} typeProperties Teradata linked service properties.
	 * @member {object} [typeProperties.server] Server name for connection. Type:
	 * string (or Expression with resultType string).
	 * @member {string} [typeProperties.authenticationType] AuthenticationType to
	 * be used for connection. Possible values include: 'Basic', 'Windows'
	 * @member {object} [typeProperties.username] Username for authentication.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password for authentication.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface TeradataLinkedService extends SharedModel.LinkedService {
		typeProperties: TeradataLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the TeradataV2LinkedServiceTypeProperties class.
	 * @constructor
	 * Teradata V2 linked service properties.
	 *
	 * @member {object} server Server name for connection. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [username] Username for authentication. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [password] Password for authentication.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface TeradataV2LinkedServiceTypeProperties {
		server: any;
		username: any;
		password?: string | SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the TeradataV2LinkedService class.
	 * @constructor
	 * Linked service for Teradata V2 data source.
	 *
	 * @member {object} typeProperties Teradata linked service properties.
	 * @member {object} [typeProperties.server] Server name for connection. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.username] Username for authentication.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password for authentication.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface TeradataV2LinkedService extends SharedModel.LinkedService {
		typeProperties: TeradataV2LinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the Db2LinkedServiceTypeProperties class.
	 * @constructor
	 * DB2 linked service properties.
	 *
	 * @member {object} server Server name for connection. Type: string (or
	 * Expression with resultType string).
	 * @member {object} database Database name for connection. Type: string (or
	 * Expression with resultType string).
	 * @member {string} [authenticationType] AuthenticationType to be used for
	 * connection. Possible values include: 'Basic'
	 * @member {object} [username] Username for authentication. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [password] Password for authentication.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface Db2LinkedServiceTypeProperties {
		server?: any;
		database?: any;
		authenticationType?: string;
		username?: any;
		password?: SharedModel.SecretBase;
		encryptedCredential?: any;
		packageCollection?: any;
		certificateCommonName?: any;
		connectionString?: SharedModel.SecretBase | string; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the Db2LinkedService class.
	 * @constructor
	 * Linked service for DB2 data source.
	 *
	 * @member {object} typeProperties DB2 linked service properties.
	 * @member {object} [typeProperties.server] Server name for connection. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.database] Database name for connection.
	 * Type: string (or Expression with resultType string).
	 * @member {string} [typeProperties.authenticationType] AuthenticationType to
	 * be used for connection. Possible values include: 'Basic'
	 * @member {object} [typeProperties.username] Username for authentication.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password for authentication.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface Db2LinkedService extends SharedModel.LinkedService {
		typeProperties: Db2LinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SybaseLinkedServiceTypeProperties class.
	 * @constructor
	 * Sybase linked service properties.
	 *
	 * @member {object} server Server name for connection. Type: string (or
	 * Expression with resultType string).
	 * @member {object} database Database name for connection. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [schema] Schema name for connection. Type: string (or
	 * Expression with resultType string).
	 * @member {string} [authenticationType] AuthenticationType to be used for
	 * connection. Possible values include: 'Basic', 'Windows'
	 * @member {object} [username] Username for authentication. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [password] Password for authentication.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface SybaseLinkedServiceTypeProperties {
		server: any;
		database: any;
		schema?: any;
		authenticationType?: string;
		username?: any;
		password?: SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SybaseLinkedService class.
	 * @constructor
	 * Linked service for Sybase data source.
	 *
	 * @member {object} typeProperties Sybase linked service properties.
	 * @member {object} [typeProperties.server] Server name for connection. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.database] Database name for connection.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.schema] Schema name for connection. Type:
	 * string (or Expression with resultType string).
	 * @member {string} [typeProperties.authenticationType] AuthenticationType to
	 * be used for connection. Possible values include: 'Basic', 'Windows'
	 * @member {object} [typeProperties.username] Username for authentication.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password for authentication.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface SybaseLinkedService extends SharedModel.LinkedService {
		typeProperties: SybaseLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the PostgreSqlLinkedServiceTypeProperties class.
	 * @constructor
	 * PostgreSQL linked service properties.
	 *
	 * @member {object} connectionString The connection string.
	 * @member {string} [connectionString.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface PostgreSqlLinkedServiceTypeProperties {
		connectionString: SharedModel.SecretBase | string; // handcrafted
		password?: SharedModel.AzureKeyVaultSecretReference; // handcrafted
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the PostgreSqlLinkedService class.
	 * @constructor
	 * Linked service for PostgreSQL data source.
	 *
	 * @member {object} typeProperties PostgreSQL linked service properties.
	 * @member {object} [typeProperties.connectionString] The connection string.
	 * @member {string} [typeProperties.connectionString.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface PostgreSqlLinkedService extends SharedModel.LinkedService {
		typeProperties: PostgreSqlLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the PostgreSqlLinkedServiceTypeProperties class.
	 * @constructor
	 * PostgreSQL linked service properties.
	 *
	 * @member {string} [server] specifies the host name (port optional) on which PostgreSQL is running
	 * @member {number} [port] the TCP port of the PostgreSQL server
	 * @member {string} [database] the PostgreSQL database to connect to
	 * @member {string} [username] the username to connect with
	 * @member {object} [password] the password to connect with
	 * @member {number} [sslMode] controls whether SSL is used, depending on server support
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 * @member {object} [authenticationType] The supported authentication type, valid value is Basic.
	 * @member {string} [schema] Part of additionalProperties. Sets the schema search path.
	 * @member {boolean} [pooling] Part of additionalProperties. Whether connection pooling should be used.
	 * @member {number} [timeout] Part of additionalProperties. the time to wait (in seconds) while trying to
	 * establish a connection before terminating the attempt and generating an error.
	 * @member {number} [commandTimeout] Part of additionalProperties. the time to wait (in seconds) while
	 * trying to execute a command before terminating the attempt and generating an error.
	 * Set to zero for infinity
	 * @member {boolean} [trustServerCertificate] Part of additionalProperties. Whether to trust the server
	 * certificate without validating it.
	 * @member {string} [sslCertificate] Part of additionalProperties. Location of a client certificate
	 * to be sent to the server.
	 * @member {string} [sslKey] Part of additionalProperties. Location of a client key for a client
	 * certificate to be sent to the server.
	 * @member {string} [sslPassword] Part of additionalProperties. Password for a key for a client certificate.
	 * @member {number} [readBufferSize] Part of additionalProperties. Determines the size of the internal
	 * buffer Npgsql uses when reading. Increasing may improve performance if transferring large values from the DB.
	 * @member {boolean} [logParameters] Part of additionalProperties. When enabled, parameter values are logged
	 * when commands are executed.
	 * @member {string} [timezone] Part of additionalProperties. Gets or sets the session timezone.
	 * @member {string} [encoding] Part of additionalProperties. Gets or sets the .NET encoing that will be used to
	 * encode/decode PostgreSQL string data
	 */
	export interface PostgreSqlV2LinkedServiceTypeProperties {
		server: any;
		port: any;
		database: any;
		username: any;
		password: SharedModel.SecretBase | string;
		sslMode: any;
		encryptedCredential?: any;
		authenticationType: any;
		// below are not shown in UI, only via additionalProperties (non-parameterizable)
		schema?: string;
		pooling?: boolean;
		timeout?: number;
		commandTimeout?: number;
		trustServerCertificate?: boolean;
		sslCertificate?: string;
		sslKey?: string;
		sslPassword?: SharedModel.SecretBase | string;
		readBufferSize?: number;
		logParameters?: boolean;
		timezone?: string;
		encoding?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the PostgreSqlV2LinkedService class.
	 * @constructor
	 * Linked service for PostgreSQL data source.
	 *
	 * @member {object} typeProperties PostgreSQL linked service properties.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface PostgreSqlV2LinkedService extends SharedModel.LinkedService {
		typeProperties: PostgreSqlV2LinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the MySqlLinkedServiceTypeProperties class.
	 * @constructor
	 * MySQL linked service properties.
	 *
	 * @member {object} connectionString The connection string.
	 * @member {string} [connectionString.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface MySqlLinkedServiceTypeProperties {
		connectionString?: SharedModel.SecretBase | string; // handcrafted
		password?: any // handcrafted
		encryptedCredential?: any;
		driverVersion?: string;
		server?: any;
		database?: any;
		port?: any,
		username?: any,
		sslMode?: any,
		useSystemTrustStore?: any,

		allowZeroDatetime?: any,
		connectionTimeout?: any,
		convertZeroDatetime?: any,
		guidFormat?: any,
		sslCert?: any,
		sslKey?: any,
		treatTinyAsBoolean?: any,
	}

	/**
	 * @class
	 * Initializes a new instance of the MySqlLinkedService class.
	 * @constructor
	 * Linked service for MySQL data source.
	 *
	 * @member {object} typeProperties MySQL linked service properties.
	 * @member {object} [typeProperties.connectionString] The connection string.
	 * @member {string} [typeProperties.connectionString.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface MySqlLinkedService extends SharedModel.LinkedService {
		typeProperties: MySqlLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMySqlLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure MySQL database linked service properties.
	 *
	 * @member {object} connectionString The connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureMySqlLinkedServiceTypeProperties {
		connectionString: any;
		password?: SharedModel.AzureKeyVaultSecretReference;	// handcrafted
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMySqlLinkedService class.
	 * @constructor
	 * Azure MySQL database linked service.
	 *
	 * @member {object} typeProperties Azure MySQL database linked service
	 * properties.
	 * @member {object} [typeProperties.connectionString] The connection string.
	 * Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureMySqlLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureMySqlLinkedServiceTypeProperties;
	}

	interface IOracleV2Properties {
		server?: any;
		authenticationType?: any;
		username?: any;
		encryptionClient?: string,
		encryptionTypesClient?: string,
		cryptoChecksumClient?: string,
		cryptoChecksumTypesClient?: string,
		initialLobFetchSize?: number,
		fetchSize?: number,
		statementCacheSize?: number,
		initializationString?: string,
		enableBulkLoad?: boolean,
		supportV1DataTypes?: boolean,
		fetchTswtzAsTimestamp?: boolean
	}

	/**
	 * @class
	 * Initializes a new instance of the OracleLinkedServiceTypeProperties class.
	 * @constructor
	 * Oracle database linked service properties.
	 *
	 * @member {object} connectionString The connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface OracleLinkedServiceTypeProperties extends IOracleV2Properties {
		connectionString?: any;
		password?: SharedModel.AzureKeyVaultSecretReference | string | SharedModel.SecretBase; // handcrafted
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the OracleLinkedService class.
	 * @constructor
	 * Oracle database.
	 *
	 * @member {object} typeProperties Oracle database linked service properties.
	 * @member {object} [typeProperties.connectionString] The connection string.
	 * Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface OracleLinkedService extends SharedModel.LinkedService {
		typeProperties: OracleLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the FileServerLinkedServiceTypeProperties class.
	 * @constructor
	 * File system linked service properties.
	 *
	 * @member {object} host Host name of the server. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [userId] User ID to logon the server. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [password] Password to logon the server.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface FileServerLinkedServiceTypeProperties {
		host: any;
		userId?: any;
		password?: string | SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	// handcrafted
	/**
	 * @class
	 * Initializes a new instance of the AzureFileStorageLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure File Storage linked service properties.
	 *
	 * @member {object} [connectionString] The connection string. It is mutually
	 * exclusive with sasUri property. Type: string, SecureString or
	 * AzureKeyVaultSecretReference.
	 * @member {object} [sasUri] SAS URI of the Azure Storage resource. It is
	 * mutually exclusive with connectionString property.
	 * @member {string} [sasUri.type] Polymorphic Discriminator
	 * @member {string} [servicePrincipalKey.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureFileStorageLinkedServiceTypeProperties {
		host?: any;
		userId?: any;
		password?: string | SharedModel.SecretBase;
		connectionString?: any;
		accountKey?: string | SharedModel.AzureKeyVaultSecretReference;
		sasUri?: any;
		sasToken?: string | SharedModel.AzureKeyVaultSecretReference;
		fileShare?: string;
		snapshot?: string;
		encryptedCredential?: any;
		serviceEndpoint?: any;
		credential?: SharedModel.CredentialReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the FileServerLinkedService class.
	 * @constructor
	 * File system linked service.
	 *
	 * @member {object} typeProperties File system linked service properties.
	 * @member {object} [typeProperties.host] Host name of the server. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.userId] User ID to logon the server. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password to logon the server.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface FileServerLinkedService extends SharedModel.LinkedService {
		typeProperties: FileServerLinkedServiceTypeProperties;
	}

	// handcrafted
	export interface AzureFileStorageLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureFileStorageLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightLinkedServiceTypeProperties class.
	 * @constructor
	 * HDInsight linked service properties.
	 *
	 * @member {object} clusterUri HDInsight cluster URI. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [userName] HDInsight cluster user name. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [password] HDInsight cluster password.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [linkedServiceName] The Azure Storage linked service
	 * reference.
	 * @member {string} [linkedServiceName.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [linkedServiceName.parameters] Arguments for LinkedService.
	 * @member {object} [hcatalogLinkedServiceName] A reference to the Azure SQL
	 * linked service that points to the HCatalog database.
	 * @member {string} [hcatalogLinkedServiceName.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [hcatalogLinkedServiceName.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 * @member {object} [fileSystem] File system. Type: string.
	 */
	export interface HDInsightLinkedServiceTypeProperties {
		clusterUri: any;
		userName?: any;
		password?: SharedModel.SecretBase;
		isEspEnabled?: boolean; // handcrafted
		linkedServiceName?: SharedModel.LinkedServiceReference;
		hcatalogLinkedServiceName?: SharedModel.LinkedServiceReference;
		encryptedCredential?: any;
		fileSystem?: string;
		clusterAuthType?: string;
		credential?: SharedModel.CredentialReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightLinkedService class.
	 * @constructor
	 * HDInsight linked service.
	 *
	 * @member {object} typeProperties HDInsight linked service properties.
	 * @member {object} [typeProperties.clusterUri] HDInsight cluster URI. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.userName] HDInsight cluster user name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] HDInsight cluster password.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.linkedServiceName] The Azure Storage linked
	 * service reference.
	 * @member {string} [typeProperties.linkedServiceName.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [typeProperties.linkedServiceName.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [typeProperties.hcatalogLinkedServiceName] A reference to
	 * the Azure SQL linked service that points to the HCatalog database.
	 * @member {string} [typeProperties.hcatalogLinkedServiceName.referenceName]
	 * Reference LinkedService name.
	 * @member {object} [typeProperties.hcatalogLinkedServiceName.parameters]
	 * Arguments for LinkedService.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface HDInsightLinkedService extends SharedModel.LinkedService {
		typeProperties: HDInsightLinkedServiceTypeProperties;
	}

	export interface SnowflakeLinkedServiceTypeProperties {
		connectionString?: any; // handcrafted
		password?: SharedModel.SecretBase; // handcrafted
		authenticationType?: string; //handcraft
		clientId?: any; // handcraft
		clientSecret?: string | SharedModel.SecretBase; // handcraft
		oauthTokenEndpoint?: any; // handcraft
		oauthUserName?: any; // handcrafted
		oauthPassword?: string | SharedModel.SecretBase; // handcrafted
		scope?: any; // handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the SnowflakeV2LinkedService class.
	 * @constructor
	 * Snowflake V2 linked service.
	 * @member {object} typeProperties Snowflake V2 linked service
	 * properties.
	 * @member {object} [typeProperties.accountIdentifier] The account name of Snowflake account.
	 * (The full account name might include additional segments that identify the region and cloud platform where the account is hosted)
	 * @member {object} [typeProperties.user] Login name for snowflake user.
	 * @member {object} [typeProperties.warehouse] The default virtual warehouse to use for the session after connecting.
	 * @member {object} [typeProperties.database] The default database to use for the session after connecting.
	 * @member {object} [typeProperties.role] The default security role to use for the session after connecting.
	 * @member {object} [typeProperties.authenticationType] Basic (Default), KeyPair, AADServicePrincipal
	 * @member {object} [typeProperties.password] Password for the Snowflake User. Required for Basic Auth.
	 * @member {object} [typeProperties.privateKey] Used for key-pair authentication. Required for KeyPair Auth.
	 * @member {object} [typeProperties.privateKeyPassphrase] The passphrase to use for
	 * decrypting the private key, if the key is encrypted
	 * @member {object} [typeProperties.tenantId] The tenant information under which
	 * your Microsoft Entra web application resides. Required for AADServicePrincipal Auth.
	 * @member {object} [typeProperties.clientId] The application's client ID. Required for AADServicePrincipal Auth.
	 * @member {object} [typeProperties.clientSecret] The application's key. Required for AADServicePrincipal Auth.
	 * @member {object} [typeProperties.scope] The scope to limit the application's access.
	 * @member {object} [typeProperties.version] ADF ONLY. The version for Snowflake V2
	 * Required for AADServicePrincipal Auth.
	 */
	export interface SnowflakeV2LinkedServiceTypeProperties {
		accountIdentifier: any;
		user: any;
		warehouse: any;
		database: any;
		role?: any;
		authenticationType?: string;
		password?: string | SharedModel.SecretBase;
		privateKey?: string | SharedModel.SecretBase;
		privateKeyPassphrase?: string | SharedModel.SecretBase;
		tenantId?: any;
		clientId?: any;
		clientSecret?: string | SharedModel.SecretBase;
		scope?: any;
		host?: any;
		encryptedCredential?: any;
		version?: any;
		// additional properties
		schema?: any;
	}

	export interface SnowflakeV2LinkedService extends SharedModel.LinkedService {
		typeProperties: SnowflakeV2LinkedServiceTypeProperties;
	}

	export interface AzureDataShareLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureDataShareLinkedServiceTypeProperties;
	}

	export interface AzureDataShareLinkedServiceTypeProperties {
		resourceId: string;
	}

	export interface SnowflakeLinkedService extends SharedModel.LinkedService {
		typeProperties: SnowflakeLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the DynamicsLinkedServiceTypeProperties class.
	 * @constructor
	 * Dynamics linked service properties.
	 *
	 * @member {object} deploymentType The deployment type of the Dynamics
	 * instance. 'Online' for Dynamics Online and 'OnPremisesWithIfd' for Dynamics
	 * on-premises with Ifd. Type: string (or Expression with resultType string).
	 * @member {object} [hostName] The host name of the on-premises Dynamics
	 * server. The property is required for on-prem and not allowed for online.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [port] The port of on-premises Dynamics server. The
	 * property is required for on-prem and not allowed for online. Default is 443.
	 * Type: integer (or Expression with resultType integer), minimum: 0.
	 * @member {object} [serviceUri] The URL to the Microsoft Dynamics server. The
	 * property is required for on-line and not allowed for on-prem. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [organizationName] The organization name of the Dynamics
	 * instance. The property is required for on-prem and required for online when
	 * there are more than one Dynamics instances associated with the user. Type:
	 * string (or Expression with resultType string).
	 * @member {object} authenticationType The authentication type to connect to
	 * Dynamics server. 'Office365' for online scenario, 'Ifd' for on-premises with
	 * Ifd scenario. Type: string (or Expression with resultType string).
	 * @member {object} username User name to access the Dynamics instance. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [password] Password to access the Dynamics instance.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 * @member {object} domain The Active Directory domain that will verify user credentials.
	 */
	export interface DynamicsLinkedServiceTypeProperties {
		deploymentType: any;
		hostName?: any;
		port?: any;
		serviceUri?: any;
		organizationName?: any;
		authenticationType: any;
		username: any;
		password?: SharedModel.SecretBase;
		servicePrincipalId?: string;
		servicePrincipalCredentialType?: string;
		servicePrincipalCredential?: SharedModel.SecretBase;
		encryptedCredential?: any;
		credential?: any;
		domain?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DynamicsLinkedService class.
	 * @constructor
	 * Dynamics linked service.
	 *
	 * @member {object} typeProperties Dynamics linked service properties.
	 * @member {object} [typeProperties.deploymentType] The deployment type of the
	 * Dynamics instance. 'Online' for Dynamics Online and 'OnPremisesWithIfd' for
	 * Dynamics on-premises with Ifd. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [typeProperties.hostName] The host name of the on-premises
	 * Dynamics server. The property is required for on-prem and not allowed for
	 * online. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.port] The port of on-premises Dynamics
	 * server. The property is required for on-prem and not allowed for online.
	 * Default is 443. Type: integer (or Expression with resultType integer),
	 * minimum: 0.
	 * @member {object} [typeProperties.serviceUri] The URL to the Microsoft
	 * Dynamics server. The property is required for on-line and not allowed for
	 * on-prem. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.organizationName] The organization name of
	 * the Dynamics instance. The property is required for on-prem and required for
	 * online when there are more than one Dynamics instances associated with the
	 * user. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.authenticationType] The authentication type
	 * to connect to Dynamics server. 'Office365' for online scenario, 'Ifd' for
	 * on-premises with Ifd scenario. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [typeProperties.username] User name to access the Dynamics
	 * instance. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.password] Password to access the Dynamics
	 * instance.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface DynamicsLinkedService extends SharedModel.LinkedService {
		typeProperties: DynamicsLinkedServiceTypeProperties;
	}

	// handcraft
	export interface CommonDataServiceLinkedServiceTypeProperties extends DynamicsLinkedServiceTypeProperties {
	}

	// handcraft
	export interface CommonDataServiceLinkedService extends SharedModel.LinkedService {
		typeProperties: CommonDataServiceLinkedServiceTypeProperties
	}

	// handcraft
	export interface DynamicsCRMLinkedServiceTypeProperties extends DynamicsLinkedServiceTypeProperties {
	}

	// handcraft
	export interface DynamicsCRMLinkedService extends SharedModel.LinkedService {
		typeProperties: DynamicsCRMLinkedServiceTypeProperties
	}

	/**
	 * @class
	 * Initializes a new instance of the CosmosDbLinkedServiceTypeProperties class.
	 * @constructor
	 * CosmosDB linked service properties.
	 *
	 * @member {object} connectionString The connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface CosmosDbLinkedServiceTypeProperties {
		connectionString?: any;
		accountKey?: any; // handcrafted
		encryptedCredential?: any;
		accountEndpoint?: any; // handcrafted
		database?: any; // handcrafted
		servicePrincipalId?: any; // handcrafted
		servicePrincipalCredentialType?: any; // handcrafted
		servicePrincipalCredential?: any; // handcrafted
		tenant?: any; // handcrafted
		credential?: any; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the CosmosDbLinkedService class.
	 * @constructor
	 * Microsoft Azure Cosmos Database (CosmosDB) linked service.
	 *
	 * @member {object} typeProperties CosmosDB linked service properties.
	 * @member {object} [typeProperties.connectionString] The connection string.
	 * Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface CosmosDbLinkedService extends SharedModel.LinkedService {
		typeProperties: CosmosDbLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureKeyVaultLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure Key Vault linked service properties.
	 *
	 * @member {object} baseUrl The base URL of the Azure Key Vault. e.g.
	 * https://myakv.vault.azure.net Type: string (or Expression with resultType
	 * string).
	 * @member {object} credential Reference to the credential object used to authenticate the Azure Key Vault.
	 * Type: string (or Expression with resultType string).
	 */
	export interface AzureKeyVaultLinkedServiceTypeProperties {
		baseUrl: any;
		credential?: SharedModel.CredentialReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureKeyVaultLinkedService class.
	 * @constructor
	 * Azure Key Vault linked service.
	 *
	 * @member {object} typeProperties Azure Key Vault linked service properties.
	 * @member {object} [typeProperties.baseUrl] The base URL of the Azure Key
	 * Vault. e.g. https://myakv.vault.azure.net Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureKeyVaultLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureKeyVaultLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureBatchLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure Batch linked service properties.
	 *
	 * @member {object} accountName The Azure Batch account name. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [accessKey] The Azure Batch account access key.
	 * @member {string} [accessKey.type] Polymorphic Discriminator
	 * @member {object} batchUri The Azure Batch URI. Type: string (or Expression
	 * with resultType string).
	 * @member {object} poolName The Azure Batch pool name. Type: string (or
	 * Expression with resultType string).
	 * @member {object} linkedServiceName The Azure Storage linked service
	 * reference.
	 * @member {string} [linkedServiceName.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [linkedServiceName.parameters] Arguments for LinkedService.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureBatchLinkedServiceTypeProperties {
		accountName?: any;
		accessKey?: SharedModel.SecretBase;
		batchUri: any;
		poolName: any;
		linkedServiceName: SharedModel.LinkedServiceReference;
		encryptedCredential?: any;
		credential?: SharedModel.CredentialReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureBatchLinkedService class.
	 * @constructor
	 * Azure Batch linked service.
	 *
	 * @member {object} typeProperties Azure Batch linked service properties.
	 * @member {object} [typeProperties.accountName] The Azure Batch account name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.accessKey] The Azure Batch account access
	 * key.
	 * @member {string} [typeProperties.accessKey.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.batchUri] The Azure Batch URI. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.poolName] The Azure Batch pool name. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.linkedServiceName] The Azure Storage linked
	 * service reference.
	 * @member {string} [typeProperties.linkedServiceName.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [typeProperties.linkedServiceName.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureBatchLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureBatchLinkedServiceTypeProperties;
	}

	/**
	 * @member {object} alwaysEncryptedAkvAuthType either 'ServicePrincipal' or 'ManagedIdentity'.
	 * @member {object} [servicePrincipalId] The ID of the service principal used
	 * to authenticate against Azure SQL Database. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [servicePrincipalKey] The key of the service principal used
	 * to authenticate against Azure SQL Database.
	 * @member {string} [servicePrincipalKey.type] Polymorphic Discriminator
	 */
	export interface SqlEncryptionProperties {
		alwaysEncryptedAkvAuthType: string;
		servicePrincipalId?: string; // handcrafted
		servicePrincipalKey?: string | SharedModel.SecretBase; // handcrafted
		credential?: SharedModel.CredentialReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSqlDatabaseLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure SQL Database linked service properties.
	 *
	 * @member {object} connectionString The connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [servicePrincipalId] The ID of the service principal used
	 * to authenticate against Azure SQL Database. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [servicePrincipalKey] The key of the service principal used
	 * to authenticate against Azure SQL Database.
	 * @member {string} [servicePrincipalKey.type] Polymorphic Discriminator
	 * @member {object} [tenant] The name or ID of the tenant to which the service
	 * principal belongs. Type: string (or Expression with resultType string).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureSqlDatabaseLinkedServiceTypeProperties extends ISqlV2Properties {
		connectionString?: any;
		password?: string | SharedModel.SecretBase | SharedModel.AzureKeyVaultSecretReference;	// handcrafted
		servicePrincipalId?: string; // handcrafted
		servicePrincipalKey?: string | SharedModel.SecretBase; // handcrafted
		tenant?: string; // handcrafted
		encryptedCredential?: any;
		azureCloudType?: any; // handcrafted
		credential?: SharedModel.CredentialReference;
		alwaysEncryptedSettings?: SqlEncryptionProperties;
	}

	export interface AzureDatabricksDeltaLakeLinkedServiceTypeProperties {
		domain: any;
		clusterId: any;
		accessToken?: any;
		encryptedCredential?: any;
		credential?: SharedModel.CredentialReference;
		workspaceResourceId?: any;
	}
	export interface DatabricksLinkedServiceTypeProperties {
		domain: any;
		clusterId: any;
		accessToken?: any;
		encryptedCredential?: any;
		credential?: SharedModel.CredentialReference;
		workspaceResourceId?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSqlDatabaseLinkedService class.
	 * @constructor
	 * Microsoft Azure SQL Database linked service.
	 *
	 * @member {object} typeProperties Azure SQL Database linked service
	 * properties.
	 * @member {object} [typeProperties.connectionString] The connection string.
	 * Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.servicePrincipalId] The ID of the service
	 * principal used to authenticate against Azure SQL Database. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.servicePrincipalKey] The key of the service
	 * principal used to authenticate against Azure SQL Database.
	 * @member {string} [typeProperties.servicePrincipalKey.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.tenant] The name or ID of the tenant to
	 * which the service principal belongs. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureSqlDatabaseLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureSqlDatabaseLinkedServiceTypeProperties;
	}

	export interface AzureDatabricksDeltaLakeLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureDatabricksDeltaLakeLinkedServiceTypeProperties;
	}
	export interface DatabricksLinkedService extends SharedModel.LinkedService {
		typeProperties: DatabricksLinkedServiceTypeProperties;
	}

	interface ISqlV2Properties {
		server?: string;
		database?: string;
		encrypt?: string;
		trustServerCertificate?: boolean;
		hostNameInCertificate?: string;
		authenticationType?: string;
		userName?: string;
		servicePrincipalCredential?: string | SharedModel.SecretBase;
		applicationIntent?: string;
		connectTimeout?: number;
		connectRetryCount?: number;
		connectRetryInterval?: number;
		loadBalanceTimeout?: number;
		commandTimeout?: number;
		integratedSecurity?: boolean;
		failoverPartner?: string;
		maxPoolSize?: number;
		minPoolSize?: number;
		multipleActiveResultSets?: boolean;
		multiSubnetFailover?: boolean;
		packetSize?: number;
		pooling?: boolean;
		credential?: SharedModel.CredentialReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlServerLinkedServiceTypeProperties class.
	 * @constructor
	 * SQL Server linked service properties.
	 *
	 * @member {object} connectionString The connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [userName] The on-premises Windows authentication user
	 * name. Type: string (or Expression with resultType string).
	 * @member {object} [password] The on-premises Windows authentication password.
	 * @member {string} [password.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface SqlServerLinkedServiceTypeProperties extends ISqlV2Properties {
		connectionString?: any;
		password?: string | SharedModel.SecretBase; // handcrafted
		encryptedCredential?: any;
		alwaysEncryptedSettings?: SqlEncryptionProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlServerLinkedService class.
	 * @constructor
	 * SQL Server linked service.
	 *
	 * @member {object} typeProperties SQL Server linked service properties.
	 * @member {object} [typeProperties.connectionString] The connection string.
	 * Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.userName] The on-premises Windows
	 * authentication user name. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [typeProperties.password] The on-premises Windows
	 * authentication password.
	 * @member {string} [typeProperties.password.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface SqlServerLinkedService extends SharedModel.LinkedService {
		typeProperties: SqlServerLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSqlDWLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure SQL Data Warehouse linked service properties.
	 *
	 * @member {object} connectionString The connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference. Type: string, SecureString or
	 * AzureKeyVaultSecretReference.
	 * @member {object} [servicePrincipalId] The ID of the service principal used
	 * to authenticate against Azure SQL Data Warehouse. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [servicePrincipalKey] The key of the service principal used
	 * to authenticate against Azure SQL Data Warehouse.
	 * @member {string} [servicePrincipalKey.type] Polymorphic Discriminator
	 * @member {object} [tenant] The name or ID of the tenant to which the service
	 * principal belongs. Type: string (or Expression with resultType string).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureSqlDWLinkedServiceTypeProperties extends ISqlV2Properties {
		connectionString?: any;
		password?: string | SharedModel.SecretBase | SharedModel.AzureKeyVaultSecretReference; // handcrafted
		servicePrincipalId?: string; // handcrafted
		servicePrincipalKey?: string | SharedModel.SecretBase; // handcrafted
		tenant?: string; // handcrafted
		encryptedCredential?: any;
		azureCloudType?: any;
		credential?: SharedModel.CredentialReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the DataWarehouseLinkedServiceTypeProperties class.
	 * @constructor
	 * Data Warehouse linked service properties.
	 *
	 * @member {string} [endpoint]
	 * @member {string} [artifactId]
	 */
	export interface DataWarehouseLinkedServiceTypeProperties {
		endpoint: string;
		endPoint?: string;
		artifactId: string;
		tenant?: string;
		workspaceId?: string;
		servicePrincipalId?: string;
		servicePrincipalCredentialType?: string;
		servicePrincipalCredential?: string | SharedModel.SecretBase;
		servicePrincipalKey?: string | SharedModel.SecretBase;
		credential?: SharedModel.CredentialReference;
		authenticationType?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSqlDWLinkedService class.
	 * @constructor
	 * Azure SQL Data Warehouse linked service.
	 *
	 * @member {object} typeProperties Azure SQL Data Warehouse linked service
	 * properties.
	 * @member {object} [typeProperties.connectionString] The connection string.
	 * Type: string, SecureString or AzureKeyVaultSecretReference. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.servicePrincipalId] The ID of the service
	 * principal used to authenticate against Azure SQL Data Warehouse. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.servicePrincipalKey] The key of the service
	 * principal used to authenticate against Azure SQL Data Warehouse.
	 * @member {string} [typeProperties.servicePrincipalKey.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.tenant] The name or ID of the tenant to
	 * which the service principal belongs. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureSqlDWLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureSqlDWLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the DataWarehouseLinkedService class.
	 * @constructor
	 * Data Warehouse linked service.
	 *
	 * @member {string} [endpoint]
	 * @member {string} [artifactId]
	 */
	export interface DataWarehouseLinkedService extends SharedModel.LinkedService {
		typeProperties: DataWarehouseLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureStorageLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure Storage linked service properties.
	 *
	 * @member {object} [connectionString] The connection string. It is mutually
	 * exclusive with sasUri property. Type: string, SecureString or
	 * AzureKeyVaultSecretReference.
	 * @member {object} [sasUri] SAS URI of the Azure Storage resource. It is
	 * mutually exclusive with connectionString property.
	 * @member {string} [sasUri.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureStorageLinkedServiceTypeProperties {
		connectionString?: any;
		accountKey?: string | SharedModel.SecretBase;
		sasUri?: any;	// handcrafted
		sasToken?: string | SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureStorageLinkedService class.
	 * @constructor
	 * The storage account linked service.
	 *
	 * @member {object} typeProperties Azure Storage linked service properties.
	 * @member {object} [typeProperties.connectionString] The connection string. It
	 * is mutually exclusive with sasUri property. Type: string, SecureString or
	 * AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.sasUri] SAS URI of the Azure Storage
	 * resource. It is mutually exclusive with connectionString property.
	 * @member {string} [typeProperties.sasUri.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureStorageLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureStorageLinkedServiceTypeProperties;
	}

	export interface AzureTableStorageLinkedServiceTypeProperties extends AzureStorageLinkedServiceTypeProperties {
		serviceEndpoint?: any;
		credential?: SharedModel.CredentialReference;
	}

	export interface AzureTableStorageLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureTableStorageLinkedServiceTypeProperties;
	}

	// handcrafted
	export interface AzureSqlMILinkedService extends SharedModel.LinkedService {
		typeProperties: AzureSqlMILinkedServiceTypeProperties;
	}

	// handcrafted
	export interface AzureSqlMILinkedServiceTypeProperties {
		connectionString: any;
		userName?: any;
		password?: string | SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ResponsysObjectDataset class.
	 * @constructor
	 * Responsys dataset.
	 *
	 */
	export interface ResponsysObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceMarketingCloudObjectDataset class.
	 * @constructor
	 * Salesforce Marketing Cloud dataset.
	 *
	 */
	export interface SalesforceMarketingCloudObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the VerticaTableDataset class.
	 * @constructor
	 * Vertica dataset.
	 *
	 */
	export interface VerticaTableDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the NetezzaTableDataset class.
	 * @constructor
	 * Netezza dataset.
	 *
	 */
	export interface NetezzaTableDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the ZohoObjectDataset class.
	 * @constructor
	 * Zoho server dataset.
	 *
	 */
	export interface ZohoObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the XeroObjectDataset class.
	 * @constructor
	 * Xero Serivce dataset.
	 *
	 */
	export interface XeroObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the SquareObjectDataset class.
	 * @constructor
	 * Square Serivce dataset.
	 *
	 */
	export interface SquareObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the SparkObjectDataset class.
	 * @constructor
	 * Spark Server dataset.
	 *
	 */
	export interface SparkObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the ShopifyObjectDataset class.
	 * @constructor
	 * Shopify Serivce dataset.
	 *
	 */
	export interface ShopifyObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the ServiceNowObjectDataset class.
	 * @constructor
	 * ServiceNow server dataset.
	 *
	 */
	export interface ServiceNowObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the ServiceNowV2ObjectDataset class.
	 * @constructor
	 * ServiceNow V2 server dataset.
	 *
	 */
	export interface ServiceNowV2ObjectDataset extends ServiceNowObjectDataset {
		valueType?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the QuickBooksObjectDataset class.
	 * @constructor
	 * QuickBooks server dataset.
	 *
	 */
	export interface QuickBooksObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the PrestoObjectDataset class.
	 * @constructor
	 * Presto server dataset.
	 *
	 */
	export interface PrestoObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the PhoenixObjectDataset class.
	 * @constructor
	 * Phoenix server dataset.
	 *
	 */
	export interface PhoenixObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the PaypalObjectDataset class.
	 * @constructor
	 * Paypal Serivce dataset.
	 *
	 */
	export interface PaypalObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the MarketoObjectDataset class.
	 * @constructor
	 * Marketo server dataset.
	 *
	 */
	export interface MarketoObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the MariaDBTableDataset class.
	 * @constructor
	 * MariaDB server dataset.
	 *
	 */
	export interface MariaDBTableDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the MagentoObjectDataset class.
	 * @constructor
	 * Magento server dataset.
	 *
	 */
	export interface MagentoObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the JiraObjectDataset class.
	 * @constructor
	 * Jira Serivce dataset.
	 *
	 */
	export interface JiraObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the VivaInsightsReportDatasetTypeProperties class.
	 * @constructor
	 * Viva Insights report dataset properties.
	 *
	 * @member {object} [reportId] The Viva Insights report, as either its ID or its display
	 * name. Type: string (or Expression with resultType string).
	 */
	export interface VivaInsightsReportDatasetTypeProperties {
		reportId?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the VivaInsightsReportDataset class.
	 * @constructor
	 * Viva Insights report dataset.
	 *
	 * @member {object} typeProperties Viva Insights report dataset properties.
	 * @member {object} [typeProperties.reportId] The Viva Insights report, as either its ID
	 * or its display name. Type: string (or Expression with resultType string).
	 */
	export interface VivaInsightsReportDataset extends Dataset {
		typeProperties: VivaInsightsReportDatasetTypeProperties;
	}

	export interface AttioObjectDataset extends Dataset {
	}

	export interface BitbucketTableDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the ImpalaObjectDataset class.
	 * @constructor
	 * Impala server dataset.
	 *
	 */
	export interface ImpalaObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the HubspotObjectDataset class.
	 * @constructor
	 * Hubspot Serivce dataset.
	 *
	 */
	export interface HubspotObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the HiveObjectDataset class.
	 * @constructor
	 * Hive Server dataset.
	 *
	 */
	export interface HiveObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the HBaseObjectDataset class.
	 * @constructor
	 * HBase server dataset.
	 *
	 */
	export interface HBaseObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the GreenplumTableDataset class.
	 * @constructor
	 * Greenplum Database dataset.
	 *
	 */
	export interface GreenplumTableDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the GoogleBigQueryObjectDataset class.
	 * @constructor
	 * Google BigQuery service dataset.
	 *
	 */
	export interface GoogleBigQueryObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the EloquaObjectDataset class.
	 * @constructor
	 * Eloqua server dataset.
	 *
	 */
	export interface EloquaObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the DrillTableDataset class.
	 * @constructor
	 * Drill server dataset.
	 *
	 */
	export interface DrillTableDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the CouchbaseTableDataset class.
	 * @constructor
	 * Couchbase server dataset.
	 *
	 */
	export interface CouchbaseTableDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the ConcurObjectDataset class.
	 * @constructor
	 * Concur Serivce dataset.
	 *
	 */
	export interface ConcurObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the AzurePostgreSqlTableDataset class.
	 * @constructor
	 * Azure PostgreSQL dataset.
	 *
	 */
	export interface AzurePostgreSqlTableDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the AmazonMWSObjectDataset class.
	 * @constructor
	 * Amazon Marketplace Web Service dataset.
	 *
	 */
	export interface AmazonMWSObjectDataset extends Dataset {
	}

	/**
	 * @class
	 * Initializes a new instance of the DatasetCompression class.
	 * @constructor
	 * The compression method used on a dataset.
	 *
	 * @member {string} type Polymorphic Discriminator
	 */
	export interface DatasetCompression {
		type: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DatasetZipDeflateCompression class.
	 * @constructor
	 * The ZipDeflate compression method used on a dataset.
	 *
	 * @member {string} [level] The ZipDeflate compression level. Possible values
	 * include: 'Optimal', 'Fastest'
	 */
	export interface DatasetZipDeflateCompression extends DatasetCompression {
		level?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the DatasetDeflateCompression class.
	 * @constructor
	 * The Deflate compression method used on a dataset.
	 *
	 * @member {string} [level] The Deflate compression level. Possible values
	 * include: 'Optimal', 'Fastest'
	 */
	export interface DatasetDeflateCompression extends DatasetCompression {
		level?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the DatasetGZipCompression class.
	 * @constructor
	 * The GZip compression method used on a dataset.
	 *
	 * @member {string} [level] The GZip compression level. Possible values
	 * include: 'Optimal', 'Fastest'
	 */
	export interface DatasetGZipCompression extends DatasetCompression {
		level?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the DatasetBZip2Compression class.
	 * @constructor
	 * The BZip2 compression method used on a dataset.
	 *
	 */
	export interface DatasetBZip2Compression extends DatasetCompression {
	}

	/**
	 * @class
	 * Initializes a new instance of the DatasetStorageFormat class.
	 * @constructor
	 * The format definition of a storage.
	 *
	 * @member {object} [serializer] Serializer. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [deserializer] Deserializer. Type: string (or Expression
	 * with resultType string).
	 * @member {string} type Polymorphic Discriminator
	 */
	export interface DatasetStorageFormat {
		serializer?: any;
		deserializer?: any;
		type: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ParquetFormat class.
	 * @constructor
	 * The data stored in Parquet format.
	 *
	 */
	export interface ParquetFormat extends DatasetStorageFormat {
	}

	/**
	 * @class
	 * Initializes a new instance of the OrcFormat class.
	 * @constructor
	 * The data stored in Optimized Row Columnar (ORC) format.
	 *
	 */
	export interface OrcFormat extends DatasetStorageFormat {
	}

	/**
	 * @class
	 * Initializes a new instance of the AvroFormat class.
	 * @constructor
	 * The data stored in Avro format.
	 *
	 */
	export interface AvroFormat extends DatasetStorageFormat {
	}

	/**
	 * @class
	 * Initializes a new instance of the JsonFormat class.
	 * @constructor
	 * The data stored in JSON format.
	 *
	 * @member {string} [filePattern] File pattern of JSON. To be more specific,
	 * the way of separating a collection of JSON objects. The default value is
	 * 'setOfObjects'. It is case-sensitive. Possible values include:
	 * 'setOfObjects', 'arrayOfObjects'
	 * @member {object} [nestingSeparator] The character used to separate nesting
	 * levels. Default value is '.' (dot). Type: string (or Expression with
	 * resultType string).
	 * @member {object} [encodingName] The code page name of the preferred
	 * encoding. If not provided, the default value is 'utf-8', unless the byte
	 * order mark (BOM) denotes another Unicode encoding. The full list of
	 * supported values can be found in the 'Name' column of the table of encodings
	 * in the following reference: https://go.microsoft.com/fwlink/?linkid=861078.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [jsonNodeReference] The JSONPath of the JSON array element
	 * to be flattened. Example: "$.ArrayPath". Type: string (or Expression with
	 * resultType string).
	 * @member {object} [jsonPathDefinition] The JSONPath definition for each
	 * column mapping with a customized column name to extract data from JSON file.
	 * For fields under root object, start with "$"; for fields inside the array
	 * chosen by jsonNodeReference property, start from the array element. Example:
	 * {"Column1": "$.Column1Path", "Column2": "Column2PathInArray"}. Type: object
	 * (or Expression with resultType object).
	 */
	export interface JsonFormat extends DatasetStorageFormat {
		filePattern?: string;
		nestingSeparator?: any;
		encodingName?: any;
		jsonNodeReference?: any;
		jsonPathDefinition?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the TextFormat class.
	 * @constructor
	 * The data stored in text format.
	 *
	 * @member {object} [columnDelimiter] The column delimiter. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [rowDelimiter] The row delimiter. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [escapeChar] The escape character. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [quoteChar] The quote character. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [nullValue] The null value string. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [encodingName] The code page name of the preferred
	 * encoding. If miss, the default value is ΓÇ£utf-8ΓÇ¥, unless BOM denotes
	 * another Unicode encoding. Refer to the ΓÇ£NameΓÇ¥ column of the table in the
	 * following link to set supported values:
	 * https://msdn.microsoft.com/library/system.text.encoding.aspx. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [treatEmptyAsNull] Treat empty column values in the text
	 * file as null. The default value is true. Type: boolean (or Expression with
	 * resultType boolean).
	 * @member {object} [skipLineCount] The number of lines/rows to be skipped when
	 * parsing text files. The default value is 0. Type: integer (or Expression
	 * with resultType integer).
	 * @member {object} [firstRowAsHeader] When used as input, treat the first row
	 * of data as headers. When used as output,write the headers into the output as
	 * the first row of data. The default value is false. Type: boolean (or
	 * Expression with resultType boolean).
	 */
	export interface TextFormat extends DatasetStorageFormat {
		columnDelimiter?: any;
		rowDelimiter?: any;
		escapeChar?: any;
		quoteChar?: any;
		nullValue?: any;
		encodingName?: any;
		treatEmptyAsNull?: any;
		skipLineCount?: any;
		firstRowAsHeader?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the HttpDatasetTypeProperties class.
	 * @constructor
	 * Properties specific to this dataset type.
	 *
	 * @member {object} [relativeUrl] The relative URL based on the URL in the
	 * HttpLinkedService refers to an HTTP file Type: string (or Expression with
	 * resultType string).
	 * @member {object} [requestMethod] The HTTP method for the HTTP request. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [requestBody] The body for the HTTP request. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [additionalHeaders] The headers for the HTTP Request. e.g.
	 * request-header-name-1:request-header-value-1
	 * ...
	 * request-header-name-n:request-header-value-n Type: string (or Expression
	 * with resultType string).
	 * @member {object} [format] The format of files.
	 * @member {object} [format.serializer] Serializer. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [format.deserializer] Deserializer. Type: string (or
	 * Expression with resultType string).
	 * @member {string} [format.type] Polymorphic Discriminator
	 * @member {object} [compression] The data compression method used on files.
	 * @member {string} [compression.type] Polymorphic Discriminator
	 */
	export interface HttpDatasetTypeProperties {
		relativeUrl?: any;
		requestMethod?: any;
		requestBody?: any;
		additionalHeaders?: any;
		format?: DatasetStorageFormat;
		compression?: DatasetCompression;
	}

	/**
	 * @class
	 * Initializes a new instance of the HttpDataset class.
	 * @constructor
	 * A file in an HTTP web server.
	 *
	 * @member {object} typeProperties Properties specific to this dataset type.
	 * @member {object} [typeProperties.relativeUrl] The relative URL based on the
	 * URL in the HttpLinkedService refers to an HTTP file Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.requestMethod] The HTTP method for the HTTP
	 * request. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.requestBody] The body for the HTTP request.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.additionalHeaders] The headers for the HTTP
	 * Request. e.g. request-header-name-1:request-header-value-1
	 * ...
	 * request-header-name-n:request-header-value-n Type: string (or Expression
	 * with resultType string).
	 * @member {object} [typeProperties.format] The format of files.
	 * @member {object} [typeProperties.format.serializer] Serializer. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.format.deserializer] Deserializer. Type:
	 * string (or Expression with resultType string).
	 * @member {string} [typeProperties.format.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.compression] The data compression method
	 * used on files.
	 * @member {string} [typeProperties.compression.type] Polymorphic Discriminator
	 */
	export interface HttpDataset extends Dataset {
		typeProperties: HttpDatasetTypeProperties;
	}

	// handcrafted
	export interface SnowflakeTableTypeProperties {
		tableName: string;
	}

	export interface SnowflakeTableDataset extends Dataset {
		typeProperties: SnowflakeTableTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSearchIndexDatasetTypeProperties class.
	 * @constructor
	 * Properties specific to this dataset type.
	 *
	 * @member {object} indexName The name of the Azure Search Index. Type: string
	 * (or Expression with resultType string).
	 */
	export interface AzureSearchIndexDatasetTypeProperties {
		indexName: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSearchIndexDataset class.
	 * @constructor
	 * The Azure Search Index.
	 *
	 * @member {object} typeProperties Properties specific to this dataset type.
	 * @member {object} [typeProperties.indexName] The name of the Azure Search
	 * Index. Type: string (or Expression with resultType string).
	 */
	export interface AzureSearchIndexDataset extends Dataset {
		typeProperties: AzureSearchIndexDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the WebTableDatasetTypeProperties class.
	 * @constructor
	 * Web table dataset properties.
	 *
	 * @member {object} index The zero-based index of the table in the web page.
	 * Type: integer (or Expression with resultType integer), minimum: 0.
	 * @member {object} [path] The relative URL to the web page from the linked
	 * service URL. Type: string (or Expression with resultType string).
	 */
	export interface WebTableDatasetTypeProperties {
		index: any;
		path?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the WebTableDataset class.
	 * @constructor
	 * The dataset points to a HTML table in the web page.
	 *
	 * @member {object} typeProperties Web table dataset properties.
	 * @member {object} [typeProperties.index] The zero-based index of the table in
	 * the web page. Type: integer (or Expression with resultType integer),
	 * minimum: 0.
	 * @member {object} [typeProperties.path] The relative URL to the web page from
	 * the linked service URL. Type: string (or Expression with resultType string).
	 */
	export interface WebTableDataset extends Dataset {
		typeProperties: WebTableDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlServerTableDatasetTypeProperties class.
	 * @constructor
	 * On-premises SQL Server dataset properties.
	 *
	 * @member {object} tableName The table name of the SQL Server dataset. Type:
	 * string (or Expression with resultType string).
	 */
	export interface SqlServerTableDatasetTypeProperties {
		tableName: any;
		sqlReaderQuery?: string; // handcrafted
		sqlReaderStoredProcedureName?: string; // handcrafted
		sqlWriterStoredProcedureName?: string; // handcrafted
		sqlReaderStoredProcedureParameters?: any; // handcrafted
		sqlWriterStoredProcedureParameters?: any; // handcrafted
		sqlWriterTableType?: string; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlServerTableDataset class.
	 * @constructor
	 * The on-premises SQL Server dataset.
	 *
	 * @member {object} typeProperties On-premises SQL Server dataset properties.
	 * @member {object} [typeProperties.tableName] The table name of the SQL Server
	 * dataset. Type: string (or Expression with resultType string).
	 */
	export interface SqlServerTableDataset extends Dataset {
		typeProperties: SqlServerTableDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SapEccResourceDatasetTypeProperties class.
	 * @constructor
	 * Sap ECC OData resource dataset properties.
	 *
	 * @member {string} path The path of the SAP ECC OData entity. Type: string (or
	 * Expression with resultType string).
	 */
	export interface SapEccResourceDatasetTypeProperties {
		path: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the SapEccResourceDataset class.
	 * @constructor
	 * The path of the SAP ECC OData entity.
	 *
	 * @member {object} typeProperties SAP ECC OData resource dataset properties.
	 * @member {string} [typeProperties.path] The path of the SAP ECC OData entity.
	 * Type: string (or Expression with resultType string).
	 */
	export interface SapEccResourceDataset extends Dataset {
		typeProperties: SapEccResourceDatasetTypeProperties;
	}

	// handcrafted
	export interface SapOpenHubTableDatasetTypeProperties {
		openHubDestinationName: string;
		excludeLastRequest?: boolean;
		baseRequestId?: number;
	}

	// handcrafted
	export interface SapOpenHubDataset extends Dataset {
		typeProperties: SapOpenHubTableDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SapCloudForCustomerResourceDatasetTypeProperties class.
	 * @constructor
	 * Sap Cloud For Customer OData resource dataset properties.
	 *
	 * @member {object} path The path of the SAP Cloud for Customer OData entity.
	 * Type: string (or Expression with resultType string).
	 */
	export interface SapCloudForCustomerResourceDatasetTypeProperties {
		path: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SapCloudForCustomerResourceDataset class.
	 * @constructor
	 * The path of the SAP Cloud for Customer OData entity.
	 *
	 * @member {object} typeProperties SAP Cloud For Customer OData resource
	 * dataset properties.
	 * @member {object} [typeProperties.path] The path of the SAP Cloud for
	 * Customer OData entity. Type: string (or Expression with resultType string).
	 */
	export interface SapCloudForCustomerResourceDataset extends Dataset {
		typeProperties: SapCloudForCustomerResourceDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceObjectDatasetTypeProperties class.
	 * @constructor
	 * Salesforce object dataset properties.
	 *
	 * @member {object} [objectApiName] The Salesforce object API name. Type:
	 * string (or Expression with resultType string).
	 */
	export interface SalesforceObjectDatasetTypeProperties {
		objectApiName?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceObjectDataset class.
	 * @constructor
	 * The Salesforce object dataset.
	 *
	 * @member {object} typeProperties Salesforce object dataset properties.
	 * @member {object} [typeProperties.objectApiName] The Salesforce object API
	 * name. Type: string (or Expression with resultType string).
	 */
	export interface SalesforceObjectDataset extends Dataset {
		typeProperties: SalesforceObjectDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceV2ObjectDatasetTypeProperties class.
	 * @constructor
	 * Salesforce object dataset properties.
	 *
	 * @member {object} [objectApiName] The Salesforce object API name. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [reportId] The Salesforce object report name ID. Type:
	 * string (or Expression with resultType string).
	 */
	export interface SalesforceV2ObjectDatasetTypeProperties extends SalesforceObjectDatasetTypeProperties {
		reportId?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceV2ObjectDataset class.
	 * @constructor
	 * The Salesforce object dataset.
	 *
	 * @member {object} typeProperties Salesforce object dataset properties.
	 * @member {object} [typeProperties.objectApiName] The Salesforce object API
	 * name. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.reportId] The Salesforce object report name ID.
	 * Type: string (or Expression with resultType string).
	 */
	export interface SalesforceV2ObjectDataset extends Dataset {
		typeProperties: SalesforceV2ObjectDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the RelationalTableDatasetTypeProperties class.
	 * @constructor
	 * Relational table dataset properties.
	 *
	 * @member {object} [tableName] The relational table name. Type: string (or
	 * Expression with resultType string).
	 */
	export interface RelationalTableDatasetTypeProperties {
		tableName?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the RelationalTableDataset class.
	 * @constructor
	 * The relational table dataset.
	 *
	 * @member {object} typeProperties Relational table dataset properties.
	 * @member {object} [typeProperties.tableName] The relational table name. Type:
	 * string (or Expression with resultType string).
	 */
	export interface RelationalTableDataset extends Dataset {
		typeProperties: RelationalTableDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMySqlTableDatasetTypeProperties class.
	 * @constructor
	 * Azure MySQL database dataset properties.
	 *
	 * @member {object} [tableName] The Azure MySQL database table name. Type:
	 * string (or Expression with resultType string).
	 */
	export interface AzureMySqlTableDatasetTypeProperties {
		tableName?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMySqlTableDataset class.
	 * @constructor
	 * The Azure MySQL database dataset.
	 *
	 * @member {object} typeProperties Azure MySQL database dataset properties.
	 * @member {object} [typeProperties.tableName] The Azure MySQL database table
	 * name. Type: string (or Expression with resultType string).
	 */
	export interface AzureMySqlTableDataset extends Dataset {
		typeProperties: AzureMySqlTableDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the OracleTableDatasetTypeProperties class.
	 * @constructor
	 * On-premises Oracle dataset properties.
	 *
	 * @member {object} tableName The table name of the on-premises Oracle
	 * database. Type: string (or Expression with resultType string).
	 */
	export interface OracleTableDatasetTypeProperties {
		tableName: any;
		oracleReaderQuery?: string; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the OracleTableDataset class.
	 * @constructor
	 * The on-premises Oracle database dataset.
	 *
	 * @member {object} typeProperties On-premises Oracle dataset properties.
	 * @member {object} [typeProperties.tableName] The table name of the
	 * on-premises Oracle database. Type: string (or Expression with resultType
	 * string).
	 */
	export interface OracleTableDataset extends Dataset {
		typeProperties: OracleTableDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ODataResourceDatasetTypeProperties class.
	 * @constructor
	 * OData dataset properties.
	 *
	 * @member {object} [path] The OData resource path. Type: string (or Expression
	 * with resultType string).
	 */
	export interface ODataResourceDatasetTypeProperties {
		path?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ODataResourceDataset class.
	 * @constructor
	 * The Open Data Protocol (OData) resource dataset.
	 *
	 * @member {object} typeProperties OData dataset properties.
	 * @member {object} [typeProperties.path] The OData resource path. Type: string
	 * (or Expression with resultType string).
	 */
	export interface ODataResourceDataset extends Dataset {
		typeProperties: ODataResourceDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the MongoDbCollectionDatasetTypeProperties class.
	 * @constructor
	 * MongoDB database dataset properties.
	 *
	 * @member {object} collectionName The table name of the MongoDB database.
	 * Type: string (or Expression with resultType string).
	 */
	export interface MongoDbCollectionDatasetTypeProperties {
		collectionName: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the MongoDbCollectionDataset class.
	 * @constructor
	 * The MongoDB database dataset.
	 *
	 * @member {object} typeProperties MongoDB database dataset properties.
	 * @member {object} [typeProperties.collectionName] The table name of the
	 * MongoDB database. Type: string (or Expression with resultType string).
	 */
	export interface MongoDbCollectionDataset extends Dataset {
		typeProperties: MongoDbCollectionDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the FileShareDatasetTypeProperties class.
	 * @constructor
	 * On-premises file system dataset properties.
	 *
	 * @member {object} [folderPath] The path of the on-premises file system. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [fileName] The name of the on-premises file system. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [format] The format of the files.
	 * @member {object} [format.serializer] Serializer. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [format.deserializer] Deserializer. Type: string (or
	 * Expression with resultType string).
	 * @member {string} [format.type] Polymorphic Discriminator
	 * @member {object} [fileFilter] Specify a filter to be used to select a subset
	 * of files in the folderPath rather than all files. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [compression] The data compression method used for the file
	 * system.
	 * @member {string} [compression.type] Polymorphic Discriminator
	 */
	export interface FileShareDatasetTypeProperties {
		folderPath?: any;
		fileName?: any;
		wildcardPath?: string; // handcrafted
		format?: DatasetStorageFormat;
		fileFilter?: any;
		compression?: DatasetCompression;
		modifiedDatetimeStart?: any // handcrafted
		modifiedDatetimeEnd?: any // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the FileShareDataset class.
	 * @constructor
	 * An on-premises file system dataset.
	 *
	 * @member {object} typeProperties On-premises file system dataset properties.
	 * @member {object} [typeProperties.folderPath] The path of the on-premises
	 * file system. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.fileName] The name of the on-premises file
	 * system. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.format] The format of the files.
	 * @member {object} [typeProperties.format.serializer] Serializer. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.format.deserializer] Deserializer. Type:
	 * string (or Expression with resultType string).
	 * @member {string} [typeProperties.format.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.fileFilter] Specify a filter to be used to
	 * select a subset of files in the folderPath rather than all files. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.compression] The data compression method
	 * used for the file system.
	 * @member {string} [typeProperties.compression.type] Polymorphic Discriminator
	 */
	export interface FileShareDataset extends Dataset {
		typeProperties: FileShareDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreDatasetTypeProperties class.
	 * @constructor
	 * Azure Data Lake Store dataset properties.
	 *
	 * @member {object} folderPath Path to the folder in the Azure Data Lake Store.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [fileName] The name of the file in the Azure Data Lake
	 * Store. Type: string (or Expression with resultType string).
	 * @member {object} [format] The format of the Data Lake Store.
	 * @member {object} [format.serializer] Serializer. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [format.deserializer] Deserializer. Type: string (or
	 * Expression with resultType string).
	 * @member {string} [format.type] Polymorphic Discriminator
	 * @member {object} [compression] The data compression method used for the
	 * item(s) in the Azure Data Lake Store.
	 * @member {string} [compression.type] Polymorphic Discriminator
	 * @member {boolean} [generatedFromActivity] Whether the dataset was generated as part
	 * of an activity action (e.g. script compilation)
	 */
	export interface AzureDataLakeStoreDatasetTypeProperties {
		folderPath: any;
		fileName?: any;
		wildcardPath?: string; // handcrafted
		format?: DatasetStorageFormat;
		compression?: DatasetCompression;
		generatedFromActivity?: boolean;
		modifiedDatetimeStart?: any // handcrafted
		modifiedDatetimeEnd?: any // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreDataset class.
	 * @constructor
	 * Azure Data Lake Store dataset.
	 *
	 * @member {object} typeProperties Azure Data Lake Store dataset properties.
	 * @member {object} [typeProperties.folderPath] Path to the folder in the Azure
	 * Data Lake Store. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.fileName] The name of the file in the Azure
	 * Data Lake Store. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.format] The format of the Data Lake Store.
	 * @member {object} [typeProperties.format.serializer] Serializer. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.format.deserializer] Deserializer. Type:
	 * string (or Expression with resultType string).
	 * @member {string} [typeProperties.format.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.compression] The data compression method
	 * used for the item(s) in the Azure Data Lake Store.
	 * @member {string} [typeProperties.compression.type] Polymorphic Discriminator
	 * @member {boolean} [typeProperties.generatedFromActivity] Whether the dataset
	 * was generated as part of an activity action (e.g. script compilation)
	 */
	export interface AzureDataLakeStoreDataset extends Dataset {
		typeProperties: AzureDataLakeStoreDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreCosmosStructuredStreamDatasetTypeProperties class.
	 * @constructor
	 * Azure Data Lake Store for Cosmos Structured Stream dataset properties.
	 *
	 * @member {object} [folderPath] Path to the folder in the Azure Data Lake Store for Cosmos Structured Stream.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [fileName] The name of the file in the Azure Data Lake Store for Cosmos Structured Stream.
	 * Store. Type: string (or Expression with resultType string).
	 * @member {boolean} [generatedFromActivity] Whether the dataset was generated as part
	 * of an activity action (e.g. script compilation)
	 */
	export interface AzureDataLakeStoreCosmosStructuredStreamDatasetTypeProperties {
		folderPath: any;
		fileName?: any;
		generatedFromActivity?: boolean;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreCosmosStructuredStreamDataset class.
	 * @constructor
	 * Azure Data Lake Store for Cosmos Structured Stream dataset.
	 *
	 * @member {object} typeProperties Azure Data Lake Store for Cosmos Structured Stream dataset properties.
	 * @member {object} [typeProperties.folderPath] Path to the folder in the Azure
	 * Data Lake Store for Cosmos Structured Stream. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.fileName] The name of the file in the Azure
	 * Data Lake Store for Cosmos Structured Stream. Type: string (or Expression with resultType string).
	 * @member {boolean} [typeProperties.generatedFromActivity] Whether the dataset
	 * was generated as part of an activity action (e.g. script compilation)
	 */
	export interface AzureDataLakeStoreCosmosStructuredStreamDataset extends Dataset {
		typeProperties: AzureDataLakeStoreCosmosStructuredStreamDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreGen2CosmosStructuredStreamDatasetTypeProperties class.
	 * @constructor
	 * Azure Data Lake Store Gen2 for Cosmos Structured Stream dataset properties.
	 *
	 * @member {object} [fileSystem] FileSystem name in the Azure Data Lake Store Gen2 for Cosmos Structured Stream.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [folderPath] Path to the folder in the Azure Data Lake Store Gen2 for Cosmos Structured Stream.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [fileName] The name of the file in the Azure Data Lake Store Gen2 for Cosmos Structured Stream.
	 * Store. Type: string (or Expression with resultType string).
		   * @member {boolean} [generatedFromActivity] Whether the dataset was generated as part
	 * of an activity action (e.g. script compilation)
	 */
	export interface AzureDataLakeStoreGen2CosmosStructuredStreamDatasetTypeProperties {
		fileSystem?: any;
		folderPath: any;
		fileName?: any;
		generatedFromActivity?: boolean;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreGen2CosmosStructuredStreamDataset class.
	 * @constructor
	 * Azure Data Lake Store Gen2 for Cosmos Structured Stream dataset.
	 *
	 * @member {object} typeProperties Azure Data Lake Store Gen2 for Cosmos Structured Stream dataset properties.
	 * @member {object} [typeProperties.folderPath] Path to the folder in the Azure
	 * Data Lake Store Gen2 for Cosmos Structured Stream. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.fileName] The name of the file in the Azure
	 * Data Lake Store Gen2 for Cosmos Structured Stream. Type: string (or Expression with resultType string).
		 * @member {boolean} [typeProperties.generatedFromActivity] Whether the dataset
	 * was generated as part of an activity action (e.g. script compilation)
	 */
	export interface AzureDataLakeStoreGen2CosmosStructuredStreamDataset extends Dataset {
		typeProperties: AzureDataLakeStoreGen2CosmosStructuredStreamDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the DynamicsEntityDatasetTypeProperties class.
	 * @constructor
	 * Dynamics entity dataset properties.
	 *
	 * @member {object} [entityName] The logical name of the entity. Type: string
	 * (or Expression with resultType string).
	 */
	export interface DynamicsEntityDatasetTypeProperties {
		entityName?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DynamicsEntityDataset class.
	 * @constructor
	 * The Dynamics entity dataset.
	 *
	 * @member {object} typeProperties Dynamics entity dataset properties.
	 * @member {object} [typeProperties.entityName] The logical name of the entity.
	 * Type: string (or Expression with resultType string).
	 */
	export interface DynamicsEntityDataset extends Dataset {
		typeProperties: DynamicsEntityDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the DocumentDbCollectionDatasetTypeProperties class.
	 * @constructor
	 * DocumentDB Collection dataset properties.
	 *
	 * @member {object} collectionName Document Database collection name. Type:
	 * string (or Expression with resultType string).
	 * @member {object} database Document Database database name. Cosmos DB NoSQL DMTS connection doesn't have database
	 * information. Type: string (or Expression with resultType string).
	 */
	export interface DocumentDbCollectionDatasetTypeProperties {
		collectionName: any;
		database?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DocumentDbCollectionDataset class.
	 * @constructor
	 * Microsoft Azure Document Database Collection dataset.
	 *
	 * @member {object} typeProperties DocumentDB Collection dataset properties.
	 * @member {object} [typeProperties.collectionName] Document Database
	 * collection name. Type: string (or Expression with resultType string).
	 */
	export interface DocumentDbCollectionDataset extends Dataset {
		typeProperties: DocumentDbCollectionDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the CustomDataset class.
	 * @constructor
	 * The custom dataset.
	 *
	 * @member {object} typeProperties Custom dataset properties.
	 */
	export interface CustomDataset extends Dataset {
		typeProperties: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the CassandraTableDatasetTypeProperties class.
	 * @constructor
	 * Cassandra dataset properties.
	 *
	 * @member {object} [tableName] The table name of the Cassandra database. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [keyspace] The keyspace of the Cassandra database. Type:
	 * string (or Expression with resultType string).
	 */
	export interface CassandraTableDatasetTypeProperties {
		tableName?: any;
		keyspace?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the CassandraTableDataset class.
	 * @constructor
	 * The Cassandra database dataset.
	 *
	 * @member {object} typeProperties Cassandra dataset properties.
	 * @member {object} [typeProperties.tableName] The table name of the Cassandra
	 * database. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.keyspace] The keyspace of the Cassandra
	 * database. Type: string (or Expression with resultType string).
	 */
	export interface CassandraTableDataset extends Dataset {
		typeProperties: CassandraTableDatasetTypeProperties;
	}

	// handcrafted
	export interface IcebergDatasetTypeProperties {
		location?: AzureBlobLocation | ADLSGen2Location | ADLSGen1Location;
		columnDelimiter?: any;
		rowDelimiter?: any;
		compressionCodec?: any;
		compressionLevel?: any;
		quoteChar?: any;
		escapeChar?: any;
		encodingName?: any;
		firstRowAsHeader?: any;
		nullValue?: any;
	}

	// handcrafted
	export interface IcebergDataset extends DatasetV2 {
		typeProperties: IcebergDatasetTypeProperties;
	}


	/**
	 * @class
	 * Initializes a new instance of the AzureSqlDWTableDatasetTypeProperties class.
	 * @constructor
	 * Azure SQL Data Warehouse dataset properties.
	 *
	 * @member {object} tableName The table name of the Azure SQL Data Warehouse.
	 * Type: string (or Expression with resultType string).
	 */
	export interface AzureSqlDWTableDatasetTypeProperties {
		tableName: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSqlDWTableDataset class.
	 * @constructor
	 * The Azure SQL Data Warehouse dataset.
	 *
	 * @member {object} typeProperties Azure SQL Data Warehouse dataset properties.
	 * @member {object} [typeProperties.tableName] The table name of the Azure SQL
	 * Data Warehouse. Type: string (or Expression with resultType string).
	 */
	export interface AzureSqlDWTableDataset extends Dataset {
		typeProperties: AzureSqlDWTableDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSqlTableDatasetTypeProperties class.
	 * @constructor
	 * Azure SQL dataset properties.
	 *
	 * @member {object} tableName The table name of the Azure SQL database. Type:
	 * string (or Expression with resultType string).
	 */
	export interface AzureSqlTableDatasetTypeProperties {
		tableName: any;
		sqlReaderQuery?: string; // handcrafted
		sqlReaderStoredProcedureName?: string; // handcrafted
		sqlWriterStoredProcedureName?: string; // handcrafted
		sqlReaderStoredProcedureParameters?: any; // handcrafted
		sqlWriterStoredProcedureParameters?: any; // handcrafted
		sqlWriterTableType?: string; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSqlTableDataset class.
	 * @constructor
	 * The Azure SQL Server database dataset.
	 *
	 * @member {object} typeProperties Azure SQL dataset properties.
	 * @member {object} [typeProperties.tableName] The table name of the Azure SQL
	 * database. Type: string (or Expression with resultType string).
	 */
	export interface AzureSqlTableDataset extends Dataset {
		typeProperties: AzureSqlTableDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureTableDatasetTypeProperties class.
	 * @constructor
	 * Azure Table dataset properties.
	 *
	 * @member {object} tableName The table name of the Azure Table storage. Type:
	 * string (or Expression with resultType string).
	 */
	export interface AzureTableDatasetTypeProperties {
		tableName: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureTableDataset class.
	 * @constructor
	 * The Azure Table storage dataset.
	 *
	 * @member {object} typeProperties Azure Table dataset properties.
	 * @member {object} [typeProperties.tableName] The table name of the Azure
	 * Table storage. Type: string (or Expression with resultType string).
	 */
	export interface AzureTableDataset extends Dataset {
		typeProperties: AzureTableDatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureBlobDatasetTypeProperties class.
	 * @constructor
	 * Azure Blob dataset properties.
	 *
	 * @member {object} [folderPath] The path of the Azure Blob storage. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [tableRootLocation] The root of blob path. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [fileName] The name of the Azure Blob. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [format] The format of the Azure Blob storage.
	 * @member {object} [format.serializer] Serializer. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [format.deserializer] Deserializer. Type: string (or
	 * Expression with resultType string).
	 * @member {string} [format.type] Polymorphic Discriminator
	 * @member {object} [compression] The data compression method used for the blob
	 * storage.
	 * @member {string} [compression.type] Polymorphic Discriminator
	 */
	export interface AzureBlobDatasetTypeProperties {
		folderPath?: any;
		tableRootLocation?: any;
		wildcardPath?: string; // handcrafted
		fileName?: any;
		format?: DatasetStorageFormat;
		compression?: DatasetCompression;
		modifiedDatetimeStart?: any // handcrafted
		modifiedDatetimeEnd?: any // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureBlobDataset class.
	 * @constructor
	 * The Azure Blob storage.
	 *
	 * @member {object} typeProperties Azure Blob dataset properties.
	 * @member {object} [typeProperties.folderPath] The path of the Azure Blob
	 * storage. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.tableRootLocation] The root of blob path.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.fileName] The name of the Azure Blob. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.format] The format of the Azure Blob
	 * storage.
	 * @member {object} [typeProperties.format.serializer] Serializer. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.format.deserializer] Deserializer. Type:
	 * string (or Expression with resultType string).
	 * @member {string} [typeProperties.format.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.compression] The data compression method
	 * used for the blob storage.
	 * @member {string} [typeProperties.compression.type] Polymorphic Discriminator
	 */
	export interface AzureBlobDataset extends Dataset {
		typeProperties: AzureBlobDatasetTypeProperties;
	}

	// handcrafted
	export interface AzureBlobFSDataset extends Dataset {
		typeProperties: AzureBlobDatasetTypeProperties;
	}

	// handcrafted
	export interface LakehouseDataset extends Dataset {
		typeProperties: AzureBlobDatasetTypeProperties;
	}

	export interface LakehouseTableDataset extends Dataset {
		typeProperties: {
			table: string;
		};
	}

	/**
	 * @class
	 * Initializes a new instance of the AmazonS3DatasetTypeProperties class.
	 * @constructor
	 * Amazon S3 dataset properties.
	 *
	 * @member {object} bucketName The name of the Amazon S3 bucket. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [key] The key of the Amazon S3 object. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [prefix] The prefix filter for the S3 object name. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [version] The version for the S3 object. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [format] The format of files.
	 * @member {object} [format.serializer] Serializer. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [format.deserializer] Deserializer. Type: string (or
	 * Expression with resultType string).
	 * @member {string} [format.type] Polymorphic Discriminator
	 * @member {object} [compression] The data compression method used for the
	 * Amazon S3 object.
	 * @member {string} [compression.type] Polymorphic Discriminator
	 */
	export interface AmazonS3DatasetTypeProperties {
		bucketName: any;
		key?: any;
		prefix?: any;
		version?: any;
		format?: DatasetStorageFormat;
		wildcardPath?: string; // handcrafted
		compression?: DatasetCompression;
		modifiedDatetimeStart?: any // handcrafted
		modifiedDatetimeEnd?: any // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the AmazonS3Dataset class.
	 * @constructor
	 * A single Amazon Simple Storage Service (S3) object or a set of S3 objects.
	 *
	 * @member {object} typeProperties Amazon S3 dataset properties.
	 * @member {object} [typeProperties.bucketName] The name of the Amazon S3
	 * bucket. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.key] The key of the Amazon S3 object. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.prefix] The prefix filter for the S3 object
	 * name. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.version] The version for the S3 object.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.format] The format of files.
	 * @member {object} [typeProperties.format.serializer] Serializer. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [typeProperties.format.deserializer] Deserializer. Type:
	 * string (or Expression with resultType string).
	 * @member {string} [typeProperties.format.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.compression] The data compression method
	 * used for the Amazon S3 object.
	 * @member {string} [typeProperties.compression.type] Polymorphic Discriminator
	 */
	export interface AmazonS3Dataset extends Dataset {
		typeProperties: AmazonS3DatasetTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the RetryPolicy class.
	 * @constructor
	 * Execution policy for an activity.
	 *
	 * @member {object} [count] Maximum ordinary retry attempts. Default is 0.
	 * Type: integer (or Expression with resultType integer), minimum: 0.
	 * @member {number} [intervalInSeconds] Interval between retries in seconds.
	 * Default is 30.
	 */
	export interface RetryPolicy {
		count?: any;
		intervalInSeconds?: number;
	}

	/**
	 * @class
	 * Initializes a new instance of the TumblingWindowTriggerTypeProperties class.
	 * @constructor
	 * Tumbling Window Trigger properties.
	 *
	 * @member {string} frequency The frequency of the time windows. Possible
	 * values include: 'Minute', 'Hour'
	 * @member {number} interval The interval of the time windows. The minimum
	 * interval allowed is 5 Minutes.
	 * @member {date} startTime The start time for the time period for the trigger
	 * during which events are fired for windows that are ready. Only UTC time is
	 * currently supported.
	 * @member {date} [endTime] The end time for the time period for the trigger
	 * during which events are fired for windows that are ready. Only UTC time is
	 * currently supported.
	 * @member {object} [delay] Specifies how long the trigger waits past due time
	 * before triggering new run. It doesn't alter window start and end time. The
	 * default is 0. Type: string (or Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {number} maxConcurrency The max number of parallel time windows
	 * (ready for execution) for which a new run is triggered.
	 * @member {object} [retryPolicy] Retry policy that will be applied for failed
	 * pipeline runs.
	 * @member {object} [retryPolicy.count] Maximum ordinary retry attempts.
	 * Default is 0. Type: integer (or Expression with resultType integer),
	 * minimum: 0.
	 * @member {number} [retryPolicy.intervalInSeconds] Interval between retries in
	 * seconds. Default is 30.
	 */
	export interface TumblingWindowTriggerTypeProperties {
		frequency: string;
		interval: number;
		startTime: string;
		endTime?: string;
		delay?: any;
		maxConcurrency: number;
		retryPolicy?: RetryPolicy;
		dependsOn?: TriggerDependency[]; // handcrafted
	}

	export interface RerunTumblingWindowTrigger extends Trigger {
		typeProperties: RerunTumblingWindowTriggerTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the RerunTumblingWindowTriggerTypeProperties
	 * class.
	 * @constructor
	 * Rerun Tumbling Window Trigger properties.
	 *
	 * @member {object} ParentTrigger Parent trigger
	 * @member {string} ParentTrigger.type trigger type of the parent trigger
	 * @member {string} ParentTrigger.referenceName name of the parent trigger
	 * @member {date} startTime The start time for the time period for the trigger
	 * during which events are fired for windows that are ready. Only UTC time is
	 * currently supported.
	 * @member {date} endTime The end time for the time period for the trigger
	 * during which events are fired for windows that are ready. Only UTC time is
	 * currently supported.
	 * @member {number} maxConcurrency The max number of parallel time windows
	 * (ready for execution) for which a new run is triggered.
	 */
	export interface RerunTumblingWindowTriggerTypeProperties {
		parentTrigger: TriggerReference;
		requestedStartTime: Date;
		requestedEndTime: Date;
		rerunConcurrency: number;
	}

	/**
	 * @class
	 * Initializes a new instance of the TriggerQueryFilter class.
	 * @constructor
	 * Query filter option for listing triggers.
	 *
	 * @member {string} triggerType type of the trigger to query metadata
	 * @member {string} parentTriggerName attribute parent trigger name TODO: consider making this generic and property name can be ke a key value pair
	 */
	export interface TriggerQueryFilter {
		triggerType: string;
		parentTriggerName: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the TumblingWindowTrigger class.
	 * @constructor
	 * Trigger that schedules pipeline runs for all fixed time interval windows
	 * from a start time without gaps and also supports backfill scenarios (when
	 * start time is in the past).
	 *
	 * @member {object} pipelineProperty Pipeline for which runs are created when
	 * an event is fired for trigger window that is ready.
	 * @member {object} [pipelineProperty.pipelineReference] Pipeline reference.
	 * @member {string} [pipelineProperty.pipelineReference.referenceName]
	 * Reference pipeline name.
	 * @member {string} [pipelineProperty.pipelineReference.name] Reference name.
	 * @member {object} [pipelineProperty.parameters] Pipeline parameters.
	 * @member {object} typeProperties Tumbling Window Trigger properties.
	 * @member {string} [typeProperties.frequency] The frequency of the time
	 * windows. Possible values include: 'Minute', 'Hour'
	 * @member {number} [typeProperties.interval] The interval of the time windows.
	 * The minimum interval allowed is 5 Minutes.
	 * @member {date} [typeProperties.startTime] The start time for the time period
	 * for the trigger during which events are fired for windows that are ready.
	 * Only UTC time is currently supported.
	 * @member {date} [typeProperties.endTime] The end time for the time period for
	 * the trigger during which events are fired for windows that are ready. Only
	 * UTC time is currently supported.
	 * @member {object} [typeProperties.delay] Specifies how long the trigger waits
	 * past due time before triggering new run. It doesn't alter window start and
	 * end time. The default is 0. Type: string (or Expression with resultType
	 * string), pattern: ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {number} [typeProperties.maxConcurrency] The max number of parallel
	 * time windows (ready for execution) for which a new run is triggered.
	 * @member {object} [typeProperties.retryPolicy] Retry policy that will be
	 * applied for failed pipeline runs.
	 * @member {object} [typeProperties.retryPolicy.count] Maximum ordinary retry
	 * attempts. Default is 0. Type: integer (or Expression with resultType
	 * integer), minimum: 0.
	 * @member {number} [typeProperties.retryPolicy.intervalInSeconds] Interval
	 * between retries in seconds. Default is 30.
	 */
	export interface TumblingWindowTrigger extends Trigger {
		pipeline: TriggerPipelineReference; //handcrafted
		typeProperties: TumblingWindowTriggerTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the EventsTriggerTypeProperties class.
	 * @constructor
	 * Events Trigger properties.
	 *
	 * @member {string} subjectBeginsWith The subject path must begin with the
	 * pattern provided for trigger to fire.
	 * @member {string} subjectEndsWith The subject path must end with the pattern
	 * provided for trigger to fire.
	 * @member {array} events The type of events that cause this trigger to fire.
	 * @member {string} scope The ARM resource ID of where the event will be emitted from.
	 */
	export interface CustomEventsTriggerTypeProperties {
		subjectBeginsWith?: string;
		subjectEndsWith?: string;
		events: string[];
		scope: string;
		tenantId: string;
		advancedFilters?: IAdvancedFilter[];
	}

	/**
	 * @class
	 * Initializes a new instance of the BlobEventsTriggerTypeProperties class.
	 * @constructor
	 * Blob Events Trigger properties.
	 *
	 * @member {string} [blobPathBeginsWith] The blob path must begin with the
	 * pattern provided for trigger to fire. For example,
	 * '/records/blobs/december/' will only fire the trigger for blobs in the
	 * december folder under the records container. At least one of these must be
	 * provided: blobPathBeginsWith, blobPathEndsWith.
	 * @member {string} [blobPathEndsWith] The blob path must end with the pattern
	 * provided for trigger to fire. For example, 'december/boxes.csv' will only
	 * fire the trigger for blobs named boxes in a december folder. At least one of
	 * these must be provided: blobPathBeginsWith, blobPathEndsWith.
	 * @member {array} events The type of events that cause this trigger to fire.
	 * @member {string} scope The ARM resource ID of the Storage Account.
	 */
	export interface BlobEventsTriggerTypeProperties {
		blobPathBeginsWith?: string;
		blobPathEndsWith?: string;
		ignoreEmptyBlobs?: boolean;
		events: string[];
		scope: string;
		dmtsConnectionId?: string; // for fabric
		storageAccountName?: string; // for fabric
		tenantId: string;
		maxConcurrency?: number; //handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the MultiplePipelineTrigger class.
	 * @constructor
	 * Base class for all triggers that support one to many model for trigger to
	 * pipeline.
	 *
	 * @member {array} [pipelines] Pipelines that need to be started.
	 */
	export interface MultiplePipelineTrigger extends Trigger {
		pipelines?: TriggerPipelineReference[];
	}

	/**
	 * @class
	 * Initializes a new instance of the CustomEventsTrigger class.
	 * @constructor
	 * Trigger that runs everytime a custom event occurs.
	 *
	 * @member {object} typeProperties Custom Events Trigger properties.
	 * @member {string} [typeProperties.subjectBeginsWith] The subject path must begin with the
	 * pattern provided for trigger to fire.
	 * @member {string} [typeProperties.subjectEndsWith] The subject path must end with the pattern
	 * provided for trigger to fire.
	 * @member {array} [typeProperties.events] The type of events that cause this trigger to fire.
	 * @member {string} [typeProperties.scope] The ARM resource ID of where the event will be emitted from.
	 * Account.
	 */
	export interface CustomEventsTrigger extends MultiplePipelineTrigger {
		typeProperties: CustomEventsTriggerTypeProperties;
	}
	/**
	 * @class
	 * Initializes a new instance of the BlobEventsTrigger class.
	 * @constructor
	 * Trigger that runs everytime a Blob event occurs.
	 *
	 * @member {object} typeProperties Blob Events Trigger properties.
	 * @member {string} [typeProperties.blobPathBeginsWith] The blob path must
	 * begin with the pattern provided for trigger to fire. For example,
	 * '/records/blobs/december/' will only fire the trigger for blobs in the
	 * december folder under the records container. At least one of these must be
	 * provided: blobPathBeginsWith, blobPathEndsWith.
	 * @member {string} [typeProperties.blobPathEndsWith] The blob path must end
	 * with the pattern provided for trigger to fire. For example,
	 * 'december/boxes.csv' will only fire the trigger for blobs named boxes in a
	 * december folder. At least one of these must be provided: blobPathBeginsWith,
	 * blobPathEndsWith.
	 * @member {array} [typeProperties.events] The type of events that cause this
	 * trigger to fire.
	 * @member {string} [typeProperties.scope] The ARM resource ID of the Storage
	 * Account.
	 */
	export interface BlobEventsTrigger extends MultiplePipelineTrigger {
		typeProperties: BlobEventsTriggerTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the BlobTriggerTypeProperties class.
	 * @constructor
	 * Blob Trigger properties.
	 *
	 * @member {string} folderPath The path of the container/folder that will
	 * trigger the pipeline.
	 * @member {number} maxConcurrency The max number of parallel files to handle
	 * when it is triggered.
	 * @member {object} linkedService The Azure Storage linked service reference.
	 * @member {string} [linkedService.referenceName] Reference LinkedService name.
	 * @member {object} [linkedService.parameters] Arguments for LinkedService.
	 */
	export interface BlobTriggerTypeProperties {
		folderPath: string;
		maxConcurrency: number;
		linkedService: SharedModel.LinkedServiceReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the BlobTrigger class.
	 * @constructor
	 * Trigger that runs everytime the selected Blob container changes.
	 *
	 * @member {object} typeProperties Blob Trigger properties.
	 * @member {string} [typeProperties.folderPath] The path of the
	 * container/folder that will trigger the pipeline.
	 * @member {number} [typeProperties.maxConcurrency] The max number of parallel
	 * files to handle when it is triggered.
	 * @member {object} [typeProperties.linkedService] The Azure Storage linked
	 * service reference.
	 * @member {string} [typeProperties.linkedService.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [typeProperties.linkedService.parameters] Arguments for
	 * LinkedService.
	 */
	export interface BlobTrigger extends MultiplePipelineTrigger {
		typeProperties: BlobTriggerTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the RecurrenceScheduleOccurrence class.
	 * @constructor
	 * The recurrence schedule occurence.
	 *
	 * @member {string} [day] The day of the week. Possible values include:
	 * 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
	 * @member {number} [occurrence] The occurrence.
	 */
	export interface RecurrenceScheduleOccurrence {
		day?: string;
		occurrence?: number;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the RecurrenceSchedule class.
	 * @constructor
	 * The recurrence schedule.
	 *
	 * @member {array} [minutes] The minutes.
	 * @member {array} [hours] The hours.
	 * @member {array} [weekDays] The days of the week.
	 * @member {array} [monthDays] The month days.
	 * @member {array} [monthlyOccurrences] The monthly occurrences.
	 */
	export interface RecurrenceSchedule {
		minutes?: number[];
		hours?: number[];
		weekDays?: string[];
		monthDays?: number[];
		monthlyOccurrences?: RecurrenceScheduleOccurrence[];
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ScheduleTriggerRecurrence class.
	 * @constructor
	 * The workflow trigger recurrence.
	 *
	 * @member {string} [frequency] The frequency. Possible values include:
	 * 'NotSpecified', 'Minute', 'Hour', 'Day', 'Week', 'Month', 'Year'
	 * @member {number} [interval] The interval.
	 * @member {string} [startTime] The start time.
	 * @member {string} [endTime] The end time.
	 * @member {string} [timeZone] The time zone.
	 * @member {object} [schedule] The recurrence schedule.
	 * @member {array} [schedule.minutes] The minutes.
	 * @member {array} [schedule.hours] The hours.
	 * @member {array} [schedule.weekDays] The days of the week.
	 * @member {array} [schedule.monthDays] The month days.
	 * @member {array} [schedule.monthlyOccurrences] The monthly occurrences.
	 */
	export interface ScheduleTriggerRecurrence {
		frequency?: string;
		interval?: number;
		startTime?: string;
		endTime?: string;
		timeZone?: string;
		schedule?: RecurrenceSchedule;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ScheduleTriggerTypeProperties class.
	 * @constructor
	 * Schedule Trigger properties.
	 *
	 * @member {object} recurrence Recurrence schedule configuration.
	 * @member {string} [recurrence.frequency] The frequency. Possible values
	 * include: 'NotSpecified', 'Minute', 'Hour', 'Day', 'Week', 'Month', 'Year'
	 * @member {number} [recurrence.interval] The interval.
	 * @member {date} [recurrence.startTime] The start time.
	 * @member {date} [recurrence.endTime] The end time.
	 * @member {string} [recurrence.timeZone] The time zone.
	 * @member {object} [recurrence.schedule] The recurrence schedule.
	 * @member {array} [recurrence.schedule.minutes] The minutes.
	 * @member {array} [recurrence.schedule.hours] The hours.
	 * @member {array} [recurrence.schedule.weekDays] The days of the week.
	 * @member {array} [recurrence.schedule.monthDays] The month days.
	 * @member {array} [recurrence.schedule.monthlyOccurrences] The monthly
	 * occurrences.
	 */
	export interface ScheduleTriggerTypeProperties {
		recurrence?: ScheduleTriggerRecurrence; //handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the ScheduleTrigger class.
	 * @constructor
	 * Trigger that creates pipeline runs periodically, on schedule.
	 *
	 * @member {object} typeProperties Schedule Trigger properties.
	 * @member {object} [typeProperties.recurrence] Recurrence schedule
	 * configuration.
	 * @member {string} [typeProperties.recurrence.frequency] The frequency.
	 * Possible values include: 'NotSpecified', 'Minute', 'Hour', 'Day', 'Week',
	 * 'Month', 'Year'
	 * @member {number} [typeProperties.recurrence.interval] The interval.
	 * @member {date} [typeProperties.recurrence.startTime] The start time.
	 * @member {date} [typeProperties.recurrence.endTime] The end time.
	 * @member {string} [typeProperties.recurrence.timeZone] The time zone.
	 * @member {object} [typeProperties.recurrence.schedule] The recurrence
	 * schedule.
	 * @member {array} [typeProperties.recurrence.schedule.minutes] The minutes.
	 * @member {array} [typeProperties.recurrence.schedule.hours] The hours.
	 * @member {array} [typeProperties.recurrence.schedule.weekDays] The days of
	 * the week.
	 * @member {array} [typeProperties.recurrence.schedule.monthDays] The month
	 * days.
	 * @member {array} [typeProperties.recurrence.schedule.monthlyOccurrences] The
	 * monthly occurrences.
	 */
	export interface ScheduleTrigger extends MultiplePipelineTrigger {
		typeProperties: ScheduleTriggerTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ChainingTriggerTypeProperties class.
	 * @constructor
	 * Chaining Trigger properties.
	 *
	 * @member {array} dependsOn Array of pipeline reference upon which the trigger is dependent
	 * @member {string} runDimension Run dimension to trigger the pipeline upon
	 */
	export interface ChainingTriggerTypeProperties {
		dependsOn?: PipelineReference[]; //handcrafted
		runDimension: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the ChainingTrigger class.
	 * @constructor
	 * Trigger that creates pipeline after child pipelines are finished.
	 *
	 * @member {object} typeProperties Chaining Trigger properties.
	 * @member {array} [typeProperties.dependsOn] Array of pipeline reference upon which the trigger is dependent
	 * @member {string} [typeProperties.runDimension] Run dimension to trigger the pipeline upon
	 */
	export interface ChainingTrigger extends MultiplePipelineTrigger {
		typeProperties: ChainingTriggerTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ActivityPolicy class.
	 * @constructor
	 * Execution policy for an activity.
	 *
	 * @member {object} [timeout] Specifies the timeout for the activity to run.
	 * The default timeout is 7 days. Type: string (or Expression with resultType
	 * string), pattern: ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {object} [retry] Maximum ordinary retry attempts. Default is 0.
	 * Type: integer (or Expression with resultType integer), minimum: 0.
	 * @member {number} [retryIntervalInSeconds] Interval between each retry
	 * attempt (in seconds). The default is 30 sec.
	 * @member {boolean} [secureOutput] When set to true, Output from activity is
	 * considered as secure and will not be logged to monitoring.
	 */
	export interface ActivityPolicy {
		timeout?: any;
		retry?: any;
		retryIntervalInSeconds?: number;
		maxRetryIntervalInSeconds?: number;
		exponentialRetry?: boolean;
		secureOutput?: boolean;
		secureInput?: boolean;
		retryConditions?: { type?: 'Expression'; value?: string };
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	export interface ActivityColumnFlattenSettings {
		treatArrayAsString: boolean;
		treatStructAsString: boolean;
		flattenColumnDelimiter?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the Pipeline Policy class.
	 * @constructor
	 * Execution policies for a pipeline.
	 *
	 * @member {object} [elapsedTimeMetric] an elapsedTimeMetric object.
	 */

	export interface PipelinePolicy {
		elapsedTimeMetric?: ElapsedTimeMetric;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the Elapsed Time Metric class.
	 * @constructor
	 * Properties for the elapsed time metric.
	 *
	 * @member {object} [duration] a string conforming to a timespan object that represents a duration.
	 * Must be over 10 minutes in length. pattern: ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 */

	export interface ElapsedTimeMetric {
		duration?: string;

		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DatabricksNotebookActivityTypeProperties class.
	 * @constructor
	 * Databricks Notebook activity properties.
	 *
	 * @member {object} notebookPath The absolute path of the notebook to be run in
	 * the Databricks Workspace. This path must begin with a slash. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [baseParameters] Base parameters to be used for each run of
	 * this job.If the notebook takes a parameter that is not specified, the
	 * default value from the notebook will be used.
	 */

	export interface DatabricksNotebookActivityTypeProperties {
		notebookPath?: any;
		baseParameters?: { [propertyName: string]: any };
		libraries?: any; // handcrafted
		pythonFile?: string;
		parameters?: string[];
		mainClassName?: string;
		jobId?: number;
		jobParameters?: { [propertyName: string]: any };
		// fabric cluster properties
		existingClusterId?: any;
		instancePoolId?: any;
		newClusterVersion?: any;
		newClusterNumOfWorker?: any;
		newClusterNodeType?: any;
		newClusterSparkConf?: { [propertyName: string]: any };
		newClusterSparkEnvVars?: { [propertyName: string]: any };
		newClusterCustomTags?: { [propertyName: string]: any }; // handcrafted
		newClusterLogDestinationSettings?: IDatabricksClusterLogDestinationSettings; // handcrafted
		newClusterLogDestination?: string; // handcrafted
		newClusterInitScripts?: any[];
		newClusterDriverNodeType?: any;
		newClusterAttributes?: INewClusterAttributes; //handcrafted
		policyId?: string //handcrafted;
		clusterOption?: string; //handcrafted
		dataSecurityMode?: string; //handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the ExecutionActivity class.
	 * @constructor
	 * Base class for all execution activities.
	 *
	 * @member {object} [linkedServiceName] Linked service reference.
	 * @member {string} [linkedServiceName.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [linkedServiceName.parameters] Arguments for LinkedService.
	 * @member {object} [linkedService] In Fabric, we refer the artifact (such Lakehouse, Data Warehouse) directly
	 * @member {object} [connectionSettings] The same as linkedService
	 * @member {object} [policy] Activity policy.
	 * @member {object} [policy.timeout] Specifies the timeout for the activity to
	 * run. The default timeout is 7 days. Type: string (or Expression with
	 * resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {object} [policy.retry] Maximum ordinary retry attempts. Default is
	 * 0. Type: integer (or Expression with resultType integer), minimum: 0.
	 * @member {number} [policy.retryIntervalInSeconds] Interval between each retry
	 * attempt (in seconds). The default is 30 sec.
	 * @member {boolean} [policy.secureOutput] When set to true, Output from
	 * activity is considered as secure and will not be logged to monitoring.
	 */
	export interface ExecutionActivity extends Activity {
		linkedServiceName?: SharedModel.LinkedServiceReference;
		linkedService?: SharedModel.LinkedServiceResource;
		connectionSettings?: SharedModel.LinkedServiceResource;
		policy?: ActivityPolicy;
	}

	/**
	 * @class
	 * Initializes a new instance of the DatabricksNotebookActivity class.
	 * @constructor
	 * DatabricksNotebook activity.
	 *
	 * @member {object} typeProperties Databricks Notebook activity properties.
	 * @member {object} [typeProperties.notebookPath] The absolute path of the
	 * notebook to be run in the Databricks Workspace. This path must begin with a
	 * slash. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.baseParameters] Base parameters to be used
	 * for each run of this job.If the notebook takes a parameter that is not
	 * specified, the default value from the notebook will be used.
	 */
	export interface DatabricksNotebookActivity extends ExecutionActivity {
		typeProperties: DatabricksNotebookActivityTypeProperties;
	}

	// handcrafted jar activity
	export interface DatabricksJarActivity extends ExecutionActivity {
		typeProperties: DatabricksJarActivityTypeProperties;
	}

	export interface DatabricksJarActivityTypeProperties {
		mainClassName: string;
		parameters?: string[];
		libraries?: any;
	}

	// handcrafted python activity
	export interface DatabricksPythonActivity extends ExecutionActivity {
		typeProperties: DatabricksPythonActivityTypeProperties;
	}

	export interface DatabricksPythonActivityTypeProperties {
		pythonFile: string;
		parameters?: string[];
		libraries?: any;
	}

	export interface DatabricksJobyActivity extends ExecutionActivity {
		typeProperties: DatabricksJobyActivityTypeProperties;
	}

	export interface DatabricksJobyActivityTypeProperties {
		jobId: string;
		parameters?: string[];
		jobParameters?: { [propertyName: string]: any };
		libraries?: any;
	}


	/**
	 * @class
	 * Initializes a new instance of the DataLakeAnalyticsUSQLActivityTypeProperties class.
	 * @constructor
	 * DataLakeAnalyticsU-SQL activity properties.
	 *
	 * @member {object} scriptPath Case-sensitive path to folder that contains the
	 * U-SQL script. Type: string (or Expression with resultType string).
	 * @member {object} scriptLinkedService Script linked service reference.
	 * @member {string} [scriptLinkedService.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [scriptLinkedService.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [degreeOfParallelism] The maximum number of nodes
	 * simultaneously used to run the job. Default value is 1. Type: integer (or
	 * Expression with resultType integer), minimum: 1.
	 * @member {object} [priority] Determines which jobs out of all that are queued
	 * should be selected to run first. The lower the number, the higher the
	 * priority. Default value is 1000. Type: integer (or Expression with
	 * resultType integer), minimum: 1.
	 * @member {object} [parameters] Parameters for U-SQL job request.
	 * @member {object} [runtimeVersion] Runtime version of the U-SQL engine to
	 * use. Type: string (or Expression with resultType string).
	 * @member {object} [compilationMode] Compilation mode of U-SQL. Must be one of
	 * these values : Semantic, Full and SingleBox. Type: string (or Expression
	 * with resultType string).
	 */
	export interface DataLakeAnalyticsUSQLActivityTypeProperties {
		scriptPath: any;
		scriptLinkedService?: SharedModel.LinkedServiceReference; // handcrafted
		degreeOfParallelism?: any;
		priority?: any;
		parameters?: { [propertyName: string]: any };
		runtimeVersion?: any;
		compilationMode?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DataLakeAnalyticsUSQLActivity class.
	 * @constructor
	 * Data Lake Analytics U-SQL activity.
	 *
	 * @member {object} typeProperties Data Lake Analytics U-SQL activity
	 * properties.
	 * @member {object} [typeProperties.scriptPath] Case-sensitive path to folder
	 * that contains the U-SQL script. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [typeProperties.scriptLinkedService] Script linked service
	 * reference.
	 * @member {string} [typeProperties.scriptLinkedService.referenceName]
	 * Reference LinkedService name.
	 * @member {object} [typeProperties.scriptLinkedService.parameters] Arguments
	 * for LinkedService.
	 * @member {object} [typeProperties.degreeOfParallelism] The maximum number of
	 * nodes simultaneously used to run the job. Default value is 1. Type: integer
	 * (or Expression with resultType integer), minimum: 1.
	 * @member {object} [typeProperties.priority] Determines which jobs out of all
	 * that are queued should be selected to run first. The lower the number, the
	 * higher the priority. Default value is 1000. Type: integer (or Expression
	 * with resultType integer), minimum: 1.
	 * @member {object} [typeProperties.parameters] Parameters for U-SQL job
	 * request.
	 * @member {object} [typeProperties.runtimeVersion] Runtime version of the
	 * U-SQL engine to use. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.compilationMode] Compilation mode of U-SQL.
	 * Must be one of these values : Semantic, Full and SingleBox. Type: string (or
	 * Expression with resultType string).
	 */
	export interface DataLakeAnalyticsUSQLActivity extends ExecutionActivity {
		typeProperties: DataLakeAnalyticsUSQLActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMLUpdateResourceActivityTypeProperties class.
	 * @constructor
	 * Azure ML Update Resource activity properties.
	 *
	 * @member {object} trainedModelName Name of the Trained Model module in the
	 * Web Service experiment to be updated. Type: string (or Expression with
	 * resultType string).
	 * @member {object} trainedModelLinkedServiceName Name of Azure Storage linked
	 * service holding the .ilearner file that will be uploaded by the update
	 * operation.
	 * @member {string} [trainedModelLinkedServiceName.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [trainedModelLinkedServiceName.parameters] Arguments for
	 * LinkedService.
	 * @member {object} trainedModelFilePath The relative file path in
	 * trainedModelLinkedService to represent the .ilearner file that will be
	 * uploaded by the update operation.  Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureMLUpdateResourceActivityTypeProperties {
		trainedModelName: any;
		trainedModelLinkedServiceName: SharedModel.LinkedServiceReference;
		trainedModelFilePath: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMLUpdateResourceActivity class.
	 * @constructor
	 * Azure ML Update Resource management activity.
	 *
	 * @member {object} typeProperties Azure ML Update Resource management activity
	 * properties.
	 * @member {object} [typeProperties.trainedModelName] Name of the Trained Model
	 * module in the Web Service experiment to be updated. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.trainedModelLinkedServiceName] Name of
	 * Azure Storage linked service holding the .ilearner file that will be
	 * uploaded by the update operation.
	 * @member {string}
	 * [typeProperties.trainedModelLinkedServiceName.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [typeProperties.trainedModelLinkedServiceName.parameters]
	 * Arguments for LinkedService.
	 * @member {object} [typeProperties.trainedModelFilePath] The relative file
	 * path in trainedModelLinkedService to represent the .ilearner file that will
	 * be uploaded by the update operation.  Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureMLUpdateResourceActivity extends ExecutionActivity {
		typeProperties: AzureMLUpdateResourceActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMLWebServiceFile class.
	 * @constructor
	 * Azure ML WebService Input/Output file
	 *
	 * @member {object} filePath The relative file path, including container name,
	 * in the Azure Blob Storage specified by the LinkedService. Type: string (or
	 * Expression with resultType string).
	 * @member {object} linkedServiceName Reference to an Azure Storage
	 * LinkedService, where Azure ML WebService Input/Output file located.
	 * @member {string} [linkedServiceName.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [linkedServiceName.parameters] Arguments for LinkedService.
	 */
	export interface AzureMLWebServiceFile {
		filePath: any;
		linkedServiceName: SharedModel.LinkedServiceReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMLBatchExecutionActivityTypeProperties class.
	 * @constructor
	 * Azure ML Batch Execution activity properties.
	 *
	 * @member {object} [globalParameters] Key,Value pairs to be passed to the
	 * Azure ML Batch Execution Service endpoint. Keys must match the names of web
	 * service parameters defined in the published Azure ML web service. Values
	 * will be passed in the GlobalParameters property of the Azure ML batch
	 * execution request.
	 * @member {object} [webServiceOutputs] Key,Value pairs, mapping the names of
	 * Azure ML endpoint's Web Service Outputs to AzureMLWebServiceFile objects
	 * specifying the output Blob locations. This information will be passed in the
	 * WebServiceOutputs property of the Azure ML batch execution request.
	 * @member {object} [webServiceInputs] Key,Value pairs, mapping the names of
	 * Azure ML endpoint's Web Service Inputs to AzureMLWebServiceFile objects
	 * specifying the input Blob locations.. This information will be passed in the
	 * WebServiceInputs property of the Azure ML batch execution request.
	 */
	export interface AzureMLBatchExecutionActivityTypeProperties {
		globalParameters?: { [propertyName: string]: any };
		webServiceOutputs?: { [propertyName: string]: AzureMLWebServiceFile };
		webServiceInputs?: { [propertyName: string]: AzureMLWebServiceFile };
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMLBatchExecutionActivity class.
	 * @constructor
	 * Azure ML Batch Execution activity.
	 *
	 * @member {object} typeProperties Azure ML Batch Execution activity
	 * properties.
	 * @member {object} [typeProperties.globalParameters] Key,Value pairs to be
	 * passed to the Azure ML Batch Execution Service endpoint. Keys must match the
	 * names of web service parameters defined in the published Azure ML web
	 * service. Values will be passed in the GlobalParameters property of the Azure
	 * ML batch execution request.
	 * @member {object} [typeProperties.webServiceOutputs] Key,Value pairs, mapping
	 * the names of Azure ML endpoint's Web Service Outputs to
	 * AzureMLWebServiceFile objects specifying the output Blob locations. This
	 * information will be passed in the WebServiceOutputs property of the Azure ML
	 * batch execution request.
	 * @member {object} [typeProperties.webServiceInputs] Key,Value pairs, mapping
	 * the names of Azure ML endpoint's Web Service Inputs to AzureMLWebServiceFile
	 * objects specifying the input Blob locations.. This information will be
	 * passed in the WebServiceInputs property of the Azure ML batch execution
	 * request.
	 */
	export interface AzureMLBatchExecutionActivity extends ExecutionActivity {
		typeProperties: AzureMLBatchExecutionActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMLExecutePipelineActivityTypeProperties class.
	 * @constructor
	 * Azure ML Execute Pipeline activity properties.
	 *
	 * @member {object} [mlPipelineId] ID of the published Azure ML pipeline.
	 * @member {object} [experimentName] Run history experiment name of the pipeline run.
	 * This information will be passed in the ExperimentName property of the published pipeline execution request.
	 * @member {object} [mlPipelineParameters] Key,Value pairs to be passed to the published Azure ML pipeline endpoint.
	 * Keys must match the names of pipeline parameters defined in the published pipeline.
	 * Values will be passed in the ParameterAssignments property of the published pipeline execution request.
	 * @member {object} [mlParentRunId] The parent Azure ML Service pipeline run id.
	 * This information will be passed in the ParentRunId property of the published pipeline execution request.
	 * @member {object} [continueOnStepFailure] Whether to continue execution of other steps in the PipelineRun if a step fails.
	 * This information will be passed in the continueOnStepFailure property of the published pipeline execution request.
	 */
	export interface AzureMLExecutePipelineActivityTypeProperties {
		mlPipelineId?: string;
		mlPipelineEndpointId?: string;
		version?: string;
		dataPathAssignments?: { [propertyName: string]: any };
		experimentName?: string;
		mlPipelineParameters?: { [propertyName: string]: any };
		mlParentRunId?: string;
		continueOnStepFailure?: boolean;
		mlBatchEndpointName?: string;
		mlBatchDeploymentName?: string;
		mlExecutionType?: string;
		jobSettings?: { [propertyName: string]: any };
		jobInputs?: { [propertyName: string]: any };
		jobOutputs?: { [propertyName: string]: any };
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMLExecutePipelineActivity class.
	 * @constructor
	 * Azure ML Execute Pipeline activity.
	 *
	 * @member {object} typeProperties Azure ML Batch Execution activity
	 * properties.
	 * @member {object} [typeProperties.mlPipelineId] ID of the published Azure ML pipeline.
	 * @member {object} [experimentName] Run history experiment name of the pipeline run.
	 * This information will be passed in the ExperimentName property of the published pipeline execution request.
	 * @member {object} [typeProperties.mlPipelineParameters] Key,Value pairs to be passed to the published Azure ML pipeline endpoint.
	 * Keys must match the names of pipeline parameters defined in the published pipeline.
	 * Values will be passed in the ParameterAssignments property of the published pipeline execution request.
	 * @member {object} [typeProperties.mlParentRunId] The parent Azure ML Service pipeline run id.
	 * This information will be passed in the ParentRunId property of the published pipeline execution request.
	 * @member {object} [typeProperties.continueOnStepFailure] Whether to continue execution of other steps in the PipelineRun if a step fails.
	 * This information will be passed in the continueOnStepFailure property of the published pipeline execution request.
	 */
	export interface AzureMLExecutePipelineActivity extends ExecutionActivity {
		typeProperties: AzureMLExecutePipelineActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the GetMetadataActivityTypeProperties class.
	 * @constructor
	 * GetMetadata activity properties.
	 *
	 * @member {object} dataset GetMetadata activity dataset reference.
	 * @member {string} [dataset.referenceName] Reference dataset name.
	 * @member {object} [dataset.parameters] Arguments for dataset.
	 * @member {array} [fieldList] Fields of metadata to get from dataset.
	 */
	export interface GetMetadataActivityTypeProperties {
		dataset?: DatasetReference;
		datasetSettings?: any;
		fieldList?: any[];

		storeSettings?: StoreReadSettings; // handcrafted
		formatSettings?: FormatReadSettings; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the GetMetadataActivity class.
	 * @constructor
	 * Activity to get metadata of dataset
	 *
	 * @member {object} typeProperties GetMetadata activity properties.
	 * @member {object} [typeProperties.dataset] GetMetadata activity dataset
	 * reference.
	 * @member {string} [typeProperties.dataset.referenceName] Reference dataset
	 * name.
	 * @member {object} [typeProperties.dataset.parameters] Arguments for dataset.
	 * @member {array} [typeProperties.fieldList] Fields of metadata to get from
	 * dataset.
	 */
	export interface GetMetadataActivity extends ExecutionActivity {
		typeProperties: GetMetadataActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ValidationActivity class.
	 * @constructor
	 * Activity to validate existance of dataset.
	 *
	 * @member {object} [typeProperties] Validation activity properties.
	 * @member {object} [typeProperties.timeout] Timeout. Type: string (or Expression
	 * with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {number} [typeProperties.sleep]
	 * @member {object} [typeProperties.dataset] Validation activity dataset reference.
	 * @member {string} [typeProperties.dataset.referenceName] Reference dataset name.
	 * @member {object} [typeProperties.dataset.parameters] Arguments for dataset.
	 * @member {object} [typeProperties.minimumSize]
	 * @member {object} [typeProperties.childItems]
	 */
	export interface ValidationActivity extends ExecutionActivity {
		typeProperties: ValidationActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ValidationActivityTypeProperties class.
	 * @constructor
	 * Validation activity properties.
	 *
	 * @member {object} [timeout] Timeout. Type: string (or Expression
	 * with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {number} [sleep]
	 * @member {object} [dataset] Validation activity dataset reference.
	 * @member {string} [dataset.referenceName] Reference dataset name.
	 * @member {object} [dataset.parameters] Arguments for dataset.
	 * @member {object} [minimumSize]
	 * @member {object} [childItems]
	 */
	export interface ValidationActivityTypeProperties {
		timeout?: any;
		sleep?: number;
		dataset: DatasetReference;
		datasetSettings?: any;
		minimumSize?: number;
		childItems?: boolean;
	}

	/**
	 * @class
	 * Initializes a new instance of the WebActivityAuthentication class.
	 * @constructor
	 * Web activity authentication properties.
	 *
	 * @member {string} type Web activity authentication
	 * (Basic/ClientCertificate/MSI)
	 * @member {object} [pfx] Base64-encoded contents of a PFX file or an AKV secret.
	 * @member {string} [username] Web activity authentication user name for basic
	 * authentication.
	 * @member {object} [password] Password for the PFX file or basic or an AKV secret
	 * @member {string} [resource] Resource for which Azure Auth token will be
	 * requested when using MSI Authentication.
	 */
	export interface WebActivityAuthentication {
		type?: string;
		pfx?: any;
		username?: any;
		password?: any;
		resource?: any;
		disableCertValidation?: boolean;
		userTenant?: any;
		credential?: SharedModel.CredentialReference;
		authenticationMethod?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the WebActivityTypeProperties class.
	 * @constructor
	 * Web activity type properties.
	 *
	 * @member {string} method Rest API method for target endpoint. Possible values
	 * include: 'GET', 'POST', 'PUT', 'DELETE', 'PATCH'
	 * @member {object} url Web activity target endpoint and path. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [headers] Represents the headers that will be sent to the
	 * request. For example, to set the language and type on a request: "headers" :
	 * { "Accept-Language": "en-us", "Content-Type": "application/json" }. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [body] Represents the payload that will be sent to the
	 * endpoint. Required for POST/PUT method, not allowed for GET method Type:
	 * string (or Expression with resultType string).
	 * @member {object} [authentication] Authentication method used for calling the
	 * endpoint.
	 * @member {string} [authentication.type] Web activity authentication
	 * (Basic/ClientCertificate/MSI)
	 * @member {object} [authentication.pfx] Base64-encoded contents of a PFX file.
	 * @member {string} [authentication.username] Web activity authentication user
	 * name for basic authentication.
	 * @member {object} [authentication.password] Password for the PFX file or
	 * basic authentication.
	 * @member {string} [authentication.resource] Resource for which Azure Auth
	 * token will be requested when using MSI Authentication.
	 * @member {array} [datasets] List of datasets passed to web endpoint.
	 * @member {array} [linkedServices] List of linked services passed to web
	 * endpoint.
	 */
	export interface WebActivityTypeProperties {
		connectVia?: SharedModel.IntegrationRuntimeReference
		method: string;
		url?: any;
		relativeUrl?: any;
		headers?: any;
		body?: any;
		authentication?: WebActivityAuthentication;
		datasets?: DatasetReference[];
		linkedServices?: SharedModel.LinkedServiceReference[];
		disableCertValidation?: boolean;
		disableFollowAuthOnRedirect?: boolean;
		httpRequestTimeout?: string;
		turnOffAsync?: string;
		userTenant?: string;
		pollUrlPattern?: string;
		pollStatusKey?: string;
		pollSuccessStatusValues?: string;
		pollFailedStatusValues?: string;
		useFileContentForRequestBody?: boolean;
		linkedServiceForRequestBody?: SharedModel.LinkedServiceReference;
		filePathForRequestBody?: any;
		responseJsonPath?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the WebActivity class.
	 * @constructor
	 * Web activity.
	 *
	 * @member {object} typeProperties Web activity properties.
	 * @member {string} [typeProperties.connectVia] IntegrationRuntime reference
	 * @member {string} [connectVia.referenceName] Reference integration runtime
	 * name.
	 * @member {object} [connectVia.parameters] Arguments for integration runtime.
	 * @member {string} [typeProperties.method] Rest API method for target
	 * endpoint. Possible values include: 'GET', 'POST', 'PUT', 'DELETE', 'PATCH'
	 * @member {object} [typeProperties.url] Web activity target endpoint and path.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.headers] Represents the headers that will
	 * be sent to the request. For example, to set the language and type on a
	 * request: "headers" : { "Accept-Language": "en-us", "Content-Type":
	 * "application/json" }. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.body] Represents the payload that will be
	 * sent to the endpoint. Required for POST/PUT method, not allowed for GET
	 * method Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.authentication] Authentication method used
	 * for calling the endpoint.
	 * @member {string} [typeProperties.authentication.type] Web activity
	 * authentication (Basic/ClientCertificate/MSI)
	 * @member {object} [typeProperties.authentication.pfx] Base64-encoded contents
	 * of a PFX file.
	 * @member {string} [typeProperties.authentication.pfx.value] Value of secure
	 * string.
	 * @member {string} [typeProperties.authentication.username] Web activity
	 * authentication user name for basic authentication.
	 * @member {object} [typeProperties.authentication.password] Password for the
	 * PFX file or basic authentication.
	 * @member {string} [typeProperties.authentication.password.value] Value of
	 * secure string.
	 * @member {string} [typeProperties.authentication.resource] Resource for which
	 * Azure Auth token will be requested when using MSI Authentication.
	 * @member {array} [typeProperties.datasets] List of datasets passed to web
	 * endpoint.
	 * @member {array} [typeProperties.linkedServices] List of linked services
	 * passed to web endpoint.
	 */
	export interface WebActivity extends ExecutionActivity {
		typeProperties: WebActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the WebActivityLinkedService class.
	 * @constructor
	 * Linked service for Web Activity.
	 *
	 * @member {object} typeProperties Web Activity linked service properties.
	 */
	export interface WebActivityLinkedService extends SharedModel.LinkedService {
		typeProperties: WebActivityLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the WebActivityLinkedServiceTypeProperties class.
	 * @constructor
	 * Web Activity linked service properties.
	 *
	 * @member {object} [Url] Terget endpoint. Type: string (or Expression with resultType string).
	 */
	export interface WebActivityLinkedServiceTypeProperties {
		authenticationType?: string;
		url: any;
		userName?: any;
		aadResourceId?: any;
		tenant?: any;
		password?: string | SharedModel.SecretBase;
		pfx?: string | SharedModel.SecretBase;
		credential?: SharedModel.CredentialReference;
		enableServerCertificateValidation: boolean;
		servicePrincipalId?: any;
		servicePrincipalKey?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DBTActivityProjectSettings class.
	 * @constructor
	 * dbt activity project specific properties.
	 *
	 * @member {string} type Specifies the type of project storage to be used
	 */
	export interface DBTActivityProjectSettings {
		type: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the DBTActivityGitHubProjectSettings class.
	 * @constructor
	 * dbt activity project specific properties.
	 *
	 * @member {object} branch Git branch to use for the dbt project
	 */
	export interface DBTActivityGitHubProjectSettings extends DBTActivityProjectSettings {
		branch?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DBTActivityLakehouseProjectSettings class.
	 * @constructor
	 * dbt activity project specific properties.
	 *
	 * @member {object} folderPath Path to the dbt project within Lakehouse storage
	 */
	export interface DBTActivityLakehouseProjectSettings extends DBTActivityProjectSettings {
		folderPath?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DBTActivityProject class.
	 * @constructor
	 * dbt activity project properties.
	 *
	 * @member {object} select Specifies a selection criteria for node selection during dbt run
	 * @member {object} exclude Specifies an exclusion criteria for node selection during dbt run
	 * @member {object} selectorName Name of model to execute, if only one is to be executed
	 * @member {object} failFast Specifies if dbt should exit as soon as there's a failure
	 * @member {object} fullRefresh Specifies if dbt should rebuild all models
	 * @member {object} projectSettings Settings specific to the given project
	 * @member {object} connectionSettings Linked service to be used if internal to workspace
	 * @member {object} linkedServiceName Linked service to be used if external to workspace
	 */
	export interface DBTActivityProject {
		select?: any;
		exclude?: any;
		selectorName?: any;
		failFast?: any;
		fullRefresh?: any;
		projectSettings: DBTActivityProjectSettings;
		connectionSettings?: SharedModel.LinkedServiceResource;
		linkedServiceName?: SharedModel.LinkedServiceReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the DBTActivityLogger class.
	 * @constructor
	 * dbt activity logger properties.
	 *
	 * @member {object} path Specifies a path where the logs should be uploaded
	 * @member {object} connectionSettings Linked service to be used if internal to workspace
	 * @member {object} linkedServiceName Linked service to be used if external to workspace
	 */
	export interface DBTActivityLogger {
		path?: any;
		connectionSettings?: SharedModel.LinkedServiceResource;
		linkedServiceName?: SharedModel.LinkedServiceReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the DBTActivityProfileSettings class.
	 * @constructor
	 * dbt activity project specific properties.
	 *
	 * @member {object} schema Name of database schema to use for activity
	 */
	export interface DBTActivityDWProfileSettings extends DBTActivityProfileSettings {
		schema?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DBTActivityProfileSettings class.
	 * @constructor
	 * dbt activity project specific properties.
	 *
	 * @member {object} schema Name of database schema to use for activity
	 * @member {object} database Name of database to use for activity
	 */
	export interface DBTActivitySnowflakeProfileSettings extends DBTActivityProfileSettings {
		schema: any;
		database: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DBTActivityProfileSettings class.
	 * @constructor
	 * dbt activity project specific properties.
	 *
	 * @member {object} schema Name of database schema to use for activity
	 * @member {object} http_path Name of SQL Warehouse or all-purpose cluster to use for activity
	 * @member {object} catalog Name of unity catalog to use for activity
	 */
	export interface DBTActivityDatabricksProfileSettings extends DBTActivityProfileSettings {
		schema: any;
		http_path: any;
		catalog?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DBTActivityProfileSettings class.
	 * @constructor
	 * dbt activity project specific properties.
	 *
	 * @member {object} schema Name of database schema to use for activity
	 */
	export interface DBTActivityPSProfileSettings extends DBTActivityProfileSettings {
		schema: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DBTActivityProfileSettings class.
	 * @constructor
	 * dbt activity project specific properties.
	 *
	 * @member {object} schema Name of database schema to use for activity
	 */
	export interface DBTActivitySQLServerProfileSettings extends DBTActivityProfileSettings {
		schema: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DBTActivityProfileSettings class.
	 * @constructor
	 * dbt activity project specific properties.
	 *
	 * @member {string} type Specifies the type of profile settings
	 * @member {object} threads Specifies the number of threads to use when building models
	 */
	export interface DBTActivityProfileSettings {
		type: string;
		threads: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DBTActivityProfile class.
	 * @constructor
	 * dbt activity project properties.
	 *
	 * @member {object} profileSettings Settings specific to the given profile.
	 * @member {object} connectionSettings Linked service to use if profile is internal to workspace.
	 * @member {object} linkedServiceName Linked service to use if profile is external to workspace.
	 */
	export interface DBTActivityProfile {
		profileSettings: DBTActivityProfileSettings;
		connectionSettings?: SharedModel.LinkedServiceResource;
		linkedServiceName?: SharedModel.LinkedServiceReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the DBTActivityTypeProperties class.
	 * @constructor
	 * DBT activity type properties.
	 *
	 * @member {object} profile Target data storage
	 * @member {object} project Location of DBT project
	 */
	export interface DBTActivityTypeProperties {
		project: DBTActivityProject,
		profile: DBTActivityProfile,
		logger?: DBTActivityLogger
	}

	/**
	 * @class
	 * Initializes a new instance of the ExecuteDBTActivity class.
	 * @constructor
	 * DBT activity.
	 *
	 * @member {object} typeProperties DBT activity properties.
	 * @member {object} [typeProperties.profile] Target data storage
	 * @member {object} [typeProperties.project] Location of DBT project
	 */
	export interface ExecuteDBTActivity extends ExecutionActivity {
		typeProperties: DBTActivityTypeProperties
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataFactoryLinkedService class.
	 * @constructor
	 * Linked service for Invoke Activity.
	 *
	 * @member {object} typeProperties Invoke Activity linked service properties.
	 */
	export interface AzureDataFactoryLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureDataFactoryLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataFactoryLinkedServiceTypeProperties class.
	 * @constructor
	 * Invoke Activity linked service properties.
	 *
	 * @member {object} [Url] Terget endpoint. Type: string (or Expression with resultType string).
	 */
	export interface AzureDataFactoryLinkedServiceTypeProperties {
		subscriptionId: string;
		resourceGroupName: string;
		dataFactoryName: string;
		authenticationType?: string;
		servicePrincipalId?: any;
		tenant?: any;
		servicePrincipalKey?: SharedModel.SecretBase;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSynapseAnalyticsLinkedService class.
	 * @constructor
	 * Linked service for Invoke Activity.
	 *
	 * @member {object} typeProperties Invoke Activity linked service properties.
	 */
	export interface AzureSynapseAnalyticsLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureSynapseAnalyticsLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSynapseAnalyticsLinkedServiceTypeProperties class.
	 * @constructor
	 * Invoke Activity linked service properties.
	 *
	 * @member {object} [Url] Terget endpoint. Type: string (or Expression with resultType string).
	 */
	export interface AzureSynapseAnalyticsLinkedServiceTypeProperties {
		workspaceName: string;
		authenticationType?: string;
		servicePrincipalId?: any;
		tenant?: any;
		servicePrincipalKey?: SharedModel.SecretBase;
	}

	/**
	 * @class
	 * Initializes a new instance of the FabricLinkedService class.
	 * @constructor
	 * Linked service for Invoke Activity.
	 *
	 * @member {object} typeProperties Invoke Activity linked service properties.
	 */
	export interface FabricLinkedService extends SharedModel.LinkedService {
		typeProperties: FabricLinkedServiceTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the FabricLinkedServiceTypeProperties class.
	 * @constructor
	 * Invoke Activity linked service properties.
	 *
	 * @member {object} [Url] Terget endpoint. Type: string (or Expression with resultType string).
	 */
	export interface FabricLinkedServiceTypeProperties {
		workspaceId: string;
		authenticationType?: string;
		servicePrincipalId?: any;
		tenant?: any;
		servicePrincipalKey?: SharedModel.SecretBase;
	}

	/**
	 * @class
	 * Initializes a new instance of the ExtensibleActivityTypeProperties class.
	 * @constructor
	 * ExtensibleActivity activity properties.
	 */
	export interface ExtensibleActivityTypeProperties {
		inputs?: {
			method: string;
			path: string;
			body?: {
				[property: string]: any
			};
			queries?: {
				[property: string]: any
			}
		};
		connection: string;
		credential: SharedModel.CredentialReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the RefreshObject class.
	 * @constructor
	 * SemanticModelRefreshActivity properties.
	 */
	export interface RefreshObject {
		table: string;
		partition?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the ExtensibleActivity class.
	 * @constructor
	 * Extansible logic apps activity
	 */
	export interface ExtensibleActivity extends ExecutionActivity {
		typeProperties: ExtensibleActivityTypeProperties;
	}

	export interface LakehouseMaintenanceActivityTypeProperties {
		schema: string;
		table: string;
		optimizeEnabled: boolean;
		vOrderEnabled: boolean;
		zOrderEnabled: boolean;
		zOrderColumns: string[];
		vacuumEnabled: boolean;
        retentionPeriod: string;
		waitOnCompletion?: boolean;
	}

	export interface AirflowActivityTypeProperties {
		workspaceId?: string | SharedModel.ExpressionValue;
		airflowId?: string | SharedModel.ExpressionValue;
		dagId?: string | SharedModel.ExpressionValue;
		waitOnCompletion?: boolean;
	}

	export interface AirflowActivity extends ExecutionActivity {
		typeProperties: AirflowActivityTypeProperties;
	}

	export interface RefreshMaterializedLakeViewActivityTypeProperties {
		lakehouseId: string;
		workspaceId: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the RedshiftUnloadSettings class.
	 * @constructor
	 * The Amazon S3 settings needed for the interim Amazon S3 when copying from
	 * Amazon Redshift with unload. With this, data from Amazon Redshift source
	 * will be unloaded into S3 first and then copied into the targeted sink from
	 * the interim S3.
	 *
	 * @member {object} s3LinkedServiceName The name of the Amazon S3 linked
	 * service which will be used for the unload operation when copying from the
	 * Amazon Redshift source.
	 * @member {string} [s3LinkedServiceName.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [s3LinkedServiceName.parameters] Arguments for
	 * LinkedService.
	 * @member {object} bucketName The bucket of the interim Amazon S3 which will
	 * be used to store the unloaded data from Amazon Redshift source. The bucket
	 * must be in the same region as the Amazon Redshift source. Type: string (or
	 * Expression with resultType string).
	 */
	export interface RedshiftUnloadSettings {
		s3LinkedServiceName: SharedModel.LinkedServiceReference;
		bucketName: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the CopySource class.
	 * @constructor
	 * A copy activity source.
	 *
	 * @member {object} [sourceRetryCount] Source retry count. Type: integer (or
	 * Expression with resultType integer).
	 * @member {object} [sourceRetryWait] Source retry wait. Type: string (or
	 * Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {string} type Polymorphic Discriminator
	 */
	export interface CopySource {
		sourceRetryCount?: any;
		sourceRetryWait?: any;
		maxConcurrentConnections?: any; // handcraft
		structure?: any; // handcraft
		additionalColumns?: any; // handcraft
		type: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	//handcrafted
	export interface CopySourceAdditionalColumns {
		name: any;
		value: any;
	}

	// handcrafted
	export interface StoreReadSettings {
		type: string;
		recursive?: boolean;
		maxConcurrentConnections?: number;
		enablePartitionDiscovery?: boolean;
		partitionRootPath?: string;
		wildcardFolderPath?: string;
		wildcardFileName?: string;
		fileListPath?: string;
		prefix?: string;
		modifiedDatetimeStart?: any;
		modifiedDatetimeEnd?: any;
		useBinaryTransfer?: boolean;
		fileFilter?: any;
		deleteFilesAfterCompletion?: any;
		listBefore?: any;
		listAfter?: any;
		disableChunking?: boolean;
	}

	// handcrafted
	export interface CompressionReadSettings {
		type: string;
		preserveZipFileNameAsFolder?: any;
		preserveCompressionFileNameAsFolder?: any;
	}

	// handcrafted
	export interface HttpStoreReadSettings extends StoreReadSettings {
		requestMethod?: string;
		additionalHeaders?: string;
		requestBody?: string;
		requestTimeout?: string;
	}

	export interface HdfsStoreReadSettings extends StoreReadSettings {
		distcpSettings?: DistcpSettings;
	}

	// handcrafted
	export interface StoreWriteSettings {
		type: string;
		maxConcurrentConnections?: number;
		copyBehavior?: string;
		operationTimeout?: any;
		blockSizeInMB?: number;
		expiryDatetime?: any;
		useTempFileRename?: any;
		metadata?: any;
		tableActionOption?: any;
		partitionOption?: any;
		partitionNameList?: any;
	}

	// handcrafted
	export interface FormatReadSettings {
		type: string;
		compressionProperties?: CompressionReadSettings;
	}

	// handcrafted
	export interface DelimitedTextFormatReadSettings extends FormatReadSettings {
		commentChar?: string;
		ignoreLeadingWhiteSpace?: boolean;
		ignoreTrailingWhiteSpace?: boolean;
		addtionalNullValues?: string[];
		skipLineCount?: number;
	}

	// handcrafted
	export interface XmlFormatReadSettings extends FormatReadSettings {
		validationMode?: any;
		namespaces?: any;
		namespacePrefixes?: any;
		detectDataType?: boolean;
	}

	// handcrafted
	export interface JsonFormatReadSettings extends FormatReadSettings {
		documentForm?: string;
	}

	// handcrafted
	export interface DeltaFormatReadSettings extends FormatReadSettings {
		timestampAsOf?: string;
		versionAsOf?: number;
	}

	export interface FormatWriteSettings {
		type: string;
		quoteAllText?: boolean;
		maxRowsPerFile?: any; // handcraft
		fileNamePrefix?: any; // handcraft
	}

	// handcrafted
	export interface ParquetFormatWriteSettings extends FormatWriteSettings {
		enableVertiParquet?: boolean;
	}

	// handcrafted
	export interface DeltaFormatWriteSettings extends FormatWriteSettings {
		enableVertiParquet?: boolean;
		writeBehavior?: any; // handcrafted
		partitionOption?: any; // handcrafted
		partitionNameList?: any; // handcrafted
		upsertSettings?: any; // handcrafted
		applyVOrder?: any; // handcrafted
	}

	// handcrafted
	export interface IcebergFormatWriteSettings extends FormatWriteSettings {
	}

	// handcrafted
	export interface DelimitedTextFormatWriteSettings extends FormatWriteSettings {
		fileExtension?: string;
	}

	// handcrafted
	export interface JsonFormatWriteSettings extends FormatWriteSettings {
		filePattern?: string;
	}

	// handcrafted
	export interface AvroFormatWriteSettings extends FormatWriteSettings {
		recordName?: any;
		recordNamespace?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AmazonRedshiftSource class.
	 * @constructor
	 * A copy activity source for Amazon Redshift Source.
	 *
	 * @member {object} [query] Database query. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [redshiftUnloadSettings] The Amazon S3 settings needed for
	 * the interim Amazon S3 when copying from Amazon Redshift with unload. With
	 * this, data from Amazon Redshift source will be unloaded into S3 first and
	 * then copied into the targeted sink from the interim S3.
	 * @member {object} [redshiftUnloadSettings.s3LinkedServiceName] The name of
	 * the Amazon S3 linked service which will be used for the unload operation
	 * when copying from the Amazon Redshift source.
	 * @member {string} [redshiftUnloadSettings.s3LinkedServiceName.referenceName]
	 * Reference LinkedService name.
	 * @member {object} [redshiftUnloadSettings.s3LinkedServiceName.parameters]
	 * Arguments for LinkedService.
	 * @member {object} [redshiftUnloadSettings.bucketName] The bucket of the
	 * interim Amazon S3 which will be used to store the unloaded data from Amazon
	 * Redshift source. The bucket must be in the same region as the Amazon
	 * Redshift source. Type: string (or Expression with resultType string).
	 */
	export interface AmazonRedshiftSource extends CopySource {
		query?: any;
		redshiftUnloadSettings?: RedshiftUnloadSettings;
	}

	/**
	 * @class
	 * Initializes a new instance of the ResponsysSource class.
	 * @constructor
	 * A copy activity Responsys source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface ResponsysSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceMarketingCloudSource class.
	 * @constructor
	 * A copy activity Salesforce Marketing Cloud source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface SalesforceMarketingCloudSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the VerticaSource class.
	 * @constructor
	 * A copy activity Vertica source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface VerticaSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the NetezzaSource class.
	 * @constructor
	 * A copy activity Netezza source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface NetezzaSource extends CopySource {
		query?: any;
		partitionOption?: any; // handcrafted
		partitionSettings?: {
			partitionColumnName?: any; // handcrafted
			partitionUpperBound?: any; // handcrafted
			partitionLowerBound?: any; // handcrafted
		}
	}

	/**
	 * @class
	 * Initializes a new instance of the ZohoSource class.
	 * @constructor
	 * A copy activity Zoho server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface ZohoSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the XeroSource class.
	 * @constructor
	 * A copy activity Xero Serivce source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface XeroSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SquareSource class.
	 * @constructor
	 * A copy activity Square Serivce source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface SquareSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SparkSource class.
	 * @constructor
	 * A copy activity Spark Server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 * @member {any} [batchSize] The number of rows to fetch per batch.
	 * @member {object} [queryTimeout] Query timeout. Type: string (TimeSpan like "hh:mm:ss")
	 * (or Expression with resultType string).
	 */
	export interface SparkSource extends CopySource {
		query?: any;
		batchSize?: any;
		queryTimeout?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ShopifySource class.
	 * @constructor
	 * A copy activity Shopify Serivce source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface ShopifySource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ServiceNowSource class.
	 * @constructor
	 * A copy activity ServiceNow server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface ServiceNowSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ServiceNowV2Source class.
	 * @constructor
	 * A copy activity ServiceNowV2 server source.
	 *
	 * @member {object} [expression] query expression
	 * @member {number} [pageSize] defaults to 300
	 */
	export interface ServiceNowV2Source extends CopySource {
		expression: any;
		pageSize?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DataLakeAnalyticsScopeActivityTypeProperties class.
	 * @constructor
	 * DataLakeAnalyticsScope activity properties.
	 *
	 * @member {object} [scriptFolderPath] Case-sensitive path to folder that contains the
	 * Scope script. Type: string (or Expression with resultType string).
	 * @member {object} [scriptFileName] Case-sensitive Scope script file name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [scriptLinkedService] Script linked service reference.
	 * @member {string} [scriptLinkedService.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [scriptLinkedService.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [degreeOfParallelism] The maximum number of nodes
	 * simultaneously used to run the job. Default value is 1. Type: integer
	 * (or Expression with resultType integer), minimum: 1.
	 * @member {object} [priority] Determines which jobs out of all that are queued
	 * should be selected to run first. The lower the number, the higher the
	 * priority. Default value is 1000. Type: integer (or Expression with
	 * resultType integer), minimum: 1.
	 * @member {object} [parameters] Parameters for Scope script.
	 * @member {object} [runtimeVersion] Runtime version of the Scope engine to
	 * use. Type: string (or Expression with resultType string).
	 * @member {object} [nebulaArguments] Additional Scope parameters to pass in during job submission.
	 * E.g. -flag1 -param1 value1 -param2 value2 -flag2. Type: string. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [testIsolation] Indicate whether script should be submitted with process isolation enabled
	 * @member {array} [outputAdlsDatasets] References to ADLS datasets that were created from
	 * script compilation.
	 * @member {array} [outputAdlsCosmosStructuredStreamDatasets] References to ADLS Cosmos Structured Stream datasets that were created from
	 * script compilation.
	 * @member {date} [lastCompiledTime] Last time (UTC) the script used by this activity has been compiled
	 * for output datasets and input file information.
	 * @member {object} [outputAdlsLinkedService] Linked service that was assigned as the linked service
	 * of outputAdlsDatasets the last time script was compiled and saved.
	 * @member {object} [outputAdlsCosmosStructuredStreamLinkedService] Linked service that was assigned as the linked service
	 * of outputAdlsCosmosStructuredStreamDatasets the last time script was compiled and saved.
	 */
	export interface DataLakeAnalyticsScopeActivityTypeProperties {
		scriptFolderPath: any;
		scriptFileName: any;
		scriptLinkedService?: SharedModel.LinkedServiceReference; // handcrafted
		degreeOfParallelism?: any;
		degreeOfParallelismPercent?: any; // handcrafted
		degreeOfParallelismEco?: any;
		degreeOfParallelismPercentEco?: any; // handcrafted
		jobName?: any; // handcrafted
		jobOwner?: any; // handcrafted
		notifier?: any; // handcrafted
		scopeScriptInclusionSet?: any; // handcrafted
		priority?: any;
		parameters?: { [propertyName: string]: any };
		runtimeVersion?: any;
		nebulaArguments?: any;
		testIsolation?: any;
		// these remaining ones are almost certainly obsolete
		outputAdlsDatasets?: DatasetReference[];
		outputAdlsCosmosStructuredStreamDatasets?: DatasetReference[];
		lastCompiledTime?: Date;
		outputAdlsLinkedService?: SharedModel.LinkedServiceReference;
		outputAdlsCosmosStructuredStreamLinkedService?: SharedModel.LinkedServiceReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the QuickBooksSource class.
	 * @constructor
	 * A copy activity QuickBooks server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface QuickBooksSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the PrestoSource class.
	 * @constructor
	 * A copy activity Presto server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface PrestoSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DataLakeAnalyticsScopeActivity class.
	 * @constructor
	 * Data Lake Analytics Scope activity.
	 *
	 * @member {object} [typeProperties] Data Lake Analytics Scope activity
	 * properties.
	 * @member {object} [typeProperties.scriptFolderPath] Case-sensitive path to folder that contains the
	 * Scope script. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.scriptFileName] Case-sensitive Scope script file name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.scriptLinkedService] Script linked service reference.
	 * @member {string} [typeProperties.scriptLinkedService.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [typeProperties.scriptLinkedService.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [typeProperties.degreeOfParallelism] The maximum number of nodes
	 * simultaneously used to run the job. Default value is 0. Type: integer
	 * (or Expression with resultType integer), minimum: 1.
	 * @member {object} [typeProperties.priority] Determines which jobs out of all that are queued
	 * should be selected to run first. The lower the number, the higher the
	 * priority. Default value is 1000. Type: integer (or Expression with
	 * resultType integer), minimum: 1.
	 * @member {object} [typeProperties.parameters] Parameters for Scope script.
	 * @member {object} [typeProperties.runtimeVersion] Runtime version of the Scope engine to
	 * use. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.nebulaArguments] Additional Scope parameters to pass in during job submission.
	 * E.g. -flag1 -param1 value1 -param2 value2 -flag2. Type: string. Type: string (or Expression with
	 * resultType string).
	 * @member {array} [typeProperties.outputAdlsDatasets] References to ADLS datasets that were created from
	 * script compilation.
	 * @member {array} [typeProperties.outputAdlsCosmosStructuredStreamDatasets] References to ADLS Cosmos Structured Stream datasets that were created from
	 * script compilation.
	 * @member {date} [typeProperties.lastCompiledTime] Last time (UTC) the script used by this activity has been compiled
	 * for output datasets and input file information.
	 * @member {object} [typeProperties.outputAdlsLinkedService] Linked service that was assigned as the linked service
	 * of outputAdlsDatasets the last time script was compiled and saved.
	 * @member {object} [typeProperties.outputAdlsCosmosStructuredStreamLinkedService] Linked service that was assigned as the linked service
	 * of outputAdlsCosmosStructuredStreamDatasets the last time script was compiled and saved.
	 */
	export interface DataLakeAnalyticsScopeActivity extends ExecutionActivity {
		typeProperties: DataLakeAnalyticsScopeActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the PhoenixSource class.
	 * @constructor
	 * A copy activity Phoenix server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface PhoenixSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the PhoenixSource class.
	 * @constructor
	 * A copy activity Phoenix server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface PhoenixSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the PaypalSource class.
	 * @constructor
	 * A copy activity Paypal Serivce source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface PaypalSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the MarketoSource class.
	 * @constructor
	 * A copy activity Marketo server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface MarketoSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the MariaDBSource class.
	 * @constructor
	 * A copy activity MariaDB server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface MariaDBSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the MagentoSource class.
	 * @constructor
	 * A copy activity Magento server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface MagentoSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the JiraSource class.
	 * @constructor
	 * A copy activity Jira Serivce source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface JiraSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the VivaInsightsSource class.
	 * @constructor
	 * A copy activity Viva Insights source. The report is addressed on the dataset, so the
	 * source itself carries no properties.
	 */
	export interface VivaInsightsSource extends CopySource {
	}

	export interface GenericCDataSource extends CopySource {
		query?: any;
	}

	export interface AttioSource extends CopySource {
		pageSize?: number | Expression;
	}

	export interface BitbucketSource extends CopySource {
		batchSize?: number | Expression;
	}

	/**
	 * @class
	 * Initializes a new instance of the ImpalaSource class.
	 * @constructor
	 * A copy activity Impala server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 * @member {any} [batchSize] The number of rows to fetch per batch.
	 * @member {object} [queryTimeout] Query timeout. Type: string (TimeSpan like "hh:mm:ss")
	 * (or Expression with resultType string).
	 */
	export interface ImpalaSource extends CopySource {
		query?: any;
		batchSize?: any;
		queryTimeout?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the HubspotSource class.
	 * @constructor
	 * A copy activity Hubspot Serivce source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface HubspotSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the HiveSource class.
	 * @constructor
	 * A copy activity Hive Server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 * @member {any} [batchSize] The number of rows to fetch per batch.
	 * @member {object} [queryTimeout] Query timeout. Type: string (TimeSpan like "hh:mm:ss")
	 * (or Expression with resultType string).
	 */
	export interface HiveSource extends CopySource {
		query?: any;
		batchSize?: any;
		queryTimeout?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the HBaseSource class.
	 * @constructor
	 * A copy activity HBase server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface HBaseSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the GreenplumSource class.
	 * @constructor
	 * A copy activity Greenplum Database source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface GreenplumSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the LakehouseTableSource class.
	 * @constructor
	 * A copy activity Lakehouse Table source.
	 *
	 * @member {string} [timestampAsOf] Query an older snapshot by timestamp
	 * @member {number} [versionAsOf] Query an older snapshot by version
	 */
	export interface LakehouseTableSource extends CopySource {
		timestampAsOf?: string;
		versionAsOf?: number;
	}

	export interface SqlAnalyticsEndpointCopySource extends CopySource {
		query?: any;
		queryTimeout?: any;
		partitionOption?: any;
		partitionSettings?: {
			partitionNames?: any;
			partitionColumnName?: any;
			partitionUpperBound?: any;
			partitionLowerBound?: any;
		}
	}

	/**
	 * @class
	 * Initializes a new instance of the GoogleBigQuerySource class.
	 * @constructor
	 * A copy activity Google BigQuery service source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface GoogleBigQuerySource extends CopySource {
		query?: any;
	}

	export interface GoogleBigQueryV2Source extends CopySource {
		query?: any;
		partitionOption?: any; // handcrafted
	}

	export interface GoogleBigQueryV2Sink extends CopySink {
		preCopyScript?: any;
		writeBehavior?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the EloquaSource class.
	 * @constructor
	 * A copy activity Eloqua server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface EloquaSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DrillSource class.
	 * @constructor
	 * A copy activity Drill server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface DrillSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the CouchbaseSource class.
	 * @constructor
	 * A copy activity Couchbase server source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface CouchbaseSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ConcurSource class.
	 * @constructor
	 * A copy activity Concur Serivce source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface ConcurSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzurePostgreSqlSource class.
	 * @constructor
	 * A copy activity Azure PostgreSQL source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface AzurePostgreSqlSource extends CopySource {
		query?: any;
		queryTimeout?: any;
		partitionOption?: any; // handcrafted
		partitionSettings?: {
			partitionNames?: any;
			partitionColumnName?: any;
			partitionUpperBound?: any;
			partitionLowerBound?: any;
		}
	}

	/**
	 * @class
	 * Initializes a new instance of the AmazonMWSSource class.
	 * @constructor
	 * A copy activity Amazon Marketplace Web Service source.
	 *
	 * @member {object} [query] A query to retrieve data from source. Type: string
	 * (or Expression with resultType string).
	 */
	export interface AmazonMWSSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the HttpSource class.
	 * @constructor
	 * A copy activity source for an HTTP file.
	 *
	 * @member {object} [httpRequestTimeout] Specifies the timeout for a HTTP
	 * client to get HTTP response from HTTP server. The default value is
	 * equivalent to System.Net.HttpWebRequest.Timeout. Type: string (or Expression
	 * with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 */
	export interface HttpSource extends CopySource {
		httpRequestTimeout?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreSource class.
	 * @constructor
	 * A copy activity Azure Data Lake source.
	 *
	 * @member {object} [recursive] If true, files under the folder path will be
	 * read recursively. Default is true. Type: boolean (or Expression with
	 * resultType boolean).
	 */
	export interface AzureDataLakeStoreSource extends CopySource {
		recursive?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreCosmosStructuredStreamSource class.
	 * @constructor
	 * A copy activity Azure Data Lake Cosmos Structured Stream source.
	 */
	export interface AzureDataLakeStoreCosmosStructuredStreamSource extends CopySource {
		partitionIndex?: number; //handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreGen2CosmosStructuredStreamSource class.
	 * @constructor
	 * A copy activity Azure Data Lake Gen2 Cosmos Structured Stream source.
	 */
	export interface AzureDataLakeStoreGen2CosmosStructuredStreamSource
		extends AzureDataLakeStoreCosmosStructuredStreamSource {
	}

	/**
	 * @class
	 * Initializes a new instance of the MongoDbSource class.
	 * @constructor
	 * A copy activity source for a MongoDB database.
	 *
	 * @member {object} [query] Database query. Should be a SQL-92 query
	 * expression. Type: string (or Expression with resultType string).
	 */
	export interface MongoDbSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the CassandraSource class.
	 * @constructor
	 * A copy activity source for a Cassandra database.
	 *
	 * @member {object} [query] Database query. Should be a SQL-92 query expression
	 * or Cassandra Query Language (CQL) command. Type: string (or Expression with
	 * resultType string).
	 * @member {string} [consistencyLevel] The consistency level specifies how many
	 * Cassandra servers must respond to a read request before returning data to
	 * the client application. Cassandra checks the specified number of Cassandra
	 * servers for data to satisfy the read request. Must be one of
	 * cassandraSourceReadConsistencyLevels. The default value is 'ONE'. It is
	 * case-insensitive. Possible values include: 'ALL', 'EACH_QUORUM', 'QUORUM',
	 * 'LOCAL_QUORUM', 'ONE', 'TWO', 'THREE', 'LOCAL_ONE', 'SERIAL', 'LOCAL_SERIAL'
	 */
	export interface CassandraSource extends CopySource {
		query?: any;
		consistencyLevel?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the WebSource class.
	 * @constructor
	 * A copy activity source for web page table.
	 *
	 */
	export interface WebSource extends CopySource {
	}

	/**
	 * @class
	 * Initializes a new instance of the OracleSource class.
	 * @constructor
	 * A copy activity Oracle source.
	 *
	 * @member {object} [oracleReaderQuery] Oracle reader query. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [queryTimeout] Query timeout. Type: string (or Expression
	 * with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 */
	export interface OracleSource extends CopySource {
		oracleReaderQuery?: any;
		queryTimeout?: any;
		convertDecimalToInteger?: any;
		partitionOption?: any; // handcrafted
		partitionSettings?: {
			partitionNames?: any;
			partitionColumnName?: any;
			partitionUpperBound?: any;
			partitionLowerBound?: any;
		},
		numberPrecision?: any; // handcrafted
		numberScale?: any; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureMySqlSource class.
	 * @constructor
	 * A copy activity Azure MySQL source.
	 *
	 * @member {object} [query] Database query. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureMySqlSource extends CopySource {
		query?: any;
		queryCommandTimeout?: any;
		queryTimeout?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DistcpSettings class.
	 * @constructor
	 * Distcp settings.
	 *
	 * @member {object} resourceManagerEndpoint Specifies the Yarn ResourceManager
	 * endpoint. Type: string (or Expression with resultType string).
	 * @member {object} tempScriptPath Specifies an existing folder path which will
	 * be used to store temp Distcp command script. The script file is generated by
	 * ADF and will be removed after Copy job finished. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [distcpOptions] Specifies the Distcp options. Type: string
	 * (or Expression with resultType string).
	 */
	export interface DistcpSettings {
		resourceManagerEndpoint: any;
		tempScriptPath: any;
		distcpOptions?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the HdfsSource class.
	 * @constructor
	 * A copy activity HDFS source.
	 *
	 * @member {object} [recursive] If true, files under the folder path will be
	 * read recursively. Default is true. Type: boolean (or Expression with
	 * resultType boolean).
	 * @member {object} [distcpSettings] Specifies Distcp-related settings.
	 * @member {object} [distcpSettings.resourceManagerEndpoint] Specifies the Yarn
	 * ResourceManager endpoint. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [distcpSettings.tempScriptPath] Specifies an existing
	 * folder path which will be used to store temp Distcp command script. The
	 * script file is generated by ADF and will be removed after Copy job finished.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [distcpSettings.distcpOptions] Specifies the Distcp
	 * options. Type: string (or Expression with resultType string).
	 */
	export interface HdfsSource extends CopySource {
		recursive?: any;
		distcpSettings?: DistcpSettings;
	}

	/**
	 * @class
	 * Initializes a new instance of the FileSystemSource class.
	 * @constructor
	 * A copy activity file system source.
	 *
	 * @member {object} [recursive] If true, files under the folder path will be
	 * read recursively. Default is true. Type: boolean (or Expression with
	 * resultType boolean).
	 */
	export interface FileSystemSource extends CopySource {
		recursive?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlDWSource class.
	 * @constructor
	 * A copy activity SQL Data Warehouse source.
	 *
	 * @member {object} [sqlReaderQuery] SQL Data Warehouse reader query. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [sqlReaderStoredProcedureName] Name of the stored procedure
	 * for a SQL Data Warehouse source. This cannot be used at the same time as
	 * SqlReaderQuery. Type: string (or Expression with resultType string).
	 * @member {object} [storedProcedureParameters] Value and type setting for
	 * stored procedure parameters. Example: "{Parameter1: {value: "1", type:
	 * "int"}}". Type: object (or Expression with resultType object), itemType:
	 * StoredProcedureParameter.
	 */
	export interface SqlDWSource extends CopySource {
		sqlReaderQuery?: any;
		sqlReaderStoredProcedureName?: any;
		storedProcedureParameters?: any;
		queryTimeout?: any;
		isolationLevel?: any; // handcrafted
	}

	export interface DataWarehouseSource extends SqlDWSource {
	}

	export interface SqlPoolSource extends CopySource {
		sqlReaderQuery?: any;
		sqlReaderStoredProcedureName?: any;
		storedProcedureParameters?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the StoredProcedureParameter class.
	 * @constructor
	 * SQL stored procedure parameter.
	 *
	 * @member {object} value Stored procedure parameter value. Type: string (or
	 * Expression with resultType string).
	 * @member {string} [type] Stored procedure parameter type. Possible values
	 * include: 'String', 'Int', 'Decimal', 'Guid', 'Boolean', 'Date'
	 */
	export interface StoredProcedureParameter {
		value: any;
		type?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlSource class.
	 * @constructor
	 * A copy activity SQL source.
	 *
	 * @member {object} [sqlReaderQuery] SQL reader query. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [sqlReaderStoredProcedureName] Name of the stored procedure
	 * for a SQL Database source. This cannot be used at the same time as
	 * SqlReaderQuery. Type: string (or Expression with resultType string).
	 * @member {object} [storedProcedureParameters] Value and type setting for
	 * stored procedure parameters. Example: "{Parameter1: {value: "1", type:
	 * "int"}}".
	 */
	export interface SqlSource extends CopySource {
		sqlReaderQuery?: any;
		sqlReaderStoredProcedureName?: any;
		storedProcedureParameters?: { [propertyName: string]: StoredProcedureParameter } | SharedModel.ExpressionValue;
		queryTimeout?: any;
		isolationLevel?: any; // handcrafted
		partitionOption?: any; // handcrafted
		partitionSettings?: { // handcrafted
			partitionColumnName?: any;
			partitionUpperBound?: any;
			partitionLowerBound?: any;
		}
	}

	/**
	 * @class
	 * Initializes a new instance of the SapEccSource class.
	 * @constructor
	 * A copy activity source for SAP ECC source.
	 *
	 * @member {string} [query] SAP ECC OData query. For example, "$top=1". Type:
	 * string (or Expression with resultType string).
	 */
	export interface SapEccSource extends CopySource {
		query?: string;
		httpRequestTimeout?: any;
	}

	// handcrafted
	export interface SapOpenHubSource extends CopySource {
		excludeLastRequest?: any,
		baseRequestId?: any
		sapDataColumnDelimiter?: any; // handcraft
		customRfcReadTableFunctionModule?: any;
	}

	// handcrafted
	export interface SapTableSource extends CopySource {
		batchSize?: any;
		rowCount?: any;
		rowSkips?: any;
		rfcTableFields?: any;
		rfcTableOptions?: any;
		customRfcReadTableFunctionModule?: any;

		partitionOption?: any;
		partitionSettings?: {
			partitionColumnName?: any;
			partitionUpperBound?: any;
			partitionLowerBound?: any;
			maxPartitionsNumber?: any;
		}

		sapDataColumnDelimiter?: any; // handcraft
	}

	// handcrafted
	export interface SapOdpSource extends CopySource {
		extractionMode?: any;
		subscriberProcess?: any;
		recoveryPointer?: any;
		projection?: string[];
		selection?: Array<{
			fieldName: string;
			sign: 'I' | 'E';
			option: 'EQ' | 'CP' | 'BT';
			low: string;
			high?: string;
		}>;
	}

	// handcrafted
	export interface SapODataSource extends CopySource {
		extractionMode?: any;
		deltaToken?: string;
		projection?: string[];
		selection?: string[];
		batchSize?: number;
		httpRequestTimeout?: number;
	}

	/**
	 * @class
	 * Initializes a new instance of the SapCloudForCustomerSource class.
	 * @constructor
	 * A copy activity source for SAP Cloud for Customer source.
	 *
	 * @member {object} [query] SAP Cloud for Customer OData query. For example,
	 * "$top=1". Type: string (or Expression with resultType string).
	 */
	export interface SapCloudForCustomerSource extends CopySource {
		query?: any;
		httpRequestTimeout?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceSource class.
	 * @constructor
	 * A copy activity Salesforce source.
	 *
	 * @member {object} [query] Database query. Type: string (or Expression with
	 * resultType string).
	 * @member {string} [readBehavior] The read behavior for the operation. Default
	 * is Query. Possible values include: 'Query', 'QueryAll'
	 */
	export interface SalesforceSource extends CopySource {
		query?: any;
		readBehavior?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceV2Source class.
	 * @constructor
	 * A copy activity Salesforce v2 source.
	 *
	 * @member {object} [query] Database query. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [SOQLQuery] [deprecate] Database query. Type: string (or Expression with
	 * resultType string).
	 * @member {string} [includeDeletedObjects] Whether to query the existing records or all records.
	 * default behavior is false
	 * @member {string} [preserveScaleFromSchema] Whether or not to enable decimal scale rounding.
	 * default behavior is false
	 * @member {string} [pageSize] page size
	 */
	export interface SalesforceV2Source extends CopySource {
		query?: any;
		SOQLQuery?: any;
		includeDeletedObjects?: any;
		preserveScaleFromSchema?: any;
		pageSize?: any;
	}

	// handcrafted
	export interface SalesforceServiceCloudSource extends CopySource {
		query?: any;
		readBehavior?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceServiceCloudV2Source class.
	 * @constructor
	 * A copy activity Salesforce service cloud v2 source.
	 *
	 * @member {object} [query] Database query. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [SOQLQuery] [deprecate] Database query. Type: string (or Expression with
	 * resultType string).
	 * @member {string} [includeDeletedObjects] Whether to query the existing records or all records.
	 * default behavior is false
	 * @member {string} [preserveScaleFromSchema] Whether or not to enable decimal scale rounding.
	 * default behavior is false
	 */
	export interface SalesforceServiceCloudV2Source extends SalesforceV2Source {
	}

	/**
	 * @class
	 * Initializes a new instance of the RelationalSource class.
	 * @constructor
	 * A copy activity source for various relational databases.
	 *
	 * @member {object} [query] Database query. Type: string (or Expression with
	 * resultType string).
	 */
	export interface RelationalSource extends CopySource {
		query?: any;
	}

	// handcrafted
	export interface SybaseSource extends CopySource {
		query?: any;
	}

	// handcrafted
	export interface SapBwSource extends CopySource {
		query?: any;
	}

	// handcrafted
	export interface MysqlSource extends CopySource {
		query?: any;
	}

	// handcrafted
	export interface InformixSource extends CopySource {
		query?: any;
	}

	// handcrafted
	export interface MicrosoftAccessSource extends CopySource {
		query?: any;
	}

	// handcrafted
	export interface TeradataSource extends CopySource {
		query?: any;
		queryTimeout?: any;
		partitionOption?: any; // handcrafted
		partitionSettings?: {
			partitionColumnName?: any;
			partitionUpperBound?: any;
			partitionLowerBound?: any;
		}
	}

	// handcrafted
	export interface SapHanaSource extends CopySource {
		query?: any;
		packetSize?: any;
		partitionOption?: any; // handcrafted
		partitionSettings?: {
			partitionColumnName?: any;
		}
	}

	// handcrafted
	export interface ODataSource extends CopySource {
		query?: any;
		httpRequestTimeout?: any;
	}

	// handcrafted
	export interface SharePointOnlineListCopySource extends CopySource {
		query?: any;
		httpRequestTimeout?: any;
	}

	// handcrafted
	export interface AzureDatabricksDeltaLakeCopySource extends CopySource {
		query?: any;
		exportSettings?: AzureDatabricksDeltaLakeExportSettings;
	}
	export interface DatabricksCopySource extends CopySource {
		query?: any;
		exportSettings?: DatabricksExportSettings;
	}

	// handcrafted
	export interface AzureDatabricksDeltaLakeExportSettings {
		type: string;
		dateFormat?: any;
		timestampFormat?: any;
	}
	export interface DatabricksExportSettings {
		type: string;
		dateFormat?: any;
		timestampFormat?: any;
	}

	// handcrafted
	export interface AzureDatabricksDeltaLakeImportSettings {
		type: string;
		dateFormat?: any;
		timestampFormat?: any;
	}
	// handcrafted
	export interface DatabricksImportSettings {
		type: string;
		dateFormat?: any;
		timestampFormat?: any;
	}

	// handcrafted
	export interface Db2Source extends CopySource {
		query?: any;
	}

	// handcrafted
	export interface OdbcSource extends CopySource {
		query?: any;
		queryTimeout?: any;
	}

	// handcrafted
	export interface PostgreSqlSource extends CopySource {
		query?: any;
		queryTimeout?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DynamicsSource class.
	 * @constructor
	 * A copy activity Dynamics source.
	 *
	 * @member {object} [query] FetchXML is a proprietary query language that is
	 * used in Microsoft Dynamics (online & on-premises). Type: string (or
	 * Expression with resultType string).
	 */
	export interface DynamicsSource extends CopySource {
		query?: any;
	}

	// handcrafted
	export interface CommonDataServiceSource extends CopySource {
		query?: any;
	}

	// handcrafted
	export interface DynamicsCrmSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DocumentDbCollectionSource class.
	 * @constructor
	 * A copy activity Document Database Collection source.
	 *
	 * @member {object} [query] Documents query. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [nestingSeparator] Nested properties separator. Type:
	 * string (or Expression with resultType string).
	 */
	export interface DocumentDbCollectionSource extends CopySource {
		query?: any;
		nestingSeparator?: any;
	}

	// handcrafted
	export interface CosmosDbSqlApiSource extends CopySource {
		query?: any;
		pageSize?: any;
		detectDatetime?: any;
		preferredRegions?: any;
		includeSystemColumns?: any;
	}


	/**
	 * @class
	 * Initializes a new instance of the BlobSource class.
	 * @constructor
	 * A copy activity Azure Blob source.
	 *
	 * @member {object} [treatEmptyAsNull] Treat empty as null. Type: boolean (or
	 * Expression with resultType boolean).
	 * @member {object} [skipHeaderLineCount] Number of header lines to skip from
	 * each blob. Type: integer (or Expression with resultType integer).
	 * @member {object} [recursive] If true, files under the folder path will be
	 * read recursively. Default is true. Type: boolean (or Expression with
	 * resultType boolean).
	 */
	export interface BlobSource extends CopySource {
		treatEmptyAsNull?: any;
		skipHeaderLineCount?: any;
		recursive?: any;
	}

	// Handcrafted
	export interface AzureBlobFSSource extends CopySource {
		treatEmptyAsNull?: any;
		skipHeaderLineCount?: any;
		recursive?: any;
	}

	// handcraft
	export interface AdobeExperiencePlatformSource extends CopySource {
		completedDateTimeStart?: any;
		completedDateTimeEnd?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureTableSource class.
	 * @constructor
	 * A copy activity Azure Table source.
	 *
	 * @member {object} [azureTableSourceQuery] Azure Table source query. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [azureTableSourceIgnoreTableNotFound] Azure Table source
	 * ignore table not found. Type: boolean (or Expression with resultType
	 * boolean).
	 */
	export interface AzureTableSource extends CopySource {
		azureTableSourceQuery?: any;
		azureTableSourceIgnoreTableNotFound?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the LookupActivityTypeProperties class.
	 * @constructor
	 * Lookup activity properties.
	 *
	 * @member {object} source Dataset-specific source properties, same as copy
	 * activity source.
	 * @member {object} [source.sourceRetryCount] Source retry count. Type: integer
	 * (or Expression with resultType integer).
	 * @member {object} [source.sourceRetryWait] Source retry wait. Type: string
	 * (or Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {string} [source.type] Polymorphic Discriminator
	 * @member {object} dataset Lookup activity dataset reference.
	 * @member {string} [dataset.referenceName] Reference dataset name.
	 * @member {object} [dataset.parameters] Arguments for dataset.
	 * @member {object} [firstRowOnly] Whether to return first row or all rows.
	 * Default value is true. Type: boolean (or Expression with resultType
	 * boolean).
	 */
	export interface LookupActivityTypeProperties {
		source: CopySource;
		dataset?: DatasetReference;
		datasetSettings?: any;
		firstRowOnly?: any;
		connectionProperties?: { [propertyName: string]: any };
	}

	/**
	 * @class
	 * Initializes a new instance of the LookupActivity class.
	 * @constructor
	 * Lookup activity.
	 *
	 * @member {object} typeProperties Lookup activity properties.
	 * @member {object} [typeProperties.source] Dataset-specific source properties,
	 * same as copy activity source.
	 * @member {object} [typeProperties.source.sourceRetryCount] Source retry
	 * count. Type: integer (or Expression with resultType integer).
	 * @member {object} [typeProperties.source.sourceRetryWait] Source retry wait.
	 * Type: string (or Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {string} [typeProperties.source.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.dataset] Lookup activity dataset reference.
	 * @member {string} [typeProperties.dataset.referenceName] Reference dataset
	 * name.
	 * @member {object} [typeProperties.dataset.parameters] Arguments for dataset.
	 * @member {object} [typeProperties.firstRowOnly] Whether to return first row
	 * or all rows. Default value is true. Type: boolean (or Expression with
	 * resultType boolean).
	 */
	export interface LookupActivity extends ExecutionActivity {
		typeProperties: LookupActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlServerStoredProcedureActivityTypeProperties class.
	 * @constructor
	 * SQL stored procedure activity properties.
	 *
	 * @member {object} storedProcedureName Stored procedure name. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [storedProcedureParameters] Value and type setting for
	 * stored procedure parameters. Example: "{Parameter1: {value: "1", type:
	 * "int"}}".
	 */
	export interface SqlServerStoredProcedureActivityTypeProperties {
		database?: any;
		storedProcedureName: any;
		storedProcedureParameters?: { [propertyName: string]: StoredProcedureParameter } | SharedModel.ExpressionValue;
		enforceOneTimeExecution?: any;
		connectionProperties?: { [propertyName: string]: any };
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlServerStoredProcedureActivity class.
	 * @constructor
	 * SQL stored procedure activity type.
	 *
	 * @member {object} typeProperties SQL stored procedure activity properties.
	 * @member {object} [typeProperties.storedProcedureName] Stored procedure name.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.storedProcedureParameters] Value and type
	 * setting for stored procedure parameters. Example: "{Parameter1: {value: "1",
	 * type: "int"}}".
	 */
	export interface SqlServerStoredProcedureActivity extends ExecutionActivity {
		typeProperties: SqlServerStoredProcedureActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlScriptActivityTypePropertiesParameters class.
	 * @constructor
	 * SQL script activity parameters.
	 *
	 * @member {string} name The name of the parameter.
	 * @member {string} value The value of the parameter.
	 * @member {string} type The data type of the parameter.
	 * The type is logical type and follows type mapping of each connector.
	 * @member {string} direction The direction of the paramter.
	 * It can be 'Input', 'Output', 'InputOutput'.
	 * The value is ignored if the direction is Output.
	 * @member {int} size The max size of the parameter.
	 * Only applies to Output direction parameter of type string/byte[].
	 */
	export interface SqlScriptActivityTypePropertiesParameters {
		name: string;
		value: any;
		type: string;
		direction: string;
		size?: number;
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlScriptActivityTypePropertiesPlainScript class.
	 * @constructor
	 * SQL script activity script properties.
	 *
	 * @member {string} text The plain text of a block of queries.
	 * @member {string} type The type of the block of queries. It can be 'Query' or 'NonQuery'. Defaut to Query.
	 * @member {object} parameters The array of parameters of the script.
	 */
	export interface SqlScriptActivityTypePropertiesPlainScript {
		text: string;
		type: string;
		parameters: Array<SqlScriptActivityTypePropertiesParameters>;
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlScriptActivityTypeProperties class.
	 * @constructor
	 * SQL script activity properties.
	 *
	 * @member {object} scripts An array of objects to represent the script.
	 * @member {string} scriptBlockExecutionTimeout The execution of each script block
	 * @member {object} logSettings The settings to store the output logs. If not specified, script log is disabled.
	 * @member {string} [logSettings.logDestination] The destination of log output.
	 * It can be ActivityOutput or ExternalStore. Default o ActivityOutput.
	 * @member {object} [logSettings.logLocationSettings] the settings of the target location if logDestination is ExternalStore.
	 * @member {object} [logSettings.logLocationSettings.linkedServiceName] The linked service of the target location. Only blob storage is supported.
	 * @member {string} [logSettings.logLocationSettings.path] The folder path under which logs will be stored.
	 * @member {object} connectionVersion Snowflake V2 to support script parameter
	 */
	export interface SqlScriptActivityTypeProperties {
		scripts?: Array<SqlScriptActivityTypePropertiesPlainScript>;
		scriptBlockExecutionTimeout?: any;
		logSettings?: {
			logDestination: string;
			logLocationSettings?: {
				linkedServiceName: SharedModel.LinkedServiceReference;
				path: string;
			}
		};
		database?: any;
		connectionVersion?: any;
		treatDecimalAsString?: any;
		connectionProperties?: { [propertyName: string]: any };
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlScriptActivity class.
	 * @constructor
	 * SQL stored procedure activity type.
	 *
	 * @member {object} typeProperties SQL script activity properties.
	 */
	export interface SqlScriptActivity extends ExecutionActivity {
		typeProperties: SqlScriptActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the CustomActivityReferenceObject class.
	 * @constructor
	 * Reference objects for custom activity
	 *
	 * @member {array} [linkedServices] Linked service references.
	 * @member {array} [datasets] Dataset references.
	 */
	export interface CustomActivityReferenceObject {
		linkedServices?: SharedModel.LinkedServiceReference[];
		datasets?: DatasetReference[];
	}

	/**
	 * @class
	 * Initializes a new instance of the CustomActivityTypeProperties class.
	 * @constructor
	 * Custom activity properties.
	 *
	 * @member {object} command Command for custom activity Type: string (or
	 * Expression with resultType string).
	 * @member {object} [resourceLinkedService] Resource linked service reference.
	 * @member {string} [resourceLinkedService.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [resourceLinkedService.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [folderPath] Folder path for resource files Type: string
	 * (or Expression with resultType string).
	 * @member {object} [referenceObjects] Reference objects
	 * @member {array} [referenceObjects.linkedServices] Linked service references.
	 * @member {array} [referenceObjects.datasets] Dataset references.
	 * @member {object} [extendedProperties] User defined property bag. There is no
	 * restriction on the keys or values that can be used. The user specified
	 * custom activity has the full responsibility to consume and interpret the
	 * content defined.
	 */
	export interface CustomActivityTypeProperties {
		command: any;
		resourceLinkedService?: SharedModel.LinkedServiceReference;
		folderPath?: any;
		referenceObjects?: CustomActivityReferenceObject;
		extendedProperties?: { [propertyName: string]: any };
		retentionTimeInDays?: number;
	}

	export interface CustomActivityStagingProperties {
		linkedServiceName: SharedModel.LinkedServiceReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the CustomActivity class.
	 * @constructor
	 * Custom activity type.
	 *
	 * @member {object} typeProperties Custom activity properties.
	 * @member {object} [typeProperties.command] Command for custom activity Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.resourceLinkedService] Resource linked
	 * service reference.
	 * @member {string} [typeProperties.resourceLinkedService.referenceName]
	 * Reference LinkedService name.
	 * @member {object} [typeProperties.resourceLinkedService.parameters] Arguments
	 * for LinkedService.
	 * @member {object} [typeProperties.folderPath] Folder path for resource files
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.referenceObjects] Reference objects
	 * @member {array} [typeProperties.referenceObjects.linkedServices] Linked
	 * service references.
	 * @member {array} [typeProperties.referenceObjects.datasets] Dataset
	 * references.
	 * @member {object} [typeProperties.extendedProperties] User defined property
	 * bag. There is no restriction on the keys or values that can be used. The
	 * user specified custom activity has the full responsibility to consume and
	 * interpret the content defined.
	 * @member {object} [stagingLinkedService] Staging linked
	 * service reference.
	 */
	export interface CustomActivity extends ExecutionActivity {
		typeProperties: CustomActivityTypeProperties;
		staging?: CustomActivityStagingProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SSISPropertyOverride class.
	 * @constructor
	 * SSIS property override.
	 *
	 * @member {object} value SSIS package property override value. Type: string
	 * (or Expression with resultType string).
	 * @member {boolean} [isSensitive] Whether SSIS package property override value
	 * is sensitive data. Value will be encrypted in SSISDB if it is true
	 */
	export interface SSISPropertyOverride {
		value: any;
		isSensitive?: boolean;
	}

	/**
	 * @class
	 * Initializes a new instance of the SSISExecutionParameter class.
	 * @constructor
	 * SSIS execution parameter.
	 *
	 * @member {object} value SSIS package execution parameter value. Type: string
	 * (or Expression with resultType string).
	 */
	export interface SSISExecutionParameter {
		value: any;
	}

	/**
		   * @class
		   * Initializes a new instance of the SSISExecutionCredential class.
	 * @constructor
	 *  SSIS package execution credential.
	 *
		   * @member {object} [domain] Domain for windows authentication.
	 * @member {object} [userName] UseName for windows authentication.
	 * @member {object} [password] Password for windows authentication.
	 * @member {object} [password.value] Value of secure string.
	 */
	// handcrafted
	export interface SSISExecutionCredential {
		domain: any;
		userName: any;
		password: any;
	}

	/**
		   * @class
		   * Initializes a new instance of the SSISAccessCredential class.
	 * @constructor
	 *  SSIS package execution credential.
	 *
		   * @member {object} [domain] Domain for windows authentication.
	 * @member {object} [userName] UseName for windows authentication.
	 * @member {object} [password] Password for windows authentication.
	 */
	// handcrafted
	export interface SSISAccessCredential {
		domain: any;
		userName: any;
		password: any;
	}

	/**
		   * @class
		   * Initializes a new instance of the SSISChildPackage class.
	 * @constructor
	 *  SSIS Child Package (Execution Package Task).
	 *
		   * @member {object} [packagePath] Path for embedded child package. Type: string (or Expression with resultType string).
	 * @member {string} [packageName] Name for embedded child package.
	 * @member {object} [packageContent] Content for embedded child package. Type: string (or Expression with resultType string).
	 * @member {string} [packageLastModifiedDate] Last modified date for embedded child package.
	 */
	// handcrafted
	export interface SSISChildPackage {
		packagePath: any;
		packageName?: string;
		packageContent: any;
		packageLastModifiedDate?: string;
	}

	/**
		   * @class
		   * Initializes a new instance of the SSISPackageLocationTypeProperties class.
	 * @constructor
	 *  SSIS package location properties.
	 *
		   * @member {object} [packagePassword] Password of the package.
	 * @member {object} [accessCredential] The package access credential.
	 * @member {string} [packageName] The package name.
	 * @member {object} [packageContent] The embedded package content. Type: string (or Expression with resultType string).
	 * @member {string} [packageLastModifiedDate] The embedded package last modified date.
	 * @member {string} [childPackages] The embedded child package list.
	 * @member {object} [configurationPath] The configuration file of the package execution. Type: string (or Expression with resultType string).
	 * @member {object} [configurationAccessCredential] the configuration file access credential
	 * @member {object} [sqlLinkedService] the Sql linked service reference
	 */
	// handcrafted
	export interface SSISPackageLocationTypeProperties {
		packagePassword?: any;
		accessCredential?: SSISAccessCredential;
		packageName?: string;
		packageContent?: any;
		packageLastModifiedDate?: string;
		childPackages?: SSISChildPackage[];
		configurationPath?: any;
		configurationAccessCredential?: SSISAccessCredential;
		sqlLinkedService?: SharedModel.LinkedServiceReference;
		connectionSettings?: SharedModel.LinkedServiceResource;
	}

	/**
		   * @class
		   * Initializes a new instance of the SSISPackageLocation class.
	 * @constructor
	 *  SSIS package location.
	 *
		   * @member {object} [packagePath] The SSIS package path. Type: string (or Expression with resultType string).
	 * @member {string} [type] The type of SSIS package location. Possible values include: 'SSISDB', 'File'
	 * @member {object} [typeProperties] SSIS package location properties.
	 */
	// handcrafted
	export interface SSISPackageLocation {
		packagePath?: any;
		projectPath?: any;
		type?: string;
		typeProperties?: SSISPackageLocationTypeProperties;
	}

	/**
		   * @class
		   * Initializes a new instance of the SSISLogLocationTypeProperties class.
	 * @constructor
	 *  SSIS package execution log location properties.
	 *
	 * @member {object} [accessCredential] The log access credential.
	 */
	// handcrafted
	export interface SSISLogLocationTypeProperties {
		accessCredential?: SSISAccessCredential;
	}

	/**
		   * @class
		   * Initializes a new instance of the SSISLogLocation class.
	 * @constructor
	 *  SSIS package exection log location
	 *
		   * @member {object} [logPath] The SSIS package execution log path. Type: string (or Expression with resultType string).
	 * @member {string} [type] The type of SSIS log location.
	 * @member {object} [typeProperties] SSIS package location properties.
	 */
	// handcrafted
	export interface SSISLogLocation {
		logPath?: any;
		tableNamePrefix?: any;
		type: string;
		typeProperties?: SSISPackageLocationTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ExecuteSSISPackageActivityTypeProperties class.
	 * @constructor
	 * Execute SSIS package activity properties.
	 *
	 * @member {object} packageLocation SSIS package location.
	 * @member {string} [packageLocation.packagePath] The SSIS package path.
	 * @member {string} [runtime] Specifies the runtime to execute SSIS package.
	 * Possible values include: 'x64', 'x86'
	 * @member {string} [loggingLevel] The logging level of SSIS package execution.
	 * @member {string} [environmentPath] The environment path to execute the SSIS
	 * package.
	 * @member {object} executionCredential The package execution credential.
	 * @member {object} connectVia The integration runtime reference.
	 * @member {string} [connectVia.referenceName] Reference integration runtime
	 * name.
	 * @member {object} [connectVia.parameters] Arguments for integration runtime.
	 * @member {object} [projectParameters] The project level parameters to execute
	 * the SSIS package.
	 * @member {object} [packageParameters] The package level parameters to execute
	 * the SSIS package.
	 * @member {object} [projectConnectionManagers] The project level connection
	 * managers to execute the SSIS package.
	 * @member {object} [packageConnectionManagers] The package level connection
	 * managers to execute the SSIS package.
	 * @member {object} [propertyOverrides] The property overrides to execute the
	 * SSIS package.
	 * @member {object} [logLocation] SSIS package exection log location.
	 */
	// handcrafted
	export interface ExecuteSSISPackageActivityTypeProperties {
		packageLocation: SSISPackageLocation;
		runtime?: string;
		loggingLevel?: string;
		environmentPath?: string;
		executionCredential?: { [propertyName: string]: SSISExecutionCredential }; 	// handcrafted
		connectVia: SharedModel.IntegrationRuntimeReference;
		projectParameters?: { [propertyName: string]: SSISExecutionParameter };
		packageParameters?: { [propertyName: string]: SSISExecutionParameter };
		projectConnectionManagers?: { [propertyName: string]: { [propertyName: string]: SSISExecutionParameter } };
		packageConnectionManagers?: { [propertyName: string]: { [propertyName: string]: SSISExecutionParameter } };
		propertyOverrides?: { [propertyName: string]: SSISPropertyOverride };
		logLocation?: SSISLogLocation;
	}

	/**
	 * @class
	 * Initializes a new instance of the ExecuteSSISPackageActivityTypeProperties class.
	 * @constructor
	 * Execute SSIS package activity properties.
	 *
	 * @member {object} packageLocation SSIS package location.
	 * @member {string} [packageLocation.packagePath] The SSIS package path.
	 * @member {string} [loggingLevel] The logging level of SSIS package execution.
	 * @member {string} [environmentPath] The environment path to execute the SSIS
	 * package.
	 * @member {object} executionCredential The package execution credential.
	 * @member {object} connectVia The integration runtime reference.
	 * @member {string} [connectVia.referenceName] Reference integration runtime
	 * name.
	 * @member {object} [connectVia.parameters] Arguments for integration runtime.
	 * @member {object} [projectParameters] The project level parameters to execute
	 * the SSIS package.
	 * @member {object} [packageParameters] The package level parameters to execute
	 * the SSIS package.
	 * @member {object} [projectConnectionManagers] The project level connection
	 * managers to execute the SSIS package.
	 * @member {object} [packageConnectionManagers] The package level connection
	 * managers to execute the SSIS package.
	 * @member {object} [propertyOverrides] The property overrides to execute the
	 * SSIS package.
	 * @member {object} [logLocation] SSIS package exection log location.
	 */
	// handcrafted
	export interface InvokeSSISPackageActivityTypeProperties {
		packageLocation: SSISPackageLocation;
		loggingLevel?: string;
		// executionCredential?: { [propertyName: string]: SSISExecutionCredential }; 	// handcrafted
		projectParameters?: { [propertyName: string]: SSISExecutionParameter };
		packageParameters?: { [propertyName: string]: SSISExecutionParameter };
		projectConnectionManagers?: { [propertyName: string]: { [propertyName: string]: SSISExecutionParameter } };
		packageConnectionManagers?: { [propertyName: string]: { [propertyName: string]: SSISExecutionParameter } };
		propertyOverrides?: { [propertyName: string]: SSISPropertyOverride };
		logLocation?: SSISLogLocation;
	}

	/**
	 * @class
	 * Initializes a new instance of the ExecuteSSISPackageActivity class.
	 * @constructor
	 * Execute SSIS package activity.
	 *
	 * @member {object} typeProperties Execute SSIS package activity properties.
	 * @member {object} [typeProperties.packageLocation] SSIS package location.
	 * @member {string} [typeProperties.packageLocation.packagePath] The SSIS
	 * package path.
	 * @member {string} [typeProperties.runtime] Specifies the runtime to execute
	 * SSIS package. Possible values include: 'x64', 'x86'
	 * @member {string} [typeProperties.loggingLevel] The logging level of SSIS
	 * package execution.
	 * @member {string} [typeProperties.environmentPath] The environment path to
	 * execute the SSIS package.
	 * @member {object} [typeProperties.connectVia] The integration runtime
	 * reference.
	 * @member {string} [typeProperties.connectVia.referenceName] Reference
	 * integration runtime name.
	 * @member {object} [typeProperties.connectVia.parameters] Arguments for
	 * integration runtime.
	 * @member {object} [typeProperties.projectParameters] The project level
	 * parameters to execute the SSIS package.
	 * @member {object} [typeProperties.packageParameters] The package level
	 * parameters to execute the SSIS package.
	 * @member {object} [typeProperties.projectConnectionManagers] The project
	 * level connection managers to execute the SSIS package.
	 * @member {object} [typeProperties.packageConnectionManagers] The package
	 * level connection managers to execute the SSIS package.
	 * @member {object} [typeProperties.propertyOverrides] The property overrides
	 * to execute the SSIS package.
	 */
	export interface ExecuteSSISPackageActivity extends ExecutionActivity {
		typeProperties: ExecuteSSISPackageActivityTypeProperties;
	}

	export interface InvokeSSISPackageActivity extends ExecutionActivity {
		typeProperties: InvokeSSISPackageActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightSparkActivityTypeProperties class.
	 * @constructor
	 * HDInsight spark activity properties.
	 *
	 * @member {object} rootPath The root path in 'sparkJobLinkedService' for all
	 * the job’s files. Type: string (or Expression with resultType string).
	 * @member {object} entryFilePath The relative path to the root folder of the
	 * code/package to be executed. Type: string (or Expression with resultType
	 * string).
	 * @member {array} [arguments] The user-specified arguments to
	 * HDInsightSparkActivity.
	 * @member {string} [getDebugInfo] Debug info option. Possible values include:
	 * 'None', 'Always', 'Failure'
	 * @member {object} [sparkJobLinkedService] The storage linked service for
	 * uploading the entry file and dependencies, and for receiving logs.
	 * @member {string} [sparkJobLinkedService.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [sparkJobLinkedService.parameters] Arguments for
	 * LinkedService.
	 * @member {string} [className] The application's Java/Spark main class.
	 * @member {object} [proxyUser] The user to impersonate that will execute the
	 * job. Type: string (or Expression with resultType string).
	 * @member {object} [sparkConfig] Spark configuration property.
	 */
	export interface HDInsightSparkActivityTypeProperties {
		rootPath: any;
		entryFilePath: any;
		arguments?: any[];
		getDebugInfo?: string;
		sparkJobLinkedService?: SharedModel.LinkedServiceReference;
		className?: string;
		proxyUser?: any;
		sparkConfig?: { [propertyName: string]: any };
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightSparkActivity class.
	 * @constructor
	 * HDInsight Spark activity.
	 *
	 * @member {object} typeProperties HDInsight spark activity properties.
	 * @member {object} [typeProperties.rootPath] The root path in
	 * 'sparkJobLinkedService' for all the job’s files. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [typeProperties.entryFilePath] The relative path to the
	 * root folder of the code/package to be executed. Type: string (or Expression
	 * with resultType string).
	 * @member {array} [typeProperties.arguments] The user-specified arguments to
	 * HDInsightSparkActivity.
	 * @member {string} [typeProperties.getDebugInfo] Debug info option. Possible
	 * values include: 'None', 'Always', 'Failure'
	 * @member {object} [typeProperties.sparkJobLinkedService] The storage linked
	 * service for uploading the entry file and dependencies, and for receiving
	 * logs.
	 * @member {string} [typeProperties.sparkJobLinkedService.referenceName]
	 * Reference LinkedService name.
	 * @member {object} [typeProperties.sparkJobLinkedService.parameters] Arguments
	 * for LinkedService.
	 * @member {string} [typeProperties.className] The application's Java/Spark
	 * main class.
	 * @member {object} [typeProperties.proxyUser] The user to impersonate that
	 * will execute the job. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.sparkConfig] Spark configuration property.
	 */
	export interface HDInsightSparkActivity extends ExecutionActivity {
		typeProperties: HDInsightSparkActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightStreamingActivityTypeProperties class.
	 * @constructor
	 * HDInsight streaming activity properties.
	 *
	 * @member {array} [storageLinkedServices] Storage linked service references.
	 * @member {array} [arguments] User specified arguments to HDInsightActivity.
	 * @member {string} [getDebugInfo] Debug info option. Possible values include:
	 * 'None', 'Always', 'Failure'
	 * @member {object} mapper Mapper executable name. Type: string (or Expression
	 * with resultType string).
	 * @member {object} reducer Reducer executable name. Type: string (or
	 * Expression with resultType string).
	 * @member {object} input Input blob path. Type: string (or Expression with
	 * resultType string).
	 * @member {object} output Output blob path. Type: string (or Expression with
	 * resultType string).
	 * @member {array} filePaths Paths to streaming job files. Can be directories.
	 * @member {object} [fileLinkedService] Linked service reference where the
	 * files are located.
	 * @member {string} [fileLinkedService.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [fileLinkedService.parameters] Arguments for LinkedService.
	 * @member {object} [combiner] Combiner executable name. Type: string (or
	 * Expression with resultType string).
	 * @member {array} [commandEnvironment] Command line environment values.
	 * @member {object} [defines] Allows user to specify defines for streaming job
	 * request.
	 */
	export interface HDInsightStreamingActivityTypeProperties {
		storageLinkedServices?: SharedModel.LinkedServiceReference[];
		arguments?: any[];
		getDebugInfo?: string;
		mapper: any;
		reducer: any;
		input: any;
		output: any;
		filePaths: any[];
		fileLinkedService?: SharedModel.LinkedServiceReference;
		combiner?: any;
		commandEnvironment?: any[];
		defines?: { [propertyName: string]: any };
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightStreamingActivity class.
	 * @constructor
	 * HDInsight streaming activity type.
	 *
	 * @member {object} typeProperties HDInsight streaming activity properties.
	 * @member {array} [typeProperties.storageLinkedServices] Storage linked
	 * service references.
	 * @member {array} [typeProperties.arguments] User specified arguments to
	 * HDInsightActivity.
	 * @member {string} [typeProperties.getDebugInfo] Debug info option. Possible
	 * values include: 'None', 'Always', 'Failure'
	 * @member {object} [typeProperties.mapper] Mapper executable name. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.reducer] Reducer executable name. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [typeProperties.input] Input blob path. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.output] Output blob path. Type: string (or
	 * Expression with resultType string).
	 * @member {array} [typeProperties.filePaths] Paths to streaming job files. Can
	 * be directories.
	 * @member {object} [typeProperties.fileLinkedService] Linked service reference
	 * where the files are located.
	 * @member {string} [typeProperties.fileLinkedService.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [typeProperties.fileLinkedService.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [typeProperties.combiner] Combiner executable name. Type:
	 * string (or Expression with resultType string).
	 * @member {array} [typeProperties.commandEnvironment] Command line environment
	 * values.
	 * @member {object} [typeProperties.defines] Allows user to specify defines for
	 * streaming job request.
	 */
	export interface HDInsightStreamingActivity extends ExecutionActivity {
		typeProperties: HDInsightStreamingActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightMapReduceActivityTypeProperties class.
	 * @constructor
	 * HDInsight MapReduce activity properties.
	 *
	 * @member {array} [storageLinkedServices] Storage linked service references.
	 * @member {array} [arguments] User specified arguments to HDInsightActivity.
	 * @member {string} [getDebugInfo] Debug info option. Possible values include:
	 * 'None', 'Always', 'Failure'
	 * @member {object} className Class name. Type: string (or Expression with
	 * resultType string).
	 * @member {object} jarFilePath Jar path. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [jarLinkedService] Jar linked service reference.
	 * @member {string} [jarLinkedService.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [jarLinkedService.parameters] Arguments for LinkedService.
	 * @member {array} [jarLibs] Jar libs.
	 * @member {object} [defines] Allows user to specify defines for the MapReduce
	 * job request.
	 */
	export interface HDInsightMapReduceActivityTypeProperties {
		storageLinkedServices?: SharedModel.LinkedServiceReference[];
		arguments?: any[];
		getDebugInfo?: string;
		className: any;
		jarFilePath: any;
		jarLinkedService?: SharedModel.LinkedServiceReference;
		jarLibs?: any[];
		defines?: { [propertyName: string]: any };
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightMapReduceActivity class.
	 * @constructor
	 * HDInsight MapReduce activity type.
	 *
	 * @member {object} typeProperties HDInsight MapReduce activity properties.
	 * @member {array} [typeProperties.storageLinkedServices] Storage linked
	 * service references.
	 * @member {array} [typeProperties.arguments] User specified arguments to
	 * HDInsightActivity.
	 * @member {string} [typeProperties.getDebugInfo] Debug info option. Possible
	 * values include: 'None', 'Always', 'Failure'
	 * @member {object} [typeProperties.className] Class name. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.jarFilePath] Jar path. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.jarLinkedService] Jar linked service
	 * reference.
	 * @member {string} [typeProperties.jarLinkedService.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [typeProperties.jarLinkedService.parameters] Arguments for
	 * LinkedService.
	 * @member {array} [typeProperties.jarLibs] Jar libs.
	 * @member {object} [typeProperties.defines] Allows user to specify defines for
	 * the MapReduce job request.
	 */
	export interface HDInsightMapReduceActivity extends ExecutionActivity {
		typeProperties: HDInsightMapReduceActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightPigActivityTypeProperties class.
	 * @constructor
	 * HDInsight Pig activity properties.
	 *
	 * @member {array} [storageLinkedServices] Storage linked service references.
	 * @member {array} [arguments] User specified arguments to HDInsightActivity.
	 * @member {string} [getDebugInfo] Debug info option. Possible values include:
	 * 'None', 'Always', 'Failure'
	 * @member {object} [scriptPath] Script path. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [scriptLinkedService] Script linked service reference.
	 * @member {string} [scriptLinkedService.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [scriptLinkedService.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [defines] Allows user to specify defines for Pig job
	 * request.
	 */
	export interface HDInsightPigActivityTypeProperties {
		storageLinkedServices?: SharedModel.LinkedServiceReference[];
		arguments?: any[];
		getDebugInfo?: string;
		scriptPath?: any;
		scriptLinkedService?: SharedModel.LinkedServiceReference;
		defines?: { [propertyName: string]: any };
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightPigActivity class.
	 * @constructor
	 * HDInsight Pig activity type.
	 *
	 * @member {object} typeProperties HDInsight Pig activity properties.
	 * @member {array} [typeProperties.storageLinkedServices] Storage linked
	 * service references.
	 * @member {array} [typeProperties.arguments] User specified arguments to
	 * HDInsightActivity.
	 * @member {string} [typeProperties.getDebugInfo] Debug info option. Possible
	 * values include: 'None', 'Always', 'Failure'
	 * @member {object} [typeProperties.scriptPath] Script path. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.scriptLinkedService] Script linked service
	 * reference.
	 * @member {string} [typeProperties.scriptLinkedService.referenceName]
	 * Reference LinkedService name.
	 * @member {object} [typeProperties.scriptLinkedService.parameters] Arguments
	 * for LinkedService.
	 * @member {object} [typeProperties.defines] Allows user to specify defines for
	 * Pig job request.
	 */
	export interface HDInsightPigActivity extends ExecutionActivity {
		typeProperties: HDInsightPigActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightHiveActivityTypeProperties class.
	 * @constructor
	 * HDInsight Hive activity properties.
	 *
	 * @member {array} [storageLinkedServices] Storage linked service references.
	 * @member {array} [arguments] User specified arguments to HDInsightActivity.
	 * @member {string} [getDebugInfo] Debug info option. Possible values include:
	 * 'None', 'Always', 'Failure'
	 * @member {object} [scriptPath] Script path. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [scriptLinkedService] Script linked service reference.
	 * @member {string} [scriptLinkedService.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [scriptLinkedService.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [defines] Allows user to specify defines for Hive job
	 * request.
	 */
	export interface HDInsightHiveActivityTypeProperties {
		storageLinkedServices?: SharedModel.LinkedServiceReference[];
		arguments?: any[];
		getDebugInfo?: string;
		scriptPath?: any;
		scriptLinkedService?: SharedModel.LinkedServiceReference;
		defines?: { [propertyName: string]: any };
		variables?: { [propertyName: string]: any }; //handcrafted
		queryTimeout?: number; //handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightHiveActivity class.
	 * @constructor
	 * HDInsight Hive activity type.
	 *
	 * @member {object} typeProperties HDInsight Hive activity properties.
	 * @member {array} [typeProperties.storageLinkedServices] Storage linked
	 * service references.
	 * @member {array} [typeProperties.arguments] User specified arguments to
	 * HDInsightActivity.
	 * @member {string} [typeProperties.getDebugInfo] Debug info option. Possible
	 * values include: 'None', 'Always', 'Failure'
	 * @member {object} [typeProperties.scriptPath] Script path. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.scriptLinkedService] Script linked service
	 * reference.
	 * @member {string} [typeProperties.scriptLinkedService.referenceName]
	 * Reference LinkedService name.
	 * @member {object} [typeProperties.scriptLinkedService.parameters] Arguments
	 * for LinkedService.
	 * @member {object} [typeProperties.defines] Allows user to specify defines for
	 * Hive job request.
	 */
	export interface HDInsightHiveActivity extends ExecutionActivity {
		typeProperties: HDInsightHiveActivityTypeProperties;
	}

	export interface AzureHDInsightActivityScriptSettings {
		scriptPath?: any;
		fileConnection?: SharedModel.LinkedServiceReference;
	}

	export interface AzureHDInsightActivityJarSettings {
		className: any;
		jarFilePath?: any;
		fileConnection?: SharedModel.LinkedServiceReference;
		jarLibs?: any[];
	}

	export interface AzureHDInsightActivitySparkSettings {
		rootPath: any;
		entryFilePath: any;
		className?: any;
		proxyUser?: any;
		fileConnection?: SharedModel.LinkedServiceReference;
		sparkConfig?: { [propertyName: string]: any };
	}

	export interface AzureHDInsightActivityFileSettings {
		input: any;
		output: any;
		filePaths: any[];
		fileConnection?: SharedModel.LinkedServiceReference;
	}

	export interface AzureHDInsightActivityClusterSettings {
		fileConnection?: SharedModel.LinkedServiceReference;
		clusterSize?: any;
		timeToLive?: any;
		version?: any;
		clusterNamePrefix?: any;
		clusterUserName?: any;
		clusterPassword?: SharedModel.SecretBase;
		clusterSshUserName?: any;
		clusterSshPassword?: SharedModel.SecretBase;
		clusterType?: any;
		osType?: string; // handcrafted
		scriptActions?: any[]; // handcrafted
		sparkVersion?: any;
		coreConfiguration?: any;
		hBaseConfiguration?: any;
		hdfsConfiguration?: any;
		hiveConfiguration?: any;
		mapReduceConfiguration?: any;
		oozieConfiguration?: any;
		stormConfiguration?: any;
		yarnConfiguration?: any;
		encryptedCredential?: any;
		headNodeSize?: string; // handcrafted
		dataNodeSize?: string; // handcrafted
		zookeeperNodeSize?: string; // handcrafted
	}

	export interface AzureHDInsightActivityHCatalogSettings {
		fileConnection?: SharedModel.LinkedServiceReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the HDInsightHiveActivityTypeProperties class.
	 * @constructor
	 * HDInsight Hive activity properties.
	 *
	 * @member {array} [storageLinkedServices] Storage linked service references.
	 * @member {array} [arguments] User specified arguments to HDInsightActivity.
	 * @member {string} [getDebugInfo] Debug info option. Possible values include:
	 * 'None', 'Always', 'Failure'
	 * @member {object} [scriptPath] Script path. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [scriptLinkedService] Script linked service reference.
	 * @member {string} [scriptLinkedService.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [scriptLinkedService.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [defines] Allows user to specify defines for Hive job
	 * request.
	 */
	export interface AzureHDInsightActivityTypeProperties {
		storageLinkedServices?: SharedModel.LinkedServiceReference[];
		arguments?: any[];
		getDebugInfo?: string;
		mapper?: any;
		reducer?: any;
		combiner?: any;
		commandEnvironment?: any[];
		defines?: { [propertyName: string]: any };
		variables?: { [propertyName: string]: any }; //handcrafted
		queryTimeout?: number; //handcrafted
		scriptSettings?: AzureHDInsightActivityScriptSettings; //handcrafted
		jarSettings?: AzureHDInsightActivityJarSettings; //handcrafted
		sparkSettings?: AzureHDInsightActivitySparkSettings; //handcrafted
		fileSettings?: AzureHDInsightActivityFileSettings; //handcrafted
		clusterSettings?: AzureHDInsightActivityClusterSettings; //handcrafted
		hCatalogSettings?: AzureHDInsightActivityHCatalogSettings;
		hdiActivityType: string; //handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureHDInsightActivity class.
	 * @constructor
	 * Azure HDInsight activity type.
	 *
	 * @member {object} typeProperties AzureHDInsightActivity activity properties.
	 * @member {array} [typeProperties.storageLinkedServices] Storage linked
	 * service references.
	 * @member {array} [typeProperties.arguments] User specified arguments to
	 * HDInsightActivity.
	 * @member {string} [typeProperties.getDebugInfo] Debug info option. Possible
	 * values include: 'None', 'Always', 'Failure'
	 * @member {object} [typeProperties.scriptPath] Script path. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.scriptLinkedService] Script linked service
	 * reference.
	 * @member {string} [typeProperties.scriptLinkedService.referenceName]
	 * Reference LinkedService name.
	 * @member {object} [typeProperties.scriptLinkedService.parameters] Arguments
	 * for LinkedService.
	 * @member {object} [typeProperties.defines] Allows user to specify defines for
	 * Hive job request.
	 */
	export interface AzureHDInsightActivity extends ExecutionActivity {
		typeProperties: AzureHDInsightActivityTypeProperties;
	}

	// handcrafted
	export interface KustoActivityTypeProperties {
		database?: string;
		command?: string;
		commandType?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the KustoActivity class.
	 * @constructor
	 * Kusto activity type.
	 *
	 * @member {object} typeProperties Kusto activity properties.
	 * @member {object} [typeProperties.database] Database name.
	 * @member {object} [typeProperties.command] Kusto command to run. Type: string (or
	 * Expression with resultType string).
	 * @member {string} [typeProperties.commandType] Type of command.
	 */
	// handcrafted
	export interface KustoActivity extends ExecutionActivity {
		typeProperties: KustoActivityTypeProperties;
	}

	// handcrafted
	export interface KqlActivityTypeProperties {
		database?: string;
		command?: string;
		commandTimeout?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the KqlActivity class.
	 * @constructor
	 * KQL activity type.
	 *
	 * @member {object} typeProperties KQL activity properties.
	 * @member {object} [typeProperties.database] Database name.
	 * @member {object} [typeProperties.command] KQL command to run. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.commandTimeout] KQL command timeout. Type: string (or
	 * Expression with resultType string).
	 */
	// handcrafted
	export interface KqlActivity extends ExecutionActivity {
		typeProperties: KqlActivityTypeProperties;
	}

	// handcrafted
	export interface AzureDataExplorerCommandActivityTypeProperties {
		command: any;
		commandTimeout?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataExplorerCommandActivity class.
	 * @constructor
	 * AzureDataExplorerCommandActivity type.
	 *
	 * @member {object} typeProperties Azure Data Explorer Command Activity properties.
	 * @member {object} [typeProperties.command] Azure Data Explorer command to run. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.commandTimeout] Azure Data Explorer control command timeout. Type: string (or
	 * Expression with resultType string).
	 */
	// handcrafted
	export interface AzureDataExplorerCommandActivity extends ExecutionActivity {
		typeProperties: AzureDataExplorerCommandActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the RedirectIncompatibleRowSettings class.
	 * @constructor
	 * Redirect incompatible row settings
	 *
	 * @member {object} linkedServiceName Name of the Azure Storage, Storage SAS,
	 * or Azure Data Lake Store linked service used for redirecting incompatible
	 * row. Must be specified if redirectIncompatibleRowSettings is specified.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [path] The path for storing the redirect incompatible row
	 * data. Type: string (or Expression with resultType string).
	 */
	export interface RedirectIncompatibleRowSettings {
		linkedServiceName: any;
		path?: any;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	export interface LogStorageSettings {
		linkedServiceName: any;
		path?: any;
		logLevel?: any;
		enableReliableLogging?: any;
	}

	// handcrafted
	export interface LogSettings {
		enableCopyActivityLog: any;
		enableOdbcDiagnosticLog: any;
		copyActivityLogSettings?: {
			logLevel?: any;
			enableReliableLogging?: any;
		},
		logLocationSettings: {
			linkedServiceName: any;
			path?: any;
		}
	}

	//handcrafted
	export interface SkipErrorFileSettings {
		fileMissing?: any;
		dataInconsistency?: any;
		fileForbidden?: any;
		invalidFileName?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the StagingSettings class.
	 * @constructor
	 * Staging settings.
	 *
	 * @member {object} linkedServiceName Staging linked service reference.
	 * @member {string} [linkedServiceName.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [linkedServiceName.parameters] Arguments for LinkedService.
	 * @member {object} [path] The path to storage for storing the interim data.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [enableCompression] Specifies whether to use compression
	 * when copying data via an interim staging. Default value is false. Type:
	 * boolean (or Expression with resultType boolean).
	 */
	export interface StagingSettings {
		linkedServiceName: SharedModel.LinkedServiceReference;
		path?: any;
		enableCompression?: any;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the CopyTranslator class.
	 * @constructor
	 * A copy activity translator.
	 *
	 * @member {string} type Polymorphic Discriminator
	 */
	export interface CopyTranslator {
		type: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	// handcrafted
	export interface MappingItem {
		name?: string;
		ordinal?: number;
		path?: string;
		type?: string;
		physicalType?: string;
		culture?: string;
		format?: string;
		scale?: number;
		precision?: number;
		length?: string;
		unit?: string;
		isNullable?: boolean;
		parquetProperties?: IParquetPorperties;
	}

	// handcrafted
	export interface MappingPair {
		source?: MappingItem;
		sink?: MappingItem;
	}

	/**
	 * @class
	 * Initializes a new instance of the TabularTranslator class.
	 * @constructor
	 * A copy activity tabular translator.
	 *
	 * @member {object} [columnMappings] Column mappings. Example: "UserId:
	 * MyUserId, Group: MyGroup, Name: MyName" Type: string (or Expression with
	 * resultType string).
	 * @member {object} [schemaMapping] The schema mapping to map between tabular
	 * data and hierarchical data. Example: {"Column1": "$.Column1", "Column2":
	 * "$.Column2.Property1", "Column3": "$.Column2.Property2"}. Type: object (or
	 * Expression with resultType object).
	 */
	export interface TabularTranslator extends CopyTranslator {
		columnMappings?: any;
		schemaMapping?: any;
		mappings?: MappingPair[];
	}

	/**
	 * @class
	 * Initializes a new instance of the CopySink class.
	 * @constructor
	 * A copy activity sink.
	 *
	 * @member {object} [writeBatchSize] Write batch size. Type: integer (or
	 * Expression with resultType integer), minimum: 0.
	 * @member {object} [writeBatchTimeout] Write batch timeout. Type: string (or
	 * Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {object} [sinkRetryCount] Sink retry count. Type: integer (or
	 * Expression with resultType integer).
	 * @member {object} [sinkRetryWait] Sink retry wait. Type: string (or
	 * Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {string} type Polymorphic Discriminator
	 */
	export interface CopySink {
		writeBatchSize?: any;
		writeBatchTimeout?: any;
		sinkRetryCount?: any;
		sinkRetryWait?: any;
		maxConcurrentConnections?: any; // handcraft
		structure?: any; // handcraft
		type: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceSink class.
	 * @constructor
	 * A copy activity Salesforce sink.
	 *
	 * @member {string} [writeBehavior] The write behavior for the operation.
	 * Default is Insert. Possible values include: 'Insert', 'Upsert'
	 * @member {object} [externalIdFieldName] The name of the external ID field for
	 * upsert operation. Default value is 'Id' column. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [ignoreNullValues] The flag indicating whether or not to
	 * ignore null values from input dataset (except key fields) during write
	 * operation. Default value is false. If set it to true, it means ADF will
	 * leave the data in the destination object unchanged when doing upsert/update
	 * operation and insert defined default value when doing insert operation,
	 * versus ADF will update the data in the destination object to NULL when doing
	 * upsert/update operation and insert NULL value when doing insert operation.
	 * Type: boolean (or Expression with resultType boolean).
	 */
	export interface SalesforceSink extends CopySink {
		writeBehavior?: string;
		externalIdFieldName?: any;
		ignoreNullValues?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceV2Sink class.
	 * @constructor
	 * A copy activity Salesforce V2 sink.
	 */
	export interface SalesforceV2Sink extends SalesforceSink {
	}

	// handcrafted
	export interface SalesforceServiceCloudSink extends CopySink {
		writeBehavior?: string;
		externalIdFieldName?: any;
		ignoreNullValues?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SalesforceServiceCloudV2Sink class.
	 * @constructor
	 * A copy activity Salesforce Service Cloud V2 sink.
	 */
	export interface SalesforceServiceCloudV2Sink extends SalesforceV2Sink {
	}

	/**
 * @class
 * Initializes a new instance of the LakehouseTableSink class.
 * @constructor
 * A copy activity LakehouseTable sink.
 */
	export interface LakehouseTableSink extends CopySink {
		maxRowsPerFile?: any;
		tableActionOption?: any;
		partitionOption?: any;
		partitionNameList?: any;
		enableVertiParquet?: boolean;
		upsertSettings?: WriteBehaviorUpsertOptions;
		deltaSinkSettings?: {
			vacuum: number;
			autoCompact: boolean;
			optimizedWrite: boolean;
		},
		applyVOrder?: boolean;
		enableTimestampNtz?: boolean;
		enableChangeDataFeed?: boolean;
	}

	/**
* @class
* Initializes a new instance of the MountedRelationalDatabaseTableCopySink or Generic Mirror class.
* @constructor
* A copy activity MountedRelationalDatabaseTable sink.
*/
	export interface MountedRelationalDatabaseTableCopySink extends CopySink {
		keyColumns?: string[];
		rowMarkerColumn?: string;
		defaultRowMarker?: string;
		tableName?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the DynamicsSink class.
	 * @constructor
	 * A copy activity Dynamics sink.
	 *
	 * @member {object} [ignoreNullValues] The flag indicating whether ignore null
	 * values from input dataset (except key fields) during write operation.
	 * Default is false. Type: boolean (or Expression with resultType boolean).
	 * @member {string} [writeBehavior] Specifies the write option. Supported value is 'Upsert'.
	 * @member {object} [alternateKeyName]
	 * @member {object} [bypassBusinessLogicExecution] Bypass parameter associated with each request
	 * made through the SDK client. Requires a custom privilege associated with Dataverse security role.
	 * Accepted values are: "CustomSync", "CustomAsync", "CustomSync,CustomAsync",
	 * or a comma separated list of GUID values
	 * @member {object} [bypassPowerAutomateFlows] For perf. If enabled, Dataverse will not create system jobs.
	 */
	export interface DynamicsSink extends CopySink {
		ignoreNullValues?: any;
		writeBehavior?: string;
		alternateKeyName?: any;
		bypassBusinessLogicExecution?: any;
		bypassPowerAutomateFlows?: any;
	}

	// handcrafted
	export interface CommonDataServiceSink extends DynamicsSink {
	}

	// handcrafted
	export interface DynamicsCrmSink extends DynamicsSink {
	}

	// handcrafted
	export interface EventStreamSink extends CopySink {
		writeBehavior?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the OdbcSink class.
	 * @constructor
	 * A copy activity ODBC sink.
	 *
	 * @member {object} [preCopyScript] A query to execute before starting the
	 * copy. Type: string (or Expression with resultType string).
	 */
	export interface OdbcSink extends CopySink {
		preCopyScript?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureSearchIndexSink class.
	 * @constructor
	 * A copy activity Azure Search Index sink.
	 *
	 * @member {string} [writeBehavior] Madrid only. Specify the write behavior when upserting
	 * documents into Azure Search Index. Possible values include: 'Merge', 'Upload'
	 * @member {string} [indexAction] Trident only. Specify the write behavior when upserting
	 * documents into Azure Search Index. Possible values include: 'Merge', 'Upload'
	 */
	export interface AzureSearchIndexSink extends CopySink {
		writeBehavior?: string;
		indexAction?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureDataLakeStoreSink class.
	 * @constructor
	 * A copy activity Azure Data Lake Store sink.
	 *
	 * @member {string} [copyBehavior] The type of copy behavior for copy sink.
	 * Possible values include: 'PreserveHierarchy', 'FlattenHierarchy',
	 * 'MergeFiles'
	 */
	export interface AzureDataLakeStoreSink extends CopySink {
		copyBehavior?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the OracleSink class.
	 * @constructor
	 * A copy activity Oracle sink.
	 *
	 * @member {object} [preCopyScript] SQL pre-copy script. Type: string (or
	 * Expression with resultType string).
	 */
	export interface OracleSink extends CopySink {
		preCopyScript?: any;
	}

	// handcrafted
	export interface SnowflakeSink extends CopySink {
		preCopyScript?: any;
		importSettings?: ImportSettings;

		// These two properties only for copy job CDF
		writeBehavior?: string | SharedModel.ExpressionValue;
		upsertSettings?: WriteBehaviorUpsertOptions | SharedModel.ExpressionValue;
	}

	export interface SnowflakeV2Sink extends SnowflakeSink { }

	export interface ImportSettings {
		type?: any;
		additionalCopyOptions?: any;
		additionalFormatOptions?: any;
		storageIntegration?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the TeradataV2Sink class.
	 * @constructor
	 * A copy activity Teradata sink.
	 *
	 * @member {object} [preCopyScript] SQL pre-copy script. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [importSettings] Advanced settings used to write data into Teradata.
	 * @member {object} [importSettings.type] The type of import command, set to TeradataImportCommand.
	 * @member {object} [importSettings.additionalFormatOptions] This will only take effect for direct copy from CSV to Teradata,
	 * provided as a dictionary of key-value pairs.
	 */
	export interface TeradataSink extends CopySink {
		preCopyScript?: any;
		importSettings?: ImportSettings;
	}

	/**
	 * @class
	 * Initializes a new instance of the WriteBehaviorUpsertOptions class.
	 * @constructor
	 * Write behavior upsert options in copy sink
	 *
	 * @member {boolean} [useTempDB] Default to true.
	 * @member {string} [interimSchemaName] Interim schema for creating interim table if physical table is used.
	 * Default to sink table schema.
	 * @member {string[]} [keys] column names for unique row identification.
	 * Either a single key or a series of keys can be used.
	 * Default to primary key.
	 * @member {object} [deltaSinkSettings] Collection of settings applicable to delta sinks
	 * Currently available only in Lakehouse Table Sink.
	 * @member {number} [vacuum] Removes files no longer referenced by a Delta table and are older than
	 * the retention threshold. Default is 0 (treated as 30 days).
	 * @member {boolean} [autoCompact] Spark runs OPTIMIZE jon to re-organize data. Large number of small files
	 * are converted to less number of large files. Default is false.
	 * @member {boolean} [optimizedWrite] Destination dynamically optimizes partition sizes based on the actual data.
	 * Default is false.
	 */
	export interface WriteBehaviorUpsertOptions {
		useTempDB?: boolean;
		interimSchemaName?: string;
		keys: string[];
		deltaSinkSettings?: {
			vacuum: number;
			autoCompact: boolean;
			optimizedWrite: boolean;
		}
	}

	/**
	 * @class
	 * Initializes a new instance of the PolybaseSettings class.
	 * @constructor
	 * PolyBase settings.
	 *
	 * @member {string} [rejectType] Reject type. Possible values include: 'value',
	 * 'percentage'
	 * @member {object} [rejectValue] Specifies the value or the percentage of rows
	 * that can be rejected before the query fails. Type: number (or Expression
	 * with resultType number), minimum: 0.
	 * @member {object} [rejectSampleValue] Determines the number of rows to
	 * attempt to retrieve before the PolyBase recalculates the percentage of
	 * rejected rows. Type: integer (or Expression with resultType integer),
	 * minimum: 0.
	 * @member {object} [useTypeDefault] Specifies how to handle missing values in
	 * delimited text files when PolyBase retrieves data from the text file. Type:
	 * boolean (or Expression with resultType boolean).
	 */
	export interface PolybaseSettings {
		rejectType?: string;
		rejectValue?: any;
		rejectSampleValue?: any;
		useTypeDefault?: any;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	export interface KustoActivity extends ExecutionActivity {
		typeProperties: KustoActivityTypeProperties; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureFunctionActivityTypeProperties class.
	 * @constructor
	 * Azure Function activity type properties.
	 * @member {object} [linkedServiceName] The Azure Function linked service
	 * reference.
	 * @member {string} [linkedServiceName.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [linkedServiceName.parameters] Arguments for LinkedService.
	 * @member {string} functionName Name of the function that this activity should
	 * trigger.
	 * @member {string} method Rest API method for target endpoint. Possible values
	 * include: 'GET', 'POST', 'PUT', 'DELETE', 'OPTIONS', 'HEAD', 'TRACE'
	 * @member {object} [headers] Represents the headers that will be sent to the
	 * request. For example, to set the language and type on a request: "headers" :
	 * { "Accept-Language": "en-us", "Content-Type": "application/json" }. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [body] Represents the payload that will be sent to the

	 * endpoint.
	 */
	export interface AzureFunctionActivityTypeProperties {
		linkedServiceName?: SharedModel.LinkedServiceReference;
		functionName: string;
		method?: string;
		headers?: any;
		body?: any;
		functionSetId?: string;
		workspaceId?: string;
		parameters?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureFunctionActivity class.
	 * @constructor
	 * Azure Function activity.
	 *
	 * @member {object} typeProperties Azure Function activity properties.
	 * @member {object} [typeProperties.linkedServiceName] The Azure Function linked service
	 * reference.
	 * @member {string} [typeProperties.linkedServiceName.referenceName] Reference LinkedService
	 * name.
	 * @member {object} [typeProperties.linkedServiceName.parameters] Arguments for LinkedService.
	 * @member {string} [typeProperties.functionName] Name of the function that this activity should
	 * trigger.
	 * @member {string} [typeProperties.method] Rest API method for target
	 * endpoint. Possible values include: 'GET', 'POST', 'PUT', 'DELETE', 'OPTIONS',
	 * 'HEAD', 'TRACE'
	 * @member {object} [typeProperties.headers] Represents the headers that will
	 * be sent to the request. For example, to set the language and type on a
	 * request: "headers" : { "Accept-Language": "en-us", "Content-Type":
	 * "application/json" }. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.body] Represents the payload that will be
	 * sent to the endpoint. Required for POST/PUT method, not allowed for GET
	 * method Type: string (or Expression with resultType string).
	 */
	export interface AzureFunctionActivity extends ExecutionActivity {
		typeProperties: AzureFunctionActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlDWSink class.
	 * @constructor
	 * A copy activity SQL Data Warehouse sink.
	 *
	 * @member {object} [preCopyScript] SQL pre-copy script. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [allowPolyBase] Indicates to use PolyBase to copy data into
	 * SQL Data Warehouse when applicable. Type: boolean (or Expression with
	 * resultType boolean).
	 * @member {object} [polyBaseSettings] Specifies PolyBase-related settings when
	 * allowPolyBase is true.
	 * @member {string} [polyBaseSettings.rejectType] Reject type. Possible values
	 * include: 'value', 'percentage'
	 * @member {object} [polyBaseSettings.rejectValue] Specifies the value or the
	 * percentage of rows that can be rejected before the query fails. Type: number
	 * (or Expression with resultType number), minimum: 0.
	 * @member {object} [polyBaseSettings.rejectSampleValue] Determines the number
	 * of rows to attempt to retrieve before the PolyBase recalculates the
	 * percentage of rejected rows. Type: integer (or Expression with resultType
	 * integer), minimum: 0.
	 * @member {object} [polyBaseSettings.useTypeDefault] Specifies how to handle
	 * missing values in delimited text files when PolyBase retrieves data from the
	 * text file. Type: boolean (or Expression with resultType boolean).
	 * @member {string} [tableOption] Table option when copy into sink, value could be autoCreate
	 * Type: string (or Expression with resultType string).
	 * @member {object} [sqlWriterUseTableLock] Use table lock when insert and upsert copy method
	 * is selected to improve copy performance
	 * Type: boolean (or Expression with resultType string).
	 * @member {object} [disableMetricsCollection] Whether disable the information collection which is for performance optimization
	 * Default is false. Type: boolean (or Expression with resultType boolean).
	 * @member {string} [writeBehavior] Specifies the write option. Supported values are 'Insert' or 'Upsert'.
	 * @member {object} [upsertSettings] Specifies the upsert options when writeBehavior is 'Upsert'.
	 */
	export interface SqlDWSink extends CopySink {
		preCopyScript?: any;
		allowPolyBase?: any;
		polyBaseSettings?: PolybaseSettings;
		tableOption?: any; // handcrafted
		sqlWriterUseTableLock?: any;
		disableMetricsCollection?: any; // handcrafted
		allowCopyCommand?: any; // handcrafted
		copyCommandSettings?: {
			defaultValues?: any; // handcrafted
			additionalOptions?: any; // handcrafted
		}
		writeBehavior?: string | SharedModel.ExpressionValue;
		upsertSettings?: WriteBehaviorUpsertOptions | SharedModel.ExpressionValue;
	}

	// handcrafted
	export interface SqlPoolSink extends SqlDWSink {
	}

	export interface DataWarehouseSink extends SqlDWSink {
	}

	/**
	 * @class
	 * Initializes a new instance of the SqlSink class.
	 * @constructor
	 * A copy activity SQL sink.
	 *
	 * @member {object} [sqlWriterStoredProcedureName] SQL writer stored procedure
	 * name. Type: string (or Expression with resultType string).
	 * @member {object} [sqlWriterTableType] SQL writer table type. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [preCopyScript] SQL pre-copy script. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [storedProcedureParameters] SQL stored procedure
	 * parameters.
	 * @member {string} [tableOption] Table option when copy into sink, value could be autoCreate
	 * Type: string (or Expression with resultType string).
	 * @member {object} [sqlWriterUseTableLock] Use table lock when insert and upsert copy method
	 * is selected to improve copy performance
	 * Type: boolean (or Expression with resultType string).
	 * @member {object} [disableMetricsCollection] Whether disable the information collection which is for performance optimization
	 * Default is false. Type: boolean (or Expression with resultType boolean).
	 * @member {string} [writeBehavior] Specifies the write option. Supported values are 'Insert' or 'Upsert'.
	 * @member {object} [upsertSettings] Specifies the upsert options when writeBehavior is 'Upsert'.
	 */
	export interface SqlSink extends CopySink {
		sqlWriterStoredProcedureName?: any;
		sqlWriterTableType?: any;
		preCopyScript?: any;
		storedProcedureTableTypeParameterName?: any;
		storedProcedureParameters?: { [propertyName: string]: StoredProcedureParameter } | SharedModel.ExpressionValue;
		tableOption?: any; // handcrafted
		sqlWriterUseTableLock?: any;
		disableMetricsCollection?: any; // handcrafted
		writeBehavior?: string | SharedModel.ExpressionValue;
		upsertSettings?: WriteBehaviorUpsertOptions | SharedModel.ExpressionValue;
	}

	/**
 * @class
 * Initializes a new instance of the AzurePostgreSqlSink class.
 * @constructor
 * A copy activity AzurePostgreSql Sink.
 *
 * @member {object} [writeBatchSize] Write batch size. Type: integer (or
 * Expression with resultType integer).
 * @member {object} [writeBatchTimeout] Write batch timeout. Type: string
 * (or Expression with resultType string), pattern:
 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
 * @member {object} [preCopyScript] SQL pre-copy script. Type: string (or
 * Expression with resultType string).
 */
	export interface AzurePostgreSQLSink extends CopySink {
		writeBatchSize?: any;
		writeBatchTimeout?: any;
		preCopyScript?: any;
		writeMethod?: any;
		upsertSettings?: WriteBehaviorUpsertOptions | SharedModel.ExpressionValue;
	}

	export interface PostgreSqlV2Sink extends CopySink {
		writeBatchSize?: any;
		writeBatchTimeout?: any;
		preCopyScript?: any;
		maxConcurrentConnections?: any;
		writeBehavior?: any;
	}

	export interface AzureMySqlSink extends CopySink {
		preCopyScript?: any;
	}

	export interface MySqlSink extends CopySink {
		preCopyScript?: any;
	}
	export interface AzureDatabricksDeltaLakeSink extends CopySink {
		preCopyScript?: any;
		importSettings?: AzureDatabricksDeltaLakeImportSettings;
	}
	export interface DatabricksSink extends CopySink {
		preCopyScript?: any;
		importSettings?: DatabricksImportSettings;
	}
	export interface AmazonRedshiftSink extends CopySink {
		writeBehavior?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DocumentDbCollectionSink class.
	 * @constructor
	 * A copy activity Document Database Collection sink.
	 *
	 * @member {object} [nestingSeparator] Nested properties separator. Default is
	 * . (dot). Type: string (or Expression with resultType string).
	 * @member {object} [disableMetricsCollection] Whether disable the information collection which is for performance optimization
	 * Default is false. Type: boolean (or Expression with resultType boolean).
	 */
	export interface DocumentDbCollectionSink extends CopySink {
		nestingSeparator?: any;
		writeBehavior?: string; // handcrafted
		disableMetricsCollection?: any; // handcrafted
	}

	// handcrafted
	export interface CosmosDbSqlApiSink extends CopySink {
		disableMetricsCollection?: any; // handcrafted
		writeBehavior?: string; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the FileSystemSink class.
	 * @constructor
	 * A copy activity file system sink.
	 *
	 * @member {string} [copyBehavior] The type of copy behavior for copy sink.
	 * Possible values include: 'PreserveHierarchy', 'FlattenHierarchy',
	 * 'MergeFiles'
	 */
	export interface FileSystemSink extends CopySink {
		copyBehavior?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the BlobSink class.
	 * @constructor
	 * A copy activity Azure Blob sink.
	 *
	 * @member {object} [blobWriterOverwriteFiles] Blob writer overwrite files.
	 * Type: boolean (or Expression with resultType boolean).
	 * @member {object} [blobWriterDateTimeFormat] Blob writer date time format.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [blobWriterAddHeader] Blob writer add header. Type: boolean
	 * (or Expression with resultType boolean).
	 * @member {string} [copyBehavior] The type of copy behavior for copy sink.
	 * Possible values include: 'PreserveHierarchy', 'FlattenHierarchy',
	 * 'MergeFiles'
	 */
	export interface BlobSink extends CopySink {
		blobWriterOverwriteFiles?: any;
		blobWriterDateTimeFormat?: any;
		blobWriterAddHeader?: any;
		copyBehavior?: string;
	}

	// handcrafted
	export interface AzureBlobFSSink extends CopySink {
		blobWriterOverwriteFiles?: any;
		blobWriterDateTimeFormat?: any;
		blobWriterAddHeader?: any;
		copyBehavior?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureTableSink class.
	 * @constructor
	 * A copy activity Azure Table sink.
	 *
	 * @member {object} [azureTableDefaultPartitionKeyValue] Azure Table default
	 * partition key value. Type: string (or Expression with resultType string).
	 * @member {object} [azureTablePartitionKeyName] Azure Table partition key
	 * name. Type: string (or Expression with resultType string).
	 * @member {object} [azureTableRowKeyName] Azure Table row key name. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [azureTableInsertType] Azure Table insert type. Type:
	 * string (or Expression with resultType string).
	 */
	export interface AzureTableSink extends CopySink {
		azureTableDefaultPartitionKeyValue?: any;
		azureTablePartitionKeyName?: any;
		azureTableRowKeyName?: any;
		azureTableInsertType?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the AzureQueueSink class.
	 * @constructor
	 * A copy activity Azure Queue sink.
	 *
	 */
	export interface AzureQueueSink extends CopySink {
	}

	/**
	 * @class
	 * Initializes a new instance of the SapCloudForCustomerSink class.
	 * @constructor
	 * A copy activity SAP Cloud for Customer sink.
	 *
	 * @member {string} [writeBehavior] The write behavior for the operation.
	 * Default is 'Insert'. Possible values include: 'Insert', 'Update'
	 */
	export interface SapCloudForCustomerSink extends CopySink {
		writeBehavior?: string;
		httpRequestTimeout?: any; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the CopyActivityTypeProperties class.
	 * @constructor
	 * Copy activity properties.
	 *
	 * @member {object} source Copy activity source.
	 * @member {object} [source.sourceRetryCount] Source retry count. Type: integer
	 * (or Expression with resultType integer).
	 * @member {object} [source.sourceRetryWait] Source retry wait. Type: string
	 * (or Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {string} [source.type] Polymorphic Discriminator
	 * @member {object} sink Copy activity sink.
	 * @member {object} [sink.writeBatchSize] Write batch size. Type: integer (or
	 * Expression with resultType integer), minimum: 0.
	 * @member {object} [sink.writeBatchTimeout] Write batch timeout. Type: string
	 * (or Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {object} [sink.sinkRetryCount] Sink retry count. Type: integer (or
	 * Expression with resultType integer).
	 * @member {object} [sink.sinkRetryWait] Sink retry wait. Type: string (or
	 * Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {string} [sink.type] Polymorphic Discriminator
	 * @member {object} [translator] Copy activity translator. If not specificed,
	 * tabular translator is used.
	 * @member {string} [translator.type] Polymorphic Discriminator
	 * @member {object} [enableStaging] Specifies whether to copy data via an
	 * interim staging. Default value is false. Type: boolean (or Expression with
	 * resultType boolean).
	 * @member {object} [stagingSettings] Specifies interim staging settings when
	 * EnableStaging is true.
	 * @member {object} [stagingSettings.linkedServiceName] Staging linked service
	 * reference.
	 * @member {string} [stagingSettings.linkedServiceName.referenceName] Reference
	 * LinkedService name.
	 * @member {object} [stagingSettings.linkedServiceName.parameters] Arguments
	 * for LinkedService.
	 * @member {object} [stagingSettings.path] The path to storage for storing the
	 * interim data. Type: string (or Expression with resultType string).
	 * @member {object} [stagingSettings.enableCompression] Specifies whether to
	 * use compression when copying data via an interim staging. Default value is
	 * false. Type: boolean (or Expression with resultType boolean).
	 * @member {object} [parallelCopies] Maximum number of concurrent sessions
	 * opened on the source or sink to avoid overloading the data store. Type:
	 * integer (or Expression with resultType integer), minimum: 0.
	 * @member {object} [dataIntegrationUnits] Maximum number of data integration
	 * units that can be used to perform this data movement. Type: integer (or
	 * Expression with resultType integer), minimum: 0.
	 * @member {object} [enableSkipIncompatibleRow] Whether to skip incompatible
	 * row. Default value is false. Type: boolean (or Expression with resultType
	 * boolean).
	 * @member {object} [redirectIncompatibleRowSettings] Redirect incompatible row
	 * settings when EnableSkipIncompatibleRow is true.
	 * @member {object} [redirectIncompatibleRowSettings.linkedServiceName] Name of
	 * the Azure Storage, Storage SAS, or Azure Data Lake Store linked service used
	 * for redirecting incompatible row. Must be specified if
	 * redirectIncompatibleRowSettings is specified. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [redirectIncompatibleRowSettings.path] The path for storing
	 * the redirect incompatible row data. Type: string (or Expression with
	 * resultType string).
	 */
	export interface CopyActivityTypeProperties {
		source: CopySource;
		sink: CopySink;
		translator?: CopyTranslator;
		enableStaging?: any;
		stagingSettings?: StagingSettings;
		parallelCopies?: any;
		adaptivePerformanceTuning?: boolean;
		cloudDataMovementUnits?: any; // handcrafted for old api version
		dataIntegrationUnits?: any;
		enableSkipIncompatibleRow?: any;
		preserve?: any; // handcrafted
		redirectIncompatibleRowSettings?: RedirectIncompatibleRowSettings;
		logStorageSettings?: LogStorageSettings,
		logSettings?: LogSettings,
		skipErrorFile?: SkipErrorFileSettings,
		validateDataConsistency?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the CopyActivity class.
	 * @constructor
	 * Copy activity.
	 *
	 * @member {object} typeProperties Copy activity properties.
	 * @member {object} [typeProperties.source] Copy activity source.
	 * @member {object} [typeProperties.source.sourceRetryCount] Source retry
	 * count. Type: integer (or Expression with resultType integer).
	 * @member {object} [typeProperties.source.sourceRetryWait] Source retry wait.
	 * Type: string (or Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {string} [typeProperties.source.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.sink] Copy activity sink.
	 * @member {object} [typeProperties.sink.writeBatchSize] Write batch size.
	 * Type: integer (or Expression with resultType integer), minimum: 0.
	 * @member {object} [typeProperties.sink.writeBatchTimeout] Write batch
	 * timeout. Type: string (or Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {object} [typeProperties.sink.sinkRetryCount] Sink retry count.
	 * Type: integer (or Expression with resultType integer).
	 * @member {object} [typeProperties.sink.sinkRetryWait] Sink retry wait. Type:
	 * string (or Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {string} [typeProperties.sink.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.translator] Copy activity translator. If
	 * not specificed, tabular translator is used.
	 * @member {string} [typeProperties.translator.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.enableStaging] Specifies whether to copy
	 * data via an interim staging. Default value is false. Type: boolean (or
	 * Expression with resultType boolean).
	 * @member {object} [typeProperties.stagingSettings] Specifies interim staging
	 * settings when EnableStaging is true.
	 * @member {object} [typeProperties.stagingSettings.linkedServiceName] Staging
	 * linked service reference.
	 * @member {string}
	 * [typeProperties.stagingSettings.linkedServiceName.referenceName] Reference
	 * LinkedService name.
	 * @member {object}
	 * [typeProperties.stagingSettings.linkedServiceName.parameters] Arguments for
	 * LinkedService.
	 * @member {object} [typeProperties.stagingSettings.path] The path to storage
	 * for storing the interim data. Type: string (or Expression with resultType
	 * string).
	 * @member {object} [typeProperties.stagingSettings.enableCompression]
	 * Specifies whether to use compression when copying data via an interim
	 * staging. Default value is false. Type: boolean (or Expression with
	 * resultType boolean).
	 * @member {object} [typeProperties.parallelCopies] Maximum number of
	 * concurrent sessions opened on the source or sink to avoid overloading the
	 * data store. Type: integer (or Expression with resultType integer), minimum:
	 * 0.
	 * @member {object} [typeProperties.dataIntegrationUnits] Maximum number of
	 * data integration units that can be used to perform this data movement. Type:
	 * integer (or Expression with resultType integer), minimum: 0.
	 * @member {object} [typeProperties.enableSkipIncompatibleRow] Whether to skip
	 * incompatible row. Default value is false. Type: boolean (or Expression with
	 * resultType boolean).
	 * @member {object} [typeProperties.redirectIncompatibleRowSettings] Redirect
	 * incompatible row settings when EnableSkipIncompatibleRow is true.
	 * @member {object}
	 * [typeProperties.redirectIncompatibleRowSettings.linkedServiceName] Name of
	 * the Azure Storage, Storage SAS, or Azure Data Lake Store linked service used
	 * for redirecting incompatible row. Must be specified if
	 * redirectIncompatibleRowSettings is specified. Type: string (or Expression
	 * with resultType string).
	 * @member {object} [typeProperties.redirectIncompatibleRowSettings.path] The
	 * path for storing the redirect incompatible row data. Type: string (or
	 * Expression with resultType string).
	 * @member {array} [inputs] List of inputs for the activity.
	 * @member {array} [outputs] List of outputs for the activity.
	 */
	export interface CopyActivity extends ExecutionActivity {
		typeProperties: CopyActivityTypeProperties;
		inputs?: DatasetReference[];
		outputs?: DatasetReference[];
	}

    export interface CopyJobIncrementalColumn {
        name: string;
        type: string;
        physicalType: string;
        conversionSettings?: { type: string; format: string };
    }

	export interface CopyJobActivity extends CopyActivity {
		typeProperties: CopyActivityTypeProperties & {
			readMethod?: 'SnapshotPlusIncremental';
			captureInstanceName?: string;
			nullWatermarkBehavior?: 'Skip' | 'IncludeOnFullLoad';
			columns?: CopyJobIncrementalColumn[];
			changeReadStrategy?: 'Batch';
		};
	}

	/**
	 * @class
	 * Initializes a new instance of the FilterActivityTypeProperties class.
	 * @constructor
	 * Fitler activity properties.
	 *
	 * @member {object} items Input array on which filter should be applied.
	 * @member {string} [items.value] Expression value.
	 * @member {object} condition Condition to be used for filtering the input.
	 * @member {string} [condition.value] Expression value.
	 */
	export interface FilterActivityTypeProperties {
		items: any; // handcrafted
		condition: any; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the ControlActivity class.
	 * @constructor
	 * Base class for all control activities like IfCondition, Switch, ForEach , Until.
	 *
	 */
	export interface ControlActivity extends Activity {
	}

	/**
	 * @class
	 * Initializes a new instance of the FilterActivity class.
	 * @constructor
	 * Filter and return results from input array based on the conditions.
	 *
	 * @member {object} typeProperties Filter activity properties.
	 * @member {object} [typeProperties.items] Input array on which filter should
	 * be applied.
	 * @member {string} [typeProperties.items.value] Expression value.
	 * @member {object} [typeProperties.condition] Condition to be used for
	 * filtering the input.
	 * @member {string} [typeProperties.condition.value] Expression value.
	 */
	export interface FilterActivity extends ControlActivity {
		typeProperties: FilterActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the UntilActivityTypeProperties class.
	 * @constructor
	 * Until activity properties.
	 *
	 * @member {object} expression An expression that would evaluate to Boolean.
	 * The loop will continue until this expression evaluates to true
	 * @member {string} [expression.value] Expression value.
	 * @member {object} [timeout] Specifies the timeout for the activity to run. If
	 * there is no value specified, it takes the value of TimeSpan.FromDays(7)
	 * which is 1 week as default. Type: string (or Expression with resultType
	 * string), pattern: ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * Type: string (or Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {array} activities List of activities to execute.
	 */
	export interface UntilActivityTypeProperties {
		expression: any; // handcrafted
		timeout?: any;
		activities: Activity[];
	}

	/**
	 * @class
	 * Initializes a new instance of the UntilActivity class.
	 * @constructor
	 * This activity executes inner activities until the specified boolean
	 * expression results to true or timeout is reached, whichever is earlier.
	 *
	 * @member {object} typeProperties Until activity properties.
	 * @member {object} [typeProperties.expression] An expression that would
	 * evaluate to Boolean. The loop will continue until this expression evaluates
	 * to true
	 * @member {string} [typeProperties.expression.value] Expression value.
	 * @member {object} [typeProperties.timeout] Specifies the timeout for the
	 * activity to run. If there is no value specified, it takes the value of
	 * TimeSpan.FromDays(7) which is 1 week as default. Type: string (or Expression
	 * with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])). Type: string (or
	 * Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {array} [typeProperties.activities] List of activities to execute.
	 */
	export interface UntilActivity extends ControlActivity {
		typeProperties: UntilActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the WaitActivityTypeProperties class.
	 * @constructor
	 * Wait activity properties.
	 *
	 * @member {number} waitTimeInSeconds Duration in seconds.
	 */
	export interface WaitActivityTypeProperties {
		waitTimeInSeconds: number;
	}

	/**
	 * @class
	 * Initializes a new instance of the WaitActivity class.
	 * @constructor
	 * This activity suspends pipeline execution for the specified interval.
	 *
	 * @member {object} typeProperties Wait activity properties.
	 * @member {number} [typeProperties.waitTimeInSeconds] Duration in seconds.
	 */
	export interface WaitActivity extends ControlActivity {
		typeProperties: WaitActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the FailActivityTypeProperties class.
	 * @constructor
	 * Fail activity properties.
	 *
	 * @member {string} [typeProperties.message] Fail message.
	 * @member {string} [typeProperties.errorCode] Fail error code.
	 */
	export interface FailActivityTypeProperties {
		message: string,
		errorCode: string
	}

	/**
	 * @class
	 * Initializes a new instance of the FailActivity class.
	 * @constructor
	 * This activity suspends pipeline execution for the specified interval.
	 *
	 * @member {object} typeProperties Fail activity properties.
	 * @member {string} [typeProperties.message] Fail message.
	 * @member {string} [typeProperties.errorCode] Fail error code.
	 */
	export interface FailActivity extends ControlActivity {
		typeProperties: FailActivityTypeProperties;
	}

	/**
 * @class
 * export interface VariablesActivityTypeProperties
 * Initializes a new instance of the VariablesActivityTypeProperties class.
 * @constructor
 * Variable activity properties.
 *
 * @member {Array} variables Array of pipeline variables objects
 */
	export interface SetVariableActivityTypeProperties {
		variableName: string; // handcrafted
		value: any; // handcrafted
		setSystemVariable?: boolean; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the VariablesActivity class.
	 * @constructor
	 * This activity adds pipeline variable defined by user
	 *
	 * @member {object} typeProperties Variable activity properties.
	 * @member {number} [typeProperties.variables] Array of pipeline variables objects
	 */
	// { [propertyName: string]: StoredProcedureParameter }
	export interface SetVariableActivity extends ExecutionActivity {
		typeProperties: SetVariableActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SapResetActivity class.
	 * @constructor
	 * This activity suspends pipeline execution for the specified interval.
	 *
	 * @member {object} typeProperties Sap Command activity properties.
	 * @member {string} [typeProperties.dataset] Dataset.
	 * @member {string} [typeProperties.subscriberProcess] Subscriber process.
	 * @member {string} [typeProperties.commandType] Command type.
	 */
	export interface SapCommandActivity extends ControlActivity {
		typeProperties: {
			dataset: {
				referenceName: string;
				type: string
			};
			subscriberProcess: string;
			commandType: string;
		}
	}

	/**
 * @class
 * export interface VariablesActivityTypeProperties
 * Initializes a new instance of the VariablesActivityTypeProperties class.
 * @constructor
 * Variable activity properties.
 *
 * @member {Array} variables Array of pipeline variables objects
 */
	export interface AppendVariableActivityTypeProperties {
		variableName: string; // handcrafted
		value: any; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the VariablesActivity class.
	 * @constructor
	 * This activity adds pipeline variable defined by user
	 *
	 * @member {object} typeProperties Variable activity properties.
	 * @member {number} [typeProperties.variables] Array of pipeline variables objects
	 */
	// { [propertyName: string]: StoredProcedureParameter }
	export interface AppendVariableActivity extends ControlActivity {
		typeProperties: AppendVariableActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ForEachActivityTypeProperties class.
	 * @constructor
	 * ForEach activity properties.
	 *
	 * @member {boolean} [isSequential] Should the loop be executed in sequence or
	 * in parallel (max 50)
	 * @member {number} [batchCount] Batch count to be used for controlling the
	 * number of parallel execution (when isSequential is set to false).
	 * @member {object} items Collection to iterate.
	 * @member {string} [items.value] Expression value.
	 * @member {array} activities List of activities to execute .
	 */
	export interface ForEachActivityTypeProperties {
		isSequential?: boolean;
		batchCount?: number;
		items: any; // handcrafted
		activities?: Activity[]; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the ForEachActivity class.
	 * @constructor
	 * This activity is used for iterating over a collection and execute given
	 * activities.
	 *
	 * @member {object} typeProperties ForEach activity properties.
	 * @member {boolean} [typeProperties.isSequential] Should the loop be executed
	 * in sequence or in parallel (max 50)
	 * @member {number} [typeProperties.batchCount] Batch count to be used for
	 * controlling the number of parallel execution (when isSequential is set to
	 * false).
	 * @member {object} [typeProperties.items] Collection to iterate.
	 * @member {string} [typeProperties.items.value] Expression value.
	 * @member {array} [typeProperties.activities] List of activities to execute .
	 */
	export interface ForEachActivity extends ControlActivity {
		typeProperties: ForEachActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the IfConditionActivityTypeProperties class.
	 * @constructor
	 * IfCondition activity properties.
	 *
	 * @member {object} expression An expression that would evaluate to Boolean.
	 * This is used to determine the block of activities (ifTrueActivities or
	 * ifFalseActivities) that will be executed.
	 * @member {string} [expression.value] Expression value.
	 * @member {array} [ifTrueActivities] List of activities to execute if
	 * expression is evaluated to true. This is an optional property and if not
	 * provided, the activity will exit without any action.
	 * @member {array} [ifFalseActivities] List of activities to execute if
	 * expression is evaluated to false. This is an optional property and if not
	 * provided, the activity will exit without any action.
	 */
	export interface IfConditionActivityTypeProperties {
		expression: any; // handcrafted
		ifTrueActivities?: Activity[];
		ifFalseActivities?: Activity[];
	}

	/**
	 * @class
	 * Initializes a new instance of the IfConditionActivity class.
	 * @constructor
	 * This activity evaluates a boolean expression and executes either the
	 * activities under the ifTrueActivities property or the ifFalseActivities
	 * property depending on the result of the expression.
	 *
	 * @member {object} typeProperties IfCondition activity properties.
	 * @member {object} [typeProperties.expression] An expression that would
	 * evaluate to Boolean. This is used to determine the block of activities
	 * (ifTrueActivities or ifFalseActivities) that will be executed.
	 * @member {string} [typeProperties.expression.value] Expression value.
	 * @member {array} [typeProperties.ifTrueActivities] List of activities to
	 * execute if expression is evaluated to true. This is an optional property and
	 * if not provided, the activity will exit without any action.
	 * @member {array} [typeProperties.ifFalseActivities] List of activities to
	 * execute if expression is evaluated to false. This is an optional property
	 * and if not provided, the activity will exit without any action.
	 */
	export interface IfConditionActivity extends ControlActivity {
		typeProperties: IfConditionActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the SwitchCase class.
	 * @constructor
	 * Case that corresponds an expected string value to a block of activities to execute.
	 *
	 * @member {string} [value] Value expected to match expression evaluation.
	 * @member {array} [activities] List of activities to execute if value matches.
	 */
	export interface SwitchCase {
		value: string;
		activities: Activity[];
	}

	/**
	 * @class
	 * Initializes a new instance of the SwitchActivityTypeProperties class.
	 * @constructor
	 * Switch activity properties.
	 *
	 * @member {object} on An expression that would evaluate to string. This is
	 * used to determine which case activities that will be executed.
	 * @member {string} [on.value] On value
	 * @member {array} [cases] List of cases which have activities to execute if
	 * expression evaluation matches case value.
	 * @member {array} [defaultActivities] List of activities to execute if
	 * expression evaluation does not match any case value.
	 */
	export interface SwitchActivityTypeProperties {
		on: any; // handcrafted
		cases?: SwitchCase[];
		defaultActivities?: Activity[];
	}

	/**
	 * @class
	 * Initializes a new instance of the SwitchActivity class.
	 * @constructor
	 * This activity evalutes a string expression and executes the activities that have a matching
	 * case value or the activities in defaultActivities if there is no matching value.
	 *
	 * @member {object} typeProperties Switch activity properties.
	 * @member {string} [typeProperties.on] An expression that would evaluate to string. This is
	 * used to determine which case activities that will be executed.
	 * @member {string} [typeProperties.on.value] On value
	 * @member {array} [typeProperties.cases] List of cases which have activities to execute if
	 * expression evaluation matches case value.
	 * @member {array} [typeProperties.defaultActivities] List of activities to execute if
	 * expression evaluation does not match any case value.
	 */
	export interface SwitchActivity extends ControlActivity {
		typeProperties: SwitchActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ExecutePipelineActivityTypeProperties class.
	 * @constructor
	 * Execute pipeline activity properties.
	 *
	 * @member {object} pipelineProperty Pipeline reference.
	 * @member {string} [pipelineProperty.referenceName] Reference pipeline name.
	 * @member {string} [pipelineProperty.name] Reference name.
	 * @member {object} [parameters] Pipeline parameters.
	 * @member {boolean} [waitOnCompletion] Defines whether activity execution will
	 * wait for the dependent pipeline execution to finish. Default is false.
	 */
	export interface ExecutePipelineActivityTypeProperties {
		pipeline?: PipelineReference; //handcrafted
		pipelineId?: string;
		pipelineName?: string;
		workspaceId?: string;
		method?: string;
		parameters?: { [propertyName: string]: any };
		waitOnCompletion?: boolean;
		operationType?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the ExecutePipelineActivity class.
	 * @constructor
	 * Execute pipeline activity.
	 *
	 * @member {object} typeProperties Execute pipeline activity properties.
	 * @member {object} [typeProperties.pipelineProperty] Pipeline reference.
	 * @member {string} [typeProperties.pipelineProperty.referenceName] Reference
	 * pipeline name.
	 * @member {string} [typeProperties.pipelineProperty.name] Reference name.
	 * @member {object} [typeProperties.parameters] Pipeline parameters.
	 * @member {boolean} [typeProperties.waitOnCompletion] Defines whether activity
	 * execution will wait for the dependent pipeline execution to finish. Default
	 * is false.
	 */
	export interface ExecutePipelineActivity extends ExecutionActivity {
		typeProperties: ExecutePipelineActivityTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the LinkedIntegrationRuntime class.
	 * @constructor
	 * The linked integration runtime information.
	 *
	 * @member {string} [name] The name of the linked integration runtime.
	 * @member {string} [subscriptionId] The subscription ID for which the linked
	 * integration runtime belong to.
	 * @member {string} [dataFactoryName] The name of the data factory for which
	 * the linked integration runtime belong to.
	 * @member {string} [dataFactoryLocation] The location of the data factory for
	 * which the linked integration runtime belong to.
	 * @member {date} [createTime] The creating time of the linked integration
	 * runtime.
	 */
	export interface LinkedIntegrationRuntime {
		readonly id?: string; //handcrafted
		readonly name?: string;
		readonly subscriptionId?: string;
		readonly dataFactoryName?: string;
		readonly dataFactoryLocation?: string;
		readonly createTime?: Date;
	}

	/**
	 * @class
	 * Initializes a new instance of the SelfHostedIntegrationRuntimeNode class.
	 * @constructor
	 * Properties of Self-hosted integration runtime node.
	 *
	 * @member {string} [nodeName] Name of the integration runtime node.
	 * @member {string} [machineName] Machine name of the integration runtime node.
	 * @member {string} [hostServiceUri] URI for the host machine of the
	 * integration runtime.
	 * @member {string} [status] Status of the integration runtime node. Possible
	 * values include: 'NeedRegistration', 'Online', 'Limited', 'Offline',
	 * 'Upgrading', 'Initializing', 'InitializeFailed'
	 * @member {object} [capabilities] The integration runtime capabilities
	 * dictionary
	 * @member {string} [versionStatus] Status of the integration runtime node
	 * version.
	 * @member {string} [version] Version of the integration runtime node.
	 * @member {date} [registerTime] The time at which the integration runtime node
	 * was registered in ISO8601 format.
	 * @member {date} [lastConnectTime] The most recent time at which the
	 * integration runtime was connected in ISO8601 format.
	 * @member {date} [expiryTime] The time at which the integration runtime will
	 * expire in ISO8601 format.
	 * @member {date} [lastStartTime] The time the node last started up.
	 * @member {date} [lastStopTime] The integration runtime node last stop time.
	 * @member {string} [lastUpdateResult] The result of the last integration
	 * runtime node update. Possible values include: 'Succeed', 'Fail'
	 * @member {date} [lastStartUpdateTime] The last time for the integration
	 * runtime node update start.
	 * @member {date} [lastEndUpdateTime] The last time for the integration runtime
	 * node update end.
	 * @member {boolean} [isActiveDispatcher] Indicates whether this node is the
	 * active dispatcher for integration runtime requests.
	 * @member {number} [concurrentJobsLimit] Maximum concurrent jobs on the
	 * integration runtime node.
	 * @member {number} [maxConcurrentJobs] The maximum concurrent jobs in this
	 * integration runtime.
	 */
	export interface SelfHostedIntegrationRuntimeNode {
		readonly nodeName?: string;
		readonly machineName?: string;
		readonly hostServiceUri?: string;
		readonly status?: string;
		readonly capabilities?: { [propertyName: string]: string };
		readonly versionStatus?: string;
		readonly version?: string;
		readonly registerTime?: Date;
		readonly lastConnectTime?: Date;
		readonly expiryTime?: Date;
		readonly lastStartTime?: Date;
		readonly lastStopTime?: Date;
		readonly lastUpdateResult?: string;
		readonly lastStartUpdateTime?: Date;
		readonly lastEndUpdateTime?: Date;
		readonly isActiveDispatcher?: boolean;
		readonly concurrentJobsLimit?: number;
		readonly maxConcurrentJobs?: number;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the SelfHostedIntegrationRuntimeStatusTypeProperties class.
	 * @constructor
	 * Self-hosted integration runtime status type properties.
	 *
	 * @member {date} [createTime] The time at which the integration runtime was
	 * created, in ISO8601 format.
	 * @member {string} [taskQueueId] The task queue id of the integration runtime.
	 * @member {string} [internalChannelEncryption] It is used to set the
	 * encryption mode for node-node communication channel (when more than 2
	 * self-hosted integration runtime nodes exist). Possible values include:
	 * 'NotSet', 'SslEncrypted', 'NotEncrypted'
	 * @member {string} [version] Version of the integration runtime.
	 * @member {array} [nodes] The list of nodes for this integration runtime.
	 * @member {date} [scheduledUpdateDate] The date at which the integration
	 * runtime will be scheduled to update, in ISO8601 format.
	 * @member {string} [updateDelayOffset] The time in the date scheduled by
	 * service to update the integration runtime, e.g., PT03H is 3 hours
	 * @member {string} [localTimeZoneOffset] The local time zone offset in hours.
	 * @member {object} [capabilities] Object with additional information about
	 * integration runtime capabilities.
	 * @member {array} [serviceUrls] The URLs for the services used in integration
	 * runtime backend service.
	 * @member {string} [autoUpdate] Whether Self-hosted integration runtime auto
	 * update has been turned on. Possible values include: 'On', 'Off'
	 * @member {string} [versionStatus] Status of the integration runtime version.
	 * @member {array} [links] The list of linked integration runtimes that are
	 * created to share with this integration runtime.
	 * @member {array} [sharedWithFactories] The MSI-s of the data factories to
	 * which the integration runtime is shared.
	 * @member {string} [pushedVersion] The version that the integration runtime is
	 * going to update to.
	 * @member {string} [latestVersion] The latest version on download center.
	 */
	export interface SelfHostedIntegrationRuntimeStatusTypeProperties {
		readonly createTime?: Date;
		readonly taskQueueId?: string;
		readonly internalChannelEncryption?: string;
		readonly version?: string;
		nodes?: SelfHostedIntegrationRuntimeNode[];
		readonly scheduledUpdateDate?: Date;
		readonly updateDelayOffset?: string;
		readonly localTimeZoneOffset?: string;
		readonly capabilities?: { [propertyName: string]: string };
		readonly serviceUrls?: string[];
		readonly autoUpdate?: string;
		readonly autoUpdateETA?: Date; // handcrafted
		readonly versionStatus?: string;
		links?: LinkedIntegrationRuntime[];
		readonly sharedWithFactories?: string[];
		readonly pushedVersion?: string;
		readonly latestVersion?: string;
		readonly selfContainedInteractiveAuthoringEnabled?: boolean;
	}

	/**
	 * @class
	 * Initializes a new instance of the SelfHostedIntegrationRuntimeStatus class.
	 * @constructor
	 * Self-hosted integration runtime status.
	 *
	 * @member {object} typeProperties Self-hosted integration runtime status type
	 * properties.
	 * @member {date} [typeProperties.createTime] The time at which the integration
	 * runtime was created, in ISO8601 format.
	 * @member {string} [typeProperties.taskQueueId] The task queue id of the
	 * integration runtime.
	 * @member {string} [typeProperties.internalChannelEncryption] It is used to
	 * set the encryption mode for node-node communication channel (when more than
	 * 2 self-hosted integration runtime nodes exist). Possible values include:
	 * 'NotSet', 'SslEncrypted', 'NotEncrypted'
	 * @member {string} [typeProperties.version] Version of the integration
	 * runtime.
	 * @member {array} [typeProperties.nodes] The list of nodes for this
	 * integration runtime.
	 * @member {date} [typeProperties.scheduledUpdateDate] The date at which the
	 * integration runtime will be scheduled to update, in ISO8601 format.
	 * @member {string} [typeProperties.updateDelayOffset] The time in the date
	 * scheduled by service to update the integration runtime, e.g., PT03H is 3
	 * hours
	 * @member {string} [typeProperties.localTimeZoneOffset] The local time zone
	 * offset in hours.
	 * @member {object} [typeProperties.capabilities] Object with additional
	 * information about integration runtime capabilities.
	 * @member {array} [typeProperties.serviceUrls] The URLs for the services used
	 * in integration runtime backend service.
	 * @member {string} [typeProperties.autoUpdate] Whether Self-hosted integration
	 * runtime auto update has been turned on. Possible values include: 'On', 'Off'
	 * @member {string} [typeProperties.versionStatus] Status of the integration
	 * runtime version.
	 * @member {array} [typeProperties.links] The list of linked integration
	 * runtimes that are created to share with this integration runtime.
	 * @member {array} [typeProperties.sharedWithFactories] The MSI-s of the data
	 * factories to which the integration runtime is shared.
	 * @member {string} [typeProperties.pushedVersion] The version that the
	 * integration runtime is going to update to.
	 * @member {string} [typeProperties.latestVersion] The latest version on
	 * download center.
	 */
	export interface SelfHostedIntegrationRuntimeStatus extends IntegrationRuntimeStatus {
		typeProperties: SelfHostedIntegrationRuntimeStatusTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ManagedIntegrationRuntimeOperationResult class.
	 * @constructor
	 * Properties of managed integration runtime operation result.
	 *
	 * @member {string} [type] The operation type. Could be start or stop.
	 * @member {date} [startTime] The start time of the operation.
	 * @member {string} [result] The operation result.
	 * @member {string} [errorCode] The error code.
	 * @member {array} [parameters] Managed integration runtime error parameters.
	 * @member {string} [activityId] The activity id for the operation request.
	 */
	export interface ManagedIntegrationRuntimeOperationResult {
		readonly type?: string;
		readonly startTime?: Date;
		readonly result?: string;
		readonly errorCode?: string;
		readonly parameters?: string[];
		readonly activityId?: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ManagedIntegrationRuntimeError class.
	 * @constructor
	 * Error definition for managed integration runtime.
	 *
	 * @member {date} [time] The time when the error occurred.
	 * @member {string} [code] Error code.
	 * @member {array} [parameters] Managed integration runtime error parameters.
	 * @member {string} [message] Error message.
	 */
	export interface ManagedIntegrationRuntimeError {
		readonly time?: Date;
		readonly code?: string;
		readonly parameters?: string[];
		readonly message?: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ManagedIntegrationRuntimeNode class.
	 * @constructor
	 * Properties of integration runtime node.
	 *
	 * @member {string} [nodeId] The managed integration runtime node id.
	 * @member {string} [status] The managed integration runtime node status.
	 * Possible values include: 'Starting', 'Available', 'Recycling', 'Unavailable'
	 * @member {array} [errors] The errors that occurred on this integration
	 * runtime node.
	 */
	export interface ManagedIntegrationRuntimeNode {
		readonly nodeId?: string;
		readonly status?: string;
		errors?: ManagedIntegrationRuntimeError[];
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the ManagedIntegrationRuntimeStatusTypeProperties class.
	 * @constructor
	 * Managed integration runtime status type properties.
	 *
	 * @member {date} [createTime] The time at which the integration runtime was
	 * created, in ISO8601 format.
	 * @member {array} [nodes] The list of nodes for managed integration runtime.
	 * @member {array} [otherErrors] The errors that occurred on this integration
	 * runtime.
	 * @member {object} [lastOperation] The last operation result that occurred on
	 * this integration runtime.
	 * @member {string} [lastOperation.type] The operation type. Could be start or
	 * stop.
	 * @member {date} [lastOperation.startTime] The start time of the operation.
	 * @member {string} [lastOperation.result] The operation result.
	 * @member {string} [lastOperation.errorCode] The error code.
	 * @member {array} [lastOperation.parameters] Managed integration runtime error
	 * parameters.
	 * @member {string} [lastOperation.activityId] The activity id for the
	 * operation request.
	 */
	export interface ManagedIntegrationRuntimeStatusTypeProperties {
		readonly createTime?: Date;
		readonly nodes?: ManagedIntegrationRuntimeNode[];
		readonly otherErrors?: ManagedIntegrationRuntimeError[];
		readonly lastOperation?: ManagedIntegrationRuntimeOperationResult;
		readonly interactiveQuery?: ManagedIntegrationRuntimeInteractiveQueryStatus; // handcrafted
		readonly dataflowState?: string; // handcrafted
		readonly dataMovementState?: string; // handcrafted
		readonly dualStandbyPairRole?: string; // handcrafted
	}

	// handcrafted
	export interface ManagedIntegrationRuntimeInteractiveQueryStatus {
		readonly status: string;
		readonly autoTerminationMinutes?: number;
	}

	/**
	 * @class
	 * Initializes a new instance of the ManagedIntegrationRuntimeStatus class.
	 * @constructor
	 * Managed integration runtime status.
	 *
	 * @member {object} typeProperties Managed integration runtime status type
	 * properties.
	 * @member {date} [typeProperties.createTime] The time at which the integration
	 * runtime was created, in ISO8601 format.
	 * @member {array} [typeProperties.nodes] The list of nodes for managed
	 * integration runtime.
	 * @member {array} [typeProperties.otherErrors] The errors that occurred on
	 * this integration runtime.
	 * @member {object} [typeProperties.lastOperation] The last operation result
	 * that occurred on this integration runtime.
	 * @member {string} [typeProperties.lastOperation.type] The operation type.
	 * Could be start or stop.
	 * @member {date} [typeProperties.lastOperation.startTime] The start time of
	 * the operation.
	 * @member {string} [typeProperties.lastOperation.result] The operation result.
	 * @member {string} [typeProperties.lastOperation.errorCode] The error code.
	 * @member {array} [typeProperties.lastOperation.parameters] Managed
	 * integration runtime error parameters.
	 * @member {string} [typeProperties.lastOperation.activityId] The activity id
	 * for the operation request.
	 */
	export interface ManagedIntegrationRuntimeStatus extends IntegrationRuntimeStatus {
		typeProperties: ManagedIntegrationRuntimeStatusTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the LinkedIntegrationRuntimeType class.
	 * @constructor
	 * The base definition of a linked integration runtime.
	 *
	 * @member {string} authorizationType Polymorphic Discriminator
	 */

	export interface TopLevelIntegrationRuntimeStatus {
		type: string
		typeProperties: {
			connectInfo: {
				name: string
				properties: {
					createTime: string
				}
			}
			selfHostedDataManagementRuntime: {
				name: string
				properties: {
					createTime: string
					state: string
					version: string
				}
			}
		}
	}
	export interface LinkedIntegrationRuntimeType {
		authorizationType?: string;
		credential?: Model.CredentialReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the LinkedIntegrationRuntimeRbacAuthorization class.
	 * @constructor
	 * The role based access control (RBAC) authorization type integration runtime.
	 *
	 * @member {string} resourceId The resource identifier of the integration
	 * runtime to be shared.
	 */
	export interface LinkedIntegrationRuntimeRbacAuthorization extends LinkedIntegrationRuntimeType {
		resourceId: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the LinkedIntegrationRuntimeKeyAuthorization class.
	 * @constructor
	 * The key authorization type integration runtime.
	 *
	 * @member {object} key The key used for authorization.
	 * @member {string} [key.value] Value of secure string.
	 */
	export interface LinkedIntegrationRuntimeKeyAuthorization extends LinkedIntegrationRuntimeType {
		key: SecureString;
	}

	/**
	 * @class
	 * Initializes a new instance of the SelfHostedIntegrationRuntimeTypeProperties class.
	 * @constructor
	 * The self-hosted integration runtime properties.
	 *
	 * @member {object} [linkedInfo]
	 * @member {string} [linkedInfo.authorizationType] Polymorphic Discriminator
	 */
	export interface SelfHostedIntegrationRuntimeTypeProperties {
		linkedInfo?: LinkedIntegrationRuntimeType;
		connectInfo?: LinkedIntegrationRuntimeType;
		subType?: string;
		selfContainedInteractiveAuthoringEnabled?: boolean;
	}

	/**
	 * @class
	 * Initializes a new instance of the SelfHostedIntegrationRuntime class.
	 * @constructor
	 * Self-hosted integration runtime.
	 *
	 * @member {object} [typeProperties] When this property is not null, means this
	 * is a linked integration runtime. The property is used to access original
	 * integration runtime.
	 * @member {object} [typeProperties.linkedInfo]
	 * @member {string} [typeProperties.linkedInfo.authorizationType] Polymorphic
	 * Discriminator
	 */
	export interface SelfHostedIntegrationRuntime extends IntegrationRuntime {
		typeProperties?: SelfHostedIntegrationRuntimeTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeCustomSetupScriptProperties class.
	 * @constructor
	 * Custom setup script properties for a managed dedicated integration runtime.
	 *
	 * @member {string} [blobContainerUri] The URI of the Azure blob container that
	 * contains the custom setup script.
	 * @member {object} [sasToken] The SAS token of the Azure blob container.
	 * @member {string} [sasToken.value] Value of secure string.
	 */
	export interface IntegrationRuntimeCustomSetupScriptProperties {
		blobContainerUri?: string;
		sasToken?: SecureString;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeSsisCatalogInfo class.
	 * @constructor
	 * Catalog information for managed dedicated integration runtime.
	 *
	 * @member {string} [catalogServerEndpoint] The catalog database server URL.
	 * @member {string} [catalogAdminUserName] The administrator user name of
	 * catalog database.
	 * @member {object} [catalogAdminPassword] The password of the administrator
	 * user account of the catalog database.
	 * @member {string} [catalogAdminPassword.value] Value of secure string.
	 * @member {string} [catalogPricingTier] The pricing tier for the catalog
	 * @member {string} [dualStandbyPairName] The failover group name
	 * database. The valid values could be found in
	 * https://azure.microsoft.com/pricing/details/sql-database/. Possible
	 * values include: 'Basic', 'Standard', 'Premium', 'PremiumRS'
	 */
	export interface IntegrationRuntimeSsisCatalogInfo {
		catalogServerEndpoint?: string;
		catalogAdminUserName?: string;
		catalogAdminPassword?: SecureString;
		catalogPricingTier?: string;
		dualStandbyPairName?: string;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of package store class
	 * @constructor
	 * Package store properties for a SSIS integration runtime.
	 *
	 * @member {string} [name] The name of the package store
	 * @member {object} [packageStoreLinkedService] The package store linked service
	 */
	// handcrafted
	export interface PackageStore {
		name: string;
		packageStoreLinkedService: SharedModel.LinkedServiceReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeSsisDataProxyProperties class.
	 * @constructor
	 * Data proxy properties for self hosted IR with blob linked service.
	 *
	 * @member {object} [connectVia] The self hosted integration runtime reference.
	 * @member {object} [stagingLinkedService] The Azure blob storage linked service
	 * reference.
	 * @member {string} [path] The path to storage for storing the interim data.
	 * Type: string (or Expression with resultType string).
	 */
	// handcrafted
	export interface IntegrationRuntimeSsisDataProxyProperties {
		connectVia: SharedModel.IntegrationRuntimeReference;
		stagingLinkedService: SharedModel.LinkedServiceReference;
		path?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the custom setup class
	 * @constructor
	 * Custom Setup properties for a SSIS integration runtime.
	 *
	 * @member {string} [type] the type of the custom setup
	 */
	// handcrafted
	export interface CustomSetupBase {
		type: string;
	}

	/**
	 * @class
	 * Initializes a new instnace of the CmdkeySetupTypeProperties class.
	 * @constructor
	 * Cmdkey command properties for the Cmdkey steup.
	 *
	 * @member {object} [targetName] the target name of the cmdkey /add.
	 * @member {object} [userName] the user name of the cmdkey /user.
	 * @member {object} [password] the password of the cmdkey /pass.
	 * Type: string, SecureString or AzureKeyVaultSecretReference.
	 */
	// handcrafted
	export interface CmdkeySetupTypeProperties {
		targetName: any;
		userName: any;
		password: SecureString;
	}

	// handcrafted
	export interface CmdkeySetup extends CustomSetupBase {
		typeProperties: CmdkeySetupTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the EnvironmentVariableSetupTypeProperties class.
	 * @constructor
	 * Environment variable properties for the Environment variable setup.
	 *
	 * @member {string} [variableName] the name of the environment variable.
	 * @member {string} [variableValue] the value of the environment variable.
	 */
	// handcrafted
	export interface EnvironmentVariableSetupTypeProperties {
		variableName: string;
		variableValue: string;
	}

	// handcrafted
	export interface EnvironmentVariableSetup extends CustomSetupBase {
		typeProperties: EnvironmentVariableSetupTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the ComponentSetupTypeProperties class.
	 * @constructor
	 * Install 3rd party component properties for the Component setup.
	 *
	 * @member {string} [componentName] the name of the 3rd party component
	 */
	// handcrafted
	export interface ComponentSetupTypeProperties {
		componentName: string;
	}

	// handcrafted
	export interface ComponentSetup extends CustomSetupBase {
		typeProperties: ComponentSetupTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the LicensedComponentSetupTypeProperties class.
	 * @constructor
	 * Install Licensed components type properties for the Component setup.
	 *
	 * @member {object} [licenseKey] the license key of the component
	 */
	// handcrafted
	export interface LicensedComponentSetupTypeProperties extends ComponentSetupTypeProperties {
		licenseKey: SecureString;
	}

	// handcrafted
	export interface LicensedComponentSetup extends CustomSetupBase {
		typeProperties: LicensedComponentSetupTypeProperties;
	}

	/**
	 * @class
	 * Initializeds a new instance of the AzPowerShellSetupTypeProperties class.
	 * @constructor
	 * Install Azure PowerShell with specified version
	 *
	 * @member {string} [version] the version of Azure PowerShell
	 */
	// handcrafted
	export interface AzPowerShellSetupTypeProperties {
		version: string;
	}

	// handcrafted
	export interface AzPowerShellSetup extends CustomSetupBase {
		typeProperties: AzPowerShellSetupTypeProperties;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeSsisProperties class.
	 * @constructor
	 * SSIS properties for managed integration runtime.
	 *
	 * @member {object} [catalogInfo] Catalog information for managed dedicated
	 * integration runtime.
	 * @member {string} [catalogInfo.catalogServerEndpoint] The catalog database
	 * server URL.
	 * @member {string} [catalogInfo.catalogAdminUserName] The administrator user
	 * name of catalog database.
	 * @member {object} [catalogInfo.catalogAdminPassword] The password of the
	 * administrator user account of the catalog database.
	 * @member {string} [catalogInfo.catalogAdminPassword.value] Value of secure
	 * string.
	 * @member {string} [catalogInfo.catalogPricingTier] The pricing tier for the
	 * catalog database. The valid values could be found in
	 * https://azure.microsoft.com/pricing/details/sql-database/. Possible
	 * values include: 'Basic', 'Standard', 'Premium', 'PremiumRS'
	 * @member {string} [licenseType] License type for bringing your own license
	 * scenario. Possible values include: 'BasePrice', 'LicenseIncluded'
	 * @member {object} [customSetupScriptProperties] Custom setup script
	 * properties for a managed dedicated integration runtime.
	 * @member {string} [customSetupScriptProperties.blobContainerUri] The URI of
	 * the Azure blob container that contains the custom setup script.
	 * @member {object} [customSetupScriptProperties.sasToken] The SAS token of the
	 * Azure blob container.
	 * @member {string} [customSetupScriptProperties.sasToken.value] Value of
	 * secure string.
	 * @member {string} [edition] The edition for the SSIS Integration Runtime.
	 * Possible values include: 'Standard', 'Enterprise'
	 * @member {object} [dataProxyProperties] The data proxy properties for a self
	 * hosted integration runtime with blob linked service.
	 * @member {object} [expressCustomSetupProperties] The custom setup
	 * with script for the SSIS Integration Runtime.
	 * @member {object} [packageStores] the package store for integration runtime.
	 */
	export interface IntegrationRuntimeSsisProperties {
		catalogInfo?: IntegrationRuntimeSsisCatalogInfo;
		licenseType?: string;
		customSetupScriptProperties?: IntegrationRuntimeCustomSetupScriptProperties;
		edition?: string;
		// handcrafted
		runtimeVersion?: string;
		// handcrafted
		dataProxyProperties?: IntegrationRuntimeSsisDataProxyProperties;
		// handcrafted
		expressCustomSetupProperties?: CustomSetupBase[];
		// handcrafted
		packageStores?: PackageStore[];
		// handcrafted
		credential?: CredentialReference;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	export interface IntegrationRuntimeSharingProperties {
		linkedAzureSsisIrResourceIds?: string[];
	}

	export interface IntegrationRuntimeLinkedInfo {
		resourceId?: string;
		sharedIntegrationRuntimeLocation?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeVNetProperties class.
	 * @constructor
	 * VNet properties for managed integration runtime.
	 *
	 * @member {string} [vNetId] The ID of the VNet that this integration runtime
	 * will join.
	 * @member {string} [subnet] The name of the subnet this integration runtime
	 * will join.
	 * @member {string} [subnetId] The ID of the subnet this integration runtime
	 * will join.
	 */
	export interface IntegrationRuntimeVNetProperties {
		vNetId?: string;
		subnet?: string;
		subnetId?: string;
		publicIPs?: string[];
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	// handcrafted
	export interface IntegrationRuntimeVirtualNetwork {
		subnetId?: string;
	}

	// handcrafted
	export interface IntegrationRuntimeAirflowProperties {
		airflowVersion?: string;
		airflowWebUrl?: string;
		gitSyncProperties?: SharedModel.AirflowGitSyncProperties;
		airflowEnvironmentVariables?: { [name: string]: string };
		airflowConfigurationOverrides?: { [name: string]: string };
		environmentVariables?: { [name: string]: string };
		airflowRequirements?: string[];
		enableAADIntegration?: boolean;
		enableTriggerers?: boolean;
		userName?: string;
		password?: string;
		airflowEntityReferences?: SharedModel.AirflowEntityReference[];
		packageProviderPath?: string;
		encryptedSecrets?: SharedModel.AirflowAksEncryptedSecret[];
		secrets?: SharedModel.AirflowAksSecret[];
		migratedToFabric?: boolean;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeComputeProperties class.
	 * @constructor
	 * The compute resource properties for managed integration runtime.
	 *
	 * @member {string} [location] The location for managed integration runtime.
	 * The supported regions could be found on
	 * https://docs.microsoft.com/azure/data-factory/data-factory-data-movement-activities
	 * @member {string} [nodeSize] The node size requirement to managed integration
	 * runtime.
	 * @member {number} [numberOfNodes] The required number of nodes for managed
	 * integration runtime.
	 * @member {number} [maxParallelExecutionsPerNode] Maximum parallel executions
	 * count per node for managed integration runtime.
	 * @member {object} [vNetProperties] VNet properties for managed integration
	 * runtime.
	 * @member {string} [vNetProperties.vNetId] The ID of the VNet that this
	 * integration runtime will join.
	 * @member {string} [vNetProperties.subnet] The name of the subnet this
	 * integration runtime will join.
	 */
	export interface IntegrationRuntimeComputeProperties {
		location?: string;
		nodeSize?: string;
		computeSize?: string;
		numberOfNodes?: number;
		extraNodes?: number;
		enableAutoscale?: boolean;
		autoScaleMinNodes?: number;
		autoScaleMaxNodes?: number;
		clusterIP?: string;
		maxParallelExecutionsPerNode?: number;
		vNetProperties?: IntegrationRuntimeVNetProperties;
		virtualNetwork?: IntegrationRuntimeVirtualNetwork; // handcrafted
		dataFlowProperties?: DataFlowRunTimeProperties; // Handcrafted
		sparkProperties?: SparkProperties; // Handcrafted
		copyComputeScaleProperties?: CopyComputeScaleProperties;
		pipelineExternalComputeScaleProperties?: PipelineExternalComputeScaleProperties;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	// handcrafted
	export interface IntegrationRuntimeCustomerVirtualNetwork {
		subnetId: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the ManagedIntegrationRuntimeTypeProperties class.
	 * @constructor
	 * Managed integration runtime type properties.
	 *
	 * @member {object} [computeProperties] The compute resource for managed
	 * integration runtime.
	 * @member {string} [computeProperties.location] The location for managed
	 * integration runtime. The supported regions could be found on
	 * https://docs.microsoft.com/azure/data-factory/data-factory-data-movement-activities
	 * @member {string} [computeProperties.nodeSize] The node size requirement to
	 * managed integration runtime.
	 * @member {number} [computeProperties.numberOfNodes] The required number of
	 * nodes for managed integration runtime.
	 * @member {number} [computeProperties.maxParallelExecutionsPerNode] Maximum
	 * parallel executions count per node for managed integration runtime.
	 * @member {object} [computeProperties.vNetProperties] VNet properties for
	 * managed integration runtime.
	 * @member {string} [computeProperties.vNetProperties.vNetId] The ID of the
	 * VNet that this integration runtime will join.
	 * @member {string} [computeProperties.vNetProperties.subnet] The name of the
	 * subnet this integration runtime will join.
	 * @member {object} [ssisProperties] SSIS properties for managed integration
	 * runtime.
	 * @member {object} [ssisProperties.catalogInfo] Catalog information for
	 * managed dedicated integration runtime.
	 * @member {string} [ssisProperties.catalogInfo.catalogServerEndpoint] The
	 * catalog database server URL.
	 * @member {string} [ssisProperties.catalogInfo.catalogAdminUserName] The
	 * administrator user name of catalog database.
	 * @member {object} [ssisProperties.catalogInfo.catalogAdminPassword] The
	 * password of the administrator user account of the catalog database.
	 * @member {string} [ssisProperties.catalogInfo.catalogAdminPassword.value]
	 * Value of secure string.
	 * @member {string} [ssisProperties.catalogInfo.catalogPricingTier] The pricing
	 * tier for the catalog database. The valid values could be found in
	 * https://azure.microsoft.com/pricing/details/sql-database/. Possible
	 * values include: 'Basic', 'Standard', 'Premium', 'PremiumRS'
	 * @member {string} [ssisProperties.licenseType] License type for bringing your
	 * own license scenario. Possible values include: 'BasePrice',
	 * 'LicenseIncluded'
	 * @member {object} [ssisProperties.customSetupScriptProperties] Custom setup
	 * script properties for a managed dedicated integration runtime.
	 * @member {string}
	 * [ssisProperties.customSetupScriptProperties.blobContainerUri] The URI of the
	 * Azure blob container that contains the custom setup script.
	 * @member {object} [ssisProperties.customSetupScriptProperties.sasToken] The
	 * SAS token of the Azure blob container.
	 * @member {string} [ssisProperties.customSetupScriptProperties.sasToken.value]
	 * Value of secure string.
	 * @member {string} [ssisProperties.edition] The edition for the SSIS
	 * Integration Runtime. Possible values include: 'Standard', 'Enterprise'
	 */
	export interface ManagedIntegrationRuntimeTypeProperties {
		computeProperties?: IntegrationRuntimeComputeProperties;
		customerVirtualNetwork?: IntegrationRuntimeCustomerVirtualNetwork;
		ssisProperties?: IntegrationRuntimeSsisProperties;
		airflowProperties?: IntegrationRuntimeAirflowProperties;
		sharingProperties?: IntegrationRuntimeSharingProperties;
		linkedInfo?: IntegrationRuntimeLinkedInfo;
	}

	// Handcrafted
	export interface SparkProperties {
		useSparkGateway: boolean;
	}

	//Handcrafted
	export interface ICustomProperty {
		name: string;
		value: string
	}

	export interface DataFlowRunTimeProperties {
		timeToLive?: number;
		timeToLiveInMinutes?: number;
		computeType: string;
		coreCount: number;
		cleanup?: boolean;
		externalComputeInfo?: DataFlowExternalComputeProperties[];
		customProperties?: ICustomProperty[];
	}

	export interface CopyComputeScaleProperties {
		dataIntegrationUnit?: number;
		timeToLive?: number;
	}

	export interface PipelineExternalComputeScaleProperties {
		timeToLive?: number;
		numberOfPipelineNodes?: number;
		numberOfExternalNodes: number;
	}

	// handcrafted
	export interface ManagedVirtualNetworkReference {
		referenceName: string;
		type: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the ManagedIntegrationRuntime class.
	 * @constructor
	 * Managed integration runtime, including managed elastic and managed dedicated
	 * integration runtimes.
	 *
	 * @member {string} [state] Integration runtime state, only valid for managed
	 * dedicated integration runtime. Possible values include: 'Initial',
	 * 'Stopped', 'Started', 'Starting', 'Stopping', 'NeedRegistration', 'Online',
	 * 'Limited', 'Offline', 'AccessDenied'
	 * @member {object} typeProperties Managed integration runtime properties.
	 * @member {object} [typeProperties.computeProperties] The compute resource for
	 * managed integration runtime.
	 * @member {string} [typeProperties.computeProperties.location] The location
	 * for managed integration runtime. The supported regions could be found on
	 * https://docs.microsoft.com/azure/data-factory/data-factory-data-movement-activities
	 * @member {string} [typeProperties.computeProperties.nodeSize] The node size
	 * requirement to managed integration runtime.
	 * @member {number} [typeProperties.computeProperties.numberOfNodes] The
	 * required number of nodes for managed integration runtime.
	 * @member {number}
	 * [typeProperties.computeProperties.maxParallelExecutionsPerNode] Maximum
	 * parallel executions count per node for managed integration runtime.
	 * @member {object} [typeProperties.computeProperties.vNetProperties] VNet
	 * properties for managed integration runtime.
	 * @member {string} [typeProperties.computeProperties.vNetProperties.vNetId]
	 * The ID of the VNet that this integration runtime will join.
	 * @member {string} [typeProperties.computeProperties.vNetProperties.subnet]
	 * The name of the subnet this integration runtime will join.
	 * @member {object} [typeProperties.ssisProperties] SSIS properties for managed
	 * integration runtime.
	 * @member {object} [typeProperties.ssisProperties.catalogInfo] Catalog
	 * information for managed dedicated integration runtime.
	 * @member {string}
	 * [typeProperties.ssisProperties.catalogInfo.catalogServerEndpoint] The
	 * catalog database server URL.
	 * @member {string}
	 * [typeProperties.ssisProperties.catalogInfo.catalogAdminUserName] The
	 * administrator user name of catalog database.
	 * @member {object}
	 * [typeProperties.ssisProperties.catalogInfo.catalogAdminPassword] The
	 * password of the administrator user account of the catalog database.
	 * @member {string}
	 * [typeProperties.ssisProperties.catalogInfo.catalogAdminPassword.value] Value
	 * of secure string.
	 * @member {string}
	 * [typeProperties.ssisProperties.catalogInfo.catalogPricingTier] The pricing
	 * tier for the catalog database. The valid values could be found in
	 * https://azure.microsoft.com/pricing/details/sql-database/. Possible
	 * values include: 'Basic', 'Standard', 'Premium', 'PremiumRS'
	 * @member {string} [typeProperties.ssisProperties.licenseType] License type
	 * for bringing your own license scenario. Possible values include:
	 * 'BasePrice', 'LicenseIncluded'
	 * @member {object} [typeProperties.ssisProperties.customSetupScriptProperties]
	 * Custom setup script properties for a managed dedicated integration runtime.
	 * @member {string}
	 * [typeProperties.ssisProperties.customSetupScriptProperties.blobContainerUri]
	 * The URI of the Azure blob container that contains the custom setup script.
	 * @member {object}
	 * [typeProperties.ssisProperties.customSetupScriptProperties.sasToken] The SAS
	 * token of the Azure blob container.
	 * @member {string}
	 * [typeProperties.ssisProperties.customSetupScriptProperties.sasToken.value]
	 * Value of secure string.
	 * @member {string} [typeProperties.ssisProperties.edition] The edition for the
	 * SSIS Integration Runtime. Possible values include: 'Standard', 'Enterprise'
	 */
	export interface ManagedIntegrationRuntime extends IntegrationRuntime {
		readonly state?: string;
		typeProperties: ManagedIntegrationRuntimeTypeProperties;
		managedVirtualNetwork?: ManagedVirtualNetworkReference; // Handcrafted
		linkedService?: LinkedServiceReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeNodeIpAddress class.
	 * @constructor
	 * The IP address of self-hosted integration runtime node.
	 *
	 * @member {string} [ipAddress] The IP address of self-hosted integration
	 * runtime node.
	 */
	export interface IntegrationRuntimeNodeIpAddress {
		readonly ipAddress?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeNodeMonitoringData class.
	 * @constructor
	 * Monitoring data for integration runtime node.
	 *
	 * @member {string} [nodeName] Name of the integration runtime node.
	 * @member {number} [availableMemoryInMB] Available memory (MB) on the
	 * integration runtime node.
	 * @member {number} [cpuUtilization] CPU percentage on the integration runtime
	 * node.
	 * @member {number} [concurrentJobsLimit] Maximum concurrent jobs on the
	 * integration runtime node.
	 * @member {number} [concurrentJobsRunning] The number of jobs currently
	 * running on the integration runtime node.
	 * @member {number} [maxConcurrentJobs] The maximum concurrent jobs in this
	 * integration runtime.
	 * @member {number} [sentBytes] Sent bytes on the integration runtime node.
	 * @member {number} [receivedBytes] Received bytes on the integration runtime
	 * node.
	 */
	export interface IntegrationRuntimeNodeMonitoringData {
		readonly nodeName?: string;
		readonly availableMemoryInMB?: number;
		readonly cpuUtilization?: number;
		readonly concurrentJobsLimit?: number;
		readonly concurrentJobsRunning?: number;
		readonly maxConcurrentJobs?: number;
		readonly sentBytes?: number;
		readonly receivedBytes?: number;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeMonitoringData class.
	 * @constructor
	 * Get monitoring data response.
	 *
	 * @member {string} [name] Integration runtime name.
	 * @member {array} [nodes] Integration runtime node monitoring data.
	 */
	export interface IntegrationRuntimeMonitoringData {
		name?: string;
		nodes?: IntegrationRuntimeNodeMonitoringData[];
		averageQueueDuration?: number; //handcraft
		queueLength?: number; //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeAuthKeys class.
	 * @constructor
	 * The integration runtime authentication keys.
	 *
	 * @member {string} [authKey1] The primary integration runtime authentication
	 * key.
	 * @member {string} [authKey2] The secondary integration runtime authentication
	 * key.
	 */
	export interface IntegrationRuntimeAuthKeys {
		authKey1?: string;
		authKey2?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeRegenerateKeyParameters class.
	 * @constructor
	 * Parameters to regenerate the authentication key.
	 *
	 * @member {string} [keyName] The name of the authentication key to regenerate.
	 * Possible values include: 'authKey1', 'authKey2'
	 */
	export interface IntegrationRuntimeRegenerateKeyParameters {
		keyName?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeConnectionInfo class.
	 * @constructor
	 * Connection information for encrypting the on-premises data source
	 * credentials.
	 *
	 * @member {string} [serviceToken] The token generated in service. Callers use
	 * this token to authenticate to integration runtime.
	 * @member {string} [identityCertThumbprint] The integration runtime SSL
	 * certificate thumbprint. Click-Once application uses it to do server
	 * validation.
	 * @member {string} [hostServiceUri] The on-premises integration runtime host
	 * URL.
	 * @member {string} [version] The integration runtime version.
	 * @member {string} [publicKey] The public key for encrypting a credential when
	 * transferring the credential to the integration runtime.
	 * @member {boolean} [isIdentityCertExprired] Whether the identity certificate
	 * is expired.
	 */
	export interface IntegrationRuntimeConnectionInfo {
		readonly serviceToken?: string;
		readonly identityCertThumbprint?: string;
		readonly hostServiceUri?: string;
		readonly version?: string;
		readonly publicKey?: string;
		readonly isIdentityCertExprired?: boolean;
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	// handcrafted
	export interface IntegrationRuntimeAirflowProperties {
		readonly airflowUrl?: string;
	}

	export interface IntegrationRuntimeRegions {
		readonly resourceTypes: Array<{
			resourceType: string,
			locations: string[]
		}>
	}

	export interface IntegrationRuntimeLocation {
		value: Array<{
			displayName: string,
			name: string,
		}>
	}


	/**
	 * @class
	 * Initializes a new instance of the OperationListResponse class.
	 * @constructor
	 * A list of operations that can be performed by the Data Factory service.
	 *
	 * @member {string} [nextLink] The link to the next page of results, if any
	 * remaining results exist.
	 */
	export interface OperationListResponse extends Array<Operation> {
		nextLink?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the FactoryListResponse class.
	 * @constructor
	 * A list of factory resources.
	 *
	 * @member {string} [nextLink] The link to the next page of results, if any
	 * remaining results exist.
	 */
	export interface FactoryListResponse extends Array<Factory> {
		nextLink?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the IntegrationRuntimeListResponse class.
	 * @constructor
	 * A list of integration runtime resources.
	 *
	 * @member {string} [nextLink] The link to the next page of results, if any
	 * remaining results exist.
	 */
	export interface IntegrationRuntimeListResponse extends Array<IntegrationRuntimeResource> {
		nextLink?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the LinkedServiceListResponse class.
	 * @constructor
	 * A list of linked service resources.
	 *
	 * @member {string} [nextLink] The link to the next page of results, if any
	 * remaining results exist.
	 */
	export interface LinkedServiceListResponse extends Array<SharedModel.LinkedServiceResource> {
		nextLink?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the DatasetListResponse class.
	 * @constructor
	 * A list of dataset resources.
	 *
	 * @member {string} [nextLink] The link to the next page of results, if any
	 * remaining results exist.
	 */
	export interface DatasetListResponse extends Array<DatasetResource> {
		nextLink?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the PipelineListResponse class.
	 * @constructor
	 * A list of pipeline resources.
	 *
	 * @member {string} [nextLink] The link to the next page of results, if any
	 * remaining results exist.
	 */
	export interface PipelineListResponse extends Array<PipelineResource> {
		nextLink?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the ActivityRunsListResponse class.
	 * @constructor
	 * A list activity runs.
	 *
	 * @member {string} [nextLink] The link to the next page of results, if any
	 * remaining results exist.
	 */
	export interface ActivityRunsListResponse extends Array<ActivityRun> {
		continuationToken?: string; // handcrafted
		nextLink?: string;
		value: ActivityRun[]; // handcrafted
	}

	export interface CopyJobRunsListResponse extends Array<CopyJobActivityRun> {
		continuationToken?: string; // handcrafted
		nextLink?: string;
		value: CopyJobActivityRun[]; // handcrafted
	}

	export interface ApproveActivityRunResponse {
		code?: string; // handcrafted
		message?: string; // handcrafted
		target?: string; // handcrafted
		details?: unknown; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the TriggerListResponse class.
	 * @constructor
	 * A list of trigger resources.
	 *
	 * @member {string} [nextLink] The link to the next page of results, if any
	 * remaining results exist.
	 */
	export interface TriggerListResponse extends Array<TriggerResource> {
		nextLink?: string;
	}

	/**
	 * @class
	 * Initializes a new instance of the TriggerRunListResponse class.
	 * @constructor
	 * A list of trigger runs.
	 *
	 * @member {string} [nextLink] The link to the next page of results, if any
	 * remaining results exist.
	 */
	export interface TriggerRunListResponse extends Array<TriggerRun> {
		continuationToken?: string;
		value: TriggerRun[]; // handcrafted
	}

	// handcrafted
	/**
	 * @class
	 * Initializes a new instance of the Office365LinkedServiceTypeProperties class.
	 * @constructor
	 * Office365 linked service properties.
	 *
	 * @member {object} tenant The Office365 tenant ID. Type: string (or Expression
	 * with resultType string)
	 * @member {object} servicePrincipalId The application ID that has access to
	 * setup data access. Type: string (or Expression with resultType string)
	 * @member {object} servicePrincipalKey The application key that can be used
	 * with the principal ID to setup data access.
	 * @member {string} [servicePrincipalKey.value] Value of secure string.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface Office365LinkedServiceTypeProperties {
		office365TenantId: any;
		servicePrincipalId: any;
		servicePrincipalCredentialType?: string;
		servicePrincipalKey?: string | SharedModel.SecretBase; //handcraft
		servicePrincipalEmbeddedCert?: string | SharedModel.SecretBase; //handcraft
		servicePrincipalEmbeddedCertPassword?: string | SharedModel.SecretBase; //handcraft
		servicePrincipalTenantId: any;
		encryptedCredential?: any;
		credential?: any;
	}

	// handcrafted
	/**
	 * @class
	 * Initializes a new instance of the Office365LinkedService class.
	 * @constructor
	 * The Office365 linked service.
	 *
	 * @member {object} typeProperties Office365 linked service properties.
	 * @member {object} [typeProperties.o365DataDiscoveryServiceUrl] The Office365
	 * data discovery service URL. Type: string (or Expression with resultType
	 * string)
	 * @member {object} [typeProperties.tenant] The Office365 tenant ID. Type:
	 * string (or Expression with resultType string)
	 * @member {object} [typeProperties.office365SubscriptionId] The Office365
	 * subscription for which the application principal is authorized to setup data
	 * access. Type: string (or Expression with resultType string)
	 * @member {object} [typeProperties.servicePrincipalId] The application ID that
	 * has access to setup data access. Type: string (or Expression with resultType
	 * string)
	 * @member {object} [typeProperties.servicePrincipalKey] The application key
	 * that can be used with the principal ID to setup data access.
	 * @member {string} [typeProperties.servicePrincipalKey.value] Value of secure
	 * string.
	 * @member {object} [typeProperties.lockboxServicePrincipalId] The application
	 * ID that has permission to 'Manage Exchange configuration'. Type: string (or
	 * Expression with resultType string)
	 * @member {object} [typeProperties.lockboxServicePrincipalKey] The application
	 * key that can be used with lockbox service principal ID
	 * @member {string} [typeProperties.lockboxServicePrincipalKey.value] Value of
	 * secure string.
	 * @member {object} [typeProperties.appTermsOfUse] The link describing
	 * application's privacy policy. Type: string (or Expression with resultType
	 * string)
	 * @member {object} [typeProperties.appPrivacyPolicy] The link detailing
	 * application's terms of use. Type: string (or Expression with resultType
	 * string)
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface Office365LinkedService extends SharedModel.LinkedService {
		typeProperties: Office365LinkedServiceTypeProperties;
	}

	// handcrafted
	/**
	 * @class
	 * Initializes a new instance of the Office365DatasetTypeProperties class.
	 * @constructor
	 * Office365 dataset properties.
	 *
	 * @member {object} tableName The name of the data table. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [privacyRules] The list of selected privacy rules to be
	 * applied to the data. Type: array (or Expression with resultType array),
	 * itemType: string (or Expression with resultType string).
	 * @member {object} [predicate] The query provided for this table. Type: string
	 * (or Expression with resultType string).
	 */
	export interface Office365DatasetTypeProperties {
		tableName: any;
		privacyRules?: any;
		predicate?: any;
		dateFilterColumn?: any;
		startTime?: any;
		endTime?: any;
		allowedGroups?: any;
		userScopeFilterUri?: any;
	}

	// handcrafted
	/**
	 * @class
	 * Initializes a new instance of the Office365Dataset class.
	 * @constructor
	 * The Office365 table.
	 *
	 * @member {object} typeProperties Office365 dataset properties.
	 * @member {object} [typeProperties.tableName] The name of the data table.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.privacyRules] The list of selected privacy
	 * rules to be applied to the data. Type: array (or Expression with resultType
	 * array), itemType: string (or Expression with resultType string).
	 * @member {object} [typeProperties.predicate] The query provided for this
	 * table. Type: string (or Expression with resultType string).
	 */
	export interface Office365Dataset extends Dataset {
		typeProperties: Office365DatasetTypeProperties;
	}

	// handcrafted
	/**
	 * @class
	 * Initializes a new instance of the Office365Source class.
	 * @constructor
	 * A copy activity Office365 source.
	 *
	 * @member {object} [userList] The dataset storing the user list.
	 * @member {string} [userList.referenceName] Reference dataset name.
	 * @member {object} [userList.parameters] Arguments for dataset.
	 */
	export interface Office365Source extends CopySource {
		userList?: DatasetReference;
		dateFilterColumn?: any;
		startTime?: any;
		endTime?: any;
		allowedGroups?: any;
		userScopeFilterUri?: any;
		outputColumns?: any; // handcrafted
	}

	// handcrafted
	export interface KustoLinkedServiceTypeProperties {
		endpoint: string;
		servicePrincipalId: any;
		servicePrincipalKey: SharedModel.SecretBase;
		encryptedCredential?: any;
	}

	// handcrafted
	export interface KustoLinkedService extends SharedModel.LinkedService {
		typeProperties: KustoLinkedServiceTypeProperties;
	}

	// handcrafted
	export interface KustoDatasetTypeProperties {
	}

	// handcrafted
	export interface KustoDataset extends Dataset {
		typeProperties: KustoDatasetTypeProperties;
	}

	// handcrafted
	export interface KustoSource extends CopySource {
		database?: string;
		query?: any;
	}

	// handcrafted
	export interface AzureDataExplorerLinkedServiceTypeProperties {
		endpoint: string;
		tenant: string;
		servicePrincipalId: string;
		servicePrincipalKey: string | SharedModel.SecretBase;
		database?: string;
		encryptedCredential?: any;
		credential?: SharedModel.CredentialReference;
	}

	// handcrafted
	export interface KustoDatabaseLinkedServiceTypeProperties {
		workspaceId: string;
		endpoint: string;
		database: string;
	}

	// handcrafted
	export interface AzureDataExplorerLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureDataExplorerLinkedServiceTypeProperties;
	}

	// handcrafted
	export interface KustoDatabaseLinkedService extends SharedModel.LinkedService {
		typeProperties: KustoDatabaseLinkedServiceTypeProperties;
	}

	// handcrafted
	export interface AzureDataExplorerDatasetTypeProperties {
		table?: string;
	}

	// handcrafted
	export interface AzureDataExplorerFunctionSettings {
		functionName?: string;
		functionParameters?: { [propertyName: string]: StoredProcedureParameter };
	}

	// handcrafted
	export interface AzureDataExplorerDataset extends Dataset {
		typeProperties: AzureDataExplorerDatasetTypeProperties;
	}

	// handcrafted
	export interface AzureDataExplorerSource extends CopySource {
		query?: string;
		function?: any;
		functionParameters?: { [propertyName: string]: StoredProcedureParameter };
		queryTimeout?: string;
		noTruncation?: boolean;
	}


	// handcrafted
	export interface SnowflakeSource extends CopySource {
		query?: any;
		exportSettings?: ImportSettings;
	}
	export interface SnowflakeSourceV2 extends SnowflakeSource {
		treatDecimalAsString?: any;
	}

	// handcrafted
	export interface AzureDataExplorerDataset extends Dataset {
		typeProperties: AzureDataExplorerDatasetTypeProperties;
	}

	// handcrafted
	export interface AzureDataExplorerSink extends CopySink {
		ingestionMappingName?: any;
		additionalProperties?: any;
	}

	// handcrafted
	export interface AliasProperty {
		logicalName: string;
		logicalType: string;
	}

	// handcrafted
	export interface Alias {
		[key: string]: AliasProperty;
	}

	// handcrafted
	export interface DataTransformationTypeProperties {
		dataset?: DatasetReference;
		alias?: Alias[];
	}

	// handcrafted
	export interface ReadProperties {
		type: string;
	}

	// handcrafted
	export interface AzureBlobReadProperties extends ReadProperties {
		type: "AzureBlobReader";
		recursive: boolean;
	}

	// handcrafted
	export interface AmazonS3ReadProperties extends ReadProperties {
		type: "AmazonS3Reader";
		recursive: boolean;
	}

	// handcrafted
	export interface FileShareReadProperties extends ReadProperties {
		type: "FileShareReader";
		recursive: boolean;
	}

	// handcrafted
	export interface AzureDataLakeStoreFileReadProperties extends ReadProperties {
		type: "AzureDataLakeStoreFileReader";
		recursive: boolean;
	}

	// handcrafted
	export interface ReadTransformationTypeProperties extends DataTransformationTypeProperties {
		readProperties?: ReadProperties;
	}

	// handcrafted
	export interface Transformation {
		name: string;
		description?: string;
		type: string;
	}

	// handcrafted
	export interface ReadTransformation extends Transformation {
		typeProperties?: ReadTransformationTypeProperties;
	}

	// handcrafted
	export interface WriteProperties {
		type: string;
	}

	// handcrafted
	export interface AzureBlobWriteProperties extends WriteProperties {
		type: "AzureBlobWriter";
		copyBehavior: string;
	}

	// handcrafted
	export interface AzureDataLakeStoreFileWriteProperties extends WriteProperties {
		type: "AzureDataLakeStoreFileWriter";
		copyBehavior: string;
	}

	// handcrafted
	export interface FileShareWriteProperties extends WriteProperties {
		type: "FileShareWriter";
		copyBehavior: string;
	}

	// handcrafted
	export interface SqlDWWriteProperties extends WriteProperties {
		type: "SqlDWWriter";
		writeBatchTimeout?: string;
		writeBatchSize?: number;
		preCopyScript?: string;
		allowPolyBase?: boolean;
		polyBaseSettings?: PolybaseSettings;
	}

	// handcrafted
	export interface OracleTableWriteProperties extends WriteProperties {
		type: "OracleTableWriter";
		writeBatchTimeout?: string;
		writeBatchSize?: number;
		preCopyScript?: string;
	}

	// handcrafted
	export interface AzureSqlTableWriteProperties extends WriteProperties {
		type: "AzureSqlTableWriter";
		writeBatchTimeout?: string;
		writeBatchSize?: number;
		preCopyScript?: string;
	}

	// handcrafted
	export interface SqlServerTableWriteProperties extends WriteProperties {
		type: "SqlServerTableWriter";
		writeBatchTimeout?: string;
		writeBatchSize?: number;
		preCopyScript?: string;
	}

	// handcrafted
	export interface columnMappingRulesTypeProperties {
		mappings?: any
	}

	// handcrafted
	export interface columnMappingRule {
		type: string;
		typeProperties: columnMappingRulesTypeProperties;
	}

	// handcrafted
	export interface WriteTransformationTypeProperties extends DataTransformationTypeProperties {
		feed?: string;
		writeProperties?: WriteProperties;
		columnMappingRules?: columnMappingRule[];
	}

	// handcrafted
	export interface WriteTransformation extends Transformation {
		typeProperties?: WriteTransformationTypeProperties;
	}

	// handcrafted
	export interface DataFlowProperties {
		transformations: Transformation[];

		// executor properties
		cloudDataMovementUnits?: number;
		dataIntegrationUnits?: number;
		parallelCopies?: number;

		enableSkipIncompatibleRow?: boolean;
		redirectIncompatibleRowSettings?: RedirectIncompatibleRowSettings;

		enableStaging?: boolean;
		stagingSettings?: StagingSettings;
	}

	// handcrafted
	export interface DataFlow {
		name: string;
		description?: string;
		properties?: DataFlowProperties;
	}

	// handcrafted
	export interface DataFlowReference {
		referenceName: string;
		type: string;
		parameters?: any;
		datasetParameters?: { [propertyName: string]: any };
		linkedServiceParameters?: { [propertyName: string]: DataFlowReferenceLinkedServiceParameters };
	}

	// handcrafted
	export interface DataflowRefreshReference {
		referenceName: string;
	}

	export interface DataFlowReferenceLinkedServiceParameters {
		linkedService?: { [propertyName: string]: any };
		schemaLinkedService?: { [propertyName: string]: any };
		rejectedDataLinkedService?: { [propertyName: string]: any };
	}

	// handcrafted
	export interface DataFlowComputeProperties {
		coreCount: number;
		computeType: string;
	}

	// handcrafted
	export interface DataFlowTypeProperties {
		dataflow: DataFlowReference;
		staging?: DataFlowStagingLinkedServiceReference;
		sinks?: { [queryName: string]: IDataFlowSink }
		queries?: IDataFlowQuery[];
		integrationRuntime?: SharedModel.IntegrationRuntimeReference;
		compute?: DataFlowComputeProperties;
		traceLevel?: string;
		runConcurrently?: boolean;
		sourceStagingConcurrency?: number;
		continueOnError?: boolean;
		allowCaching?: boolean;
		cacheSinks?: CacheSinksProperties;
		dataFlowDebugSessionId?: string;
		continuationMode?: string;
		continuationSettings?: ContinuationSettings;
	}

	// handcrafted
	export interface DataflowRefreshTypeProperties {
		dataflowId: string,
		workspaceId: string,
		notifyOption: string,
		dataflowType?: string,
		credential?: SharedModel.CredentialReference;
		parameters?: { [propertyName: string]: any };
		compute?: DataFlowComputeProperties;
		traceLevel?: any;
		runConcurrently?: any;
		continueOnError?: any;
		staging?: DataFlowStagingLinkedServiceReference;
		continuationSettings?: ContinuationSettings;
		// Native Dataflow Gen2 only.
		// Name of the MDF query inside the mashup document selected to drive Spark execution.
		sparkTransformName?: string;
		// When true, the activity ignores any MDF queries in the document and runs only the
		// Power Query (M) queries.
		executeMashupQueriesOnly?: boolean;
	}

	// handcrafted
	export interface CacheSinksProperties {
		firstRowOnly: boolean;
	}

	// handcrafted
	export interface DataFlowStagingLinkedServiceReference {
		linkedService: SharedModel.LinkedServiceReference,
		linkedServiceName?: SharedModel.LinkedServiceReference,
		folderPath: string
	}

	// handcrafted
	export interface ContinuationSettings {
		customizedCheckpointKey?: string
	}

	// handcrafted
	export interface ExecuteDataFlowActivity extends Activity {
		typeProperties: DataFlowTypeProperties;
	}

	// handcrafted
	export interface ExecuteWranglingDataflowActivity extends Activity {
		typeProperties: DataFlowTypeProperties;
	}

	// handcrafted
	export interface DataflowRefreshActivity extends Activity {
		typeProperties: DataflowRefreshTypeProperties;
	}

	// handcrafted
	// Staging model for ForEach activity, which refers temp pipeline property instead of activity array.
	export interface ExecuteForEachActivity extends Activity {
		isSequential?: boolean;
		items?: any;
		pipeline?: PipelineModel;
	}

	// handcrafted
	/**
	 * @class
	 * Initializes a new instance of the AzureBlobStorageLinkedServiceTypeProperties class.
	 * @constructor
	 * Azure Blob Storage linked service properties.
	 *
	 * @member {object} [connectionString] The connection string. It is mutually
	 * exclusive with sasUri property. Type: string, SecureString or
	 * AzureKeyVaultSecretReference.
	 * @member {object} [sasUri] SAS URI of the Azure Storage resource. It is
	 * mutually exclusive with connectionString property.
	 * @member {string} [sasUri.type] Polymorphic Discriminator
	 * @member {object} [servicePrincipalId] The ID of the application used to
	 * authenticate against the Azure Blob Storage account. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [servicePrincipalKey] The Key of the application used to
	 * authenticate against the Azure Blob Storage account.
	 * @member {string} [servicePrincipalKey.type] Polymorphic Discriminator
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface AzureBlobStorageLinkedServiceTypeProperties {
		connectionString?: any;
		accountKey?: string | SharedModel.SecretBase;
		accountKind?: any;
		sasUri?: any;
		sasToken?: string | SharedModel.SecretBase;
		serviceEndpoint?: any;
		tenant?: any;
		servicePrincipalId?: any;
		servicePrincipalKey?: string | SharedModel.SecretBase;
		encryptedCredential?: any;
		azureCloudType?: any;
		credential?: SharedModel.CredentialReference;
		servicePrincipalCredentialType?: string;
		servicePrincipalCredential?: string | SharedModel.SecretBase;
		containerUri?: string;
		authenticationType?: string;
		wiUrl?: string;
	}

	// handcrafted
	/**
	 * @class
	 * Initializes a new instance of the AzureBlobStorageLinkedService class.
	 * @constructor
	 * The storage account linked service.
	 *
	 * @member {object} typeProperties Azure Blob Storage linked service properties.
	 * @member {object} [typeProperties.connectionString] The connection string. It
	 * is mutually exclusive with sasUri property. Type: string, SecureString or
	 * AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.sasUri] SAS URI of the Azure Storage
	 * resource. It is mutually exclusive with connectionString property.
	 * @member {string} [typeProperties.sasUri.type] Polymorphic Discriminator
	 * @member {object} [typeProperties.servicePrincipalId] The ID of the application used to
	 * authenticate against the Azure Blob Storage account. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [typeProperties.servicePrincipalKey] The Key of the application used to
	 * authenticate against the Azure Blob Storage account.
	 * @member {string} [typeProperties.servicePrincipalKey.type] Polymorphic
	 * Discriminator
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface AzureBlobStorageLinkedService extends SharedModel.LinkedService {
		typeProperties: AzureBlobStorageLinkedServiceTypeProperties;
	}

	/**
 * @class
 * Initializes a new instance of the native MongoDbLinkedService class.
 * @constructor
 * Linked service for MongoDb data source.
 *
 * @member {object} typeProperties native MongoDB linked service properties.
 * @member {object} [typeProperties.connectionString] The non-access credential portion of the
 * connection string as well as an optional encrypted credential. Type: string,
 * SecureString or AzureKeyVaultSecretReference.
 * @member {string} [typeProperties.databaseName ] The MongoDB database name.
 * @member {object} [encryptedCredential] The encrypted credential used for
 * authentication. Credentials are encrypted using the integration runtime
 * credential manager. Type: string (or Expression with resultType string).
 */
	export interface NativeMongoDbLinkedServiceTypeProperties { //handcraft
		connectionString: any, //handcraft
		database: string, //handcraft
		encryptedCredential?: any; //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the NativeMongoDbLinkedService class.
	 * @constructor
	 * Linked service for MongoDb data source.
	 *
	 * @member {object} typeProperties Native mongoDB linked service properties.
	 * @member {object} [typeProperties.connectionString] The non-access credential portion of the
	 * connection string as well as an optional encrypted credential. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {string} [typeProperties.databaseName ] The native mongoDB database name.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface NativeMongoDbLinkedService extends SharedModel.LinkedService { //handcraft
		typeProperties: NativeMongoDbLinkedServiceTypeProperties; //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the NativeMongoDbCollectionDatasetTypeProperties class.
	 * @constructor
	 * native MongoDB database dataset properties.
	 *
	 * @member {object} collection The collection name of the native MongoDB database.
	 * Type: string (or Expression with resultType string).
	 */
	export interface NativeMongoDbCollectionDatasetTypeProperties {//handcraft
		collection: any;  //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the NativeMongoDbCollectionDataset class.
	 * @constructor
	 * The native MongoDB database dataset.
	 *
	 * @member {object} typeProperties MongoDB database dataset properties.
	 * @member {object} [typeProperties.collection] The collection name of the
	 * native MongoDB database. Type: string (or Expression with resultType string).
	 */
	export interface NativeMongoDbCollectionDataset extends Dataset {//handcraft
		typeProperties: NativeMongoDbCollectionDatasetTypeProperties; //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the NativeMongoDbSource class.
	 * @constructor
	 * A copy activity source for a Native MongoDB database.
	 *
	 * @member {object} [batchSize] Specifies the number of documents to return in each batch of the response from MongoDB instance.
	 *  Type: int (or Expression with resultType int).
	 * @member {object} [filter] Specifies selection filter using query operators.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [cursorMethods] Specifies the cursor Methods act on the documents that match the query filter.
	 * Type: string (or Expression with resultType string).
	 */
	export interface NativeMongoDbSource extends CopySource {  //handcraft
		batchSize?: number;  //handcraft
		filter?: string;  //handcraft
		cursorMethods?: any;  //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the NativeMongoDbSink class.
	 * @constructor
	 * A copy activity sink for a Native Mongo database.
	 *
	 * @member {string} [writeBehavior] The write behavior for the operation.
	 * Default is 'insert'. Possible values include: 'insert', 'upsert'. Both upper and lower case are supported.
	 * @member {object} [writeBatchSize] Write batch size. Type: integer (or
	 * Expression with resultType integer), minimum: 0.
	 * @member {object} [writeBatchTimeout] Write batch timeout. Type: string (or
	 * Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 */
	export interface NativeMongoDbSink extends CopySink {  //handcraft
		writeBehavior?: string; // handcrafted
		writeBatchTimeout?: string; // handcrafted
		writeBatchSize?: number; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the MongoDb Atlas LinkedService class.
	 * @constructor
	 * Linked service for MongoDb data source.
	 *
	 * @member {object} typeProperties MongoDB Atlas linked service properties.
	 * @member {object} [typeProperties.connectionString] The non-access credential portion of the
	 * connection string as well as an optional encrypted credential. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {string} [typeProperties.databaseName ] The MongoDB Atlas database name.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface MongoDbAtlasLinkedServiceTypeProperties { //handcraft
		connectionString: any, //handcraft
		database: string, //handcraft
		driverVersion: string, //handcraft
		encryptedCredential?: any; //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the MongoDbAtlasLinkedService class.
	 * @constructor
	 * Linked service for MongoDb Atlas data source.
	 *
	 * @member {object} typeProperties MongoDb Atlas linked service properties.
	 * @member {object} [typeProperties.connectionString] The non-access credential portion of the
	 * connection string as well as an optional encrypted credential. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {string} [typeProperties.databaseName ] The MongoDb Atlas database name.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface MongoDbAtlasLinkedService extends SharedModel.LinkedService { //handcraft
		typeProperties: MongoDbAtlasLinkedServiceTypeProperties; //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the MongoDbAtlasCollectionDatasetTypeProperties class.
	 * @constructor
	 * MongoDB Atlas database dataset properties.
	 *
	 * @member {object} collection The collection name of the MongoDb Atlas database.
	 * Type: string (or Expression with resultType string).
	 */
	export interface MongoDbAtlasCollectionDatasetTypeProperties {//handcraft
		collection: any;  //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the MongoDbAtlasCollectionDataset class.
	 * @constructor
	 * The MongoDB Atlas database dataset.
	 *
	 * @member {object} typeProperties MongoDb Atlas database dataset properties.
	 * @member {object} [typeProperties.collection] The collection name of the
	 * MongoDB Atlas database. Type: string (or Expression with resultType string).
	 */
	export interface MongoDbAtlasCollectionDataset extends Dataset {//handcraft
		typeProperties: MongoDbAtlasCollectionDatasetTypeProperties; //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the MongoDbAtlasSource class.
	 * @constructor
	 * A copy activity source for a MongoDb Atlas database.
	 *
	 * @member {object} [batchSize] Specifies the number of documents to return in each batch of the response from MongoDB instance.
	 *  Type: int (or Expression with resultType int).
	 * @member {object} [filter] Specifies selection filter using query operators.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [cursorMethods] Specifies the cursor Methods act on the documents that match the query filter.
	 * Type: string (or Expression with resultType string).
	 */
	export interface MongoDbAtlasSource extends CopySource {  //handcraft
		batchSize?: number;  //handcraft
		filter?: string;  //handcraft
		cursorMethods?: any;  //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the MongoDbAtlasSink class.
	 * @constructor
	 * A copy activity sink for a Mongo Atlas database.
	 *
	 * @member {string} [writeBehavior] The write behavior for the operation.
	 * Default is 'insert'. Possible values include: 'insert', 'upsert'. Both upper and lower case are supported.
	 * @member {object} [writeBatchSize] Write batch size. Type: integer (or
	 * Expression with resultType integer), minimum: 0.
	 * @member {object} [writeBatchTimeout] Write batch timeout. Type: string (or
	 * Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 */
	export interface MongoDbAtlasSink extends CopySink {  //handcraft
		writeBehavior?: string; // handcrafted
		writeBatchTimeout?: string; // handcrafted
		writeBatchSize?: number; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the CosmosDbMongoDbApiLinkedServiceTypeProperties class.
	 * @constructor
	 * CosmosDB (MongoDB api) linked service properties.
	 *
	 * @member {object} connectionString The connection string. Type: string,
	 * SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface CosmosDbMongoDbApiLinkedServiceTypeProperties { // handcraft
		connectionString: any; // handcraft
		account?: any; // handcrafted
		database: string; // handcraft
		encryptedCredential?: any; // handcraft
		isServerVersionAbove32?: boolean; // handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the CosmosDbMongoDbApiLinkedService class.
	 * @constructor
	 * Microsoft Azure Cosmos Database (CosmosDB) linked service.
	 *
	 * @member {object} typeProperties CosmosDB (MongoDB api)  linked service properties.
	 * @member {object} [typeProperties.connectionString] The connection string.
	 * Type: string, SecureString or AzureKeyVaultSecretReference.
	 * @member {object} [typeProperties.encryptedCredential] The encrypted
	 * credential used for authentication. Credentials are encrypted using the
	 * integration runtime credential manager. Type: string (or Expression with
	 * resultType string).
	 */
	export interface CosmosDbMongoDbApiLinkedService extends SharedModel.LinkedService { //handcraft
		typeProperties: CosmosDbMongoDbApiLinkedServiceTypeProperties; // handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the DocumentDbCollectionDatasetTypeProperties class.
	 * @constructor
	 * DocumentDB Collection dataset properties.
	 *
	 * @member {object} collectionName Document Database collection name. Type:
	 * string (or Expression with resultType string).
	 */
	export interface DocumentDbMongoDBApiCollectionDatasetTypeProperties { //handcraft
		collection: any; //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the DocumentDbCollectionDataset class.
	 * @constructor
	 * Microsoft Azure Document Database Collection dataset.
	 *
	 * @member {object} typeProperties DocumentDB Collection dataset properties.
	 * @member {object} [typeProperties.collection] Document Database
	 * collection name. Type: string (or Expression with resultType string).
	 */
	export interface DocumentDbMongoDBApiCollectionDataset extends Dataset { //handcraft
		typeProperties: DocumentDbMongoDBApiCollectionDatasetTypeProperties; //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the DocumentDbMongoApiCollectionSource class.
	 * @constructor
	 * A copy activity Document Database Collection (Mongo Api) source.
	 *
	 * @member {object} [batchSize] Specifies the number of documents to return in each batch of the response from MongoDB instance.
	 *  Type: int (or Expression with resultType int).
	 * @member {object} [filter] Specifies selection filter using query operators.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [cursorMethods] Specifies the cursor Methods act on the documents that match the query filter.
	 * Type: string (or Expression with resultType string).
	 */
	export interface DocumentDbMongoApiCollectionSource extends CopySource {  //handcraft
		batchSize?: number;  //handcraft
		filter?: string;  //handcraft
		cursorMethods?: any;  //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the DocumentDbMongoDbApiCollectionSink class.
	 * @constructor
	 * A copy activity Document Database Collection (MongoDb Api) sink.
	 * @member {string} [writeBehavior] The write behavior for the operation.
	 * Default is 'insert'. Possible values include: 'insert', 'upsert'. Both upper and lower case are supported.
	 * @member {object} [writeBatchSize] Write batch size. Type: integer (or
	 * Expression with resultType integer), minimum: 0.
	 * @member {object} [writeBatchTimeout] Write batch timeout. Type: string (or
	 * Expression with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 */
	export interface DocumentDbMongoDbApiCollectionSink extends CopySink { // handcrafted
		writeBehavior?: string; // handcrafted
		writeBatchTimeout?: string; // handcrafted
		writeBatchSize?: number; // handcrafted
	}

	/**
	 * @class
	 * Initializes a new instance of the RestLinkedServiceTypeProperties class.
	 * @constructor
	 * Properties specific to this linked service type.
	 *
	 * @member {object} url The base URL of the REST service, e.g.
	 * http://www.microsoft.com. Type: string (or Expression with resultType
	 * string).
	 * @member {string} [authenticationType] The authentication type to be used to
	 * connect to the REST service. Allowed values are Anonymous, Basic,
	 * AadServicePrincipal and ManagedServiceIdentity
	 * @member {object} [userName] User name for Basic authentication.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [password] Password for Basic authentication.
	 * @member {string} [servicePrincipalId] The application’s client ID
	 * used in AadServicePrincipal authentication type.
	 * @member {string} [servicePrincipalKey] The application’s key used
	 * in AadServicePrincipal authentication type
	 * @member {object} [tenant] The tenant information (domain name or tenant ID)
	 * used in AadServicePrincipal authentication type under which your application residesType:
	 * string (or Expression with resultType string).
	 * @member {object} [aadResourceId] The resource you are requesting authorization to use.
	 *  Type: string (or Expression with resultType string).
	 * @member {object} [enableServerCertificateValidation] Whether to validate server side SSL
	 * certificate when connecting to the endpoint.  The default value is true. Type: boolean (or
	 * Expression with resultType boolean).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 */
	export interface RestLinkedServiceTypeProperties { //handcraft
		url: any; //handcraft
		authenticationType?: string; //handcraft
		authHeaders?: {
			[key: string]: SecretBase;
		};
		userName?: any; //handcraft
		password?: string | SharedModel.SecretBase; //handcraft
		servicePrincipalId?: any; //handcraft
		servicePrincipalCredentialType?: string;
		servicePrincipalKey?: string | SharedModel.SecretBase; //handcraft
		servicePrincipalEmbeddedCert?: string | SharedModel.SecretBase; //handcraft
		servicePrincipalEmbeddedCertPassword?: string | SharedModel.SecretBase; //handcraft
		tenant?: any; //handcraft
		aadResourceId?: any; //handcraft
		enableServerCertificateValidation?: any; //handcraft
		encryptedCredential?: any; //handcraft
		azureCloudType?: any; // handcraft
		clientId?: any; // handcraft
		clientSecret?: string | SharedModel.SecretBase; // handcraft
		tokenEndpoint?: any; // handcraft
		scope?: any; // handcraft
		resource?: any; // handcraft
		credential?: SharedModel.CredentialReference; //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the HttpLinkedService class.
	 * @constructor
	 * Linked service for an HTTP source.
	 *
	 * @member {object} url The base URL of the REST service, e.g.
	 * http://www.microsoft.com. Type: string (or Expression with resultType
	 * string).
	 * @member {string} [authenticationType] The authentication type to be used to
	 * connect to the REST service. Allowed values are Anonymous, Basic,
	 * AadServicePrincipal and ManagedServiceIdentity
	 * @member {object} [userName] User name for Basic authentication.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [password] Password for Basic authentication.
	 * @member {string} [servicePrincipalId] The application’s client ID
	 * used in AadServicePrincipal authentication type.
	 * @member {string} [servicePrincipalKey] The application’s key used
	 * in AadServicePrincipal authentication type
	 * @member {object} [tenant] The tenant information (domain name or tenant ID)
	 * used in AadServicePrincipal authentication type under which your application residesType:
	 * string (or Expression with resultType string).
	 * @member {object} [aadResourceId] The resource you are requesting authorization to use.
	 *  Type: string (or Expression with resultType string).
	 * @member {object} [encryptedCredential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager. Type: string (or Expression with resultType string).
	 * @member {object} [enableServerCertificateValidation] Whether to validate server side SSL
	 * certificate when connecting to the endpoint.  The default value is true. Type: boolean (or
	 * Expression with resultType boolean).
	 */
	export interface RestLinkedService extends SharedModel.LinkedService { //handcraft
		typeProperties: RestLinkedServiceTypeProperties; //handcraft
	}

	export interface RestLinkedServiceDummy extends SharedModel.LinkedService {
	}

	/**
	 * @class
	 * Initializes a new instance of the RestDatasetTypeProperties class.
	 * @constructor
	 * Properties specific to this dataset type.
	 *
	 * @member {object} [relativeUrl] The relative URL to the resource that the RESTful API provides.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [requestMethod] The HTTP method used to call the RESTful API.
	 * The default is GET. Type: string (or Expression with resultType string).
	 * @member {object} [requestBody] The HTTP request body to the RESTful API if requestMethod is POST. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [additionalHeaders] The additional HTTP headers in the request to the RESTful API.
	 * @member {object} [paginationRules] The pagination rules to compose next page requests.
	 */
	export interface RestDatasetTypeProperties { //handcraft
		relativeUrl?: any; //handcraft
		requestMethod?: any; //handcraft
		requestBody?: any; //handcraft
		additionalHeaders?: any;//handcraft
		paginationRules?: any; //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the RestDatasetTypeProperties class.
	 * @constructor
	 * Properties specific to this dataset type.
	 *
	 * @member {object} [relativeUrl] The relative URL to the resource that the RESTful API provides.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [requestMethod] The HTTP method used to call the RESTful API.
	 * The default is GET. Type: string (or Expression with resultType string).
	 * @member {object} [requestBody] The HTTP request body to the RESTful API if requestMethod is POST. Type: string
	 * (or Expression with resultType string).
	 * @member {object} [additionalHeaders] The additional HTTP headers in the request to the RESTful API.
	 * @member {object} [paginationRules] The pagination rules to compose next page requests.
	 */
	export interface RestDataset extends Dataset { //handcraft
		typeProperties: RestDatasetTypeProperties; //handcraft
	}

	/**
	 * @class
	 * Initializes a new instance of the RestSource class.
	 * @constructor
	 * A copy activity source for an Rest resource.
	 *
	 * @member {object} [httpRequestTimeout] Specifies the timeout for a HTTP
	 * client to get HTTP response from HTTP server. The default value is
	 * equivalent to System.Net.HttpWebRequest.Timeout. Type: string (or Expression
	 * with resultType string), pattern:
	 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {object} [requestInterval] The time to await before sending next page request.  Default value: 00:00:01
	 * Type: string (or Expression with resultType string), pattern: ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 */
	export interface RestSource extends CopySource {//handcraft
		httpRequestTimeout?: any;//handcraft
		requestInterval?: any;//handcraft
		requestMethod?: any; //handcraft
		requestBody?: any; //handcraft
		additionalHeaders?: any;//handcraft
		paginationRules?: any; //handcraft
	}

	/**
 * @class
 * Initializes a new instance of the RestSource class.
 * @constructor
 * A copy activity source for an Rest resource.
 *
 * @member {object} [httpRequestTimeout] Specifies the timeout for a HTTP
 * client to get HTTP response from HTTP server. The default value is
 * equivalent to System.Net.HttpWebRequest.Timeout. Type: string (or Expression
 * with resultType string), pattern:
 * ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
 * @member {object} [requestInterval] The time to await before sending next page request.  Default value: 00:00:01
 * Type: string (or Expression with resultType string), pattern: ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
 */
	export interface RestSink extends CopySink {//handcraft
		httpRequestTimeout?: any;//handcraft
		requestInterval?: any;//handcraft
		requestMethod?: any; //handcraft
		writeBatchSize?: any; //handcraft
		additionalHeaders?: any;//handcraft
		httpCompressionType?: any; //handcraft
	}

	// handcrafted
	export interface OracleServiceCloudLinkedServiceTypeProperties {
		host: any;
		username: any;
		password?: SharedModel.SecretBase;
		useEncryptedEndpoints?: any;
		useHostVerification?: any;
		usePeerVerification?: any;
		encryptedCredential?: any;
	}

	export interface OracleServiceCloudLinkedService extends SharedModel.LinkedService {
		typeProperties: OracleServiceCloudLinkedServiceTypeProperties;
	}

	export interface OracleServiceCloudObjectDataset extends Dataset {
	}

	export interface OracleServiceCloudSource extends CopySource {
		query?: any;
	}

	// handcrafted
	export interface GoogleAdWordsLinkedServiceTypeProperties {
		clientCustomerID?: any;
		loginCustomerID?: any;
		developerToken?: string | SharedModel.SecretBase;
		authenticationType?: string;
		refreshToken?: string | SharedModel.SecretBase;
		clientId?: string;
		clientSecret?: string | SharedModel.SecretBase;
		email?: any;
		keyFilePath?: any;
		trustedCertPath?: any;
		useSystemTrustStore?: any;
		encryptedCredential?: any;
		connectionProperties?: GoogleAdWordsV2LinkedServiceTypeProperties;
		googleAdsApiVersion?: string;
		supportLegacyDataTypes?: boolean; // write-once ready-only
		privateKey?: string | SharedModel.SecretBase;
		[key: string]: any;
	}

	export interface GoogleAdWordsV2LinkedServiceTypeProperties {
		clientCustomerID: any;
		loginCustomerID?: any;
		developerToken?: string | SharedModel.SecretBase;
		authenticationType?: string;
		refreshToken?: string | SharedModel.SecretBase;
		clientId?: string;
		clientSecret?: string | SharedModel.SecretBase;
		email?: any;
		keyFilePath?: any;
		trustedCertPath?: any;
		useSystemTrustStore?: any;
		encryptedCredential?: any;
		supportLegacyDataTypes?: boolean; // write-once ready-only
		[key: string]: any;
	}

	export interface GoogleAdWordsLinkedService extends SharedModel.LinkedService {
		typeProperties: GoogleAdWordsLinkedServiceTypeProperties;
	}

	export interface GoogleAdWordsObjectDataset extends Dataset {
	}

	export interface GoogleAdWordsSource extends CopySource {
		query?: any;
	}

	// handcrafted
	export interface DynamicsAXLinkedServiceTypeProperties {
		url: any;
		tenant: any;
		servicePrincipalId: any;
		servicePrincipalKey?: SharedModel.SecretBase;
		aadResourceId: any;
		encryptedCredential?: any;
		credential?: any;
	}

	export interface DynamicsAXLinkedService extends SharedModel.LinkedService {
		typeProperties: DynamicsAXLinkedServiceTypeProperties;
	}

	export interface DynamicsAXResourceDatasetTypeProperties {
		path?: any;
	}

	export interface DynamicsAXResourceDataset extends Dataset {
		typeProperties: DynamicsAXResourceDatasetTypeProperties;
	}

	export interface DynamicsAXSource extends CopySource {
		query?: any;
		httpRequestTimeout?: any;
	}

	export interface DeleteActivityTypeProperties {
		dataset?: DatasetReference;
		datasetSettings?: any;
		enableLogging?: any;
		logStorageSettings?: StagingSettings;
		recursive?: boolean;
		maxConcurrentConnections?: any;
		storeSettings?: StoreReadSettings;
	}

	// handcrafted
	export interface GitHubLinkedServiceTypeProperties {
		username: any;
		credential: SharedModel.SecretBase;
	}

	export interface GitHubLinkedService extends SharedModel.LinkedService {
		typeProperties: GitHubLinkedServiceTypeProperties;
	}

	// handcrafted
	export interface AdobeIntegrationLinkedServiceTypeProperties {
		orgId: any;
		techAccountId: any;
		clientId: any;
		exp: any;
		metaScopes: any;
		journalEndpoint: any;
		tenantId: any;
		privateKey: SharedModel.SecretBase;
		clientSecret: SharedModel.SecretBase;
	}

	// handcrafted
	export interface AdobeIntegrationLinkedService extends SharedModel.LinkedService {
		typeProperties: AdobeIntegrationLinkedServiceTypeProperties;
	}

	// handcrafted
	export interface CognitiveServiceLinkedServiceTypeProperties {
		subscriptionId: string;
		resourceGroup: string;
		csName: string;
		csKind: string;
		csLocation: string;
		endPoint: string;
		csKey: SharedModel.SecretBase;
	}

	// handcrafted
	export interface CognitiveServiceLinkedService extends SharedModel.LinkedService {
		typeProperties: CognitiveServiceLinkedServiceTypeProperties;
	}

	// handcrafted
	/**
	 * @class
	 * Initializes a new instance of the TrinoLinkedServiceTypeProperties class.
	 * @constructor
	 * Trino linked service properties.
	 * based-off AzureTrino.ConnectorDefinition.json in HybridDataIS repo
	 *
	 * @member {string} [host] The host name of the Trino instance.
	 * @member {string} [catelog] The default catalog context to use when executing the query.
	 * @member {string} [schema] The default schema context to use when executing the query.
	 * @member {string} [authenticationType] Authentication mode used to connect to the Trino cluster. Allowed values are: ServicePrincipal
	 * @member {string} [clientId] AAD client credential flow client id.
	 * @member {string} [clientSecret] AAD client credential flow client secret.
	 * @member {string} [tenant] AAD client credential flow tenant.
	 * @member {string} [azureCloudType] Indicates the azure cloud type of the service principle auth.
	 * @member {string} [sessionProperties] [optional] Session properties to use to modify query execution behavior.
	 * Session property should be in the form of key:value pairs, and each pair should be separated by a comma.
	 * i.e.: distributed_sort:true,colocated_join:false
	 */
	export interface TrinoLinkedServiceTypeProperties {
		host: any;
		catalog: any;
		schema?: any;
		authenticationType: any;
		servicePrincipalId: any;
		servicePrincipalKey: string | SharedModel.SecretBase;
		tenant: any;
		sessionProperties?: any;
		azureCloudType?: 'AzurePublic' | 'AzureChina' | 'AzureUSGovernment' | 'AzureGermany';
		credential?: SharedModel.CredentialReference;
	}

	// handcrafted
	/**
	 * @class
	 * Initializes a new instance of the TrinoLinkedService class.
	 * @constructor
	 * Trino linked service.
	 *
	 * @member {object} typeProperties Trino linked service properties.
	 * @member {object} [typeProperties.connectionString] The connection string.
	 * @member {object} [typeProperties.servicePrincipalId] The ID of the service principal used
	 * to authenticate against Trino Database. Type: string (or Expression with
	 * resultType string).
	 * @member {object} [typeProperties.servicePrincipalKey] The key of the service principal used
	 * to authenticate against Trino Database.
	 * @member {object} [typeProperties.tenant] The name or ID of the tenant to which the service
	 * principal belongs. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.credential] The encrypted credential used for
	 * authentication. Credentials are encrypted using the integration runtime
	 * credential manager.
	 */
	export interface TrinoLinkedService extends SharedModel.LinkedService {
		typeProperties: TrinoLinkedServiceTypeProperties;
	}

	export interface TrinoSource extends CopySource {
		query?: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the DeleteActivity class.
	 * @constructor
	 * Delete activity type.
	 *
	 * @member {object} typeProperties Delete activity properties.
	 * @member {array} [typeProperties.dataset] reference dataset name.
	 * @member {object} [typeProperties.recursive] delete files recursive.
	 */
	// handcrafted
	export interface DeleteActivity extends ExecutionActivity {
		typeProperties: DeleteActivityTypeProperties;
	}

	// handcrafted
	export interface SsisObjectMetadata {
		type: string;
		id: number;
		name: string;
		description: string;
	}

	// handcrafted
	export interface SsisFolder extends SsisObjectMetadata {
	}

	// handcrafted
	export interface SsisProject extends SsisObjectMetadata {
		folderId: number;
		version: number;
		environmentRefs: SsisEnvironmentReference[];
		parameters: SsisParameter[];
	}

	// handcrafted
	export interface SsisPackage extends SsisObjectMetadata {
		folderId: number;
		projectVersion: number;
		projectId: number;
		parameters: SsisParameter[];
	}

	// handcrafted
	export interface SsisEnvironment extends SsisObjectMetadata {
		folderId: number;
		variables: SsisVariable[];
	}

	// handcrafted
	export interface SsisParameter {
		id: number;
		name: string;
		description: string;
		dataType: string;
		required: boolean;
		sensitive: boolean;
		designDefaultValue: string;
		defaultValue: string;
		sensitiveDefaultValue: string;
		valueType: string;
		valueSet: boolean;
		variable: string;
	}

	// handcrafted
	export interface SsisVariable {
		id: number;
		name: string;
		description: string;
		dataType: string;
		sensitive: boolean;
		value: string;
		sensitiveValue: string;
	}

	// handcrafted
	export interface SsisEnvironmentReference {
		id: number;
		environmentFolderName: string;
		environmentName: string;
		referenceType: string;
	}

	export interface SsisObjectMetadataListResponse {
		value: SsisObjectMetadata[];
		nextLink?: string;
	}

	// handcrafted
	export interface GetSsisObjectMetadataRequest {
		metadataPath: string;
	}

	// handcrafted
	export interface OperationStatusResponse {
		status: string;
		operationProperties: any;
	}

	/**
	 * @class
	 * Initializes a new instance of the WebHookActivityTypeProperties class.
	 * @constructor
	 * Webhook activity type properties.
	 *
	 * @member {string} method Rest API method for target endpoint. Possible values
	 * include: 'POST'
	 * @member {object} url Webhook activity target endpoint and path. Type: string (or
	 * Expression with resultType string).
	 * @member {object} [timeout] Specifies the timeout for the activity to run.
	 * The default timeout is 7 days. Type: string (or Expression with resultType
	 * string), pattern: ((\d+)\.)?(\d\d):(60|([0-5][0-9])):(60|([0-5][0-9])).
	 * @member {object} [headers] Represents the headers that will be sent to the
	 * request. For example, to set the language and type on a request: "headers" :
	 * { "Accept-Language": "en-us", "Content-Type": "application/json" }. Type:
	 * string (or Expression with resultType string).
	 * @member {object} [body] Represents the payload that will be sent to the
	 * endpoint. Required for POST/PUT method, not allowed for GET method Type:
	 * string (or Expression with resultType string).
	 * @member {object} [reportStatusOnCallBack] If true, user can set statusCode, output and error
	 * in callback request to be consumed by the webhook activity.
	 * Default is false. Type: boolean.
	 * @member {object} [authentication] Authentication method used for calling the
	 * endpoint.
	 * @member {string} [authentication.type] Webhook activity authentication
	 * (Basic/ClientCertificate/MSI)
	 * @member {object} [authentication.pfx] Base64-encoded contents of a PFX file.
	 * @member {string} [authentication.pfx.value] Value of secure string.
	 * @member {string} [authentication.username] Webhook activity authentication user
	 * name for basic authentication.
	 * @member {object} [authentication.password] Password for the PFX file or
	 * basic authentication.
	 * @member {string} [authentication.password.value] Value of secure string.
	 * @member {string} [authentication.resource] Resource for which Azure Auth
	 * token will be requested when using MSI Authentication.
	 */
	// handcrafted
	export interface WebHookActivityTypeProperties {
		method: string;
		connectVia?: SharedModel.IntegrationRuntimeReference
		url?: any;
		relativeUrl?: any;
		resource?: any;
		timeout: string;
		headers?: any;
		body?: any;
		reportStatusOnCallBack?: boolean;
		authentication?: WebActivityAuthentication;
		disableCertValidation?: boolean;
		userTenant?: string;
		webLinkedService?: SharedModel.LinkedServiceReference;
	}

	/**
	 * @class
	 * Initializes a new instance of the WebHookActivity class.
	 * @constructor
	 * Webhook activity type.
	 *
	 * @member {object} typeProperties Webhook activity properties.
	 * @member {string} [typeProperties.method] Rest API method for target
	 * endpoint. Possible values include: 'GET', 'POST', 'PUT', 'DELETE'
	 * @member {object} [typeProperties.url] Webhook activity target endpoint and path.
	 * Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.headers] Represents the headers that will
	 * be sent to the request. For example, to set the language and type on a
	 * request: "headers" : { "Accept-Language": "en-us", "Content-Type":
	 * "application/json" }. Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.body] Represents the payload that will be
	 * sent to the endpoint. Required for POST/PUT method, not allowed for GET
	 * method Type: string (or Expression with resultType string).
	 * @member {object} [typeProperties.reportStatusOnCallBack] If true, user can set statusCode, output and error
	 * in callback request to be consumed by the webhook activity.
	 * Default is false. Type: boolean.
	 * @member {object} [typeProperties.authentication] Authentication method used
	 * for calling the endpoint.
	 * @member {string} [typeProperties.authentication.type] Webhook activity
	 * authentication (Basic/ClientCertificate/MSI)
	 * @member {object} [typeProperties.authentication.pfx] Base64-encoded contents
	 * of a PFX file.
	 * @member {string} [typeProperties.authentication.pfx.value] Value of secure
	 * string.
	 * @member {string} [typeProperties.authentication.username] Webhook activity
	 * authentication user name for basic authentication.
	 * @member {object} [typeProperties.authentication.password] Password for the
	 * PFX file or basic authentication.
	 * @member {string} [typeProperties.authentication.password.value] Value of
	 * secure string.
	 * @member {string} [typeProperties.authentication.resource] Resource for which
	 * Azure Auth token will be requested when using MSI Authentication.
	 */
	// handcrafted
	export interface WebHookActivity extends ExecutionActivity {
		typeProperties: WebHookActivityTypeProperties;
	}
	// handcrafted
	export interface DatasetV2 extends Dataset {
		schema?: any;
	}

	// handcrafted
	export interface DatasetLocation {
		type: string;
	}

	// handcrafted
	export interface AzureBlobLocation extends DatasetLocation {
		container: any;
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface ADLSGen2Location extends DatasetLocation {
		container?: any;
		fileSystem?: any;
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface LakehouseLocation extends DatasetLocation {
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface OneLakeFilesLocation extends DatasetLocation {
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface SftpLocation extends DatasetLocation {
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface HdfsLocation extends DatasetLocation {
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface HttpServerLocation extends DatasetLocation {
		relativeUrl?: any;
	}

	// handcrafted
	export interface ADLSGen1Location extends DatasetLocation {
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface AmazonS3Location extends DatasetLocation {
		bucketName?: any;
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface AmazonS3CompatibleLocation extends DatasetLocation {
		bucketName?: any;
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface OracleCloudStorageLocation extends DatasetLocation {
		bucketName?: any;
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface GoogleCloudStorageLocation extends DatasetLocation {
		bucketName?: any;
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface FtpServerLocation extends DatasetLocation {
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface FileServerLocation extends DatasetLocation {
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface AzureFileStorageLocation extends DatasetLocation {
		folderPath?: any;
		fileName?: any;
	}

	// handcrafted
	export interface SharePointOnlineFileLocation extends DatasetLocation {
		folderPath?: any;
		fileName?: any;
	}

	export interface AdobeExperiencePlatformLocation extends DatasetLocation {
		datasetId: any;
	}

	// handcraft
	export interface AdobeExperiencePlatformDatasetTypeProperties {
		datasetId: any;
	}

	export interface AdobeExperiencePlatformDataset extends Dataset {
		typeProperties: AdobeExperiencePlatformDatasetTypeProperties;
	}

	// handcrafted
	export interface ParquetDatasetTypeProperties {
		location?: AzureBlobLocation | ADLSGen2Location | ADLSGen1Location;
		compressionCodec?: any;
	}

	// handcrafted
	export interface DeltaDatasetTypeProperties {
		location?: ADLSGen2Location | LakehouseLocation;
		compressionCodec?: any;
	}

	// handcrafted
	export interface BinaryDataset extends DatasetV2 {
		typeProperties: BinaryDatasetTypeProperties;
	}

	// handcrafted
	export interface AvroDataset extends DatasetV2 {
		typeProperties: AvroDatasetTypeProperties;
	}

	// handcrafted
	export interface OrcDataset extends DatasetV2 {
		typeProperties: OrcDatasetTypeProperties;
	}

	// handcrafted
	export interface BinaryDatasetTypeProperties {
		location?: AzureBlobLocation | ADLSGen2Location | ADLSGen1Location;
		compression?: {
			type?: any;
			level?: any;
		};
	}

	// handcrafted
	export interface AvroDatasetTypeProperties {
		location?: AzureBlobLocation | ADLSGen2Location | ADLSGen1Location;
		avroCompressionCodec?: any;
		avroCompressionLevel?: any;
	}

	// handcrafted
	export interface OrcDatasetTypeProperties {
		location?: AzureBlobLocation | ADLSGen2Location | ADLSGen1Location;
		orcCompressionCodec?: any;
		enablePhysicalSchema?: boolean;
	}

	// handcrafted
	export interface ParquetDataset extends DatasetV2 {
		typeProperties: ParquetDatasetTypeProperties;
	}

	// handcrafted
	export interface DeltaDataset extends DatasetV2 {
		typeProperties: DeltaDatasetTypeProperties;
	}
	// handcrafted
	export interface JsonDatasetTypeProperties {
		location?: AzureBlobLocation | ADLSGen2Location | ADLSGen1Location;
		hierarchicalSchema?: { [key: string]: any };
		compression?: any;
		encodingName?: any;
	}

	// handcrafted
	export interface JsonDataset extends DatasetV2 {
		typeProperties: JsonDatasetTypeProperties;
	}

	// handcrafted
	export interface XmlDatasetTypeProperties {
		location?: AzureBlobLocation | ADLSGen2Location | ADLSGen1Location;
		compression?: any;
		encodingName?: any;
		nullValue?: any;
	}

	// handcrafted
	export interface XmlDataset extends DatasetV2 {
		typeProperties: XmlDatasetTypeProperties;
	}

	// handcrafted
	export interface ParquetSink extends CopySink {
		copyBehavior?: string;
		storeSettings?: StoreWriteSettings; // handcrafted
		formatSettings?: ParquetFormatWriteSettings; // handcrafted
	}

	// handcrafted
	export interface DeltaSink extends CopySink {
		copyBehavior?: string;
		storeSettings?: StoreWriteSettings; // handcrafted
		formatSettings?: FormatWriteSettings; // handcrafted
	}
	// handcrafted
	export interface DeltaSource extends CopySource {
		storeSettings?: StoreReadSettings; // handcrafted
		formatSettings?: FormatReadSettings; // handcrafted
	}
	// handcrafted
	export interface IcebergSink extends CopySink {
		storeSettings?: StoreWriteSettings; // handcrafted
		formatSettings?: FormatWriteSettings; // handcrafted
	}

	// handcrafted
	export interface AvroSink extends CopySink {
		storeSettings?: StoreWriteSettings; // handcrafted
		formatSettings?: FormatWriteSettings; // handcrafted
	}

	// handcrafted
	export interface OrcSink extends CopySink {
		storeSettings?: StoreWriteSettings; // handcrafted
		formatSettings?: FormatWriteSettings; // handcrafted
	}

	// handcrafted
	export interface BinarySink extends CopySink {
		copyBehavior?: string;
		storeSettings?: StoreWriteSettings; // handcrafted
	}

	// handcrafted
	export interface ParquetSource extends CopySource {
		readerWildcardPath?: string;
		recursive?: boolean;

		storeSettings?: StoreReadSettings; // handcrafted
		formatSettings?: FormatReadSettings; // handcrafted
	}

	// handcrafted
	export interface BinarySource extends CopySource {
		storeSettings?: StoreReadSettings; // handcrafted
		formatSettings?: FormatReadSettings; // handcrafted
	}

	// handcrafted
	export interface ExcelSource extends CopySource {
		storeSettings?: StoreReadSettings; // handcrafted
	}

	// handcrafted
	export interface SqlServerSource extends SqlSource {
	}

	// handcrafted
	export interface AzureSqlSource extends SqlSource {
	}

	// handcrafted
	export interface SqlMISource extends SqlSource {
	}

	// handcrafted
	export interface ExcelDatasetTypeProperties {
		sheetName?: any;
		sheetIndex?: any;
		location?: AzureBlobLocation | ADLSGen2Location | ADLSGen1Location;
		firstRowAsHeader?: any;
		range?: any;
		compression?: any;
		nullValue?: any;
	}

	// handcrafted
	export interface ExcelDataset extends DatasetV2 {
		typeProperties: ExcelDatasetTypeProperties;
	}

	// handcrafted
	export interface DelimitedTextDatasetTypeProperties {
		location?: AzureBlobLocation | ADLSGen2Location | ADLSGen1Location;
		columnDelimiter?: any;
		rowDelimiter?: any;
		compressionCodec?: any;
		compressionLevel?: any;
		quoteChar?: any;
		escapeChar?: any;
		encodingName?: any;
		firstRowAsHeader?: any;
		nullValue?: any;
		multiline?: any;
	}

	// handcrafted
	export interface DelimitedTextDataset extends DatasetV2 {
		typeProperties: DelimitedTextDatasetTypeProperties;
	}

	// handcrafted
	export interface DelimitedTextSink extends CopySink {
		copyBehavior?: string;
		quoteAll?: boolean;
		storeSettings?: StoreWriteSettings; // handcrafted
		formatSettings?: FormatWriteSettings; // handcrafted
	}

	// handcrafted
	export interface JsonSink extends CopySink {
		pattern?: string;
		storeSettings?: StoreWriteSettings; // handcrafted
		formatSettings?: FormatWriteSettings; // handcrafted
	}

	// handcrafted
	export interface DelimitedTextSource extends CopySource {
		readerWildcardPath?: string;
		recursive?: boolean;
		structure?: any;
		storeSettings?: StoreReadSettings; // handcrafted
		formatSettings?: FormatReadSettings; // handcrafted
	}

	// handcrafted
	export interface IcebergTableSource extends CopySource {
		readerWildcardPath?: string;
		recursive?: boolean;
		structure?: any;
		storeSettings?: StoreReadSettings; // handcrafted
		formatSettings?: FormatReadSettings; // handcrafted
	}

	// handcrafted
	export interface JsonSource extends CopySource {
		storeSettings?: StoreReadSettings; // handcrafted
		formatSettings?: FormatReadSettings; // handcrafted
	}

	// handcrafted
	export interface XmlSource extends CopySource {
		storeSettings?: StoreReadSettings; // handcrafted
		formatSettings?: FormatReadSettings; // handcrafted
	}

	// handcrafted
	export interface AvroSource extends CopySource {
		storeSettings?: StoreReadSettings; // handcrafted
		formatSettings?: FormatReadSettings; // handcrafted
	}

	// handcrafted
	export interface OrcSource extends CopySource {
		storeSettings?: StoreReadSettings; // handcrafted
		formatSettings?: FormatReadSettings; // handcrafted
	}


	// handcrafted
	export interface AzureSqlTableDatasetV2TypeProperties {
		tableName: any;
	}

	// handcrafted
	export interface AzureSqlTableDatasetV2 extends DatasetV2 {
		typeProperties: AzureSqlTableDatasetV2TypeProperties;
	}

	// handcrafted
	export interface AzureSqlDWDatasetV2TypeProperties {
		tableName: any;
	}

	// handcrafted
	export interface AzureSqlDWDatasetV2 extends DatasetV2 {
		typeProperties: AzureSqlDWDatasetV2TypeProperties;
	}

	// handcrafted
	export interface DatasetV2Resource extends SharedModel.SubResource {
		properties: DatasetV2;
	}

	// handcrafted
	export interface MappingArtifact {
		name: string;
		properties: MappingArtifactResource;
	}

	// handcrafted
	export interface MappingArtifactResource {
		PipelineName: string;
		Sources?: MappingArtifactSourceSinkResource[];
		Sinks?: MappingArtifactSourceSinkResource[];
		SinkEntityRelationships: MappingArtifactEntityRelationship[];
		MappingDirectives?: MappingArtifactMappingDirectiveResource[];
	}

	// handcrafted
	export interface AdfDataMapperArtifact {
		name: string;
		properties: AdfDataMapperArtifactProperties;
	}

	// handcrafted
	export interface AdfDataMapperArtifactProperties {
		description?: string;
		SourceConnectionsInfo: MappingSourceConnectionInfoResource[];
		TargetConnectionsInfo: MappingTargetConnectionInfoResource[];
		id?: string;

		Policy: MapperPolicyResource;
		status?: string;
		allowVNetOverride?: boolean;
		Compute?: MapperComputeResource;
		folder?: IFolder;
		typeProperties?: AdfDataMapperTypeProperties;
	}

	// handcrafted
	export interface AdfDataMapperTypeProperties {
		staging?: DataFlowStagingLinkedServiceReference;
		linkedServiceType?: string;
	}

	// handcrafted
	export interface MapperComputeResource {
		coreCount: number;
		computeType: string;
	}

	// handcrafted
	export interface MappingConnectionInfoResource {
		Connection: AdfDataMapperConnectionResource;
	}

	// handcrafted
	export interface MapperPolicyResource {
		recurrence: MapperPolicyRecurrenceResource;
		mode: string;
	}

	// handcrafted
	export interface MapperPolicyRecurrenceResource {
		frequency: string;
		interval: number;
	}

	// handcrafted
	export interface MappingSourceConnectionInfoResource extends MappingConnectionInfoResource {
		SourceEntities: MapperTableResource[];
	}

	// handcrafted
	export interface MappingTargetConnectionInfoResource extends MappingConnectionInfoResource {
		TargetEntities: MapperTableResource[];
		DataMapperMappings: AdfDataMapperMappingResource[];
		Relationships: MappingArtifactEntityRelationship[];
	}

	// handcrafted
	export interface AdfDataMapperConnectionResource {
		type: MapperConnectionType;
		linkedService?: LinkedServiceReference;
		linkedServiceType?: string;
		isInlineDataset: boolean;
		dataset?: DatasetReference;
		commonDslConnectorProperties?: MappingArtifactSourceSinkDslConnectorPropertiesResource[];
	}

	// handcrafted
	export interface MapperTableResource {
		name: string;
		properties: MapperTablePropertiesResource;
	}

	// handcrafted
	export interface MapperTablePropertiesResource {
		schema: MappingArtifactSourceSinkSchemaPropertiesResource[];
		dslConnectorProperties: MappingArtifactSourceSinkDslConnectorPropertiesResource[];
	}

	// handcrafted
	export interface MapperTableAdditionalPropertiesResource {
		name: string;
		value: string;
	}

	// handcrafted
	export interface MappingArtifactSourceSinkResource {
		name: string;
		properties: MappingArtifactSourceSinkPropertiesResource;
	}

	// handcrafted
	export interface MappingArtifactEntityRelationship {
		fromEntity: string;
		toEntity: string;
		attributePairs: MappingArtifactAttributePairs[];
	}

	// handcrafted
	export interface MappingArtifactAttributePairs {
		fromAttribute: string;
		toAttribute: string;
	}

	// handcrafted
	export interface MappingArtifactSourceSinkPropertiesResource {
		schema: MappingArtifactSourceSinkSchemaPropertiesResource[];
		dslConnectorProperties: MappingArtifactSourceSinkDslConnectorPropertiesResource[];
		linkedService?: LinkedServiceReference;
		dataset?: DatasetReference;
	}

	// handcrafted
	export interface MappingArtifactSourceSinkSchemaPropertiesResource {
		name: string;
		dataType: string;
	}

	// handcrafted
	export interface MappingArtifactSourceSinkDslConnectorPropertiesResource {
		name: string;
		value: any;
	}

	// handcrafted
	export interface MappingArtifactMappingDirectiveResource {
		sourceEntityReferences: MappingArtifactMappingDirectiveEntityReferenceResource[];
		targetEntityReference: MappingArtifactMappingDirectiveEntityReferenceResource;
		sourceDenormalizeInfo: MappingArtifactMappingDirectiveDenormalizationInfo;
		attributeMappingInfo: MappingArtifactMappingDirectiveAttributeMappingInfoResource;
	}

	// handcrafted
	export interface AdfDataMapperMappingResource {
		targetEntityName: string;
		sourceEntityName: string;
		sourceConnectionReference: MapperConnectionReferenceResource;
		sourceDenormalizeInfo?: MapperTableDenormalizeInfoResource;
		attributeMappingInfo: AdfDataMapperTableAttributeMappingInfoResource;
	}

	// handcrafted
	export interface MapperTableDenormalizeInfoResource {
		entities: MapperTableDenormalizeEntityResource[];
	}

	// handcrafted
	export interface MapperTableDenormalizeEntityResource {
		joinPairs: MapperJoinPairResource[];
		toEntity: string;
		toEntityConnectionReference: MapperConnectionReferenceResource;
	}

	// handcrafted
	export interface MapperJoinPairResource {
		fromAttribute: AdfDataMapperAttributeReferenceResource;
		toAttribute: string;
		operator?: string;
		expression?: string;
	}

	// handcrafted
	export interface AdfDataMapperTableAttributeMappingInfoResource {
		attributeMappings: AdfDataMapperTableAttributeMappingResource[];
	}

	// handcrafted
	export interface AdfDataMapperTableAttributeMappingResource {
		type: string;
		name: string;
		attributeReference?: AdfDataMapperAttributeReferenceResource
		attributeReferences?: AdfDataMapperAttributeReferenceResource[],
		functionName?: string,
		expression?: string;
		alternateKeys?: string[];
		lookupFrom?: any;
		unpivotInfo?: any;
	}


	// handcrafted
	export interface MappingArtifactMappingDirectiveDenormalizationInfo {
		entities: MappingArtifactMappingDirectiveDenormalizationEntity[];
	}

	// handcrafted
	export interface MappingArtifactMappingDirectiveDenormalizationEntity {
		toEntity: string;
		joinPairs: MappingArtifactJoinPair[];
	}

	// handcrafted
	export interface MappingArtifactJoinPair {
		fromAttribute: MappingArtifactMappingDirectiveEntityReferenceResource;
		toAttribute: string;
		operator?: string;
		expression?: string;
	}

	// handcrafted
	export interface AdfDataMapperAttributeReferenceResource extends MappingArtifactMappingDirectiveAttributeReferenceResource {
		entityConnectionReference: MapperConnectionReferenceResource;
	}

	// handcrafted
	export interface MapperConnectionReferenceResource {
		connectionName: string;
		type: MapperConnectionType;
	}

	// handcrafted
	export interface MappingArtifactMappingDirectiveEntityReferenceResource {
		name: string;
		entity?: string;
	}

	// handcrafted
	export interface MappingArtifactMappingDirectiveAttributeMappingInfoResource {
		attributeMappings: MappingArtifactMappingDirectiveAttributeMappingResource[];
	}

	// handcrafted
	export interface MappingArtifactMappingDirectiveAttributeMappingResource {
		type: string;
		name: string;
		attributeReference?: MappingArtifactMappingDirectiveAttributeReferenceResource
		attributeReferences?: MappingArtifactMappingDirectiveAttributeReferenceResource[],
		expression?: string;
		alternateKeys?: string[];
		lookupFrom?: any;
		unpivotInfo?: any;
	}

	// handcrafted
	export interface MappingArtifactMappingDirectiveAttributeReferenceResource {
		name: string;
		entity: string;
	}

	/**
	* @class
	* Initializes a new instance of the IRActivityRun class.
	* @constructor
	* Information about an IRActivityRun in IR usage.
	*
	* @member {string} [PipelineName] The name of the pipeline.
	* @member {string} [ActivityName] The name of the activity.
	* @member {string} [ActivityType] The type of the activity.
	* @member {string} [ActivityRunId] The id of the activity run.
	* @member {string} [TaskRuns] The details about activity runs.
	* @member {object} [IntegrationRuntime] The self host IntegrationRuntime for this the activity run.
	* @member {object} [LinkedIntegrationRuntime] The linked IntegrationRuntime for this the activity run.
	*/
	export interface IRActivityRun {
		readonly ActivityName?: string;
		readonly ActivityType?: string;
		readonly ActivityRunId?: string;
		readonly PipelineName?: string;
		readonly IntegrationRuntime?: { DataFactoryName?: string, [property: string]: any; };
		readonly LinkedIntegrationRuntime?: { DataFactoryName?: string, [property: string]: any; };
		readonly TaskRuns?: any[];
		/**
		 * @property Describes unknown properties. The value of an unknown property
		 * can be of "any" type.
		 */
		[property: string]: any;
	}

	/**
	* @class
	* Initializes a new instance of the ActivityRuns For SHIR usage class.
	* @constructor
	*  Activity Runs For SHIR usage.
	*
	*/
	export interface IRActivityUsageListResponse {
		value: IRActivityRun[]; // handcrafted
	}

	export interface CopyJobResource extends SubResource {
		objectId: string;
		properties: {
			jobMode: string;
            fullLoadBehavior?: 'Truncate';
			useSharedCompute?: boolean;
			dataFactoryId?: string;
			source: {
				// dataset type
				type: string;
				// dataset version (e.g. AzurePostgreSql v2 uses '2.0')
				version?: string;
				connectionSettings:
				| LinkedService
				| {
					type: string;
					typeProperties?: Record<string, any>;
					linkedServiceName?: SharedModel.LinkedServiceReference;
					connectionProperties?: {
						[key: string]: string;
					};
				};
				additionalSettings?: {
					storageIntegration?: string; // For Snowflake
					dbNameForStagingStreams?: string; // For Snowflake shared (read-only) databases
					schemaNameForStagingStreams?: string; // For Snowflake shared (read-only) databases
					protocolSettings?: {
						requestTimeout?: any;
						[property: string]: unknown;
					}
				};
				genericChangeDataProperties?: {
					rowMarkerColumn?: string;
					rowOrderColumn?: string;
					initialFolderName?: string;
					deltaFolderName?: string;
					rowMarkerValues?: {
						insert?: string;
						update_postimage?: string;
						update_preimage?: string;
						delete?: string;
					};
				};
			};
			destination: {
				// dataset type
				type: string;
				// dataset version (e.g. AzurePostgreSql v2 uses '2.0')
				version?: string;
				connectionSettings:
				| LinkedService
				| {
					type: string;
					typeProperties?: Record<string, any>;
					linkedServiceName?: SharedModel.LinkedServiceReference;
					connectionProperties?: {
						[key: string]: string;
					};
				};
				additionalSettings?: {
					storageIntegration?: string; // For Snowflake
					protocolSettings?: {
						requestMethod?: any;
						[property: string]: unknown;
					}
				};
			};
			stagingSettings?: StagingSettings;
			defaultActivityProperties?: {
				destination?: {
					schemaNameForStagingTables?: string;
				};
			};
			policy: {
				timeout: string;
				retry?: number;
			};
		};
		activities?: Array<{
			readonly id: string;
			properties: {
				source: {
					datasetSettings: Dataset['typeProperties'];
					storeSettings?: Omit<StoreReadSettings, 'type'> & { additionalHeaders?: Record<string, string> };
					formatSettings?: Omit<FormatReadSettings, 'type'>;
						changeReadStrategy?: 'Batch';
					changeDataSettings?: {
						readMethod: 'SnapshotPlusIncremental';
						captureInstanceName?: string;
						nullWatermarkBehavior?: 'Skip' | 'IncludeOnFullLoad';
						columns?: CopyJobIncrementalColumn[];
					};
					exportSettings?: {};
					protocolSettings?: {
						requestTimeout?: any;
						[property: string]: unknown;
					};
                    /**
                     * @deprecated use `partitionSettings`
                     */
                    partitionOption?: 'None'
                    partitionSettings?: {
                        partitionOption?: 'None' | 'Auto';
                    };

					[sourceProperty: string]: unknown;
				};
				destination: {
					datasetSettings: Dataset['typeProperties'];
					storeSettings?: Omit<StoreWriteSettings, 'type'>;
					formatSettings?: Omit<FormatWriteSettings, 'type'>;
					upsertSettings?: WriteBehaviorUpsertOptions;
					/**
					 * @deprecated use `writeBehavior`
					 */
					tableActionOption?: string;
					writeBehavior?: 'Append' | 'Overwrite' | 'Upsert' | 'Merge' | 'Replace' | string;
					tableOption?: 'autoCreate';
					importSettings?: {};
					protocolSettings?: {
						requestMethod?: any;
						[property: string]: unknown;
					}
                    partitionOption?: 'PartitionByKey';
                    partitionNameList?: string[];
					enableChangeDataFeed?: boolean;

					[destinationProperty: string]: unknown;
				};
				preserve?: string[];
				enableStaging?: boolean;
				stagingSettings?: StagingSettings;
				translator?: CopyTranslator;
				auditColumns?: any;
				typeConversionSettings?: {
					typeConversion: {
						allowDataTruncation: boolean;
						treatBooleanAsNumber: boolean;
						lowPrecisionDatatypeMode?: boolean;
					}
				};
				columnFlattenSettings?: {
					expandArrayIntoMultipleRows: boolean;
					flattenNestedProperties: boolean;
					flattenColumnDelimiter?: string;
				};
			};
		}>;
        libraryVariables?: Record<string, {
            variableName: string;
            libraryName: string;
        }>;
	}
}
